import React, {useState, useEffect, useRef, useMemo} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BackOfficeMenu from '../components/BackOfficeMenu';
import GestionFinanciere from '../GestionFinanciere/GestionFinanciere';
import {usePermissions} from '../../utils/usePermissions';
import SensitiveDeleteModal from '../../components/SensitiveDeleteModal';
import ScenariosProsolElec from './ScenariosProsolElec';
import {cancelDossierWithConfirmation, showDossierDeleteBlockedNotice} from './dossierCancellation';
import ReclamationFormModal from '../reclamations/ReclamationFormModal';
import ReclamationDetailModal from '../reclamations/ReclamationDetailModal';
import { Check, Package, Hash, Calendar, DollarSign, CheckCircle, Trash2, Edit, Eye, MoreVertical, PlusCircle, XCircle, RotateCcw, PackageCheck, Search, AlertTriangle, FileText, CreditCard, Tag, Lock, Info, Sliders, Clock, AlertCircle, ArrowRight, ShieldCheck, Wrench } from 'lucide-react';
import { getStegOptionsList } from '../../utils/stegOptions';
import { PAYMENT_MODES, getPaymentModeLabel } from '../../utils/paymentModes';
import './DetailDossier.css';
import './DetailDossier_MonitoringV3.css';

// 🛠️ CONFIGURATION CENTRALISÉE : MODE MAINTENANCE ÉCHEANCIER & PAIEMENTS (Piloté par le backend SystemSettings)


const CATEGORIES_STEG = [
  'Région de Tunis',
  'Région Nord',
  'Région Nord-Ouest',
  'Région Centre',
  'Région Sfax',
  'Région Sud-Ouest',
  'Région Sud',
];

const ACTION_CONFIG = {
  CREATE_DOSSIER:  { label: 'Création dossier',     color: '#10b981', icon: '📁', bg: '#d1fae5' },
  UPDATE_DOSSIER:  { label: 'Modif. dossier',       color: '#3b82f6', icon: '✏️', bg: '#dbeafe' },
  DELETE_DOSSIER:  { label: 'Suppression dossier',  color: '#ef4444', icon: '🗑️', bg: '#fee2e2' },
  CHANGE_ETAT_DOSSIER: { label: 'Changement état', color: '#f59e0b', icon: '🔄', bg: '#fef3c7' },
  ADD_NOTE_DOSSIER: { label: 'Note dossier',        color: '#8b5cf6', icon: '📝', bg: '#ede9fe' },
  ADD_PAYMENT:     { label: 'Ajout paiement',       color: '#10b981', icon: '💰', bg: '#d1fae5' },
  UPDATE_PAYMENT:  { label: 'Modif. paiement',      color: '#3b82f6', icon: '💳', bg: '#dbeafe' },
  DELETE_PAYMENT:  { label: 'Suppression paiement', color: '#ef4444', icon: '💸', bg: '#fee2e2' },
  VALIDATE_PAYMENT:{ label: 'Validation paiement',  color: '#10b981', icon: '✅', bg: '#d1fae5' },
  OTHER:           { label: 'Autre',                 color: '#6b7280', icon: '•',  bg: '#f3f4f6' },
};
const getActionConfig = (action) => ACTION_CONFIG[action] || {
  ...ACTION_CONFIG.OTHER,
  label: action || 'Action systeme'
};
const ROLE_COLORS = {
  admin: '#1e3a5f', moderateur: '#667eea', commercial: '#10b981',
  installateur: '#f59e0b', client: '#38b2ac', entreprise: '#9f7aea',
};
const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
};
const validerScenarioPuissanceMontant = (puissanceValue, montantTotalValue) => {
  return null;
};
const computeSyntheseFinanciere = (dossier) => {
  if (!dossier) {
    return {
      montantTotalDossier: 0,
      totalDejaPaye: 0,
      totalConfigure: 0,
      totalRestantAConfigurer: 0,
      resteAPayerReel: 0,
      montantLibre: 0,
      montantSteg: 0,
      stegPaye: false
    };
  }

  if (dossier.syntheseFinanciere && typeof dossier.syntheseFinanciere.totalConfigure === 'number') {
    return dossier.syntheseFinanciere;
  }

  const montantTotalDossier = Math.max(0, Number(dossier.montantTotalPaiement || 0));
  const montantSteg = Math.max(0, Number(dossier.montantSteg || 0));
  const stegPaye = !!dossier.stegPaye;
  const stegNonPayeDeduire = stegPaye ? 0 : montantSteg;

  const autofinancement = dossier.autofinancement || {};
  const totalComptantPaye = Math.max(0, Number(autofinancement.montantComptantPaye || 0));
  const totalCreditPaye = Math.max(0, Number(autofinancement.montantPaye || 0));
  const totalStegPaye = stegPaye ? montantSteg : 0;

  const paiementsArray = Array.isArray(dossier.paiements)
    ? dossier.paiements.filter(p => p && p.statut === 'paye' && p.sansImpactSolde !== true && p.categorie !== 'steg')
    : (Array.isArray(autofinancement.historiquePaiements)
      ? autofinancement.historiquePaiements.filter(p => p && p.statut === 'paye' && p.sansImpactSolde !== true && p.categorie !== 'steg')
      : []);

  const totalPaiementsArray = paiementsArray.reduce((sum, p) => sum + Math.max(0, Number(p.montant) || 0), 0);
  const totalDirectPayes = Math.max(totalComptantPaye + totalCreditPaye, totalPaiementsArray);
  const totalDejaPaye = Math.max(0, Number((totalDirectPayes + totalStegPaye).toFixed(2)));

  const resteAPayerReel = Math.max(0, Number((montantTotalDossier - totalDejaPaye - stegNonPayeDeduire).toFixed(2)));

  const echeancier = Array.isArray(autofinancement.echeancier) ? autofinancement.echeancier : [];
  let echeancesActivesRestantes = 0;
  let hasActiveEcheances = false;

  echeancier.forEach(e => {
    if (e && e.statut !== 'paye' && e.statut !== 'annule' && e.statut !== 'supprime') {
      hasActiveEcheances = true;
      const remaining = e.montantRestant !== undefined && e.montantRestant !== null
        ? Math.max(0, Number(e.montantRestant) || 0)
        : Math.max(0, (Number(e.montant) || 0) - (Number(e.montantPaye) || 0));
      echeancesActivesRestantes += remaining;
    }
  });

  // Total Configuré = somme des montants restant réellement affectés aux échéances actives non annulées
  let totalConfigure = echeancesActivesRestantes;

  totalConfigure = Math.min(resteAPayerReel, Math.max(0, Number(totalConfigure.toFixed(2))));
  const totalRestantAConfigurer = Math.max(0, Number((resteAPayerReel - totalConfigure).toFixed(2)));

  return {
    montantTotalDossier,
    totalDejaPaye,
    totalConfigure,
    totalRestantAConfigurer,
    resteAPayerReel,
    montantLibre: totalRestantAConfigurer,
    montantSteg,
    stegPaye
  };
};

const DetailDossier = ({clientId, dossierId, onNavigate, onLogout}) => {
  const {hasPermission} = usePermissions();
  const paymentFormRef = useRef(null);
  const [client, setClient] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [activeTab, setActiveTab] = useState('infos');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [project, setProject] = useState(null);

  // États pour les produits
  const [produits, setProduits] = useState([]);
  const [showInlineAddForm, setShowInlineAddForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCatalogProduct, setSelectedCatalogProduct] = useState(null);
  const [showEditProduitModal, setShowEditProduitModal] = useState(false);
  const [showDetailProduitModal, setShowDetailProduitModal] = useState(false);
  const [selectedProduit, setSelectedProduit] = useState(null);
  const [allProduits, setAllProduits] = useState([]);
  const [allProduitsLoading, setAllProduitsLoading] = useState(false);
  const [palettes, setPalettes] = useState([]);
  const [numerosSerieDisponibles, setNumerosSerieDisponibles] = useState([]);
  const [numerosSerieSelectionnes, setNumerosSerieSelectionnes] = useState([]);

  // États pour la gestion des packs
  const [assignmentMode, setAssignmentMode] = useState('single'); // 'single' ou 'pack'
  const [packs, setPacks] = useState([]);
  const [selectedPackId, setSelectedPackId] = useState('');
  const [packStockWarnings, setPackStockWarnings] = useState([]);
  const [isCheckingPackStock, setIsCheckingPackStock] = useState(false);
  const [isApplyingPack, setIsApplyingPack] = useState(false);

  // États pour le transfert de produits
  const [selectedProduitsIds, setSelectedProduitsIds] = useState([]);
  const [installedProductSearch, setInstalledProductSearch] = useState('');
  const [installedProductPage, setInstalledProductPage] = useState(1);
  const [availabilitySearch, setAvailabilitySearch] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [availabilityPage, setAvailabilityPage] = useState(1);
  
  // États pour le Bon de Livraison direct depuis le Dossier
  const [showBLForm, setShowBLForm] = useState(false);
  const [vehiculesBL, setVehiculesBL] = useState([]);
  const [equipesBL, setEquipesBL] = useState([]);
  const [vehiculeBLId, setVehiculeBLId] = useState('');
  const [chauffeurBLKey, setChauffeurBLKey] = useState('');
  const [dateBL, setDateBL] = useState(new Date().toISOString().split('T')[0]);
  const [loadingOptionsBL, setLoadingOptionsBL] = useState(false);
  const [isGeneratingBL, setIsGeneratingBL] = useState(false);
  const [showTransfertModal, setShowTransfertModal] = useState(false);
  const [refStegRecherche, setRefStegRecherche] = useState('');
  const [clientDestination, setClientDestination] = useState(null);

  // États pour l'audit
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);

  // États pour l'historique de paiement
  const [showInlinePaymentForm, setShowInlinePaymentForm] = useState(false);
  const [showAddPaiementModal, setShowAddPaiementModal] = useState(false);
  const [showEditPaiementModal, setShowEditPaiementModal] = useState(false);
  const [showDeletePaiementModal, setShowDeletePaiementModal] = useState(false);
  const [paiementSubmitting, setPaiementSubmitting] = useState(false);
  const [editingPaiementId, setEditingPaiementId] = useState(null);
  const [paiementToDelete, setPaiementToDelete] = useState(null);
  const [deletePaiementConfirmText, setDeletePaiementConfirmText] = useState('');
  const [paiementFormData, setPaiementFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    montant: 0,
    categorie: 'credit',
    methode: 'especes',
    reference: '',
    statut: 'paye',
    notes: ''
  });
  const [paiementEditFormData, setPaiementEditFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    montant: 0,
    categorie: 'credit',
    methode: 'especes',
    reference: '',
    statut: 'paye',
    notes: ''
  });

  // États pour les documents et archives
  const [showAddArchiveModal, setShowAddArchiveModal] = useState(false);
  const [editingArchive, setEditingArchive] = useState(null);
  const [archiveFile, setArchiveFile] = useState(null);
  const [documentsFinanciers, setDocumentsFinanciers] = useState([]);
  const [archiveFormData, setArchiveFormData] = useState({
    type: 'document',
    category: 'Autre',
    description: ''
  });
  const [docSearchTerm, setDocSearchTerm] = useState('');
  const [docFilterCategory, setDocFilterCategory] = useState('TOUS');
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [previewDocModal, setPreviewDocModal] = useState(null);
  const [docTargetIsArchived, setDocTargetIsArchived] = useState(false);

  // États pour les réclamations dossier
  const [dossierReclamations, setDossierReclamations] = useState([]);
  const [showAddReclamationModal, setShowAddReclamationModal] = useState(false);
  const [selectedReclamationDetail, setSelectedReclamationDetail] = useState(null);

  const syntheseFinanciere = computeSyntheseFinanciere(dossier);

  // États pour l'échéancier crédit
  const [showEcheancierModal, setShowEcheancierModal] = useState(false);
  const [showConfigEcheancierModal, setShowConfigEcheancierModal] = useState(false);
  const [showEditEcheanceModal, setShowEditEcheanceModal] = useState(false);
  const [editingEcheanceIndex, setEditingEcheanceIndex] = useState(null);
  const generateAutoEcheancesList = (nombreMoisInput, dateDebutInput, frequenceInput, totalAmountInput, existingCustom = []) => {
    const n = Math.max(1, parseInt(nombreMoisInput || 1, 10));
    const total = Math.max(0, Number(totalAmountInput) || 0);
    const freqStep = frequenceInput === 'custom' ? 1 : Math.max(1, parseInt(frequenceInput || 1, 10));
    const baseMonthly = Math.round((total / n) * 1000) / 1000;
    const baseDate = dateDebutInput ? new Date(dateDebutInput) : new Date();

    const list = [];
    for (let i = 0; i < n; i++) {
      const d = new Date(baseDate);
      if (!isNaN(d.getTime())) {
        d.setMonth(baseDate.getMonth() + (i * freqStep));
      }
      const dateStr = !isNaN(d.getTime()) ? d.toISOString().split('T')[0] : '';
      const customMontant = (i === n - 1)
        ? Math.round((total - (baseMonthly * (n - 1))) * 1000) / 1000
        : baseMonthly;
      const modePaiement = (Array.isArray(existingCustom) && existingCustom[i] && existingCustom[i].modePaiement)
        ? existingCustom[i].modePaiement
        : 'especes';
      list.push({ num: i + 1, dateEcheance: dateStr, montant: customMontant, modePaiement });
    }
    return list;
  };

  const [reprogrammMode, setReprogrammMode] = useState('all'); // 'all' | 'custom'
  const [selectedEcheancesToCancel, setSelectedEcheancesToCancel] = useState([]);
  const [annulerAcompteOption, setAnnulerAcompteOption] = useState(false);
  const [echeancierFormData, setEcheancierFormData] = useState({
    isReprogrammer: false,
    montantRestant: 0,
    montantTotal: 0,
    nombreMois: 12,
    dateDebutCredit: new Date().toISOString().split('T')[0],
    frequenceMois: '1',
    echeancesCustom: [],
    raisonReprogrammation: ''
  });
  const [echeanceEditFormData, setEcheanceEditFormData] = useState({
    dateEcheance: new Date().toISOString().split('T')[0],
    montant: 0,
    statut: 'en_attente',
    datePaiement: '',
    notes: ''
  });
  const [echeanceAPayerIndex, setEcheanceAPayerIndex] = useState(null);
  const [platformControl, setPlatformControl] = useState(null);
  const [userExceptionalAuths, setUserExceptionalAuths] = useState([]);

  const isPaiementEcheanceMaintenanceActive = useMemo(() => {
    if (!platformControl) return false;
    const isMaintenanceOn = Boolean(platformControl.paiementEcheanceMaintenance || platformControl.maintenanceMode);
    if (!isMaintenanceOn) return false;

    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const u = JSON.parse(userStr);
        const userRole = u.typeUser || u.role || '';
        if (userRole === 'admin' && platformControl.allowAdminBypass !== false) {
          return false;
        }
      }
    } catch (e) {}

    if (Array.isArray(userExceptionalAuths)) {
      const matchingAuth = userExceptionalAuths.find(auth => auth && auth.isActive && auth.bypassMaintenance);
      if (matchingAuth) {
        return false;
      }
    }

    return true;
  }, [platformControl, userExceptionalAuths]);

  const [produitFormData, setProduitFormData] = useState({
    produitId: '',
    paletteId: '',
    quantite: 1,
    prixUnitaire: 0,
    prixProduit: 0,
    TVA: 19,
    tauxGain: 0,
    prixTotal: 0,
    dateInstallation: '',
    garantieDateDebut: '',
    garantieDateFin: '',
    garantieDureeAnnees: 5,
    numeroSerie: '',
    numerosSerie: [],
    statut: 'commande',
    specifications: '',
    notes: ''
  });

  // \u00C9tats pour modifier le dossier
  const [isEditingDossier, setIsEditingDossier] = useState(false);
  const [showQuickEditDate, setShowQuickEditDate] = useState(null);
  const [quickDateValue, setQuickDateValue] = useState('');
  const [showQuickEditField, setShowQuickEditField] = useState(null);
  const [quickFieldValue, setQuickFieldValue] = useState('');
  // États recherche dynamique commercial
  const [commercialSearch, setCommercialSearch] = useState('');
  const [commercialSearchForm, setCommercialSearchForm] = useState('');
  // ── Modal Paiement STEG ──────────────────────────────────────────────────
  const [showStegPaymentModal, setShowStegPaymentModal] = useState(false);
  const [stegPaymentStep, setStegPaymentStep] = useState('form'); // 'form' | 'confirm'
  const [stegPaymentSubmitting, setStegPaymentSubmitting] = useState(false);
  const [stegPaymentForm, setStegPaymentForm] = useState({
    datePaiement: '',          // date réelle du paiement (saisie par l'utilisateur)
    modePaiement: 'virement',  // mode de règlement
    reference: '',             // numéro de virement / chèque…
    notes: ''
  });

  // État pour la notification STEG
  const [notificationSteg, setNotificationSteg] = useState(null); // { type: 'success'|'error', message: '' }
  // États pour la modale de notification STEG
  const [showStegModal, setShowStegModal] = useState(false);
  const [selectedArchiveIds, setSelectedArchiveIds] = useState([]);
  const [stegSending, setStegSending] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailTemplate, setEmailTemplate] = useState('depot');
  const [stegModalTab, setStegModalTab] = useState('compose');
  const [stegDebug, setStegDebug] = useState(null);
  const [stegLocalError, setStegLocalError] = useState(null);

  // Listes d'entités pour les selects
  const [allDistricts, setAllDistricts] = useState([]);
  const [allCommerciaux, setAllCommerciaux] = useState([]);
  const [allInstallateurs, setAllInstallateurs] = useState([]);

  // \u00C9tats pour la r\u00E9affectation de dossier
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [targetCIN, setTargetCIN] = useState('');
  const [reassignClientTarget, setReassignClientTarget] = useState(null);
  const [isReassigning, setIsReassigning] = useState(false);
  const [newClientNote, setNewClientNote] = useState('');
  const [newDossierNote, setNewDossierNote] = useState('');
  const [newNotePriorite, setNewNotePriorite] = useState('normale');
  const [newNoteAffecteA, setNewNoteAffecteA] = useState('');
  const [allAdminsModerateurs, setAllAdminsModerateurs] = useState([]);
  const [noteReponseText, setNoteReponseText] = useState({});
  const [expandedNote, setExpandedNote] = useState(null);

  const [searchLoading, setSearchLoading] = useState(false);
  const [dossierForm, setDossierForm] = useState({
    pompage: false,
    referenceSteg: '',
    puissance: 0,
    district: '',
    dateDF: '',
    moisAnnee: '',
    statut: 'nouveau',
    typePaiement: 'comptant',
    montantTotalPaiement: 0,
    montantComptant: 0,
    montantCredit: 0,
    nombreMoisCredit: 0,
    dateDebutCredit: new Date().toISOString().split('T')[0],
    modePaiementComptant: 'especes',
    commercial: '',
    installateur: '',
    etatDossier: '',
    modificationManuelleEtat: false,
    dateArrivDossier: '',
    dateApprobationBE: '',
    dateDepotSteg: '',
    dateDemandeReception: '',
    dateLivraison: '',
    dateMiseEnService: '',
    bl: '',
    facture: '',
    dateFacture: '',
    notes: ''   // ← corrigé (pluriel comme dans le modèle)
  });

  const [showAcompteCancelModal, setShowAcompteCancelModal] = useState(false);
  const [acompteToCancel, setAcompteToCancel] = useState(null);
  const [raisonAnnulationAcompte, setRaisonAnnulationAcompte] = useState('');

  // États pour les extensions
  const [showAddExtensionModal, setShowAddExtensionModal] = useState(false);
  const [extensionFormData, setExtensionFormData] = useState({
    nbrPanneaux: 0,
    puissanceAjoutee: 0,
    coutExtension: 0
  });

  const API_URL = import.meta.env.VITE_API_URL;
  const BASE_URL = API_URL.replace('/api', ''); // URL de base sans /api pour les fichiers statiques
  const normalizeDecimalInput = (value) => (value === null || value === undefined ? '' : String(value));
  const getApiUrl = (endpoint) => `${API_URL}${endpoint}`;
  const getToken = () => localStorage.getItem('token');

  const getEquipeChauffeurOptions = (equipesSource = equipesBL) => {
    const options = [];
    (equipesSource || []).forEach(equipe => {
      const equipeId = equipe._id;
      const chefNom = [equipe.chefEquipe?.prenom, equipe.chefEquipe?.nom].filter(Boolean).join(' ').trim();
      if (chefNom) {
        options.push({
          key: `${equipeId}:chefEquipe`,
          equipeId,
          role: 'chefEquipe',
          userId: equipe.chefEquipe?._id || '',
          nomComplet: chefNom,
          label: `${chefNom} - Chef d'equipe`
        });
      }
      [
        ['sousChefEquipe', 'Sous-chef'],
        ['technicien1', 'Technicien 1'],
        ['technicien2', 'Technicien 2']
      ].forEach(([role, roleLabel]) => {
        const nomComplet = String(equipe[role] || '').trim();
        if (nomComplet) {
          options.push({
            key: `${equipeId}:${role}`,
            equipeId,
            role,
            userId: '',
            nomComplet,
            label: `${nomComplet} - ${roleLabel}`
          });
        }
      });
    });
    return options;
  };

  const getSelectedChauffeurBL = () => getEquipeChauffeurOptions().find(option => option.key === chauffeurBLKey) || null;

  const fetchOptionsBL = async () => {
    setLoadingOptionsBL(true);
    try {
      const [vehiculesRes, equipesRes] = await Promise.all([
        axios.get(getApiUrl('/vehicules'), {headers: {Authorization: `Bearer ${getToken()}`}}),
        axios.get(getApiUrl('/equipes-installation'), {headers: {Authorization: `Bearer ${getToken()}`}})
      ]);
      const vehiculesData = Array.isArray(vehiculesRes.data) ? vehiculesRes.data : [];
      const equipesData = Array.isArray(equipesRes.data) ? equipesRes.data : [];
      setVehiculesBL(vehiculesData);
      setEquipesBL(equipesData);

      if (!vehiculeBLId && vehiculesData.length > 0) {
        const dossierVehicule = dossier?.installateur?.vehicule?._id || dossier?.installateur?.vehicule;
        const defaultVehicule = vehiculesData.find(v => String(v._id) === String(dossierVehicule)) || vehiculesData[0];
        setVehiculeBLId(defaultVehicule?._id || '');
      }

      if (!chauffeurBLKey) {
        const options = getEquipeChauffeurOptions(equipesData);
        const dossierEquipeId = dossier?.installateur?._id || dossier?.installateur;
        const defaultChauffeur = options.find(option => String(option.equipeId) === String(dossierEquipeId)) || options[0];
        setChauffeurBLKey(defaultChauffeur?.key || '');
      }
    } catch (err) {
      console.error('Erreur chargement options BL:', err);
      setError('Impossible de charger les vehicules ou l equipe d installation.');
    } finally {
      setLoadingOptionsBL(false);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Déposé chez Sastra':
        return { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
      case "En attente d'approbation bureau d'étude":
        return { background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa' };
      case "Approuvé bureau d'étude":
        return { background: '#ecfdf5', color: '#065f46', border: '1px solid #6ee7b7' };
      case "En attente d'approbation":
        return { background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' };
      case "En attente d'installation": 
        return { background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' };
      case "En attente de réception": 
        return { background: '#ede9fe', color: '#5b21b6', border: '1px solid #ddd6fe' };
      case 'Réceptionné': 
        return { background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' };
      case 'Manque PV': 
        return { background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' };
      case 'Dossier en facturation': 
        return { background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' };
      default: 
        return { background: '#f8fafc', color: '#64748b', border: '1px solid #f1f5f9' };
    }
  };

  const fetchDossierReclamations = async () => {
    if (!dossierId) return;
    try {
      const res = await axios.get(getApiUrl(`/reclamations/dossier/${dossierId}`), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setDossierReclamations(res.data || []);
    } catch (_) {}
  };

  useEffect(() => {
    if (clientId && dossierId) {
      fetchClientAndDossier();
      fetchDossierReclamations();
    }
    fetchEntites();
  }, [clientId, dossierId]);

  useEffect(() => {
    if (showBLForm) {
      fetchOptionsBL();
      if (!dateBL) setDateBL(new Date().toISOString().split('T')[0]);
    }
  }, [showBLForm]);

  useEffect(() => {
    if ((activeTab === 'produit' || activeTab === 'produits') && dossier) {
      fetchProduits();
      fetchAllProduits();
      fetchPacks();
    }
  }, [activeTab, dossier]);

  useEffect(() => {
    const fetchDocsFinanciers = async () => {
      if (activeTab === 'documents' && clientId && dossierId) {
        try {
          const res = await axios.get(getApiUrl(`/documents-financiers/client/${clientId}`), {
            headers: { Authorization: `Bearer ${getToken()}` }
          });
          if (res.data && res.data.documents) {
            const valides = res.data.documents.filter(d => 
              d.dossierId === dossierId && d.statut === 'valide'
            );
            setDocumentsFinanciers(valides);
          }
        } catch (error) {
          console.error("Erreur fetch documents financiers:", error);
        }
      }
    };
    fetchDocsFinanciers();
  }, [activeTab, clientId, dossierId]);

  useEffect(() => {
    if (['infos', 'historique'].includes(activeTab) && clientId && dossierId) {
      fetchAuditLogs();
    }
  }, [activeTab, clientId, dossierId]);

  const fetchAuditLogs = async () => {
    setAuditLogsLoading(true);
    try {
      const response = await axios.get(
        getApiUrl(`/audit/logs/dossier/${clientId}/${dossierId}`),
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setAuditLogs(response.data.logs || []);
      setExpandedLog(null);
    } catch (err) {
      console.error('Erreur chargement logs audit:', err);
      setAuditLogs([]);
    } finally {
      setAuditLogsLoading(false);
    }
  };

  useEffect(() => {
    if (showInlineAddForm && allProduits.length === 0) {
      fetchAllProduits();
    }
  }, [showInlineAddForm, allProduits.length]);

  const getProduitStockDisponible = (produit) => Math.max(0, (Number(produit?.quantite) || 0) - (Number(produit?.quantiteReservee) || 0));

  const splitNumerosSerie = (value) => String(value || '')
    .split(',')
    .map(numero => numero.trim())
    .filter(Boolean);

  const getProduitPaletteNumber = (produit) => {
    if (!produit) return '';
    if (typeof produit.paletteId === 'object') {
      return produit.paletteId?.numeroPalette || produit.paletteId?._id || '';
    }
    return produit.paletteId || produit.palette || '';
  };

  const getProduitPaletteIdValue = (produit) => {
    if (!produit) return '';
    if (typeof produit.paletteId === 'object') {
      return produit.paletteId?._id || produit.paletteId?.numeroPalette || '';
    }
    return produit.paletteId || '';
  };

  const getProduitNumerosSerie = (produit) => {
    const numeros = [
      ...(Array.isArray(produit?.numerosSerie) ? produit.numerosSerie : []),
      ...splitNumerosSerie(produit?.numeroSerie)
    ];
    return [...new Set(numeros.filter(Boolean))];
  };

  const getProduitQuantiteAffectee = (produit) => Math.max(0, Number(produit?.quantite) || 0);

  const getProduitStockCatalogueDisponible = (produit) => {
    const stockValue = produit?.stockDisponibleProduit ?? produit?.produitId?.quantite;
    if (stockValue === undefined || stockValue === null || stockValue === '') return null;
    const stock = Number(stockValue);
    return Number.isFinite(stock) ? Math.max(0, stock) : null;
  };

  const getProduitDisponibiliteInfo = (produit) => {
    const quantiteDemandee = getProduitQuantiteAffectee(produit);
    const stockCatalogue = getProduitStockCatalogueDisponible(produit);
    const numerosSerie = getProduitNumerosSerie(produit);
    const hasPalette = !!getProduitPaletteNumber(produit);

    if (quantiteDemandee <= 0) {
      return {
        livrable: false,
        label: 'Non disponible',
        detail: 'Quantité affectée invalide'
      };
    }

    if (['livre', 'vendu', 'defectueux', 'perdu_casse', 'retourne'].includes(produit?.statutStock) || ['livre', 'installe'].includes(produit?.statut)) {
      return {
        livrable: false,
        label: 'Deja livre',
        detail: 'Ce produit est deja inclus dans un Bon de Livraison valide'
      };
    }

    if (produit?.livraisonDisponible === false) {
      return {
        livrable: false,
        label: 'Non disponible',
        detail: produit?.motifIndisponibiliteLivraison || 'Stock non disponible'
      };
    }

    if (hasPalette || numerosSerie.length > 0) {
      const seriesSuffisantes = numerosSerie.length === 0 || numerosSerie.length >= quantiteDemandee;
      return {
        livrable: seriesSuffisantes,
        label: seriesSuffisantes ? 'Disponible' : 'Non disponible',
        detail: seriesSuffisantes
          ? `Réservé via palette${numerosSerie.length > 0 ? ` (${numerosSerie.length} série(s))` : ''}`
          : `Séries insuffisantes: ${numerosSerie.length} / ${quantiteDemandee}`
      };
    }

    if (stockCatalogue === null) {
      return {
        livrable: false,
        label: 'Non disponible',
        detail: 'Produit introuvable dans le catalogue'
      };
    }

    if (produit?.livraisonDisponible === true) {
      return {
        livrable: true,
        label: 'Disponible',
        detail: `Stock produit restant: ${stockCatalogue} / Réservé dossier: ${quantiteDemandee}`
      };
    }

    const stockSuffisant = stockCatalogue >= quantiteDemandee;
    return {
      livrable: stockSuffisant,
      label: stockSuffisant ? 'Disponible' : 'Non disponible',
      detail: `Stock produit: ${stockCatalogue} / Demandé: ${quantiteDemandee}`
    };
  };

  const isProduitLivrable = (produit) => getProduitDisponibiliteInfo(produit).livrable;

  const getProduitsLivrables = () => produits.filter(isProduitLivrable);

  const getProduitsNonLivrables = () => produits.filter(produit => !isProduitLivrable(produit));

  const isProduitInstalle = (produit) => produit?.statut === 'installe' || !!produit?.dateInstallation;

  const getProduitsInstalles = () => produits.filter(isProduitInstalle);

  const getProduitsNonInstalles = () => produits.filter(produit => !isProduitInstalle(produit));

  const getSelectedProduitsNonLivrables = () => produits.filter(
    produit => selectedProduitsIds.includes(produit._id) && !isProduitLivrable(produit)
  );

  const canGenerateBL = () => produits.length > 0 && getProduitsNonLivrables().length === 0;

  const getProduitName = (produit) => (
    produit?.produitId?.nomProduit ||
    produit?.nomProduit ||
    produit?.designation ||
    'Produit'
  );

  const getProduitReference = (produit) => (
    produit?.produitId?.reference ||
    produit?.reference ||
    produit?.produitId?.ref ||
    ''
  );

  const getProduitQuantityText = (produit) => {
    const quantite = Number(produit?.quantite) || 0;
    const unite = produit?.uniteMesure || 'unite';
    return `${quantite} ${unite}${quantite > 1 && unite === 'unite' ? 's' : ''}`;
  };

  const PRODUCTS_PAGE_SIZE = 10;

  const getInstalledProducts = () => {
    const search = installedProductSearch.trim().toLowerCase();
    return produits.filter((produit) => {
      const haystack = `${getProduitName(produit)} ${getProduitReference(produit)} ${produit?.produitId?.marque?.name || ''}`.toLowerCase();
      return !search || haystack.includes(search);
    });
  };

  const getAvailabilityProducts = () => {
    const search = availabilitySearch.trim().toLowerCase();
    return produits.filter((produit) => {
      const haystack = `${getProduitName(produit)} ${getProduitReference(produit)}`.toLowerCase();
      const matchesSearch = !search || haystack.includes(search);
      const livrable = isProduitLivrable(produit);
      const matchesFilter =
        availabilityFilter === 'all' ||
        (availabilityFilter === 'available' && livrable) ||
        (availabilityFilter === 'unavailable' && !livrable);
      return matchesSearch && matchesFilter;
    });
  };

  useEffect(() => {
    setInstalledProductPage(1);
  }, [installedProductSearch, produits.length]);

  useEffect(() => {
    setAvailabilityPage(1);
  }, [availabilitySearch, availabilityFilter, produits.length]);

  // Calcul automatique du prix total du produit
  useEffect(() => {
    const qte = parseFloat(produitFormData.quantite) || 0;
    const pu = parseFloat(produitFormData.prixUnitaire) || 0;
    const tva = parseFloat(produitFormData.TVA) || 0;
    const gain = parseFloat(produitFormData.tauxGain) || 0;
    
    // Formule: (Qte * PU) + (Qte * PU * TVA/100) + (Qte * PU * Gain/100)
    const base = qte * pu;
    const total = base + (base * tva / 100) + (base * gain / 100);
    
    // On met à jour le prix total, mais on ne touche pas aux entrées de l'utilisateur (qte, pu, etc.)
    setProduitFormData(prev => ({
      ...prev,
      prixTotal: total.toFixed(3) // On stocke en string pour la cohérence
    }));
  }, [produitFormData.quantite, produitFormData.prixUnitaire, produitFormData.TVA, produitFormData.tauxGain]);

  // États pour la suppression sensible (dossier & paiements)
  const [showSensitiveDeleteModal, setShowSensitiveDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletionConfig, setDeletionConfig] = useState({ client: { enabled: true, mode: 'SOFT_DELETE' }, dossier: { enabled: true, mode: 'SOFT_DELETE' }, payment: { enabled: true, mode: 'SOFT_DELETE' } });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchEntites = async () => {
    const token = getToken();
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [distRes, comRes, instRes, usersRes, cfgRes, platformRes, authRes] = await Promise.all([
        axios.get(getApiUrl('/steg'), { headers }),
        axios.get(getApiUrl('/commerciaux'), { headers }),
        axios.get(getApiUrl('/clients/helpers/installateurs'), { headers }),
        axios.get(getApiUrl('/users'), { headers }),
        axios.get(getApiUrl('/system-settings/deletion-config'), { headers }),
        axios.get(getApiUrl('/system-settings/platform-control'), { headers }).catch(() => null),
        axios.get(getApiUrl('/system-settings/exceptional-authorizations?status=active'), { headers }).catch(() => null)
      ]);
      setAllDistricts(distRes.data || []);
      setAllCommerciaux(comRes.data || []);
      setAllInstallateurs(instRes.data?.data || []);
      if (cfgRes.data?.data) {
        setDeletionConfig(cfgRes.data.data);
      }
      if (platformRes?.data?.success && platformRes.data?.data) {
        setPlatformControl(platformRes.data.data);
      }
      if (authRes?.data?.success && Array.isArray(authRes.data?.data)) {
        setUserExceptionalAuths(authRes.data.data);
      }
      
      const adminsMods = (usersRes.data || []).filter(u => ['admin', 'moderateur'].includes(u.typeUser));
      setAllAdminsModerateurs(adminsMods);
    } catch (err) {
      console.error('Erreur chargement entités:', err);
    }
  };

  const fetchClientAndDossier = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        getApiUrl(`/clients/${clientId}`),
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setClient(response.data.data);

      // Trouver le dossier spécifique
      const foundDossier = response.data.data.dossiers?.find(d => d._id === dossierId);
      if (foundDossier) {
        setDossier(foundDossier);
        if (foundDossier.projectId && ['MT', 'BT/MT'].includes(response.data.data.typeClient)) {
          try {
            const projRes = await axios.get(
              getApiUrl(`/clients/${clientId}/projects/${foundDossier.projectId}`),
              { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            setProject(projRes.data.data);
          } catch {
            setProject(null);
          }
        } else {
          setProject(null);
        }
      } else {
        setError('Dossier non trouvé');
      }
    } catch (err) {
      console.error('Erreur lors du chargement:', err);
      setError('Erreur lors du chargement du dossier');
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setError(null);
    setMessage(null);
  };

  const showCommissionWarning = (warning) => {
    if (!warning?.message) return;
    const isNonConforming = warning.type === 'non_conforming_client_data' || warning.type === 'combined_warning';
    const title = isNonConforming ? 'Remarque d\'information' : 'Commission non configurée';
    Swal.fire({
      title: title,
      text: warning.message,
      icon: isNonConforming ? 'info' : 'warning',
      confirmButtonText: 'Compris',
      confirmButtonColor: '#1e3a5f'
    });
  };

  // Parse les erreurs retournées par le backend (Mongoose validation, etc.)
  const parseBackendError = (err) => {
    const status = err.response?.status;
    const data = err.response?.data;

    if (status === 409 || (data?.message && data.message.toLowerCase().includes('duplicate')) ||
      (data?.error && data.error.toLowerCase().includes('e11000'))) {
      return '❌ Conflit : Cette donnée existe déjà dans le système.';
    }
    if (status === 401) return '❌ Session expirée. Veuillez vous reconnecter.';
    if (status === 403) return '❌ Vous n\'avez pas la permission d\'effectuer cette action.';
    if (status === 404) return '❌ Ressource introuvable.';
    if (status === 400) {
      if (data?.errors) {
        const fieldErrors = Object.values(data.errors).map(e => `❌ ${e.message}`);
        return fieldErrors.join('\n');
      }
      if (data?.message) return `❌ ${data.message}`;
    }
    if (status >= 500) return '❌ Erreur serveur. Veuillez réessayer (Contactez le support si le problème persiste).';
    return data?.message || '❌ Une erreur inattendue est survenue.';
  };

  const calculerRepartitionPaiement = (data) => {
    const total = Math.max(0, Number(data.montantTotalPaiement) || 0);
    const nbMois = Math.max(0, parseInt(data.nombreMoisCredit, 10) || 0);
    const acompteSaisi = Math.max(0, Number(data.montantComptant) || 0);

    let montantSteg = 0;
    const inputType = data.typePaiement;

    if (data.montantSteg !== undefined && data.montantSteg !== null && data.montantSteg !== '' && !isNaN(Number(data.montantSteg))) {
      montantSteg = Math.min(Math.max(0, Number(data.montantSteg)), total);
    } else if (inputType === 'credit') {
      montantSteg = Math.min(total, 10000);
    } else if (inputType === 'mixte') {
      montantSteg = Math.min(total, 10000);
    } else if (inputType === 'comptant') {
      montantSteg = 0;
    }

    const montantAutofinancement = Number(Math.max(0, total - montantSteg).toFixed(2));

    let typeEffectif = inputType || 'comptant';
    if (!inputType) {
      if (montantSteg === 0) {
        typeEffectif = 'comptant';
      } else if (total > 0 && Math.abs(montantSteg - total) <= 0.01) {
        typeEffectif = 'credit';
      } else if (montantSteg > 0 && montantSteg < total) {
        typeEffectif = 'mixte';
      }
    } else {
      if (inputType === 'comptant') {
        montantSteg = 0;
      }
    }

    let finalComptant = 0;
    let finalCredit = 0;

    if (nbMois > 0) {
      finalComptant = Math.min(montantAutofinancement, acompteSaisi);
      finalCredit = Math.max(0, Number((montantAutofinancement - finalComptant).toFixed(2)));
    } else {
      finalComptant = montantAutofinancement;
      finalCredit = 0;
    }

    return {
      typePaiement: typeEffectif,
      montantTotalPaiement: Number(total.toFixed(2)),
      montantSteg: Number(montantSteg.toFixed(2)),
      montantAutofinancement: Number(montantAutofinancement.toFixed(2)),
      montantComptant: Number(finalComptant.toFixed(2)),
      montantCredit: Number(finalCredit.toFixed(2))
    };
  };


  // ========== GESTION DU DOSSIER ==========
  const handleEditDossier = () => {
    // Pour district/commercial/installateur : si populé par Mongoose on récupère l'_id
    const resolveId = (field) => {
      if (!field) return '';
      if (typeof field === 'object') return field._id ? String(field._id) : '';
      return String(field);
    };
    const autofinancementDossier = dossier && dossier.autofinancement ? dossier.autofinancement : {};
    const montantComptant = Number(autofinancementDossier.montantComptantInitial) || 0;
    const montantCredit = Number(autofinancementDossier.montantTotal) || 0;
    const montantSteg = Number(dossier.montantSteg || 0);
    const montantTotalPaiement = Number(dossier.montantTotalPaiement) || Number((montantSteg + montantComptant + montantCredit).toFixed(2));

    setDossierForm({
      pompage: dossier.pompage || false,
      referenceSteg: dossier.referenceSteg || '',
      puissance: String(dossier.puissance || 0),
      district: resolveId(dossier.district),
      _categorieSteg: (dossier.district && typeof dossier.district === 'object') ? dossier.district.categorie : '',
      dateDF: dossier.dateDF ? dossier.dateDF.split('T')[0] : '',
      moisAnnee: dossier.moisAnnee || '',
      statut: dossier.statut || 'nouveau',
      typePaiement: dossier.typePaiement || 'comptant',
      montantTotalPaiement: String(montantTotalPaiement),
      montantSteg: String(montantSteg),
      montantComptant: String(montantComptant),
      montantCredit: String(montantCredit),
      nombreMoisCredit: String(autofinancementDossier.nombreMois || 0),
      dateDebutCredit: autofinancementDossier.dateDebutCredit ? new Date(autofinancementDossier.dateDebutCredit).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      modePaiementComptant: autofinancementDossier.modePaiementComptant || 'especes',
      commercial: resolveId(dossier.commercial),
      installateur: resolveId(dossier.installateur),
      etatDossier: dossier.etatDossier || '',
      modificationManuelleEtat: dossier.modificationManuelleEtat || false,
      dateArrivDossier: dossier.dateArrivDossier ? dossier.dateArrivDossier.split('T')[0] : '',
      dateApprobationBE: dossier.dateApprobationBE ? dossier.dateApprobationBE.split('T')[0] : '',
      dateDepotSteg: dossier.dateDepotSteg ? dossier.dateDepotSteg.split('T')[0] : '',
      dateDemandeReception: dossier.dateDemandeReception ? dossier.dateDemandeReception.split('T')[0] : '',
      dateLivraison: dossier.dateLivraison ? dossier.dateLivraison.split('T')[0] : '',
      dateMiseEnService: dossier.dateMiseEnService ? dossier.dateMiseEnService.split('T')[0] : '',
      bl: dossier.bl || '',
      facture: dossier.facture || '',
      dateFacture: dossier.dateFacture ? dossier.dateFacture.split('T')[0] : '',
      notes: dossier.notes || ''
    });
    setIsEditingDossier(true);
    // Initialise la recherche commercial avec le nom du commercial actuel
    if (dossier.commercial && typeof dossier.commercial === 'object') {
      setCommercialSearchForm(`${dossier.commercial.prenom || ''} ${dossier.commercial.nom || ''}`.trim());
    } else {
      setCommercialSearchForm('');
    }
  };

  const handleUpdateDossier = async (e) => {
    e.preventDefault();
    clearMessages();

    // Frontend Validation pour referenceSteg
    const refStr = dossierForm.referenceSteg || '';
    if (!dossierForm.pompage) {
      if (!refStr || refStr.trim() === '') {
        setError('❌ La référence STEG est obligatoire.');
        return;
      }
      if (client && client.typeClient === 'BT' && !/^\d{9}$/.test(refStr.trim())) {
        setError('❌ La référence STEG pour un client BT doit comporter exactement 9 chiffres.');
        return;
      }
      if (client && client.typeClient === 'MT' && !/^\d{6}$/.test(refStr.trim())) {
        setError('❌ La référence STEG pour un client MT doit comporter exactement 6 chiffres.');
        return;
      }
    }

    const repartition = calculerRepartitionPaiement(dossierForm);
    // Validation scénario puissance désactivée

    const nbMoisSaisi = Math.max(0, parseInt(dossierForm.nombreMoisCredit, 10) || 0);
    const nombreMoisCredit = repartition.montantCredit > 0 ? nbMoisSaisi : 0;
    const modePaiementComptant = dossierForm.modePaiementComptant || 'especes';
    const payload = {
      ...dossierForm,
      pompage: dossierForm.pompage,
      puissance: parseFloat(dossierForm.puissance) || 0,
      montantTotalPaiement: parseFloat(repartition.montantTotalPaiement) || 0,
      montantSteg: parseFloat(repartition.montantSteg) || 0,
      montantAutofinancement: parseFloat(repartition.montantAutofinancement) || 0,
      montantComptant: parseFloat(repartition.montantComptant) || 0,
      montantCredit: parseFloat(repartition.montantCredit) || 0,
      nombreMoisCredit: nombreMoisCredit,
      dateDebutCredit: repartition.montantCredit > 0 ? dossierForm.dateDebutCredit : null,
      modePaiementComptant
    };
    try {
      const response = await axios.put(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}`),
        payload,
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setIsEditingDossier(false);
      fetchClientAndDossier();
      setMessage('Dossier mis \u00E0 jour avec succ\u00E8s');
      showCommissionWarning(response.data?.warning);
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError(parseBackendError(err));
    }
  };

  const handleSearchTargetClient = async () => {
    if (!targetCIN.trim()) {
      setError('Veuillez saisir un CIN');
      return;
    }
    setSearchLoading(true);
    setError(null);
    setReassignClientTarget(null);
    try {
      const response = await axios.get(
        getApiUrl(`/clients/search/cin/${targetCIN.trim()}`),
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setReassignClientTarget(response.data.data);
    } catch (err) {
      console.error('Erreur recherche client:', err);
      setError('Client non trouvé ou erreur de recherche');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleConfirmReassign = async () => {
    if (!reassignClientTarget) return;
    
    setIsReassigning(true);
    clearMessages();
    try {
      await axios.post(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/reassign`),
        { targetClientId: reassignClientTarget._id },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      setMessage('Dossier réaffecté avec succès ! Redirection...');
      setShowReassignModal(false);
      
      setTimeout(() => {
        onNavigate('clients');
      }, 2000);
    } catch (err) {
      console.error('Erreur réaffectation:', err);
      setError(parseBackendError(err));
    } finally {
      setIsReassigning(false);
    }
  };

  const handleCancelDossier = async () => {
    await cancelDossierWithConfirmation({
      clientId,
      dossierId,
      dossierLabel: dossier?.referenceSteg || 'ce dossier',
      getApiUrl,
      getToken,
      onSuccess: fetchClientAndDossier
    });
  };

  const handleDeleteDossier = () => {
    setDeleteTarget({
      itemType: 'dossier',
      clientId,
      dossierId,
      name: `Dossier ${dossier?.referenceSteg || ''}`
    });
    setShowSensitiveDeleteModal(true);
  };

  const handleOpenDeletePaiementModal = (paiement) => {
    setDeleteTarget({
      itemType: 'payment',
      clientId,
      dossierId,
      paymentId: paiement._id,
      name: `Paiement ${paiement.montant || 0} DT (${paiement.modePaiement || 'Espèces'})`
    });
    setShowSensitiveDeleteModal(true);
  };

  const handleConfirmSensitiveDelete = async (reasonArg, modeArg) => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      const reason = typeof reasonArg === 'object' ? reasonArg.reason : reasonArg;
      const mode = typeof reasonArg === 'object' ? reasonArg.mode : (modeArg || 'SOFT_DELETE');

      if (deleteTarget.itemType === 'dossier') {
        const response = await axios.delete(
          getApiUrl(`/clients/${clientId}/dossiers/${dossierId}`),
          { data: { reason, mode }, headers: { Authorization: `Bearer ${getToken()}` } }
        );
        setShowSensitiveDeleteModal(false);
        Swal.fire({
          icon: 'success',
          title: 'Dossier supprimé',
          text: response.data.message || 'Le dossier a été supprimé / archivé avec succès.',
          confirmButtonColor: '#1e3a5f'
        });
        setTimeout(() => onNavigate('clients'), 1500);
      } else if (deleteTarget.itemType === 'payment') {
        const response = await axios.delete(
          getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/paiements/${deleteTarget.paymentId}`),
          { data: { reason, mode }, headers: { Authorization: `Bearer ${getToken()}` } }
        );
        setShowSensitiveDeleteModal(false);
        Swal.fire({
          icon: 'success',
          title: 'Paiement supprimé',
          text: response.data.message || 'Le paiement a été supprimé / archivé avec succès.',
          confirmButtonColor: '#1e3a5f'
        });
        await fetchClientAndDossier();
      }
    } catch (err) {
      console.error('Erreur suppression sensible:', err);
      const msg = err.response?.data?.message || 'Erreur lors de la suppression';
      Swal.fire('Erreur', msg, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleStartQuickEditDate = (dateField, currentValue) => {
    setShowQuickEditDate(dateField);
    setQuickDateValue(currentValue ? currentValue.split('T')[0] : '');
  };

  const handleQuickUpdateDate = async (dateField) => {
    if (showQuickEditDate !== dateField) return;

    clearMessages();
    try {
      await axios.put(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}`),
        {[dateField]: quickDateValue},
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setShowQuickEditDate(null);
      await fetchClientAndDossier();
      setMessage('Date mise à jour avec succès');

      // ── Auto-notification STEG quand dateDepotSteg est renseignée ──────────
      if (dateField === 'dateDepotSteg' && quickDateValue) {
        await handleNotifierSteg();
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError(parseBackendError(err));
      setShowQuickEditDate(null);
    }
  };

  // ── Templates d'email prédéfinis ──────────────────────────────────────────
  const buildTemplates = (d, c, stegRegion) => ({
    depot: {
      label: 'Notification de Dépôt Dossier Photovoltaïque',
      subject: '[SASTRA Energy] Dépôt dossier photovoltaïque — Réf. {{referenceSteg}}',
      body: [
        'Nous vous informons du dépôt d\'un dossier de raccordement photovoltaïque auprès de votre bureau ' + stegRegion + '.',
        '',
        'Nous vous prions de bien vouloir réceptionner ce dossier et de procéder aux étapes nécessaires dans les meilleurs délais.',
        '',
        'Les pièces justificatives requises sont jointes à ce message.',
      ].join('\n')
    },
    rappel: {
      label: 'Rappel — Dossier en attente de traitement',
      subject: '[SASTRA Energy] RAPPEL — Dossier en attente — Réf. {{referenceSteg}}',
      body: [
        'Nous nous permettons de vous relancer concernant le dossier de raccordement photovoltaïque de notre client {{nomComplet}} (Réf. {{referenceSteg}}), déposé le {{dateDepotSteg}}.',
        '',
        'Ce dossier est actuellement en attente de traitement auprès de votre bureau ' + stegRegion + '. Nous vous serions reconnaissants de bien vouloir accélérer son traitement.',
        '',
        'Nous restons disponibles pour toute information supplémentaire.',
      ].join('\n')
    }
  });

  // â”€â”€ Ouvrir la modale STEG (charge le template par dÃ©faut) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleOpenStegModal = () => {
    // Vérifier si le district a une adresse email
    setSelectedArchiveIds([]);
    setNotificationSteg(null);
    setStegDebug(null);
    setStegLocalError(null);
    setStegModalTab('compose');

    if (!dossier?.district?.adresseMail) {
      setStegLocalError("Le bureau STEG " + (dossier?.district?.region || '') + " n'a pas d'adresse email configurée. L'envoi est impossible.");
    }
    const stegRegion = dossier?.district?.region || 'votre bureau';
    const tpl = buildTemplates(dossier, client, stegRegion).depot;
    setEmailTemplate('depot');
    setEmailSubject(tpl.subject);
    setEmailBody(tpl.body);
    setShowStegModal(true);
  };

  // â”€â”€ Changer de template â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleTemplateChange = (key) => {
    const stegRegion = dossier?.district?.region || 'votre bureau';
    const tpl = buildTemplates(dossier, client, stegRegion)[key];
    if (tpl) {setEmailTemplate(key); setEmailSubject(tpl.subject); setEmailBody(tpl.body);}
  };

  // â”€â”€ Toggle sÃ©lection d'une archive â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const toggleArchiveSelection = (archiveId) => {
    setSelectedArchiveIds(prev =>
      prev.includes(archiveId) ? prev.filter(id => id !== archiveId) : [...prev, archiveId]
    );
  };

  // ── Envoyer notification STEG (customSubject + customBody + archiveIds) ──────────────────
  const handleNotifierSteg = async () => {
    setStegSending(true);
    setNotificationSteg(null);
    setStegDebug(null);
    try {
      const res = await axios.post(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/notifier-steg`),
        {archiveIds: selectedArchiveIds, customSubject: emailSubject, customBody: emailBody},
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setNotificationSteg({type: 'success', message: "\u2705 " + (res.data?.message || "Email envoyé avec succès")});
      setStegDebug(res.data?.details || null);
      setShowStegModal(false);
    } catch (err) {
      const errMsg = err.response?.data?.message || "Erreur lors de l'envoi";
      setNotificationSteg({type: 'error', message: "\u274C " + errMsg});
      setStegDebug(err.response?.data || null);
      console.error('handleNotifierSteg:', err);
    } finally {
      setStegSending(false);
    }
  };


  const handleStartQuickEditField = (fieldName, currentValue) => {
    setShowQuickEditField(fieldName);
    // Assurer que la valeur est une chaîne d'ID si c'est un objet
    if (currentValue && typeof currentValue === 'object') {
      setQuickFieldValue(currentValue._id ? String(currentValue._id) : '');
    } else {
      setQuickFieldValue(currentValue ? String(currentValue) : '');
    }
    // Pour commercial : pré-remplir le champ recherche avec le nom actuel
    if (fieldName === 'commercial' && dossier?.commercial && typeof dossier.commercial === 'object') {
      setCommercialSearch(`${dossier.commercial.prenom || ''} ${dossier.commercial.nom || ''}`.trim());
    } else if (fieldName === 'commercial') {
      setCommercialSearch('');
    }
  };

  const handleQuickUpdateField = async (fieldName) => {
    if (showQuickEditField !== fieldName) return;

    clearMessages();
    try {
      const response = await axios.put(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}`),
        {[fieldName]: quickFieldValue},
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setShowQuickEditField(null);
      fetchClientAndDossier();
      setMessage('Champ mis \u00E0 jour avec succ\u00E8s');
      showCommissionWarning(response.data?.warning);
    } catch (err) {
      console.error('Erreur lors de la mise \u00E0 jour:', err);
      setError(parseBackendError(err));
      setShowQuickEditField(null);
    }
  };

  const handleCancelQuickEdit = () => {
    setShowQuickEditDate(null);
    setShowQuickEditField(null);
    setQuickDateValue('');
    setQuickFieldValue('');
  };

  // ========== GESTION DES PRODUITS ==========
  const fetchProduits = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/produits`),
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setProduits(response.data.data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des produits:', err);
    }
  };

  const fetchAllProduits = async () => {
    setAllProduitsLoading(true);
    try {
      const response = await axios.get(
        getApiUrl('/produits'),
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      const payload = response.data;
      const produitsList = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload.data) ? payload.data : []);
      setAllProduits(produitsList);
    } catch (err) {
      console.error('Erreur lors du chargement des produits:', err);
      setAllProduits([]);
    } finally {
      setAllProduitsLoading(false);
    }
  };

  const fetchPacks = async () => {
    try {
      const response = await axios.get(
        getApiUrl('/packs?limit=100&statut=actif'),
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      const payload = response.data;
      const packsList = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload.packs) ? payload.packs : []);
      setPacks(packsList);
    } catch (err) {
      console.error('Erreur lors du chargement des packs:', err);
      setPacks([]);
    }
  };

  const handleCheckPackStock = async (packId) => {
    if (!packId) {
      setPackStockWarnings([]);
      return;
    }
    setIsCheckingPackStock(true);
    try {
      const response = await axios.get(
        getApiUrl(`/packs/${packId}/verifier-stock`),
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setPackStockWarnings(response.data.details || []);
    } catch (err) {
      console.error('Erreur lors de la vérification de stock du pack:', err);
      setPackStockWarnings([]);
    } finally {
      setIsCheckingPackStock(false);
    }
  };

  const handleApplyPack = async () => {
    const selectedPack = packs.find(p => p._id === selectedPackId);
    if (!selectedPack) return;

    clearMessages();
    setIsApplyingPack(true);

    try {
      const response = await axios.post(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/pack`),
        { packId: selectedPack._id },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      fetchProduits();
      fetchClientAndDossier();
      setMessage(response.data.message || `Le pack "${selectedPack.nom}" a été affecté avec succès !`);
      if (response.data.warnings && response.data.warnings.length > 0) {
        Swal.fire({
          title: 'Avertissement Stock',
          html: response.data.warnings.join('<br>'),
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#1e3a5f'
        });
      }
      setSelectedPackId('');
      setPackStockWarnings([]);
      setShowInlineAddForm(false);
    } catch (err) {
      console.error('Erreur lors de l\'application du pack:', err);
      setError(parseBackendError(err));
    } finally {
      setIsApplyingPack(false);
    }
  };

  const handleUnassignPack = async () => {
    const result = await Swal.fire({
      title: 'Désaffecter le pack commercial ?',
      text: 'Les produits du pack non encore livrés/installés seront retirés du dossier.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, désaffecter',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#ef4444'
    });

    if (!result.isConfirmed) return;

    setIsApplyingPack(true);
    try {
      const response = await axios.post(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/pack`),
        { packId: 'none' },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      fetchProduits();
      fetchClientAndDossier();
      setMessage(response.data.message || 'Le pack a été désaffecté avec succès.');
      setSelectedPackId('');
      setPackStockWarnings([]);
      Swal.fire({
        title: 'Pack désaffecté',
        text: response.data.message || 'Le pack a été désaffecté avec succès.',
        icon: 'success',
        confirmButtonColor: '#1e3a5f'
      });
    } catch (err) {
      console.error('Erreur désaffectation pack:', err);
      setError(parseBackendError(err));
    } finally {
      setIsApplyingPack(false);
    }
  };

  const fetchPalettesForProduit = async (produitId) => {
    if (!produitId) return;
    try {
      const response = await axios.get(
        getApiUrl(`/palettes/produit/${produitId}`),
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      // Filtrer les palettes qui ont des num\u00E9ros de s\u00E9rie disponibles
      const palettesDisponibles = response.data.data.filter(palette =>
        palette.numerosSerie && palette.numerosSerie.some(ns => ns.statut === 'disponible')
      );
      setPalettes(palettesDisponibles || []);
    } catch (err) {
      console.error('Erreur lors du chargement des palettes:', err);
      setPalettes([]);
    }
  };

  const fetchNumerosSerieFromPalette = async (paletteId, quantiteNecessaire, numerosAInclure = []) => {
    if (!paletteId) {
      setNumerosSerieDisponibles([]);
      setNumerosSerieSelectionnes([]);
      return;
    }
    try {
      const response = await axios.get(
        getApiUrl(`/palettes/${paletteId}/details`),
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      const {articles} = response.data.data;
      const quantiteASelectionner = Math.max(1, parseInt(quantiteNecessaire, 10) || 1);
      const numerosInclus = new Set(numerosAInclure.map(numero => String(numero).trim()).filter(Boolean));
      // Récupérer les numéros disponibles, et garder visibles ceux déjà affectés à ce produit.
      const seriesDisponibles = articles
        .filter(art => art.statusNumeroSerie === 'disponible' || numerosInclus.has(art.numeroSerie))
        .map(art => ({
          _id: art._id,
          numero: art.numeroSerie,
          statut: art.statusNumeroSerie
        }));

      setNumerosSerieDisponibles(seriesDisponibles);
      if (numerosInclus.size > 0) {
        setNumerosSerieSelectionnes(seriesDisponibles
          .filter(ns => numerosInclus.has(ns.numero))
          .slice(0, quantiteASelectionner)
          .map(ns => ns.numero));
      } else {
        setNumerosSerieSelectionnes(seriesDisponibles.slice(0, quantiteASelectionner).map(ns => ns.numero));
      }
    } catch (err) {
      console.error('Erreur lors du chargement des numÃ©ros de sÃ©rie:', err);
      setNumerosSerieDisponibles([]);
      setNumerosSerieSelectionnes([]);
    }
  };

  const handleToggleNumeroSerieSelection = (numero) => {
    setNumerosSerieSelectionnes(prev => {
      if (prev.includes(numero)) {
        return prev.filter(selectedNumero => selectedNumero !== numero);
      }

      const maxSelection = Math.max(1, parseInt(produitFormData.quantite, 10) || 1);
      if (prev.length >= maxSelection) {
        return prev;
      }

      return [...prev, numero];
    });
  };

  // ========== GESTION DES EXTENSIONS ==========
  const handleAddExtension = async (e) => {
    e.preventDefault();
    clearMessages();
    try {
      await axios.post(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/extensions`),
        extensionFormData,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setShowAddExtensionModal(false);
      fetchClientAndDossier();
      setMessage('Demande d\'extension ajoutée avec succès');
      setExtensionFormData({ nbrPanneaux: 0, puissanceAjoutee: 0, coutExtension: 0 });
    } catch (err) {
      setError(parseBackendError(err));
    }
  };

  const handleValidateExtension = async (extensionId, action) => {
    if (action === 'supprimee') {
      const { value: confirmText } = await Swal.fire({
        title: 'Supprimer l\'extension ?',
        html: `
          <div style="text-align: left; background: #fff5f5; padding: 15px; border-left: 4px solid #ef4444; border-radius: 4px; margin-bottom: 15px;">
            <p style="color: #991b1b; font-weight: 600;">⚠️ Suppression d'extension</p>
            <p style="color: #b91c1c; font-size: 0.9rem;">Si l'extension est déjà validée, sa puissance et son coût seront retirés du dossier principal.</p>
          </div>
          <p>Veuillez taper <b style="color: #ef4444;">SUPPRIMER</b> pour confirmer :</p>
        `,
        input: 'text',
        inputPlaceholder: 'Tapez SUPPRIMER ici...',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#94a3b8',
        confirmButtonText: 'Supprimer',
        cancelButtonText: 'Annuler',
        inputValidator: (value) => {
          if (value !== 'SUPPRIMER') return 'Vous devez taper SUPPRIMER en majuscules';
        }
      });
      if (!confirmText) return;
    } else {
      const confirmMsg = action === 'validee' 
          ? "Voulez-vous valider cette extension ? Cela mettra à jour la puissance du dossier et créera une ligne de paiement comptant."
          : "Voulez-vous rejeter cette extension ?";
      
      if (!confirm(confirmMsg)) return;
    }

    clearMessages();
    try {
      await axios.put(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/extensions/${extensionId}/validate`),
        { action },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      fetchClientAndDossier();
      setMessage(`Extension ${action === 'validee' ? 'validée' : action === 'rejetee' ? 'rejetée' : 'supprimée'} avec succès`);
    } catch (err) {
      setError(parseBackendError(err));
    }
  };

  const handleAddProduit = async (e) => {
    e.preventDefault();
    clearMessages();

    const hasPalette = !!produitFormData.paletteId;

    // La validation stricte est retirée car le numéro de série n'est plus obligatoire
    // pour tous les types de produits.
    /*
    if (hasPalette && numerosSerieSelectionnes.length !== produitFormData.quantite) {
      setError(`Vous devez sélectionner ${produitFormData.quantite} numéro(s) de série pour cette palette`);
      return;
    }
    */

    try {
      // 1. Réserver les numéros de série et mettre à jour le stock (seulement si une palette est sélectionnée)
      if (hasPalette && numerosSerieSelectionnes.length > 0) {
        await axios.put(
          getApiUrl(`/palettes/${produitFormData.paletteId}/reserver`),
          {
            numerosSerie: numerosSerieSelectionnes,
            clientId: clientId,
            dossierId: dossierId
          },
          {headers: {Authorization: `Bearer ${getToken()}`}}
        );
      }

      // 2. Ajouter le produit au dossier avec les num\u00E9ros de s\u00E9rie
      await axios.post(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/produits`),
        {
          produitId: produitFormData.produitId,
          paletteId: produitFormData.paletteId,
          quantite: produitFormData.quantite,
          numerosSerie: numerosSerieSelectionnes.length > 0 ? numerosSerieSelectionnes : splitNumerosSerie(produitFormData.numeroSerie),
          prixUnitaire: produitFormData.prixUnitaire,
          TVA: produitFormData.TVA,
          tauxGain: produitFormData.tauxGain,
          prixTotal: produitFormData.prixTotal,
          numeroSerie: numerosSerieSelectionnes.length > 0 ? numerosSerieSelectionnes.join(', ') : produitFormData.numeroSerie,
          dateInstallation: produitFormData.dateInstallation,
          garantie: {
            dateDebut: produitFormData.garantieDateDebut,
            dateFin: produitFormData.garantieDateFin,
            dureeAnnees: produitFormData.garantieDureeAnnees
          },
          statut: produitFormData.statut,
          specifications: produitFormData.specifications,
          notes: produitFormData.notes
        },
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );

      setShowInlineAddForm(false);
      setSelectedCategory(null);
      setSelectedCatalogProduct(null);
      fetchProduits();
      fetchAllProduits();
      setMessage('Produit ajouté avec succès');

      // Réinitialiser les états
      setPalettes([]);
      setNumerosSerieDisponibles([]);
      setNumerosSerieSelectionnes([]);
    } catch (err) {
      console.error('Erreur lors de l\'ajout du produit:', err);
      setError(parseBackendError(err));
    }
  };

  const handleDeleteProduit = async (produitId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;

    clearMessages();
    try {
      await axios.delete(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/produits/${produitId}`),
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      fetchProduits();
      setMessage('Produit supprimé avec succès');
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError(err.response?.data?.message || 'Erreur lors de la suppression du produit');
    }
  };

  const handleEditProduit = (produit) => {
    setSelectedProduit(produit);
    const prixProduit = produit.produitId?.prix || 0;
    const quantite = produit.quantite || 1;
    const paletteId = getProduitPaletteIdValue(produit);
    const numerosSerieProduit = getProduitNumerosSerie(produit);
    setProduitFormData({
      produitId: produit.produitId?._id || produit.produitId || '',
      paletteId,
      referenceNumero: produit.produitId?.reference || '',
      quantite: quantite,
      prixUnitaire: produit.prixUnitaire || 0,
      prixProduit: prixProduit,
      TVA: produit.TVA || 0,
      tauxGain: produit.tauxGain || 0,
      prixTotal: produit.prixTotal || (quantite * (produit.prixUnitaire || 0)),
      dateInstallation: produit.dateInstallation ? produit.dateInstallation.split('T')[0] : '',
      garantieDateDebut: produit.garantie?.dateDebut ? produit.garantie.dateDebut.split('T')[0] : '',
      garantieDateFin: produit.garantie?.dateFin ? produit.garantie.dateFin.split('T')[0] : '',
      garantieDureeAnnees: produit.garantie?.dureeAnnees || 5,
      numeroSerie: numerosSerieProduit.length > 0 ? numerosSerieProduit.join(', ') : (produit.numeroSerie || ''),
      numerosSerie: numerosSerieProduit,
      statut: produit.statut || 'commande',
      specifications: produit.specifications || '',
      notes: produit.notes || ''
    });
    setSelectedCatalogProduct(produit.produitId || null);
    setNumerosSerieSelectionnes(numerosSerieProduit);
    setNumerosSerieDisponibles([]);
    setPalettes([]);
    const produitId = produit.produitId?._id || produit.produitId || '';
    if (produitId) {
      fetchPalettesForProduit(produitId);
    }
    if (paletteId) {
      fetchNumerosSerieFromPalette(paletteId, quantite, numerosSerieProduit);
    }
    setShowEditProduitModal(true);
  };

  const handleViewProduitDetails = (produit) => {
    setSelectedProduit(produit);
    setShowDetailProduitModal(true);
  };

  // ========== GESTION DE L'ÉCHÉANCIER CRÉDIT ==========
  const handleOpenConfigEcheancierModal = () => {
    const autofinancement = dossier && dossier.autofinancement ? dossier.autofinancement : null;
    const hasExistingEcheancier = Array.isArray(autofinancement?.echeancier) && autofinancement.echeancier.length > 0;

    const totalPayeDossierVal = (dossier?.stegPaye ? Number(dossier?.montantSteg || 0) : 0) + Number(autofinancement?.montantComptantPaye || 0) + Number(autofinancement?.montantPaye || 0);
    const isDossierTotalementPayeVal = montantTotalRestant <= 0.009 && (montantTotalPaiement <= 0 || totalPayeDossierVal >= (montantTotalPaiement - 0.009));

    if (isDossierTotalementPayeVal) {
      Swal.fire({
        html: `
          <div style="padding: 10px 5px; text-align: center; font-family: inherit;">
            <div style="width: 72px; height: 72px; margin: 0 auto 16px; background: linear-gradient(135deg, #10b981 0%, #059669 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 10px 25px -5px rgba(16, 185, 129, 0.4); border: 3px solid #ffffff;">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h3 style="color: #0f172a; font-size: 1.35rem; font-weight: 800; margin: 0 0 6px 0; letter-spacing: -0.02em;">
              Dossier Totalement Payé
            </h3>
            <span style="display: inline-block; background: #d1fae5; color: #047857; font-weight: 700; font-size: 0.78rem; padding: 4px 12px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 20px;">
              Solde Réglé à 100%
            </span>
            <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border: 1.5px solid #e2e8f0; border-radius: 14px; padding: 16px; margin-bottom: 20px; text-align: left;">
              <div style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 10px; border-bottom: 1px solid #e2e8f0; margin-bottom: 10px;">
                <span style="font-size: 0.86rem; color: #64748b; font-weight: 600;">Montant total dossier :</span>
                <strong style="font-size: 0.95rem; color: #1e293b; font-weight: 700;">${formatCurrency(montantTotalPaiement)}</strong>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 10px; border-bottom: 1px solid #e2e8f0; margin-bottom: 10px;">
                <span style="font-size: 0.86rem; color: #64748b; font-weight: 600;">Total perçu :</span>
                <strong style="font-size: 0.95rem; color: #059669; font-weight: 700;">${formatCurrency(totalPayeDossierVal)}</strong>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-size: 0.86rem; color: #64748b; font-weight: 600;">Reste à payer :</span>
                <span style="font-size: 0.95rem; color: #059669; font-weight: 800; background: #ecfdf5; padding: 2px 8px; border-radius: 6px;">0,000 DT</span>
              </div>
            </div>
            <div style="background: #eff6ff; border-left: 4px solid #3b82f6; border-radius: 8px; padding: 12px 14px; text-align: left;">
              <p style="color: #1e3a5f; font-size: 0.92rem; line-height: 1.45; margin: 0; font-weight: 600;">
                Ce dossier est totalement payé. Vous ne pouvez pas programmer/reprogrammer des échéances.
              </p>
            </div>
          </div>
        `,
        showConfirmButton: true,
        confirmButtonText: 'Compris',
        confirmButtonColor: '#1e3a5f',
        customClass: {
          popup: 'swal2-modal-premium-card'
        }
      });
      return;
    }

    const creditTotalEffective = Number(autofinancement?.montantTotal) > 0
      ? Number(autofinancement.montantTotal)
      : Math.max(0, Number(dossier.montantTotalPaiement || 0) - (dossier.stegPaye ? 0 : Number(dossier.montantSteg || 0)) - Number(autofinancement?.montantComptantInitial || 0));
    const montantPayeCreditEffective = Number(autofinancement?.montantPaye) || 0;
    const montantRestantCreditCalculated = Math.max(0, Number((creditTotalEffective - montantPayeCreditEffective).toFixed(2)));
    
    // Si le type de paiement est mixte ou avec STEG, déduire la part STEG non payée du solde à échelonner
    const isMixteOuSteg = dossier.typePaiement === 'mixte' || Number(dossier.montantSteg || 0) > 0;
    const stegNonPayeDeduire = (isMixteOuSteg && !dossier.stegPaye) ? Number(dossier.montantSteg || 0) : 0;
    const totalRestantCalculated = Math.max(0, Number((Number(montantTotalRestant ?? 0) - stegNonPayeDeduire).toFixed(2)));

    const montantAReprogrammer = totalRestantCalculated > 0
      ? totalRestantCalculated
      : (montantRestantCreditCalculated > 0 ? montantRestantCreditCalculated : Math.max(0, Number(montantRestantCredit || 0)));

    const existingList = autofinancement?.echeancier || [];
    const allCancellableIndices = existingList
      .map((e, idx) => (e && e.statut !== 'paye' && e.statut !== 'annule' ? idx : null))
      .filter((idx) => idx !== null);

    setReprogrammMode(hasExistingEcheancier ? 'all' : 'keep');
    setSelectedEcheancesToCancel(hasExistingEcheancier ? allCancellableIndices : []);
    setAnnulerAcompteOption(false);

    if (hasExistingEcheancier) {
      const defaultMois = autofinancement?.nombreMois || 0;
      const defaultDate = new Date().toISOString().split('T')[0];
      const defaultFreq = '1';
      const autoList = generateAutoEcheancesList(defaultMois, defaultDate, defaultFreq, montantAReprogrammer);
      setEcheancierFormData({
        isReprogrammer: true,
        montantRestant: String(montantAReprogrammer),
        montantTotal: '0',
        nombreMois: String(defaultMois),
        dateDebutCredit: defaultDate,
        frequenceMois: defaultFreq,
        echeancesCustom: autoList,
        montantTotalPaiement: String(dossier.montantTotalPaiement || 0),
        typePaiement: dossier.typePaiement || 'comptant',
        montantComptant: String(autofinancement?.montantComptantInitial || 0),
        modePaiementComptant: autofinancement?.modePaiementComptant || 'especes',
        raisonReprogrammation: ''
      });
    } else {
      // Première programmation : logique financière complète
      const defaultMois = autofinancement?.nombreMois || 0;
      const defaultDate = autofinancement?.dateDebutCredit
        ? new Date(autofinancement.dateDebutCredit).toISOString().split('T')[0]
        : (dossier.dateDebutCredit ? new Date(dossier.dateDebutCredit).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
      const defaultFreq = '1';
      const rep = calculerRepartitionPaiement({
        montantTotalPaiement: dossier.montantTotalPaiement || 0,
        typePaiement: dossier.typePaiement || 'comptant',
        montantComptant: autofinancement?.montantComptantInitial || 0,
        nombreMoisCredit: defaultMois
      });
      const autoList = generateAutoEcheancesList(defaultMois, defaultDate, defaultFreq, rep.montantCredit);
      setEcheancierFormData({
        isReprogrammer: false,
        montantRestant: '0',
        montantTotalPaiement: String(dossier.montantTotalPaiement || 0),
        typePaiement: dossier.typePaiement || 'comptant',
        montantComptant: String(autofinancement?.montantComptantInitial || 0),
        modePaiementComptant: autofinancement?.modePaiementComptant || 'especes',
        montantTotal: String(autofinancement?.montantTotal || 0),
        nombreMois: String(defaultMois),
        dateDebutCredit: defaultDate,
        frequenceMois: defaultFreq,
        echeancesCustom: autoList,
        raisonReprogrammation: ''
      });
    }
    setShowConfigEcheancierModal(true);
  };

  const handleConfigurerEcheancier = async (e, confirmCancelAcompte = false) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    clearMessages();

    const nombreMois = Number(echeancierFormData.nombreMois) || 0;
    const dateDebut = echeancierFormData.dateDebutCredit;

    if (nombreMois <= 0) {
      setError('Le nombre de mois doit être supérieur à 0.');
      return;
    }

    // ── Mode reprogrammation ──
    if (echeancierFormData.isReprogrammer) {
      const montantRestant = Number(echeancierFormData.montantRestant) || 0;
      if (montantRestant <= 0) {
        setError('Le montant à reprogrammer doit être supérieur à 0.');
        return;
      }

      const raisonReprog = (echeancierFormData.raisonReprogrammation || raisonAnnulationAcompte || '').trim();
      if (reprogrammMode !== 'keep' && !raisonReprog) {
        setError('La raison de la reprogrammation est obligatoire.');
        return;
      }

      try {
        const payload = {
          montantTotal: montantRestant,
          nombreMois,
          dateDebutCredit: dateDebut,
          frequenceMois: parseInt(echeancierFormData.frequenceMois || 1, 10),
          echeancesCustom: Array.isArray(echeancierFormData.echeancesCustom) && echeancierFormData.echeancesCustom.length > 0
            ? echeancierFormData.echeancesCustom.map(item => ({ dateEcheance: item.dateEcheance, montant: item.montant, modePaiement: item.modePaiement || 'especes' }))
            : undefined,
          isReprogrammation: true,
          modeConservation: reprogrammMode,
          allCancellable: reprogrammMode === 'all',
          echeancesToCancel: reprogrammMode === 'keep' ? [] : selectedEcheancesToCancel,
          annulerAcompte: annulerAcompteOption || confirmCancelAcompte,
          raisonReprogrammation: raisonReprog || 'Reprogrammation de l\'échéancier',
          raisonAnnulation: raisonReprog || 'Reprogrammation de l\'échéancier'
        };

        await axios.post(
          getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/echeancier`),
          payload,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        setShowConfigEcheancierModal(false);
        setShowAcompteCancelModal(false);
        setRaisonAnnulationAcompte('');
        fetchClientAndDossier();
        setMessage(`Échéancier reprogrammé pour ${formatCurrency(montantRestant)} sur ${nombreMois} mois.`);
      } catch (err) {
        console.error('Erreur lors de la reprogrammation:', err);
        if (err.response?.data?.requiresAcompteCancellation) {
          setAcompteToCancel(err.response.data.acompte);
          setShowAcompteCancelModal(true);
          setError(null);
        } else {
          setError(parseBackendError(err));
        }
      }
      return;
    }

    // ── Mode première programmation / logique financière ──
    const rep = calculerRepartitionPaiement({
      montantTotalPaiement: echeancierFormData.montantTotalPaiement,
      typePaiement: echeancierFormData.typePaiement,
      montantComptant: echeancierFormData.montantComptant,
      nombreMoisCredit: nombreMois
    });

    const totalDossier = rep.montantTotalPaiement;
    const typeP = rep.typePaiement;
    const montantSteg = rep.montantSteg;
    const montantAutofinancement = rep.montantAutofinancement;
    const montantCreditFinal = rep.montantCredit;
    const modePaiementComptant = echeancierFormData.modePaiementComptant || 'especes';

    const messageScenario = validerScenarioPuissanceMontant(dossier?.puissance, totalDossier);
    if (messageScenario) {
      setError(messageScenario);
      return;
    }

    const raisonStr = (echeancierFormData.raisonReprogrammation || raisonAnnulationAcompte || '').trim();
    if ((reprogrammMode !== 'keep' && selectedEcheancesToCancel.length > 0) || annulerAcompteOption) {
      if (!raisonStr) {
        setError('La raison de la modification des échéances ou de l\'acompte est obligatoire.');
        return;
      }
    }

    const montantTotalRestantActuel = Math.max(0, Number(montantTotalRestant ?? 0));
    if (montantCreditFinal - montantTotalRestantActuel > 0.009 && !confirmCancelAcompte && reprogrammMode === 'keep' && !annulerAcompteOption) {
      setError(`Le montant du crédit programmé (${formatCurrency(montantCreditFinal)}) dépasse le solde disponible (${formatCurrency(montantTotalRestantActuel)}).`);
      return;
    }

    try {
      await axios.put(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}`),
        {
          typePaiement: typeP,
          montantTotalPaiement: totalDossier,
          montantSteg,
          montantAutofinancement,
          nombreMoisCredit: nombreMois,
          dateDebutCredit: dateDebut,
          modePaiementComptant
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (montantCreditFinal > 0 && nombreMois > 0) {
        const payload = {
          montantTotal: montantCreditFinal,
          nombreMois,
          dateDebutCredit: dateDebut,
          frequenceMois: parseInt(echeancierFormData.frequenceMois || 1, 10),
          echeancesCustom: Array.isArray(echeancierFormData.echeancesCustom) && echeancierFormData.echeancesCustom.length > 0
            ? echeancierFormData.echeancesCustom.map(item => ({ dateEcheance: item.dateEcheance, montant: item.montant, modePaiement: item.modePaiement || 'especes' }))
            : undefined,
          modeConservation: reprogrammMode,
          isReprogrammation: false,
          allCancellable: reprogrammMode === 'all',
          echeancesToCancel: reprogrammMode === 'keep' ? [] : selectedEcheancesToCancel,
          annulerAcompte: annulerAcompteOption || confirmCancelAcompte,
          raisonReprogrammation: raisonStr || 'Configuration de l\'échéancier',
          raisonAnnulation: raisonStr || 'Configuration de l\'échéancier'
        };

        await axios.post(
          getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/echeancier`),
          payload,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
      }

      setShowConfigEcheancierModal(false);
      setShowAcompteCancelModal(false);
      setRaisonAnnulationAcompte('');
      fetchClientAndDossier();
      setMessage('Logique financière et échéancier mis à jour avec succès');
    } catch (err) {
      console.error('Erreur lors de la configuration financière:', err);
      if (err.response?.data?.requiresAcompteCancellation) {
        setAcompteToCancel(err.response.data.acompte);
        setShowAcompteCancelModal(true);
        setError(null);
      } else {
        setError(parseBackendError(err));
      }
    }
  };

  const handleUpdateProduit = async (e) => {
    e.preventDefault();
    clearMessages();
    const quantiteDemandee = Number(produitFormData.quantite);
    if (!Number.isFinite(quantiteDemandee) || quantiteDemandee <= 0) {
      setError('La quantité du produit doit être supérieure à 0');
      return;
    }

    const numerosSeriePayload = numerosSerieSelectionnes.length > 0
      ? numerosSerieSelectionnes
      : splitNumerosSerie(produitFormData.numeroSerie);

    try {
      await axios.put(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/produits/${selectedProduit._id}`),
        {
          ...produitFormData,
          quantite: quantiteDemandee,
          numerosSerie: numerosSeriePayload,
          numeroSerie: numerosSeriePayload.length > 0 ? numerosSeriePayload.join(', ') : produitFormData.numeroSerie
        },
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setShowEditProduitModal(false);
      fetchProduits();
      setMessage('Produit mis à jour avec succès');
    } catch (err) {
      console.error('Erreur lors de la mise à jour du produit:', err);
      setError(parseBackendError(err));
    }
  };

  const handlePayerEcheance = (echeanceIndex) => {
    const echeance = dossier.autofinancement.echeancier[echeanceIndex];

    setEditingPaiementId(null);
    setPaiementFormData({
      date: new Date().toISOString().split('T')[0],
      montant: echeance.montant,
      categorie: 'credit',
      methode: (dossier?.autofinancement?.modePaiementComptant || dossierForm.modePaiementComptant || 'especes'),
      reference: `Échéance ${echeance.mois}/${echeance.annee}`,
      statut: 'paye',
      notes: `Paiement de l'échéance du ${echeance.mois}/${echeance.annee}`
    });

    setEcheanceAPayerIndex(echeanceIndex);
    setShowInlinePaymentForm(true);

    setTimeout(() => {
      paymentFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const getEcheanceStatut = (echeance) => {
    if (!echeance) return 'en_attente';
    if (echeance.statut === 'paye') return 'paye';

    const montantPaye = Number(echeance.montantPaye) || 0;
    const montantTotal = Number(echeance.montant) || 0;
    const montantRestant = echeance.montantRestant !== undefined ? Number(echeance.montantRestant) : Math.max(0, montantTotal - montantPaye);

    if (montantRestant <= 0) return 'paye';
    if (echeance.statut === 'partiellement_paye' || montantPaye > 0) return 'partiellement_paye';

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateEcheance = echeance.dateEcheance ? new Date(echeance.dateEcheance) : null;
    if (dateEcheance && !Number.isNaN(dateEcheance.getTime())) {
      dateEcheance.setHours(0, 0, 0, 0);
      if (dateEcheance < today) return 'retard';
    }

    return 'en_attente';
  };

  const getCategoriePaiementParDefaut = (currentDossier = dossier) => {
    const auto = currentDossier?.autofinancement || {};
    const comptantInitial = Number(auto.montantComptantInitial) || Number(currentDossier?.montantComptant) || 0;
    const comptantPaye = Number(auto.montantComptantPaye) || 0;
    return comptantPaye < comptantInitial ? 'comptant' : 'credit';
  };

  const handleOpenEditEcheanceModal = (echeanceIndex) => {
    const echeance = dossier && dossier.autofinancement && Array.isArray(dossier.autofinancement.echeancier)
      ? dossier.autofinancement.echeancier[echeanceIndex]
      : null;
    if (!echeance) return;

    setEditingEcheanceIndex(echeanceIndex);
    setEcheanceEditFormData({
      dateEcheance: echeance.dateEcheance ? new Date(echeance.dateEcheance).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      montant: Number(echeance.montant) || 0,
      statut: getEcheanceStatut(echeance),
      datePaiement: echeance.datePaiement ? new Date(echeance.datePaiement).toISOString().split('T')[0] : '',
      notes: Array.isArray(echeance.notes) ? echeance.notes.join(', ') : (echeance.notes || '')
    });
    setShowEditEcheanceModal(true);
  };

  const handleUpdateEcheance = async (e) => {
    e.preventDefault();
    if (editingEcheanceIndex === null) return;

    clearMessages();
    try {
      await axios.put(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/echeancier/${editingEcheanceIndex}`),
        {
          dateEcheance: echeanceEditFormData.dateEcheance,
          montant: Number(echeanceEditFormData.montant) || 0,
          statut: echeanceEditFormData.statut,
          datePaiement: echeanceEditFormData.statut === 'paye' ? echeanceEditFormData.datePaiement : null,
          notes: echeanceEditFormData.notes
        },
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );

      setShowEditEcheanceModal(false);
      setEditingEcheanceIndex(null);
      setEcheanceEditFormData({
        dateEcheance: new Date().toISOString().split('T')[0],
        montant: 0,
        statut: 'en_attente',
        datePaiement: ''
      });
      await fetchClientAndDossier();
      setMessage('Échéance modifiée avec succès');
    } catch (err) {
      console.error('Erreur lors de la modification de l\'échéance:', err);
      setError(err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Erreur lors de la modification de l\'échéance');
    }
  };


  const handleAddClientNote = async () => {
    if (!newClientNote.trim()) return;
    try {
      await axios.put(getApiUrl(`/clients/${clientId}`), {
        nouvelleNote: newClientNote,
        notePriorite: newNotePriorite,
        noteAffecteA: newNoteAffecteA || undefined
      }, { headers: {Authorization: `Bearer ${getToken()}`} });
      setNewClientNote('');
      setNewNotePriorite('normale');
      setNewNoteAffecteA('');
      await fetchClientAndDossier();
      setMessage('Note client ajoutée');
    } catch (err) {
      console.error(err);
      setError('Erreur lors de l\'ajout de la note client');
    }
  };


  const handleAddDossierNote = async () => {
    if (!newDossierNote.trim()) return;
    try {
      await axios.put(getApiUrl(`/clients/${clientId}/dossiers/${dossierId}`), {
        nouvelleNote: newDossierNote,
        notePriorite: newNotePriorite,
        noteAffecteA: newNoteAffecteA || undefined
      }, { headers: {Authorization: `Bearer ${getToken()}`} });
      setNewDossierNote('');
      setNewNotePriorite('normale');
      setNewNoteAffecteA('');
      await fetchClientAndDossier();
      setMessage('Note dossier ajoutée');
    } catch (err) {
      console.error(err);
      setError('Erreur lors de l\'ajout de la note dossier');
    }
  };


  const handleDeleteClientNote = async (noteId) => {
    const result = await Swal.fire({
      title: 'Supprimer cette note ?',
      text: "Cette action est irréversible",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(getApiUrl(`/clients/${clientId}/notes/${noteId}`), {
          headers: {Authorization: `Bearer ${getToken()}`}
        });
        await fetchClientAndDossier();
        setMessage('Note supprimée');
      } catch (err) {
        console.error(err);
        setError('Erreur lors de la suppression');
      }
    }
  };

  const handleDeleteDossierNote = async (noteId) => {
    const result = await Swal.fire({
      title: 'Supprimer cette note ?',
      text: "Cette action est irréversible",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/notes/${noteId}`), {
          headers: {Authorization: `Bearer ${getToken()}`}
        });
        await fetchClientAndDossier();
        setMessage('Note supprimée');
      } catch (err) {
        console.error(err);
        setError('Erreur lors de la suppression');
      }
    }
  };

  // ========== GESTION DE L'HISTORIQUE DE PAIEMENT ==========
  const handleAddPaiement = async (e) => {
    e.preventDefault();
    clearMessages();
    if (paiementSubmitting) return;
    setPaiementSubmitting(true);

    try {
      const echeanceSelectionnee = echeanceAPayerIndex !== null &&
        dossier?.autofinancement?.echeancier &&
        dossier.autofinancement.echeancier[echeanceAPayerIndex]
        ? dossier.autofinancement.echeancier[echeanceAPayerIndex]
        : null;

      const payloadPaiement = {
        ...paiementFormData,
        categorie: paiementFormData.categorie || getCategoriePaiementParDefaut(dossier),
        sansImpactSolde: echeanceAPayerIndex !== null,
        origineEcheance: !!echeanceSelectionnee,
        echeanceMois: echeanceSelectionnee ? echeanceSelectionnee.mois : null,
        echeanceAnnee: echeanceSelectionnee ? echeanceSelectionnee.annee : null,
        echeanceDateEcheance: echeanceSelectionnee ? echeanceSelectionnee.dateEcheance : null
      };

      const montantPaiement = Math.max(0, Number(payloadPaiement.montant) || 0);
      if (montantPaiement <= 0) {
        setError('Le montant du paiement doit être supérieur à 0.');
        return;
      }

      if (echeanceSelectionnee) {
        const montantEcheance = Math.max(0, Number(echeanceSelectionnee.montant) || 0);
        if (Math.abs(montantPaiement - montantEcheance) > 0.009) {
          setError(`Pour marquer cette echeance comme payee, le montant doit etre exactement ${formatCurrency(montantEcheance)}. Utilisez Ajouter un paiement pour un paiement partiel.`);
          return;
        }
      } else {
        const existingEcheancesImpayeesList = Array.isArray(dossier?.autofinancement?.echeancier)
          ? dossier.autofinancement.echeancier.filter(e => e && e.statut !== 'paye' && e.statut !== 'annule')
          : [];
        const montantEcheancesConfigurees = existingEcheancesImpayeesList.reduce((sum, e) => {
          const tot = Math.max(0, Number(e.montant) || 0);
          const paye = Math.max(0, Number(e.montantPaye) || 0);
          const rest = e.montantRestant !== undefined && e.montantRestant !== null
            ? Math.max(0, Number(e.montantRestant) || 0)
            : Math.max(0, tot - paye);
          return sum + rest;
        }, 0);

        const montantLibre = Math.max(0, Number(montantTotalRestant) - montantEcheancesConfigurees);
        let plafondAutorise = montantLibre;
        let libellePlafond = 'montant réellement libre';

        if (payloadPaiement.categorie === 'comptant') {
          plafondAutorise = Math.min(montantComptantRestant, montantLibre);
          libellePlafond = 'montant comptant libre';
        } else if (payloadPaiement.categorie === 'credit') {
          plafondAutorise = Math.min(montantRestantCredit, montantLibre);
          libellePlafond = 'montant crédit libre';
        }

        if (montantPaiement - plafondAutorise > 0.009) {
          setError(`Le montant saisi (${formatCurrency(montantPaiement)}) ne doit pas dépasser le ${libellePlafond} (${formatCurrency(plafondAutorise)}) disponible après prise en compte des échéances déjà configurées (${formatCurrency(montantEcheancesConfigurees)}).`);
          return;
        }
      }

      if (echeanceSelectionnee) {
        await axios.put(
          getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/echeancier/${echeanceAPayerIndex}/payer`),
          {
            datePaiement: paiementFormData.date,
            montant: montantPaiement,
            methode: paiementFormData.methode,
            reference: paiementFormData.reference,
            notes: paiementFormData.notes
          },
          {headers: {Authorization: `Bearer ${getToken()}`}}
        );
      } else {
        const res = await axios.post(
          getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/paiements`),
          payloadPaiement,
          {headers: {Authorization: `Bearer ${getToken()}`}}
        );
        checkAndNotifyStatutPaye(res?.data?.data);
      }

      handleClosePaymentForm();
      fetchClientAndDossier();
      setMessage(echeanceAPayerIndex !== null ? 'Échéance payée et enregistrée dans l\'historique' : 'Paiement ajouté avec succès');
    } catch (err) {
      console.error('Erreur lors de l\'ajout du paiement:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'ajout du paiement');
      setEcheanceAPayerIndex(null);
    } finally {
      setPaiementSubmitting(false);
    }
  };

  const handleResetPaiementForm = () => {
    setEditingPaiementId(null);
    setEcheanceAPayerIndex(null);
    setPaiementFormData({
      date: new Date().toISOString().split('T')[0],
      montant: 0,
      categorie: getCategoriePaiementParDefaut(dossier),
      methode: (dossier?.autofinancement?.modePaiementComptant || dossierForm.modePaiementComptant || 'especes'),
      reference: '',
      statut: 'paye',
      notes: ''
    });
  };

  const handleClosePaymentForm = () => {
    handleResetPaiementForm();
    setShowInlinePaymentForm(false);
  };

  const handleOpenEditPaiementModal = (paiement) => {
    if (!paiement) return;

    setEditingPaiementId(paiement._id);
    setEcheanceAPayerIndex(null);
    setPaiementFormData({
      date: paiement.datePaiement ? new Date(paiement.datePaiement).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      montant: String(paiement.montant || 0),
      categorie: paiement.categorie || getCategoriePaiementParDefaut(dossier),
      methode: paiement.modePaiement || 'especes',
      reference: paiement.reference || '',
      statut: paiement.statut || 'paye',
      notes: Array.isArray(paiement.notes) ? paiement.notes.join(', ') : (paiement.notes || '')
    });
    setShowInlinePaymentForm(true);

    setTimeout(() => {
      paymentFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const handleUpdatePaiement = async (e) => {
    e.preventDefault();
    if (!editingPaiementId) return;
    if (paiementSubmitting) return;
    setPaiementSubmitting(true);

    clearMessages();
    try {
      const resUpdate = await axios.put(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/paiements/${editingPaiementId}`),
        {
          date: paiementFormData.date,
          montant: Number(paiementFormData.montant) || 0,
          categorie: paiementFormData.categorie,
          methode: paiementFormData.methode,
          reference: paiementFormData.reference,
          statut: paiementFormData.statut,
          notes: paiementFormData.notes
        },
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );

      handleClosePaymentForm();
      await fetchClientAndDossier();
      checkAndNotifyStatutPaye(resUpdate?.data?.data);
      setMessage('Paiement modifié avec succès');
    } catch (err) {
      console.error('Erreur lors de la modification du paiement:', err);
      setError(parseBackendError(err));
    } finally {
      setPaiementSubmitting(false);
    }
  };

  const handleAnnulerPaiement = async (paiement) => {
    if (!paiement) return;

    const isAcompte = paiement.categorie === 'comptant';
    const titlePaiement = isAcompte ? 'Annulation de l\'Acompte' : 'Annulation du Paiement';
    const badgeText = isAcompte ? 'ACOMPTE / COMPTANT' : (paiement.categorie ? paiement.categorie.toUpperCase() : 'PAIEMENT');

    const { value: raisonStr, isConfirmed } = await Swal.fire({
      html: `
        <div style="padding: 10px 4px; text-align: center; font-family: inherit;">
          <div style="width: 72px; height: 72px; margin: 0 auto 16px; background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 10px 25px -5px rgba(239, 68, 68, 0.4); border: 3px solid #ffffff;">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
          <h3 style="color: #0f172a; font-size: 1.35rem; font-weight: 800; margin: 0 0 6px 0; letter-spacing: -0.02em;">
            ${titlePaiement}
          </h3>
          <span style="display: inline-block; background: #fee2e2; color: #991b1b; font-weight: 700; font-size: 0.78rem; padding: 4px 12px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 20px;">
            ${badgeText} — ACTION TRAÇABLE
          </span>

          <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border: 1.5px solid #e2e8f0; border-radius: 14px; padding: 16px; margin-bottom: 20px; text-align: left;">
            <div style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 10px; border-bottom: 1px solid #e2e8f0; margin-bottom: 10px;">
              <span style="font-size: 0.86rem; color: #64748b; font-weight: 600;">Montant du règlement :</span>
              <strong style="font-size: 1rem; color: #1e293b; font-weight: 800;">${formatCurrency(paiement.montant)}</strong>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 10px; border-bottom: 1px solid #e2e8f0; margin-bottom: 10px;">
              <span style="font-size: 0.86rem; color: #64748b; font-weight: 600;">Mode de paiement :</span>
              <span style="font-size: 0.88rem; color: #1e3a5f; font-weight: 700; text-transform: capitalize;">${paiement.modePaiement || 'Espèces'}</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="font-size: 0.86rem; color: #64748b; font-weight: 600;">Date du règlement :</span>
              <span style="font-size: 0.88rem; color: #475569; font-weight: 600;">${formatDate(paiement.datePaiement)}</span>
            </div>
          </div>

          <div style="background: #eff6ff; border-left: 4px solid #3b82f6; border-radius: 8px; padding: 12px 14px; text-align: left; margin-bottom: 16px;">
            <p style="color: #1e3a5f; font-size: 0.86rem; line-height: 1.45; margin: 0; font-weight: 600;">
              ℹ️ L'annulation conservera toute la traçabilité dans l'historique sans supprimer l'enregistrement.
            </p>
          </div>
        </div>
      `,
      input: 'textarea',
      inputLabel: 'Raison obligatoire de l\'annulation *',
      inputPlaceholder: 'Saisissez le motif de l\'annulation (ex: Erreur de saisie, chèque rejeté, accord client)...',
      inputValidator: (value) => {
        if (!value || !value.trim()) return 'La raison de l\'annulation est obligatoire';
      },
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Confirmer l\'annulation',
      cancelButtonText: 'Annuler',
      customClass: {
        popup: 'swal2-modal-premium-card'
      }
    });

    if (!isConfirmed || !raisonStr) return;

    clearMessages();
    try {
      await axios.post(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/paiements/${paiement._id}/annuler`),
        { raisonAnnulation: raisonStr.trim() },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      await fetchClientAndDossier();
      setMessage('Paiement annulé avec succès');
    } catch (err) {
      console.error('Erreur lors de l\'annulation du paiement:', err);
      setError(parseBackendError(err));
    }
  };

  const handleAnnulerEcheance = async (echeanceIndex) => {
    const echeance = dossier?.autofinancement?.echeancier?.[echeanceIndex];
    if (!echeance) return;

    if (echeance.statut === 'paye') {
      Swal.fire({
        html: `
          <div style="padding: 10px 4px; text-align: center; font-family: inherit;">
            <div style="width: 72px; height: 72px; margin: 0 auto 16px; background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 10px 25px -5px rgba(245, 158, 11, 0.4); border: 3px solid #ffffff;">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </div>
            <h3 style="color: #0f172a; font-size: 1.35rem; font-weight: 800; margin: 0 0 6px 0; letter-spacing: -0.02em;">
              Échéance Déjà Payée
            </h3>
            <span style="display: inline-block; background: #fef3c7; color: #92400e; font-weight: 700; font-size: 0.78rem; padding: 4px 12px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 20px;">
              Action Non Autorisée
            </span>
            <div style="background: #eff6ff; border-left: 4px solid #3b82f6; border-radius: 8px; padding: 14px; text-align: left;">
              <p style="color: #1e3a5f; font-size: 0.92rem; line-height: 1.5; margin: 0; font-weight: 600;">
                Une échéance déjà payée ne peut pas être annulée directement.<br/><br/>
                Veuillez annuler le paiement correspondant depuis l'<strong>Historique des Paiements</strong> ci-dessous.
              </p>
            </div>
          </div>
        `,
        confirmButtonColor: '#1e3a5f',
        confirmButtonText: 'Compris',
        customClass: {
          popup: 'swal2-modal-premium-card'
        }
      });
      return;
    }

    const montantTotalEch = Number(echeance.montant) || 0;
    const montantPayeEch = Number(echeance.montantPaye) || 0;
    const montantRestantEch = echeance.montantRestant !== undefined && echeance.montantRestant !== null
      ? Number(echeance.montantRestant)
      : Math.max(0, montantTotalEch - montantPayeEch);

    const { value: raisonStr, isConfirmed } = await Swal.fire({
      html: `
        <div style="padding: 10px 4px; text-align: center; font-family: inherit;">
          <div style="width: 72px; height: 72px; margin: 0 auto 16px; background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 10px 25px -5px rgba(239, 68, 68, 0.4); border: 3px solid #ffffff;">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
              <line x1="10" y1="14" x2="10" y2="18"></line>
              <line x1="14" y1="14" x2="10" y2="18"></line>
            </svg>
          </div>
          <h3 style="color: #0f172a; font-size: 1.35rem; font-weight: 800; margin: 0 0 6px 0; letter-spacing: -0.02em;">
            Annulation de l'Échéance
          </h3>
          <span style="display: inline-block; background: #fee2e2; color: #991b1b; font-weight: 700; font-size: 0.78rem; padding: 4px 12px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 20px;">
            ÉCHÉANCE DE CRÉDIT
          </span>

          <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border: 1.5px solid #e2e8f0; border-radius: 14px; padding: 16px; margin-bottom: 20px; text-align: left;">
            <div style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 10px; border-bottom: 1px solid #e2e8f0; margin-bottom: 10px;">
              <span style="font-size: 0.86rem; color: #64748b; font-weight: 600;">Échéance concernée :</span>
              <strong style="font-size: 0.95rem; color: #1e293b; font-weight: 700;">${echeance.mois}/${echeance.annee}</strong>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 10px; border-bottom: 1px solid #e2e8f0; margin-bottom: 10px;">
              <span style="font-size: 0.86rem; color: #64748b; font-weight: 600;">Montant total échéance :</span>
              <strong style="font-size: 0.95rem; color: #1e293b; font-weight: 800;">${formatCurrency(montantTotalEch)}</strong>
            </div>
            ${montantPayeEch > 0 ? `
              <div style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 10px; border-bottom: 1px solid #e2e8f0; margin-bottom: 10px;">
                <span style="font-size: 0.86rem; color: #64748b; font-weight: 600;">Part déjà réglée (conservée) :</span>
                <strong style="font-size: 0.95rem; color: #059669; font-weight: 700;">${formatCurrency(montantPayeEch)}</strong>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-size: 0.86rem; color: #64748b; font-weight: 600;">Solde restant à annuler :</span>
                <strong style="font-size: 0.95rem; color: #dc2626; font-weight: 800;">${formatCurrency(montantRestantEch)}</strong>
              </div>
            ` : `
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-size: 0.86rem; color: #64748b; font-weight: 600;">Reste à payer à annuler :</span>
                <strong style="font-size: 0.95rem; color: #dc2626; font-weight: 800;">${formatCurrency(montantRestantEch)}</strong>
              </div>
            `}
          </div>
        </div>
      `,
      input: 'textarea',
      inputLabel: 'Raison obligatoire de l\'annulation de l\'échéance *',
      inputPlaceholder: 'Saisissez le motif de l\'annulation (ex: Révision contrat, accord commercial, remise)...',
      inputValidator: (value) => {
        if (!value || !value.trim()) return 'La raison de l\'annulation de l\'échéance est obligatoire';
      },
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Confirmer l\'annulation',
      cancelButtonText: 'Annuler',
      customClass: {
        popup: 'swal2-modal-premium-card'
      }
    });

    if (!isConfirmed || !raisonStr) return;

    clearMessages();
    try {
      await axios.post(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/echeancier/${echeanceIndex}/annuler`),
        { raisonAnnulation: raisonStr.trim() },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      await fetchClientAndDossier();
      setMessage('Échéance annulée avec succès');
    } catch (err) {
      console.error('Erreur lors de l\'annulation de l\'échéance:', err);
      setError(parseBackendError(err));
    }
  };

  const handleAnnulerAcompteUnpaid = async () => {
    const { value: raisonStr, isConfirmed } = await Swal.fire({
      html: `
        <div style="padding: 10px 4px; text-align: center; font-family: inherit;">
          <div style="width: 72px; height: 72px; margin: 0 auto 16px; background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 10px 25px -5px rgba(239, 68, 68, 0.4); border: 3px solid #ffffff;">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
          <h3 style="color: #0f172a; font-size: 1.35rem; font-weight: 800; margin: 0 0 6px 0; letter-spacing: -0.02em;">
            Annulation de l'Acompte Initial
          </h3>
          <span style="display: inline-block; background: #fee2e2; color: #991b1b; font-weight: 700; font-size: 0.78rem; padding: 4px 12px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 20px;">
            ACOMPTE INITIAL — REMISE À ZÉRO
          </span>

          <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border: 1.5px solid #e2e8f0; border-radius: 14px; padding: 16px; margin-bottom: 20px; text-align: left;">
            <div style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 10px; border-bottom: 1px solid #e2e8f0; margin-bottom: 10px;">
              <span style="font-size: 0.86rem; color: #64748b; font-weight: 600;">Montant Acompte Initial :</span>
              <strong style="font-size: 1rem; color: #1e293b; font-weight: 800;">${formatCurrency(montantComptantInitial)}</strong>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="font-size: 0.86rem; color: #64748b; font-weight: 600;">Statut actuel :</span>
              <span style="font-size: 0.88rem; color: #dc2626; font-weight: 700;">À PAYER</span>
            </div>
          </div>

          <div style="background: #eff6ff; border-left: 4px solid #3b82f6; border-radius: 8px; padding: 12px 14px; text-align: left; margin-bottom: 16px;">
            <p style="color: #1e3a5f; font-size: 0.86rem; line-height: 1.45; margin: 0; font-weight: 600;">
              ℹ️ L'annulation remettra le montant de l'acompte exigé à 0,000 DT tout en conservant la traçabilité dans l'historique du dossier.
            </p>
          </div>
        </div>
      `,
      input: 'textarea',
      inputLabel: 'Raison obligatoire de l\'annulation de l\'acompte *',
      inputPlaceholder: 'Saisissez le motif de l\'annulation de l\'acompte (ex: Exonération accordée, révision offre, pas d\'acompte requis)...',
      inputValidator: (value) => {
        if (!value || !value.trim()) return 'La raison de l\'annulation de l\'acompte est obligatoire';
      },
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Confirmer l\'annulation',
      cancelButtonText: 'Annuler',
      customClass: {
        popup: 'swal2-modal-premium-card'
      }
    });

    if (!isConfirmed || !raisonStr) return;

    clearMessages();
    try {
      await axios.post(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/echeancier/annuler-acompte`),
        { raisonAnnulation: raisonStr.trim() },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      await fetchClientAndDossier();
      setMessage('Acompte initial annulé avec succès');
    } catch (err) {
      console.error('Erreur lors de l\'annulation de l\'acompte initial:', err);
      setError(parseBackendError(err));
    }
  };

  /**
   * ── Notification automatique : client passé à "Payé" ──────────────────────
   * À appeler après chaque soumission de paiement réussie.
   * Lit le document client retourné par le backend et, si le statut global
   * est passé à 'paye', affiche une SweetAlert de félicitations.
   */
  const checkAndNotifyStatutPaye = (clientData) => {
    if (!clientData) return;
    const statutTotal = clientData.statutPaiementTotal;
    if (statutTotal === 'paye') {
      Swal.fire({
        icon: 'success',
        title: '✅ Dossier entièrement soldé !',
        html: `
          <p style="font-size:1rem; color:#1e3a5f; margin:0">
            Le solde restant est maintenant <strong>0,000 DT</strong>.<br/>
            Le statut du client a été automatiquement mis à jour en
            <span style="background:#d1fae5;color:#065f46;padding:2px 8px;border-radius:12px;font-weight:700">
              PAYÉ
            </span>.
          </p>`,
        confirmButtonColor: '#10b981',
        confirmButtonText: 'Parfait !',
        showClass: { popup: 'animate__animated animate__zoomIn' }
      });
    }
  };

  /** Ouvre le formulaire de paiement STEG (ne valide pas encore) */
  const handlePayerSteg = () => {
    setStegPaymentForm({
      datePaiement: '',
      modePaiement: 'virement',
      reference: '',
      notes: ''
    });
    setStegPaymentStep('form');
    setShowStegPaymentModal(true);
  };

  /** Étape 1 : Valide la saisie et passe à la carte de confirmation sans aucun appel API */
  const handleProceedToStegConfirmation = (e) => {
    if (e) e.preventDefault();
    if (!stegPaymentForm.datePaiement) {
      setError('Veuillez saisir la date du paiement STEG.');
      return;
    }
    clearMessages();
    setStegPaymentStep('confirm');
  };

  /** Soumission du formulaire — valide le paiement STEG avec la date saisie (appelé uniquement au clic sur Confirmer) */
  const handleConfirmPayerSteg = async (e) => {
    if (e) e.preventDefault();
    if (stegPaymentSubmitting) return;

    if (!stegPaymentForm.datePaiement) {
      setError('Veuillez saisir la date du paiement STEG.');
      setStegPaymentStep('form');
      return;
    }

    clearMessages();
    setStegPaymentSubmitting(true);

    // Date réelle du paiement (choisie par l'utilisateur)
    const datePaiementReelle = new Date(stegPaymentForm.datePaiement).toISOString();
    // Date de remplissage du formulaire (maintenant)
    const dateRemplissage = new Date().toISOString();

    try {
      // 1. Mettre à jour le flag stegPaye + date officielle dans le dossier
      await axios.put(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}`),
        {
          stegPaye: true,
          datePaiementSteg: datePaiementReelle
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      // 2. Enregistrer dans l'historique de paiements avec tous les attributs
      const resHistorique = await axios.post(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/paiements`),
        {
          date:          datePaiementReelle,          // date réelle du paiement
          montant:       dossier.montantSteg,
          categorie:     'steg',
          methode:       stegPaymentForm.modePaiement,
          reference:     stegPaymentForm.reference || `Règlement Crédit STEG - ${dossier.referenceSteg}`,
          statut:        'paye',
          notes:         [
            stegPaymentForm.notes,
            `Formulaire rempli le : ${new Date(dateRemplissage).toLocaleString('fr-FR')}`
          ].filter(Boolean).join(' | ')
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      checkAndNotifyStatutPaye(resHistorique?.data?.data);

      setShowStegPaymentModal(false);
      setStegPaymentStep('form');
      await fetchClientAndDossier();
      setMessage('Paiement STEG validé et enregistré dans l\'historique');
    } catch (err) {
      console.error('Erreur lors de la validation du paiement STEG :', err);
      setError(parseBackendError(err));
    } finally {
      setStegPaymentSubmitting(false);
    }
  };

  // ========== GESTION DES DOCUMENTS ET ARCHIVES ==========
  const formatFileSizeFormatted = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleAddArchive = async (e) => {
    if (e) e.preventDefault();
    clearMessages();

    if (!archiveFile && !editingArchive) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    setIsUploadingDoc(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      if (archiveFile) {
        formData.append('file', archiveFile);
      }
      formData.append('type', archiveFormData.type || 'document');
      formData.append('category', archiveFormData.category || 'Autre');
      formData.append('description', archiveFormData.description || '');
      formData.append('isArchived', docTargetIsArchived ? 'true' : 'false');

      const archiveUrl = editingArchive
        ? getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/archives/${editingArchive._id}`)
        : getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/archives`);
      const archiveMethod = editingArchive ? 'put' : 'post';

      await axios[archiveMethod](
        archiveUrl,
        formData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percent);
            }
          }
        }
      );

      setShowAddArchiveModal(false);
      setEditingArchive(null);
      setArchiveFile(null);
      setArchiveFormData({
        type: 'document',
        category: 'Autre',
        description: ''
      });
      setDocTargetIsArchived(false);
      fetchClientAndDossier();
      setMessage(editingArchive ? 'Document modifié avec succès' : (docTargetIsArchived ? 'Document archivé avec succès' : 'Document ajouté avec succès'));
    } catch (err) {
      console.error('Erreur lors de l\'enregistrement du document:', err);
      setError(parseBackendError(err));
    } finally {
      setIsUploadingDoc(false);
      setUploadProgress(0);
    }
  };

  const handleToggleArchiveStatus = async (archiveId) => {
    clearMessages();
    try {
      const res = await axios.patch(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/archives/${archiveId}/toggle`),
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      fetchClientAndDossier();
      setMessage(res.data?.message || 'Statut du document mis à jour');
    } catch (err) {
      console.error('Erreur lors de la bascule d\'archive:', err);
      setError(parseBackendError(err));
    }
  };

  const handleDeleteArchive = async (archiveId, docName = '') => {
    const result = await Swal.fire({
      title: '<span style="color:#991b1b; font-weight:800;">Supprimer définitivement ?</span>',
      html: `
        <div style="text-align:left; font-size:14px; color:#475569; line-height:1.5;">
          <p style="margin-bottom:10px;">Vous êtes sur le point de <strong>supprimer définitivement</strong> le document :</p>
          <div style="padding:10px 14px; background:#fef2f2; border:1px solid #fecaca; border-radius:8px; font-weight:700; color:#991b1b; margin-bottom:12px;">
            📄 ${docName || 'ce document'}
          </div>
          <p style="color:#7f1d1d; font-size:13px;">
            ⚠️ <strong>Impact :</strong> Le fichier sera définitivement supprimé du disque serveur de la VM et de la base de données. Cette action est irréversible.
          </p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Oui, supprimer définitivement',
      cancelButtonText: 'Annuler'
    });

    if (!result.isConfirmed) return;

    clearMessages();
    try {
      await axios.delete(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/archives/${archiveId}`),
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      fetchClientAndDossier();
      setMessage('Document supprimé définitivement du serveur');
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError(parseBackendError(err));
    }
  };

  const handleOpenEditArchive = (archive) => {
    setEditingArchive(archive);
    setArchiveFile(null);
    setArchiveFormData({
      type: archive?.type || 'document',
      category: archive?.category || archive?.type || 'Autre',
      description: archive?.description || ''
    });
    setShowAddArchiveModal(true);
  };

  // ========== GESTION DU TRANSFERT DE PRODUITS ==========
  const handleToggleSelectProduit = (produitId) => {
    const produit = produits.find(item => item._id === produitId);
    if (produit && !selectedProduitsIds.includes(produitId) && !isProduitLivrable(produit)) {
      const info = getProduitDisponibiliteInfo(produit);
      Swal.fire({
        icon: 'warning',
        title: 'Produit non disponible',
        text: `${getProduitName(produit)} ne peut pas etre selectionne pour le Bon de Livraison. ${info.detail || 'Stock insuffisant.'}`,
        confirmButtonColor: '#f59e0b'
      });
      return;
    }

    setSelectedProduitsIds(prev => {
      if (prev.includes(produitId)) {
        return prev.filter(id => id !== produitId);
      } else {
        return [...prev, produitId];
      }
    });
  };

  const handleRechercherClientParRefSTEG = async () => {
    if (!refStegRecherche.trim()) {
      setError('Veuillez saisir une référence STEG');
      return;
    }

    clearMessages();
    try {
      const response = await axios.get(
        getApiUrl(`/clients/search/refsteg/${refStegRecherche}`),
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );

      setClientDestination(response.data.data);
      setMessage(`Client trouvé: ${response.data.data.clientNom} ${response.data.data.clientPrenom || ''}`);
    } catch (err) {
      console.error('Erreur lors de la recherche:', err);
      setError(parseBackendError(err));
      setClientDestination(null);
    }
  };

  const handleTransfererProduits = async () => {
    if (!clientDestination) {
      setError('Veuillez rechercher et sélectionner un client de destination');
      return;
    }

    if (selectedProduitsIds.length === 0) {
      setError('Veuillez sélectionner au moins un produit à transférer');
      return;
    }

    // Demander confirmation
    const confirmation = confirm(
      `Êtes-vous sûr de vouloir transférer ${selectedProduitsIds.length} produit(s) vers le dossier ${clientDestination.referenceSteg} du client ${clientDestination.clientNom} ${clientDestination.clientPrenom || ''}?`
    );

    if (!confirmation) return;

    clearMessages();
    try {
      await axios.post(
        getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/transferer-produits`),
        {
          clientIdDest: clientDestination.clientId,
          dossierIdDest: clientDestination.dossierId,
          produitsIds: selectedProduitsIds
        },
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );

      setShowTransfertModal(false);
      setSelectedProduitsIds([]);
      setRefStegRecherche('');
      setClientDestination(null);
      fetchProduits();
      setMessage('Produits transf\u00E9r\u00E9s avec succ\u00E8s');
    } catch (err) {
      console.error('Erreur lors du transfert:', err);
      setError(parseBackendError(err));
    }
  };

  const handleGenererBL = async (e) => {
    if (e) e.preventDefault();
    clearMessages();

    if (selectedProduitsIds.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Aucun produit sélectionné',
        text: 'Veuillez sélectionner au moins un produit pour générer le Bon de Livraison.',
        confirmButtonColor: '#1e3a5f'
      });
      return;
    }

    const nonLivrables = getSelectedProduitsNonLivrables();
    if (nonLivrables.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Livraison impossible',
        text: 'Un ou plusieurs produits sélectionnés sont affectés au client mais ne sont pas disponibles en stock.',
        confirmButtonColor: '#f59e0b'
      });
      return;
    }

    const chauffeurSelectionne = getSelectedChauffeurBL();

    if (!dateBL) {
      Swal.fire({
        icon: 'warning',
        title: 'Date manquante',
        text: 'Veuillez saisir la date officielle du Bon de Livraison.',
        confirmButtonColor: '#1e3a5f'
      });
      return;
    }

    if (!chauffeurSelectionne) {
      Swal.fire({
        icon: 'warning',
        title: 'Chauffeur manquant',
        text: 'Veuillez selectionner un membre de l equipe d installation comme chauffeur.',
        confirmButtonColor: '#1e3a5f'
      });
      return;
    }

    if (!vehiculeBLId) {
      Swal.fire({
        icon: 'warning',
        title: 'Vehicule manquant',
        text: 'Veuillez selectionner un vehicule existant.',
        confirmButtonColor: '#1e3a5f'
      });
      return;
    }

    setIsGeneratingBL(true);
    try {
      const res = await axios.post(
        getApiUrl('/documents-financiers'),
        {
          clientId: clientId,
          dossierId: dossierId,
          type: 'bon_livraison',
          dateCreation: dateBL,
          vehiculeId: vehiculeBLId,
          chauffeurMembre: {
            equipeId: chauffeurSelectionne.equipeId,
            role: chauffeurSelectionne.role,
            userId: chauffeurSelectionne.userId,
            nomComplet: chauffeurSelectionne.nomComplet
          },
          produitsSelectionnes: selectedProduitsIds
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      Swal.fire({
        icon: 'success',
        title: 'Succès !',
        text: 'Le Bon de Livraison a été généré avec succès. Le PDF va s\'ouvrir automatiquement.',
        confirmButtonColor: '#10b981'
      });

      if (res.data?.document?._id) {
        window.open(`${API_URL}/documents-financiers/${res.data.document._id}/pdf?token=${getToken()}`, '_blank');
      }

      setSelectedProduitsIds([]);
      setShowBLForm(false);
      fetchClientAndDossier();
    } catch (err) {
      console.error('Erreur lors de la génération du BL:', err);
      const errMsg = err.response?.data?.message || 'Une erreur est survenue lors de la génération du Bon de Livraison.';
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: errMsg,
        confirmButtonColor: '#ef4444'
      });
    } finally {
      setIsGeneratingBL(false);
    }
  };

  // Fonctions utilitaires
  const getStatutColor = (statut) => {
    const colors = {
      'commande': '#f59e0b',
      'en_stock': '#3b82f6',
      'installe': '#10b981',
      'maintenance': '#ef4444'
    };
    return colors[statut] || '#6b7280';
  };


  const formatDate = (date) => {
    return date ? new Date(date).toLocaleDateString('fr-FR') : 'N/A';
  };

  const formatDateTime = (date) => {
    return date ? new Date(date).toLocaleString('fr-FR') : '-';
  };

  const formatUserDisplay = (user) => {
    if (!user) return '-';
    if (typeof user === 'string') return '-';

    const prenom = user.prenom ? String(user.prenom) : '';
    const nom = user.nom ? String(user.nom) : '';
    const fullName = `${prenom} ${nom}`.trim();

    if (fullName) return fullName;
    if (user.mail) return String(user.mail);
    if (user.typeUser) return String(user.typeUser);
    return '-';
  };

  const formatAuditActorName = (actor) => {
    if (!actor) return '-';
    const prenom = actor.prenom ? String(actor.prenom) : '';
    const nom = actor.nom ? String(actor.nom) : '';
    const fullName = `${prenom} ${nom}`.trim();
    return fullName || actor.email || actor.mail || actor.userId || '-';
  };

  const formatAuditRole = (actor) => actor?.role || actor?.typeUser || 'Role inconnu';

  const formatAuditValue = (value) => {
    if (value === null || value === undefined || value === '') return 'vide';
    if (value instanceof Date) return formatDateTime(value);
    if (Array.isArray(value)) {
      if (value.length === 0) return '[]';
      return value.map(item => formatAuditValue(item)).join(', ');
    }
    if (typeof value === 'object') {
      if (value.nom || value.prenom) return formatAuditActorName(value);
      if (value.label) return String(value.label);
      if (value.referenceSteg) return String(value.referenceSteg);
      if (value._id) return String(value._id);
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-TN', {
      style: 'currency',
      currency: 'TND'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="detail-dossier-container">
        <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} />
        <div className="detail-dossier-content">
          <div className="loading">Chargement...</div>
        </div>
      </div>
    );
  }

  if (!client || !dossier) {
    return (
      <div className="detail-dossier-container">
        <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} />
        <div className="detail-dossier-content">
          <div className="error-message">Dossier non trouvé</div>
        </div>
      </div>
    );
  }

  const dossierAutofinancement = dossier.autofinancement ? dossier.autofinancement : {};
  const echeancierRows = Array.isArray(dossierAutofinancement.echeancier) ? dossierAutofinancement.echeancier : [];
  const historiquePaiements = Array.isArray(dossierAutofinancement.historiquePaiements)
    ? dossierAutofinancement.historiquePaiements
    : [];
  const paiementsActifs = historiquePaiements.filter((p) => p && p.statut !== 'supprime');
  const acomptePaiementActif = historiquePaiements.find((p) => p && p.categorie === 'comptant' && p.statut === 'paye' && !p.sansImpactSolde);
  const stegPaiementActif = historiquePaiements.find((p) => p && p.categorie === 'steg' && p.statut === 'paye');
  const totalStegPayeActif = historiquePaiements
    .filter((p) => p && p.categorie === 'steg' && p.statut === 'paye')
    .reduce((sum, p) => sum + Number(p.montant || 0), 0);
  const isStegPayeEffectif = Boolean(
    dossier.stegPaye && (stegPaiementActif || (Number(dossier.montantSteg || 0) > 0 && totalStegPayeActif >= (Number(dossier.montantSteg || 0) - 0.01)))
  );
  const paiementsSupprimes = historiquePaiements.filter((p) => p && p.statut === 'supprime');
  const hasEcheancier = echeancierRows.length > 0;
  const editingEcheance = editingEcheanceIndex !== null && echeancierRows[editingEcheanceIndex]
    ? echeancierRows[editingEcheanceIndex]
    : null;
  const editingEcheancePayee = editingEcheance ? getEcheanceStatut(editingEcheance) === 'paye' : false;
  const editingPaiement = editingPaiementId
    ? historiquePaiements.find((paiement) => String(paiement?._id) === String(editingPaiementId))
    : null;
  const editingPaiementOrigineEcheance = editingPaiement?.origineEcheance === true;
  
  // Somme des parts (fallback si montantTotalPaiement n'est pas en base)
  const montantComptantInitial = Number(dossierAutofinancement.montantComptantInitial) || 0;
  const montantComptantPaye = Number(dossierAutofinancement.montantComptantPaye) || 0;
  const montantComptantRestant = Math.max(0, montantComptantInitial - montantComptantPaye);

  const montantTotalCredit = Number(dossierAutofinancement.montantTotal) || 0;
  const montantPayeCredit = Number(dossierAutofinancement.montantPaye) || 0;
  const montantRestantCredit = Math.max(0, montantTotalCredit - montantPayeCredit);

  const montantSteg = Number(dossier.montantSteg || 0);
  const montantTotalPaiement = Number(dossier.montantTotalPaiement) || Number((montantSteg + montantComptantInitial + montantTotalCredit).toFixed(2));
  const montantTotalRestant = Number((montantComptantRestant + montantRestantCredit + (!dossier.stegPaye ? montantSteg : 0)).toFixed(2));
  const montantMensuelCredit = Number(dossierAutofinancement.montantMensuel) || 0;
  const montantPaiementMaxAutorise = (() => {
    if (echeanceAPayerIndex !== null && Array.isArray(dossierAutofinancement.echeancier) && dossierAutofinancement.echeancier[echeanceAPayerIndex]) {
      return Math.max(0, Number(dossierAutofinancement.echeancier[echeanceAPayerIndex].montant) || 0);
    }
    if (paiementFormData.categorie === 'comptant') {
      return montantComptantRestant;
    }
    if (paiementFormData.categorie === 'credit') {
      return montantRestantCredit;
    }
    return montantTotalRestant;
  })();
  const montantPaiementSaisi = Math.max(0, Number(paiementFormData.montant) || 0);
  const paiementDepasseMontantRestant = montantPaiementSaisi - montantPaiementMaxAutorise > 0.009;
  const paiementMontantInvalide = montantPaiementSaisi <= 0;
  const boutonAjoutPaiementDesactive = paiementDepasseMontantRestant || paiementMontantInvalide;
  
  // Logique améliorée pour la prochaine échéance : priorise le comptant restant
  let prochainPaiementAffiche = null;
  if (montantComptantRestant > 0) {
    prochainPaiementAffiche = {
      label: 'Acompte / Comptant restant',
      montant: montantComptantRestant,
      date: 'Immédiat'
    };
  } else if (dossierAutofinancement.prochaineEcheance && dossierAutofinancement.prochaineEcheance.dateEcheance) {
    prochainPaiementAffiche = {
      label: 'Prochaine échéance',
      montant: dossierAutofinancement.prochaineEcheance.montant,
      date: formatDate(dossierAutofinancement.prochaineEcheance.dateEcheance)
    };
  }

  const handlePayerComptant = () => {
    setEditingPaiementId(null);
    setEcheanceAPayerIndex(null);
    setPaiementFormData({
      date: new Date().toISOString().split('T')[0],
      montant: String(montantComptantRestant),
      categorie: 'comptant',
      methode: (dossier?.autofinancement?.modePaiementComptant || dossierForm.modePaiementComptant || 'especes'),
      reference: 'Paiement Initial / Acompte',
      statut: 'paye',
      notes: 'Règlement de la partie comptante (Acompte)'
    });
    setShowInlinePaymentForm(true);
    setTimeout(() => {
      paymentFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };


  const repartitionFormEdition = calculerRepartitionPaiement(dossierForm);

  return (
    <div className="detail-dossier-container">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} />
      <div className="detail-dossier-content">
        <div className="detail-dossier-main">
          {/* Bouton retour */}
          <button className="btn-back-hero" onClick={() => onNavigate('client-detail', clientId)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span>Retour au client</span>
          </button>

          {/* En-tête MODERNE SOMBRE */}
          <div className="dossier-header-modern">
            <div className="dossier-header-top-modern">
              <div className="dossier-identity-modern">
                <h2>Dossier: {dossier.referenceSteg}</h2>
                <p>
                  <span>Client: {client.prenom} {client.nom}</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </p>
              </div>
              {hasPermission('dossiers', 'update') && (
                <button className="btn-edit-modern" onClick={handleEditDossier}>
                  Modifier le dossier
                </button>
              )}
            </div>

            {/* Onglets de navigation dans l'en-tête */}
            <div className="tabs-modern">
              <button
                className={`tab-modern ${activeTab === 'infos' ? 'active' : ''}`}
                onClick={() => setActiveTab('infos')}
              >
                Informations
              </button>
              <button
                className={`tab-modern ${activeTab === 'produits' ? 'active' : ''}`}
                onClick={() => setActiveTab('produits')}
              >
                Produits
              </button>
              <button
                className={`tab-modern ${activeTab === 'finance' ? 'active' : ''}`}
                onClick={() => setActiveTab('finance')}
              >
                Finances
              </button>
              <button
                className={`tab-modern ${activeTab === 'historique-paiement' ? 'active' : ''}`}
                onClick={() => setActiveTab('historique-paiement')}
              >
                Prochaine échéance
              </button>
              <button
                className={`tab-modern ${activeTab === 'historique' ? 'active' : ''}`}
                onClick={() => setActiveTab('historique')}
              >
                Historique
              </button>
              <button
                className={`tab-modern ${activeTab === 'documents' ? 'active' : ''}`}
                onClick={() => setActiveTab('documents')}
              >
                Documents
              </button>
              <button
                className={`tab-modern ${activeTab === 'archive' ? 'active' : ''}`}
                onClick={() => setActiveTab('archive')}
              >
                Archives
              </button>
              <button
                className={`tab-modern ${activeTab === 'extensions' ? 'active' : ''}`}
                onClick={() => setActiveTab('extensions')}
              >
                Extensions
              </button>
              <button
                className={`tab-modern ${activeTab === 'scenarios' ? 'active' : ''}`}
                onClick={() => setActiveTab('scenarios')}
              >
                Prochaines étapes
              </button>
              <button
                className={`tab-modern ${activeTab === 'reclamations' ? 'active' : ''}`}
                onClick={() => setActiveTab('reclamations')}
              >
                ⚡ Réclamations
                {dossierReclamations.length > 0 && (
                  <span style={{ marginLeft: '6px', background: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '11px', fontWeight: 800 }}>
                    {dossierReclamations.length}
                  </span>
                )}
              </button>
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          {message && <div className="success-message">{message}</div>}

          {/* Contenu des onglets */}
          <div className="tab-content">
            {/* ONGLET INFORMATIONS - DESIGN EXACT */}
            {activeTab === 'infos' && (
              <div className="dossier-info-modern">
                {isEditingDossier ? (
                  <div className="edit-dossier-inline-container">
                    <div className="edit-dossier-header-inline">
                      <h3>MODIFIER LE DOSSIER</h3>
                      <button className="btn-cancel-inline-main" onClick={() => setIsEditingDossier(false)}>Annuler</button>
                    </div>
                    <form onSubmit={handleUpdateDossier} className="form-inline-modern">
                      <div className="form-grid-modern">
                        {/* Section 1: Infos Générales */}
                        <div className="form-section-modern">
                          <h4>Informations Générales</h4>
                          <div className="form-row-modern" style={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#f0f9ff', padding: '10px', borderRadius: '8px', border: '1px solid #bae6fd', marginBottom: '15px' }}>
                            <input 
                              type="checkbox" 
                              id="pompageEditDossier" 
                              name="pompage" 
                              checked={dossierForm.pompage} 
                              onChange={(e) => {
                                const isPompage = e.target.checked;
                                setDossierForm(prev => ({
                                  ...prev, 
                                  pompage: isPompage,
                                  typePaiement: isPompage ? 'comptant' : prev.typePaiement
                                }));
                              }} 
                              style={{ width: '18px', height: '18px', cursor: 'pointer', margin: 0 }} 
                            />
                            <label htmlFor="pompageEditDossier" style={{ margin: 0, cursor: 'pointer', fontWeight: 'bold', color: '#0369a1', width: 'auto' }}>
                              Dossier Pompage (Paiement 100% Comptant, référence STEG non obligatoire)
                            </label>
                          </div>
                          <div className="form-row-modern">
                            <label>Référence STEG {!dossierForm.pompage && '*'}</label>
                            <input
                              type="text"
                              value={dossierForm.referenceSteg}
                              onChange={(e) => setDossierForm({...dossierForm, referenceSteg: e.target.value})}
                              required={!dossierForm.pompage}
                            />
                          </div>
                          <div className="form-row-modern">
                            <label>Puissance (kW) *</label>
                            <input
                                type="text"
                                inputMode="decimal"
                              step="any"
                              value={dossierForm.puissance}
                                onChange={(e) => setDossierForm({...dossierForm, puissance: normalizeDecimalInput(e.target.value)})}
                              required
                            />
                          </div>
                          <div className="form-row-modern">
                            <label>Région STEG (Catégorie)</label>
                            <select
                              value={dossierForm._categorieSteg || ''}
                              onChange={(e) => setDossierForm({...dossierForm, _categorieSteg: e.target.value, district: ''})}
                            >
                              <option value="">-- Sélectionner une région --</option>
                              {CATEGORIES_STEG.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-row-modern">
                            <label>District (Bureau)</label>
                            <select
                              value={String(dossierForm.district || '')}
                              onChange={(e) => setDossierForm({...dossierForm, district: e.target.value})}
                              required
                            >
                              <option value="">-- Sélectionner un bureau --</option>
                              {allDistricts
                                .filter(d => !dossierForm._categorieSteg || d.categorie === dossierForm._categorieSteg)
                                .map(d => (
                                  <option key={String(d._id)} value={String(d._id)}>{d.region}</option>
                                ))
                              }
                            </select>
                          </div>
                          <div className="form-row-modern">
                            <label>Mois/Année</label>
                            <input
                              type="text"
                              placeholder="Exemple: Novembre 2025"
                              value={dossierForm.moisAnnee}
                              onChange={(e) => setDossierForm({...dossierForm, moisAnnee: e.target.value})}
                            />
                          </div>
                        </div>

                        {/* Section 2: Intervenants & Paiement */}
                        <div className="form-section-modern">
                          <h4>Intervenants & Paiement</h4>
                          <div className="form-row-modern">
                            <label>Commercial</label>
                            <div style={{position:'relative'}}>
                              <input
                                type="text"
                                placeholder="Rechercher un commercial..."
                                value={commercialSearchForm}
                                onChange={(e) => {
                                  setCommercialSearchForm(e.target.value);
                                  if (!e.target.value) setDossierForm(prev => ({...prev, commercial: ''}));
                                }}
                                style={{width:'100%', padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:'8px', fontSize:'0.9375rem', color:'#1e293b', background:'#f8fafc', boxSizing:'border-box'}}
                              />
                              {dossierForm.commercial && (
                                <span style={{position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', color:'#10b981', fontWeight:'700', fontSize:'14px', cursor:'pointer'}} onClick={() => { setDossierForm(prev => ({...prev, commercial: ''})); setCommercialSearchForm(''); }}>✕</span>
                              )}
                              {commercialSearchForm && !dossierForm.commercial && (
                                <div style={{position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1.5px solid #3b82f6', borderTop:'none', borderRadius:'0 0 8px 8px', maxHeight:'200px', overflowY:'auto', zIndex:200, boxShadow:'0 4px 16px rgba(0,0,0,0.12)'}}>
                                  <div
                                    style={{padding:'9px 14px', cursor:'pointer', fontSize:'13px', color:'#6b7280', borderBottom:'1px solid #f1f5f9'}}
                                    onClick={() => { setDossierForm(prev => ({...prev, commercial: ''})); setCommercialSearchForm(''); }}
                                  >
                                    — Aucun commercial —
                                  </div>
                                  {allCommerciaux
                                    .filter(c => `${c.prenom} ${c.nom}`.toLowerCase().includes(commercialSearchForm.toLowerCase()))
                                    .map(c => (
                                      <div
                                        key={String(c._id)}
                                        style={{padding:'9px 14px', cursor:'pointer', fontSize:'13px', color:'#1e293b', borderBottom:'1px solid #f9fafb'}}
                                        onMouseEnter={e => e.currentTarget.style.background='#f0f9ff'}
                                        onMouseLeave={e => e.currentTarget.style.background='transparent'}
                                        onClick={() => { setDossierForm(prev => ({...prev, commercial: String(c._id)})); setCommercialSearchForm(`${c.prenom} ${c.nom}`); }}
                                      >
                                        <strong>{c.prenom} {c.nom}</strong>
                                      </div>
                                    ))
                                  }
                                  {allCommerciaux.filter(c => `${c.prenom} ${c.nom}`.toLowerCase().includes(commercialSearchForm.toLowerCase())).length === 0 && (
                                    <div style={{padding:'9px 14px', fontSize:'12px', color:'#9ca3af', fontStyle:'italic'}}>Aucun résultat pour "{commercialSearchForm}"</div>
                                  )}
                                </div>
                              )}
                            </div>
                            {dossierForm.commercial && (
                              <small style={{color:'#10b981', fontSize:'0.78rem', marginTop:'4px', display:'block'}}>
                                ✓ Sélectionné : {allCommerciaux.find(c => String(c._id) === String(dossierForm.commercial)) ? `${allCommerciaux.find(c => String(c._id) === String(dossierForm.commercial)).prenom} ${allCommerciaux.find(c => String(c._id) === String(dossierForm.commercial)).nom}` : ''}
                              </small>
                            )}
                          </div>
                          <div className="form-row-modern">
                            <label>Chef d'équipe (Installateur)</label>
                            <select
                              value={String(dossierForm.installateur || '')}
                              onChange={(e) => setDossierForm({...dossierForm, installateur: e.target.value})}
                            >
                              <option value="">-- Aucun --</option>
                              {allInstallateurs.map(i => (
                                <option key={String(i._id)} value={String(i._id)}>
                                  {i.prenom} {i.nom} {i.isEquipe ? `(Équipe: ${i.typeEquipe})` : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="form-row-modern">
                            <label>Montant Crédit STEG (TND)</label>
                            <select
                              value={dossierForm.montantSteg !== undefined && dossierForm.montantSteg !== null ? dossierForm.montantSteg : ''}
                              onChange={(e) => setDossierForm({...dossierForm, montantSteg: e.target.value})}
                              disabled={dossierForm.pompage}
                            >
                              <option value="0">Sélectionner le crédit STEG (0 DT - Comptant)</option>
                              {getStegOptionsList(dossierForm.montantTotalPaiement, dossierForm.montantSteg).map(opt => (
                                <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="form-row-modern">
                            <label>Montant total paiement (TND)</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              step="any"
                              min="0"
                              value={dossierForm.montantTotalPaiement}
                              onChange={(e) => setDossierForm({...dossierForm, montantTotalPaiement: normalizeDecimalInput(e.target.value)})}
                            />
                          </div>
                          <div className="form-row-modern">
                            <label>Autofinancement (Calculé TND)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={repartitionFormEdition.montantAutofinancement}
                              readOnly
                              style={{ backgroundColor: '#f1f5f9', color: '#1e293b', fontWeight: '600' }}
                            />
                          </div>
                          <div className="form-row-modern">
                            <label>Montant comptant (TND)</label>
                            <input
                                type="text"
                                inputMode="decimal"
                              step="any"
                              min="0"
                              value={dossierForm.montantComptant}
                                onChange={(e) => setDossierForm({...dossierForm, montantComptant: normalizeDecimalInput(e.target.value)})}
                            />
                          </div>
                          <div className="form-row-modern">
                            <label>Montant crédit (calculé)</label>
                            <input
                              type="number"
                              step="0.001"
                              value={repartitionFormEdition.montantCredit}
                              readOnly
                              style={{backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280'}}
                            />
                          </div>
                          {repartitionFormEdition.montantCredit > 0 && (
                            <>
                              <div className="form-row-modern">
                                <label>Nombre de mois crédit</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="120"
                                  value={dossierForm.nombreMoisCredit}
                                  onChange={(e) => setDossierForm({...dossierForm, nombreMoisCredit: e.target.value})}
                                />
                              </div>
                              <div className="form-row-modern">
                                <label>Date début crédit</label>
                                <input
                                  type="date"
                                  value={dossierForm.dateDebutCredit}
                                  onChange={(e) => setDossierForm({...dossierForm, dateDebutCredit: e.target.value})}
                                />
                              </div>
                            </>
                          )}
                          <div className="form-row-modern">
                            <label>Mode paiement comptant</label>
                            <select
                              value={dossierForm.modePaiementComptant || 'especes'}
                              onChange={(e) => setDossierForm({...dossierForm, modePaiementComptant: e.target.value})}
                            >
                              {PAYMENT_MODES.map(m => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Section 3: État & Documents */}
                        <div className="form-section-modern">
                          <h4>État & Suivi</h4>
                          <div className="form-row-modern">
                            <label>État du Dossier</label>
                            {['Réceptionné', 'Manque PV', 'Dossier en facturation'].includes(dossier?.etatDossier) ? (
                              <select
                                value={dossierForm.etatDossier}
                                onChange={(e) => setDossierForm({...dossierForm, etatDossier: e.target.value, modificationManuelleEtat: true})}
                              >
                                <option value="Réceptionné">Réceptionné</option>
                                <option value="Manque PV">Manque PV</option>
                                <option value="Dossier en facturation">Dossier en facturation</option>
                              </select>
                            ) : (
                              <div>
                                <input
                                  type="text"
                                  value={dossierForm.etatDossier || 'Déposé chez Sastra'}
                                  disabled
                                  style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed', color: '#64748b', fontWeight: '600' }}
                                />
                                <small style={{ color: '#64748b', fontSize: '0.78rem', display: 'block', marginTop: '4px' }}>
                                  💡 L'état du dossier évolue automatiquement via les dates de la Chronologie.
                                </small>
                              </div>
                            )}
                          </div>
                          <div className="form-row-modern">
                            <label>BL</label>
                            <input
                              type="date"
                              value={dossierForm.bl ? (dossierForm.bl.includes('T') ? dossierForm.bl.split('T')[0] : dossierForm.bl) : ''}
                              onChange={(e) => setDossierForm({...dossierForm, bl: e.target.value})}
                            />
                          </div>
                          <div className="form-row-modern">
                            <label>Facture</label>
                            <input
                              type="text"
                              value={dossierForm.facture}
                              onChange={(e) => setDossierForm({...dossierForm, facture: e.target.value})}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="form-notes-modern">
                        <label>Notes</label>
                        <textarea
                          value={dossierForm.notes}
                          onChange={(e) => setDossierForm({...dossierForm, notes: e.target.value})}
                          rows="3"
                          placeholder="Commentaires sur le dossier..."
                        />
                      </div>

                      <div className="form-actions-inline">
                        <button type="button" className="btn-cancel-inline-main" onClick={() => setIsEditingDossier(false)}>Annuler</button>
                        <button type="submit" className="btn-save-inline-main">Enregistrer les modifications</button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <>
                    {/* Actions de gestion */}
                    {hasPermission('dossiers', 'update') && (
                      <div className="management-actions-bar">
                        <div className="management-actions-left">
                          <button className="btn-management btn-reassign" onClick={() => {
                            setTargetCIN('');
                            setReassignClientTarget(null);
                            setShowReassignModal(true);
                          }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M16 3h5v5M4 20L20.2 3.8M21 16v5h-5M15 15l5.1 5.1M4 4l5 5"/>
                            </svg>
                            Réaffecter le dossier
                          </button>
                        </div>
                        <div className="management-actions-right">
                          <button className="btn-management btn-edit" onClick={handleEditDossier} disabled={dossier.isCanceled}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            Modifier
                          </button>
                          {hasPermission('dossiers', 'update') && !dossier.isCanceled && (
                            <button className="btn-management btn-delete-dossier" style={{backgroundColor: '#f59e0b', color: 'white', borderColor: '#f59e0b'}} onClick={handleCancelDossier}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="15" y1="9" x2="9" y2="15"></line>
                                <line x1="9" y1="9" x2="15" y2="15"></line>
                              </svg>
                              Annuler le dossier
                            </button>
                          )}
                          {hasPermission('dossiers', 'delete') && (
                            <button className="btn-management btn-delete-dossier" onClick={handleDeleteDossier}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                              Supprimer
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Badges supérieurs */}
                <div className="info-badges-top">
                  {dossier.isCanceled && (
                    <div className="badge-puissance" style={{backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5'}}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                      <span>DOSSIER ANNULÉ</span>
                    </div>
                  )}
                  <div className="badge-puissance">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="5" />
                      <line x1="12" y1="1" x2="12" y2="3" />
                      <line x1="12" y1="21" x2="12" y2="23" />
                      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                      <line x1="1" y1="12" x2="3" y2="12" />
                      <line x1="21" y1="12" x2="23" y2="12" />
                      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                    </svg>
                    <span>PUISSANCE: {dossier.puissance} kW</span>
                  </div>
                  {prochainPaiementAffiche && (
                    <div className="badge-credit-alert">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                      <span>
                        {montantRestantCredit > 0 ? `CRÉDIT RESTANT: ${formatCurrency(montantRestantCredit)} - ` : ''}
                        {prochainPaiementAffiche.label}: {prochainPaiementAffiche.date} ({formatCurrency(prochainPaiementAffiche.montant)})
                      </span>
                    </div>
                  )}
                </div>

                {/* Grid 3 colonnes */}
                <div className="info-cards-grid-modern">
                  {/* Card 1: Informations G\u00E9n\u00E9rales */}
                  <div className="info-card-modern">
                    <h3 className="info-card-title-modern">INFORMATIONS GÉNÉRALES</h3>
                    <div className="info-card-content-modern">
                      {/* RÃ‰FÃ‰RENCE STEG */}
                      <div className="info-item-modern">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <div className="info-item-content">
                          <span className="info-label-modern">RÉFÉRENCE STEG</span>
                          {showQuickEditField === 'referenceSteg' ? (
                            <div className="quick-edit-inline">
                              <input
                                type="text"
                                value={quickFieldValue}
                                onChange={(e) => setQuickFieldValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleQuickUpdateField('referenceSteg');
                                  if (e.key === 'Escape') handleCancelQuickEdit();
                                }}
                                autoFocus
                                className="quick-edit-input"
                              />
                              <button onClick={() => handleQuickUpdateField('referenceSteg')} className="btn-save-inline">✓</button>
                              <button onClick={handleCancelQuickEdit} className="btn-cancel-inline">✕</button>
                            </div>
                          ) : hasPermission('dossiers', 'update') ? (
                            <span
                              className="info-value-modern editable-field"
                              onClick={() => handleStartQuickEditField('referenceSteg', dossier.referenceSteg)}
                              title="Cliquez pour modifier"
                            >
                              {dossier.referenceSteg}
                              <span className="edit-icon">✎</span>
                            </span>
                          ) : (
                            <span className="info-value-modern">
                              {dossier.referenceSteg}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* DISTRICT */}
                      <div className="info-item-modern">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        <div className="info-item-content">
                          <span className="info-label-modern">DISTRICT</span>
                          {showQuickEditField === 'district' ? (
                            <div className="quick-edit-inline">
                              <select
                                value={quickFieldValue}
                                onChange={(e) => setQuickFieldValue(e.target.value)}
                                autoFocus
                                className="quick-edit-select"
                              >
                                <option value="">-- Aucun --</option>
                                {allDistricts.map(d => (
                                  <option key={String(d._id)} value={String(d._id)}>{d.region} ({d.categorie})</option>
                                ))}
                              </select>
                              <button onClick={() => handleQuickUpdateField('district')} className="btn-save-inline">✓</button>
                              <button onClick={handleCancelQuickEdit} className="btn-cancel-inline">✕</button>
                            </div>
                          ) : (
                            <span
                              className="info-value-modern editable-field"
                              onClick={() => handleStartQuickEditField('district', dossier.district?._id || '')}
                              title="Cliquez pour modifier"
                            >
                              <span>{typeof dossier.district === 'object' ? (dossier.district?.region || '—') : '—'}</span>
                              {typeof dossier.district === 'object' && dossier.district?.categorie && (
                                <span style={{display:'block',fontSize:'11px',color:'#6b7280',marginTop:'2px'}}>{dossier.district.categorie}</span>
                              )}
                              <span className="edit-icon">✎</span>
                            </span>
                          )}
                        </div>
                      </div>

                      {/* PROJET MT */}
                      {project && (
                      <div className="info-item-modern">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                        </svg>
                        <div className="info-item-content">
                          <span className="info-label-modern">PROJET MT</span>
                          <span className="info-value-modern" style={{display:'inline-flex',alignItems:'center',padding:'3px 10px',borderRadius:'12px',background:'#eff6ff',color:'#1d4ed8',border:'1px solid #bfdbfe',fontWeight:'600',fontSize:'0.82rem'}}>
                            {project.nomProjet}
                          </span>
                        </div>
                      </div>
                      )}

                      {/* MOIS/ANNÉE */}
                      <div className="info-item-modern">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        <div className="info-item-content">
                          <span className="info-label-modern">MOIS/ANNÉE</span>
                          {showQuickEditField === 'moisAnnee' ? (
                            <div className="quick-edit-inline">
                              <input
                                type="text"
                                value={quickFieldValue}
                                onChange={(e) => setQuickFieldValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleQuickUpdateField('moisAnnee');
                                  if (e.key === 'Escape') handleCancelQuickEdit();
                                }}
                                placeholder="MM/YYYY"
                                autoFocus
                                className="quick-edit-input"
                              />
                              <button onClick={() => handleQuickUpdateField('moisAnnee')} className="btn-save-inline">✓</button>
                              <button onClick={handleCancelQuickEdit} className="btn-cancel-inline">✕</button>
                            </div>
                          ) : (
                            <span
                              className="info-value-modern editable-field"
                              onClick={() => handleStartQuickEditField('moisAnnee', dossier.moisAnnee)}
                              title="Cliquez pour modifier"
                            >
                              {dossier.moisAnnee}
                              <span className="edit-icon">✎</span>
                            </span>
                          )}
                        </div>
                      </div>

                  
                    </div>
                  </div>

                  {/* Card 2: Intervenants */}
                  <div className="info-card-modern">
                    <h3 className="info-card-title-modern">INTERVENANTS</h3>
                    <div className="info-card-content-modern">
                      {/* COMMERCIAL */}
                      <div className="info-item-modern">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        <div className="info-item-content">
                          <span className="info-label-modern">COMMERCIAL</span>
                          {showQuickEditField === 'commercial' ? (
                            <div className="quick-edit-inline" style={{flexDirection:'column', alignItems:'flex-start', gap:'6px'}}>
                              <div style={{position:'relative', width:'100%'}}>
                                <input
                                  type="text"
                                  placeholder="Rechercher un commercial..."
                                  value={commercialSearch}
                                  onChange={(e) => setCommercialSearch(e.target.value)}
                                  autoFocus
                                  className="quick-edit-input"
                                  style={{width:'100%', minWidth:'200px'}}
                                />
                                {commercialSearch && (
                                  <div style={{position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'2px solid #3b82f6', borderTop:'none', borderRadius:'0 0 6px 6px', maxHeight:'180px', overflowY:'auto', zIndex:100, boxShadow:'0 4px 12px rgba(0,0,0,0.1)'}}>
                                    <div
                                      style={{padding:'8px 12px', cursor:'pointer', fontSize:'13px', color:'#6b7280', borderBottom:'1px solid #f1f5f9'}}
                                      onClick={() => { setQuickFieldValue(''); setCommercialSearch(''); }}
                                    >
                                      — Aucun commercial —
                                    </div>
                                    {allCommerciaux
                                      .filter(c => `${c.prenom} ${c.nom}`.toLowerCase().includes(commercialSearch.toLowerCase()))
                                      .map(c => (
                                        <div
                                          key={String(c._id)}
                                          style={{padding:'8px 12px', cursor:'pointer', fontSize:'13px', fontWeight: String(c._id) === quickFieldValue ? '700' : '500', background: String(c._id) === quickFieldValue ? '#eff6ff' : 'transparent', color:'#1e293b', borderBottom:'1px solid #f9fafb'}}
                                          onMouseEnter={e => e.currentTarget.style.background='#f0f9ff'}
                                          onMouseLeave={e => e.currentTarget.style.background = String(c._id) === quickFieldValue ? '#eff6ff' : 'transparent'}
                                          onClick={() => { setQuickFieldValue(String(c._id)); setCommercialSearch(`${c.prenom} ${c.nom}`); }}
                                        >
                                          {c.prenom} {c.nom}
                                        </div>
                                      ))
                                    }
                                    {allCommerciaux.filter(c => `${c.prenom} ${c.nom}`.toLowerCase().includes(commercialSearch.toLowerCase())).length === 0 && (
                                      <div style={{padding:'8px 12px', fontSize:'12px', color:'#9ca3af', fontStyle:'italic'}}>Aucun résultat</div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div style={{display:'flex', gap:'6px'}}>
                                <button onClick={() => { handleQuickUpdateField('commercial'); setCommercialSearch(''); }} className="btn-save-inline">✓</button>
                                <button onClick={() => { handleCancelQuickEdit(); setCommercialSearch(''); }} className="btn-cancel-inline">✕</button>
                              </div>
                            </div>
                          ) : (
                            <span
                              className="info-value-modern editable-field"
                              onClick={() => handleStartQuickEditField('commercial', dossier.commercial?._id || '')}
                              title="Cliquez pour modifier"
                            >
                              {typeof dossier.commercial === 'object'
                                ? (dossier.commercial ? `${dossier.commercial.prenom || ''} ${dossier.commercial.nom || ''}`.trim() || '—' : '—')
                                : '—'}
                              <span className="edit-icon">✎</span>
                            </span>
                          )}
                        </div>
                      </div>

                      {/* CHEF D'ÉQUIPE */}
                      <div className="info-item-modern">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        <div className="info-item-content">
                          <span className="info-label-modern">CHEF D'ÉQUIPE (INSTALLATEUR)</span>
                          {showQuickEditField === 'installateur' ? (
                            <div className="quick-edit-inline">
                              <select
                                value={quickFieldValue}
                                onChange={(e) => setQuickFieldValue(e.target.value)}
                                autoFocus
                                className="quick-edit-select"
                              >
                                <option value="">-- Aucun --</option>
                                {allInstallateurs.map(i => (
                                  <option key={String(i._id)} value={String(i._id)}>
                                    {i.prenom} {i.nom} {i.isEquipe ? `(${i.typeEquipe})` : ''}
                                  </option>
                                ))}
                              </select>
                              <button onClick={() => handleQuickUpdateField('installateur')} className="btn-save-inline">✓</button>
                              <button onClick={handleCancelQuickEdit} className="btn-cancel-inline">✕</button>
                            </div>
                          ) : (
                            <span
                              className="info-value-modern editable-field"
                              onClick={() => handleStartQuickEditField('installateur', dossier.installateur?._id || '')}
                              title="Cliquez pour modifier"
                            >
                              {typeof dossier.installateur === 'object'
                                ? (dossier.installateur ? `${dossier.installateur.chefEquipe?.prenom || ''} ${dossier.installateur.chefEquipe?.nom || ''}`.trim() || '—' : '—')
                                : '—'}
                              <span className="edit-icon">✎</span>
                            </span>
                          )}
                        </div>
                      </div>

                      {/* BL (Bon de Livraison) */}
                      <div className="info-item-modern">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        <div className="info-item-content">
                          <span className="info-label-modern">BL</span>
                          {showQuickEditField === 'bl' ? (
                            <div className="quick-edit-inline">
                              <input
                                type="date"
                                value={quickFieldValue ? (quickFieldValue.includes('T') ? quickFieldValue.split('T')[0] : quickFieldValue) : ''}
                                onChange={(e) => setQuickFieldValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleQuickUpdateField('bl');
                                  if (e.key === 'Escape') handleCancelQuickEdit();
                                }}
                                autoFocus
                                className="quick-edit-input"
                              />
                              <button onClick={() => handleQuickUpdateField('bl')} className="btn-save-inline">✓</button>
                              <button onClick={handleCancelQuickEdit} className="btn-cancel-inline">✕</button>
                            </div>
                          ) : (
                            <span
                              className="info-value-modern editable-field"
                              onClick={() => handleStartQuickEditField('bl', dossier.bl ? (dossier.bl.includes('T') ? dossier.bl.split('T')[0] : dossier.bl) : '')}
                              title="Cliquez pour modifier la date BL"
                            >
                              {dossier.bl ? (
                                !isNaN(new Date(dossier.bl).getTime()) && dossier.bl.length >= 8
                                  ? new Date(dossier.bl).toLocaleDateString('fr-FR')
                                  : dossier.bl
                              ) : '—'}
                              <span className="edit-icon">✎</span>
                            </span>
                          )}
                        </div>
                      </div>

                      {/* ÉTAT DU DOSSIER */}
                      <div className="info-item-modern">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <div className="info-item-content">
                          <span className="info-label-modern">ÉTAT DU DOSSIER</span>
                          {showQuickEditField === 'etatDossier' && ['Réceptionné', 'Manque PV', 'Dossier en facturation'].includes(dossier?.etatDossier) ? (
                            <div className="quick-edit-inline">
                              <select
                                value={quickFieldValue}
                                onChange={(e) => setQuickFieldValue(e.target.value)}
                                autoFocus
                                className="quick-edit-select"
                              >
                                <option value="Réceptionné">Réceptionné</option>
                                <option value="Manque PV">Manque PV</option>
                                <option value="Dossier en facturation">Dossier en facturation</option>
                              </select>
                              <button onClick={() => handleQuickUpdateField('etatDossier')} className="btn-save-inline">✓</button>
                              <button onClick={handleCancelQuickEdit} className="btn-cancel-inline">✕</button>
                            </div>
                          ) : (
                            <span
                              className={`info-value-modern ${['Réceptionné', 'Manque PV', 'Dossier en facturation'].includes(dossier?.etatDossier) ? 'editable-field' : ''}`}
                              onClick={() => {
                                if (['Réceptionné', 'Manque PV', 'Dossier en facturation'].includes(dossier?.etatDossier)) {
                                  handleStartQuickEditField('etatDossier', dossier.etatDossier);
                                }
                              }}
                              title={['Réceptionné', 'Manque PV', 'Dossier en facturation'].includes(dossier?.etatDossier) ? "Cliquez pour basculer entre Réceptionné / Manque PV / Dossier en facturation" : "L'état évolue automatiquement via la Chronologie du projet"}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '0.85rem',
                                fontWeight: '700',
                                ...getStatusStyle(dossier.etatDossier)
                              }}
                            >
                              {dossier.etatDossier || '—'}
                              {['Réceptionné', 'Manque PV', 'Dossier en facturation'].includes(dossier?.etatDossier) && (
                                <span className="edit-icon" style={{marginLeft: '8px', color: 'inherit'}}>✎</span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Chronologie du Projet */}
                  <div className="info-card-modern">
                    <h3 className="info-card-title-modern">CHRONOLOGIE DU PROJET</h3>
                    <div className="info-card-content-modern">
                      {dossier.pompage ? (
                        <div className="timeline-modern">
                          {/* 1. DATE ARRIVÉE DOSSIER */}
                          <div className="timeline-item-modern">
                            <div className="timeline-dot-modern timeline-dot-green"></div>
                            <div className="timeline-content-modern">
                              {showQuickEditDate === 'dateArrivDossier' ? (
                                <div className="quick-edit-inline-date">
                                  <input type="date" value={quickDateValue}
                                    onChange={(e) => setQuickDateValue(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleQuickUpdateDate('dateArrivDossier'); if (e.key === 'Escape') handleCancelQuickEdit(); }}
                                    autoFocus className="quick-edit-date-input" />
                                  <button onClick={() => handleQuickUpdateDate('dateArrivDossier')} className="btn-save-inline">✓</button>
                                  <button onClick={handleCancelQuickEdit} className="btn-cancel-inline">✕</button>
                                </div>
                              ) : (
                                <span className="timeline-date-modern editable-field"
                                  onClick={() => handleStartQuickEditDate('dateArrivDossier', dossier.dateArrivDossier)} title="Cliquez pour modifier">
                                  {formatDate(dossier.dateArrivDossier)}<span className="edit-icon">✎</span>
                                </span>
                              )}
                              <span className="timeline-label-modern">DATE ARRIVÉE DOSSIER</span>
                              {dossier.dateArrivDossier && (
                                <span style={{display:'inline-block',marginTop:'4px',padding:'2px 8px',borderRadius:'10px',fontSize:'11px',fontWeight:'600',background:'#d1fae5',color:'#065f46'}}>→ Déposé chez Sastra</span>
                              )}
                            </div>
                          </div>

                          {/* 2. DÉPOSÉ CHEZ SASTRA */}
                          <div className="timeline-item-modern">
                            <div className="timeline-dot-modern timeline-dot-green"></div>
                            <div className="timeline-content-modern">
                              <span className="timeline-date-modern" style={{fontWeight:'700',color:'#0284c7'}}>DÉPOSÉ CHEZ SASTRA</span>
                              <span className="timeline-label-modern">ÉTAT INITIAL</span>
                              <span style={{display:'inline-block',marginTop:'4px',padding:'2px 8px',borderRadius:'10px',fontSize:'11px',fontWeight:'600',background: dossier.etatDossier === 'Déposé chez Sastra' ? '#e0f2fe' : '#ecfdf5', color: dossier.etatDossier === 'Déposé chez Sastra' ? '#0369a1' : '#047857'}}>
                                {dossier.etatDossier === 'Déposé chez Sastra' ? '✓ État actuel' : 'Étape effectuée'}
                              </span>
                            </div>
                          </div>

                          {/* 3. EN ATTENTE D'INSTALLATION */}
                          <div className="timeline-item-modern">
                            <div className="timeline-dot-modern timeline-dot-orange"></div>
                            <div className="timeline-content-modern">
                              <span className="timeline-date-modern" style={{fontWeight:'700',color:'#d97706'}}>EN ATTENTE D'INSTALLATION</span>
                              <span className="timeline-label-modern">ÉTAT PLANIFICATION</span>
                              <span style={{display:'inline-block',marginTop:'4px',padding:'2px 8px',borderRadius:'10px',fontSize:'11px',fontWeight:'600',background: dossier.etatDossier === 'En attente d\'installation' ? '#fef3c7' : dossier.dateLivraison ? '#ecfdf5' : '#f3f4f6', color: dossier.etatDossier === 'En attente d\'installation' ? '#92400e' : dossier.dateLivraison ? '#047857' : '#6b7280'}}>
                                {dossier.etatDossier === 'En attente d\'installation' ? '✓ État actuel' : dossier.dateLivraison ? 'Étape planifiée' : 'En attente'}
                              </span>
                            </div>
                          </div>

                          {/* 4. DATE D'INSTALLATION */}
                          <div className="timeline-item-modern">
                            <div className="timeline-dot-modern timeline-dot-blue"></div>
                            <div className="timeline-content-modern">
                              {showQuickEditDate === 'dateLivraison' ? (
                                <div className="quick-edit-inline-date">
                                  <input type="date" value={quickDateValue}
                                    onChange={(e) => setQuickDateValue(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleQuickUpdateDate('dateLivraison'); if (e.key === 'Escape') handleCancelQuickEdit(); }}
                                    autoFocus className="quick-edit-date-input" />
                                  <button onClick={() => handleQuickUpdateDate('dateLivraison')} className="btn-save-inline">✓</button>
                                  <button onClick={handleCancelQuickEdit} className="btn-cancel-inline">✕</button>
                                </div>
                              ) : (
                                <span className="timeline-date-modern editable-field"
                                  onClick={() => handleStartQuickEditDate('dateLivraison', dossier.dateLivraison)} title="Cliquez pour modifier">
                                  {formatDate(dossier.dateLivraison)}<span className="edit-icon">✎</span>
                                </span>
                              )}
                              <span className="timeline-label-modern">DATE D'INSTALLATION</span>
                              {dossier.dateLivraison && (
                                <span style={{display:'inline-block',marginTop:'4px',padding:'2px 8px',borderRadius:'10px',fontSize:'11px',fontWeight:'600',background:'#dbeafe',color:'#1e40af'}}>→ Date d'installation fixée</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="timeline-modern">
                          {/* 1. DATE ARRIVÉE DOSSIER → Déposé chez Sastra */}
                          <div className="timeline-item-modern">
                            <div className="timeline-dot-modern timeline-dot-green"></div>
                            <div className="timeline-content-modern">
                              {showQuickEditDate === 'dateArrivDossier' ? (
                                <div className="quick-edit-inline-date">
                                  <input type="date" value={quickDateValue}
                                    onChange={(e) => setQuickDateValue(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleQuickUpdateDate('dateArrivDossier'); if (e.key === 'Escape') handleCancelQuickEdit(); }}
                                    autoFocus className="quick-edit-date-input" />
                                <button onClick={() => handleQuickUpdateDate('dateArrivDossier')} className="btn-save-inline">✓</button>
                                <button onClick={handleCancelQuickEdit} className="btn-cancel-inline">✕</button>
                                </div>
                              ) : (
                                <span className="timeline-date-modern editable-field"
                                  onClick={() => handleStartQuickEditDate('dateArrivDossier', dossier.dateArrivDossier)} title="Cliquez pour modifier">
                                  {formatDate(dossier.dateArrivDossier)}<span className="edit-icon">✎</span>
                                </span>
                              )}
                              <span className="timeline-label-modern">DATE ARRIVÉE DOSSIER</span>
                              {dossier.dateArrivDossier && (
                                dossier.projectId
                                  ? <span style={{display:'inline-block',marginTop:'4px',padding:'2px 8px',borderRadius:'10px',fontSize:'11px',fontWeight:'600',background:'#fff7ed',color:'#9a3412'}}>→ En attente d'approbation bureau d'étude</span>
                                  : <span style={{display:'inline-block',marginTop:'4px',padding:'2px 8px',borderRadius:'10px',fontSize:'11px',fontWeight:'600',background:'#d1fae5',color:'#065f46'}}>→ Déposé chez Sastra</span>
                              )}
                            </div>
                          </div>

                          {/* 1.5. DATE APPROBATION BE → Approuvé bureau d'étude (MT only) */}
                          {dossier.projectId && (
                          <div className="timeline-item-modern">
                            <div className="timeline-dot-modern timeline-dot-green"></div>
                            <div className="timeline-content-modern">
                              {showQuickEditDate === 'dateApprobationBE' ? (
                                <div className="quick-edit-inline-date">
                                  <input type="date" value={quickDateValue}
                                    onChange={(e) => setQuickDateValue(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleQuickUpdateDate('dateApprobationBE'); if (e.key === 'Escape') handleCancelQuickEdit(); }}
                                    autoFocus className="quick-edit-date-input" />
                                  <button onClick={() => handleQuickUpdateDate('dateApprobationBE')} className="btn-save-inline">✓</button>
                                  <button onClick={handleCancelQuickEdit} className="btn-cancel-inline">✕</button>
                                </div>
                              ) : (
                                <span className="timeline-date-modern editable-field"
                                  onClick={() => handleStartQuickEditDate('dateApprobationBE', dossier.dateApprobationBE)} title="Cliquez pour modifier">
                                  {formatDate(dossier.dateApprobationBE)}<span className="edit-icon">✎</span>
                                </span>
                              )}
                              <span className="timeline-label-modern">DATE APPROBATION BUREAU D'ÉTUDE</span>
                              {dossier.dateApprobationBE && (
                                <span style={{display:'inline-block',marginTop:'4px',padding:'2px 8px',borderRadius:'10px',fontSize:'11px',fontWeight:'600',background:'#ecfdf5',color:'#065f46'}}>→ Approuvé bureau d'étude</span>
                              )}
                            </div>
                          </div>
                          )}

                          {/* 2. DATE DÉPÔT STEG → En attente d'approbation */}
                          <div className={`timeline-item-modern ${dossier.pompage ? 'opacity-50' : ''}`}>
                            <div className="timeline-dot-modern timeline-dot-orange"></div>
                            <div className="timeline-content-modern">
                              {dossier.pompage ? (
                                <span className="timeline-date-modern" style={{color: '#9ca3af', fontStyle: 'italic'}}>Non applicable (Dossier Pompage)</span>
                              ) : showQuickEditDate === 'dateDepotSteg' ? (
                                <div className="quick-edit-inline-date">
                                  <input type="date" value={quickDateValue}
                                    onChange={(e) => setQuickDateValue(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleQuickUpdateDate('dateDepotSteg'); if (e.key === 'Escape') handleCancelQuickEdit(); }}
                                    autoFocus className="quick-edit-date-input" />
                                <button onClick={() => handleQuickUpdateDate('dateDepotSteg')} className="btn-save-inline">✓</button>
                                <button onClick={handleCancelQuickEdit} className="btn-cancel-inline">✕</button>
                                </div>
                              ) : (
                                <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap'}}>
                                  <span className="timeline-date-modern editable-field"
                                    onClick={() => handleStartQuickEditDate('dateDepotSteg', dossier.dateDepotSteg)} title="Cliquez pour modifier">
                                    {formatDate(dossier.dateDepotSteg)}<span className="edit-icon">✎</span>
                                  </span>
                                  {dossier.dateDepotSteg && dossier.district && (
                                    <button onClick={handleOpenStegModal} title="Envoyer au bureau STEG"
                                      style={{padding:'3px 10px',fontSize:'12px',background:'linear-gradient(135deg,#f59e0b,#d97706)',color:'#fff',border:'none',borderRadius:'20px',cursor:'pointer',fontWeight:'600',display:'flex',alignItems:'center',gap:'4px',boxShadow:'0 2px 6px rgba(245,158,11,0.4)',transition:'transform 0.15s'}}
                                      onMouseOver={e => e.currentTarget.style.transform='scale(1.05)'}
                                      onMouseOut={e => e.currentTarget.style.transform='scale(1)'}>
                                      📧 Notifier STEG
                                    </button>
                                  )}
                                </div>
                              )}
                              <span className="timeline-label-modern">DATE DÉPÔT STEG</span>
                              {!dossier.pompage && dossier.dateDepotSteg && (
                                <span style={{display:'inline-block',marginTop:'4px',padding:'2px 8px',borderRadius:'10px',fontSize:'11px',fontWeight:'600',background:'#fef3c7',color:'#92400e'}}>→ En attente d'approbation</span>
                              )}
                              {!dossier.pompage && notificationSteg && (
                                <div style={{marginTop:'6px',padding:'6px 12px',borderRadius:'6px',fontSize:'12px',fontWeight:'500',background:notificationSteg.type==='success'?'#d1fae5':'#fee2e2',color:notificationSteg.type==='success'?'#065f46':'#991b1b',border:`1px solid ${notificationSteg.type==='success'?'#6ee7b7':'#fca5a5'}`}}>
                                  {notificationSteg.message}
                                  <button onClick={() => setNotificationSteg(null)} style={{marginLeft:'8px',background:'none',border:'none',cursor:'pointer',fontSize:'11px',opacity:0.6}}>✕</button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* 3. DATE PAIEMENT DF → En attente d'installation */}
                          <div className={`timeline-item-modern ${dossier.pompage ? 'opacity-50' : ''}`}>
                            <div className="timeline-dot-modern timeline-dot-blue"></div>
                            <div className="timeline-content-modern">
                              {dossier.pompage ? (
                                <span className="timeline-date-modern" style={{color: '#9ca3af', fontStyle: 'italic'}}>Non applicable (Dossier Pompage)</span>
                              ) : showQuickEditDate === 'dateDF' ? (
                                <div className="quick-edit-inline-date">
                                  <input type="date" value={quickDateValue}
                                    onChange={(e) => setQuickDateValue(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleQuickUpdateDate('dateDF'); if (e.key === 'Escape') handleCancelQuickEdit(); }}
                                    autoFocus className="quick-edit-date-input" />
                                <button onClick={() => handleQuickUpdateDate('dateDF')} className="btn-save-inline">✓</button>
                                <button onClick={handleCancelQuickEdit} className="btn-cancel-inline">✕</button>
                                </div>
                              ) : (
                                <span className="timeline-date-modern editable-field"
                                  onClick={() => handleStartQuickEditDate('dateDF', dossier.dateDF)} title="Cliquez pour modifier">
                                  {formatDate(dossier.dateDF)}<span className="edit-icon">✎</span>
                                </span>
                              )}
                              <span className="timeline-label-modern">DATE PAIEMENT DF</span>
                              {!dossier.pompage && dossier.dateDF && (
                                <span style={{display:'inline-block',marginTop:'4px',padding:'2px 8px',borderRadius:'10px',fontSize:'11px',fontWeight:'600',background:'#dbeafe',color:'#1e40af'}}>→ En attente d'installation</span>
                              )}
                            </div>
                          </div>

                          {/* 4. DATE LIVRAISON → En attente de réception */}
                          <div className="timeline-item-modern">
                            <div className="timeline-dot-modern timeline-dot-orange"></div>
                            <div className="timeline-content-modern">
                              {showQuickEditDate === 'dateLivraison' ? (
                                <div className="quick-edit-inline-date">
                                  <input type="date" value={quickDateValue}
                                    onChange={(e) => setQuickDateValue(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleQuickUpdateDate('dateLivraison'); if (e.key === 'Escape') handleCancelQuickEdit(); }}
                                    autoFocus className="quick-edit-date-input" />
                                <button onClick={() => handleQuickUpdateDate('dateLivraison')} className="btn-save-inline">✓</button>
                                <button onClick={handleCancelQuickEdit} className="btn-cancel-inline">✕</button>
                                </div>
                              ) : (
                                <span className="timeline-date-modern editable-field"
                                  onClick={() => handleStartQuickEditDate('dateLivraison', dossier.dateLivraison)} title="Cliquez pour modifier">
                                  {formatDate(dossier.dateLivraison)}<span className="edit-icon">✎</span>
                                </span>
                              )}
                              <span className="timeline-label-modern">DATE LIVRAISON</span>
                              {dossier.dateLivraison && (
                                <span style={{display:'inline-block',marginTop:'4px',padding:'2px 8px',borderRadius:'10px',fontSize:'11px',fontWeight:'600',background:'#ede9fe',color:'#5b21b6'}}>→ En attente de réception</span>
                              )}
                            </div>
                          </div>

                          {/* 5. DATE MISE EN SERVICE → Réceptionné / Manque PV */}
                          <div className="timeline-item-modern">
                            <div className="timeline-dot-modern timeline-dot-blue"></div>
                            <div className="timeline-content-modern">
                              {showQuickEditDate === 'dateMiseEnService' ? (
                                <div className="quick-edit-inline-date">
                                  <input type="date" value={quickDateValue}
                                    onChange={(e) => setQuickDateValue(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleQuickUpdateDate('dateMiseEnService'); if (e.key === 'Escape') handleCancelQuickEdit(); }}
                                    autoFocus className="quick-edit-date-input" />
                                  <button onClick={() => handleQuickUpdateDate('dateMiseEnService')} className="btn-save-inline">✓</button>
                                  <button onClick={handleCancelQuickEdit} className="btn-cancel-inline">✕</button>
                                </div>
                              ) : (
                                <span className="timeline-date-modern editable-field"
                                  onClick={() => handleStartQuickEditDate('dateMiseEnService', dossier.dateMiseEnService)} title="Cliquez pour modifier">
                                  {formatDate(dossier.dateMiseEnService)}<span className="edit-icon">✎</span>
                                </span>
                              )}
                              <span className="timeline-label-modern">DATE MISE EN SERVICE</span>
                              {dossier.dateMiseEnService && (
                                <span style={{display:'inline-block',marginTop:'4px',padding:'2px 8px',borderRadius:'10px',fontSize:'11px',fontWeight:'600',background:dossier.etatDossier==='Manque PV'?'#fee2e2':'#d1fae5',color:dossier.etatDossier==='Manque PV'?'#991b1b':'#065f46'}}>
                                  {'→ '}{dossier.etatDossier === 'Manque PV' ? 'Manque PV' : 'Réceptionné'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      </div>
                    </div>
                  </div>

                {/* SECTION NOTES & HISTORIQUE - VERSION ENRICHIE */}
                <div className="notes-history-section-v3" style={{marginTop: '30px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                  {/* Helper: badge priorité */}
                  {/* Notes Client */}
                  <div className="m-v3-panel">
                    <h3 style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      HISTORIQUE DES NOTES (CLIENT)
                      <span style={{marginLeft:'auto',fontSize:'12px',background:'#e0e7ff',color:'#4338ca',padding:'2px 8px',borderRadius:'12px',fontWeight:'600'}}>{client?.notes_array?.length || 0}</span>
                    </h3>
                    <div className="notes-list-v3" style={{maxHeight: '320px', overflowY: 'auto', marginBottom: '15px', background: '#f8fafc', borderRadius: '8px', padding: '10px'}}>
                      {client?.notes_array?.length > 0 ? (
                        [...(client.notes_array)].reverse().map((note, i) => {
                          const prioriteStyle = note.priorite === 'haute_importance'
                            ? {background:'#fef2f2',border:'1px solid #fecaca',borderLeft:'4px solid #ef4444'}
                            : note.priorite === 'importante'
                            ? {background:'#fffbeb',border:'1px solid #fde68a',borderLeft:'4px solid #f59e0b'}
                            : {background:'white',border:'1px solid #e2e8f0'};
                          const prioriteBadge = note.priorite === 'haute_importance'
                            ? <span style={{fontSize:'10px',padding:'2px 6px',borderRadius:'8px',background:'#fef2f2',color:'#dc2626',fontWeight:'700',border:'1px solid #fecaca'}}>🔥 URGENTE</span>
                            : note.priorite === 'importante'
                            ? <span style={{fontSize:'10px',padding:'2px 6px',borderRadius:'8px',background:'#fffbeb',color:'#d97706',fontWeight:'700',border:'1px solid #fde68a'}}>⭐ IMPORTANTE</span>
                            : <span style={{fontSize:'10px',padding:'2px 6px',borderRadius:'8px',background:'#f0fdf4',color:'#16a34a',fontWeight:'600',border:'1px solid #bbf7d0'}}>✓ Normale</span>;
                          const statutBadge = note.statut === 'resolue'
                            ? <span style={{fontSize:'10px',padding:'2px 6px',borderRadius:'8px',background:'#dcfce7',color:'#166534',fontWeight:'600'}}>Résolue</span>
                            : note.statut === 'en_cours'
                            ? <span style={{fontSize:'10px',padding:'2px 6px',borderRadius:'8px',background:'#dbeafe',color:'#1d4ed8',fontWeight:'600'}}>En cours</span>
                            : <span style={{fontSize:'10px',padding:'2px 6px',borderRadius:'8px',background:'#f1f5f9',color:'#64748b',fontWeight:'600'}}>Ouverte</span>;
                          return (
                            <div key={note._id || i} style={{padding:'12px',marginBottom:'8px',borderRadius:'8px',...prioriteStyle,fontSize:'0.88rem'}}>
                              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'6px',flexWrap:'wrap',gap:'4px'}}>
                                <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
                                  <strong style={{color:'#2563eb'}}>{note.author || 'Inconnu'}</strong>
                                  <small style={{color:'#94a3b8'}}>{formatDate(note.date)}</small>
                                  <div style={{display:'flex',gap:'4px',flexWrap:'wrap'}}>{prioriteBadge}{statutBadge}</div>
                                </div>
                                {hasPermission('clients', 'delete') && (
                                  <button onClick={() => handleDeleteClientNote(note._id)} style={{background:'none',border:'none',color:'#ef4444',cursor:'pointer',padding:'4px',opacity:0.7}} title="Supprimer">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" /></svg>
                                  </button>
                                )}
                              </div>
                              <p style={{margin:'0 0 6px 0',color:'#475569',lineHeight:'1.4'}}>{note.text}</p>
                              {note.reponses?.length > 0 && (
                                <button onClick={() => setExpandedNote(expandedNote === `c-${note._id}` ? null : `c-${note._id}`)} style={{fontSize:'11px',color:'#6366f1',background:'none',border:'1px solid #c7d2fe',borderRadius:'6px',padding:'2px 8px',cursor:'pointer',marginBottom:'6px'}}>
                                  {note.reponses.length} réponse{note.reponses.length > 1 ? 's' : ''} {expandedNote === `c-${note._id}` ? '▲' : '▼'}
                                </button>
                              )}
                              {expandedNote === `c-${note._id}` && (
                                <div style={{marginLeft:'12px',borderLeft:'2px solid #c7d2fe',paddingLeft:'10px',marginBottom:'8px'}}>
                                  {note.reponses.map((r,ri) => (
                                    <div key={ri} style={{marginBottom:'8px',fontSize:'0.82rem'}}>
                                      <strong style={{color:'#6366f1'}}>{r.author || 'Admin'}</strong> <small style={{color:'#94a3b8'}}>{formatDate(r.date)}</small>
                                      <p style={{margin:'2px 0',color:'#334155'}}>{r.text}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p style={{textAlign:'center',color:'#94a3b8',padding:'20px'}}>Aucune note pour ce client.</p>
                      )}
                    </div>
                    {hasPermission('clients', 'update') && (
                      <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                        <textarea
                          className="premium-input"
                          placeholder="Ajouter une note client..."
                          value={newClientNote}
                          onChange={(e) => setNewClientNote(e.target.value)}
                          rows={2}
                          style={{resize:'vertical',minHeight:'60px'}}
                        />
                        <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
                          <select value={newNotePriorite} onChange={(e) => setNewNotePriorite(e.target.value)} style={{padding:'6px 10px',borderRadius:'8px',border:'1px solid #e2e8f0',fontSize:'12px',fontWeight:'600',cursor:'pointer',background:'white'}}>
                            <option value="normale">✓ Normale</option>
                            <option value="importante">⭐ Importante</option>
                            <option value="haute_importance">🔥 Urgente</option>
                          </select>
                          <select value={newNoteAffecteA} onChange={(e) => setNewNoteAffecteA(e.target.value)} style={{padding:'6px 10px',borderRadius:'8px',border:'1px solid #e2e8f0',fontSize:'12px',fontWeight:'600',cursor:'pointer',background:'white'}}>
                            <option value="">👤 Affecter à...</option>
                            {allAdminsModerateurs.map(u => (
                              <option key={u._id} value={u._id}>{u.prenom} {u.nom} ({u.typeUser})</option>
                            ))}
                          </select>
                          <button className="btn-save-inline" onClick={handleAddClientNote} style={{padding:'8px 16px',display:'flex',alignItems:'center',gap:'6px',fontSize:'13px',width:'auto',height:'auto',flexShrink:0}}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            Ajouter
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notes Dossier */}
                  <div className="m-v3-panel">
                    <h3 style={{display:'flex',alignItems:'center',gap:'8px'}}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      HISTORIQUE DES NOTES (DOSSIER)
                      <span style={{marginLeft:'auto',fontSize:'12px',background:'#d1fae5',color:'#065f46',padding:'2px 8px',borderRadius:'12px',fontWeight:'600'}}>{dossier?.notes_array?.length || 0}</span>
                    </h3>
                    <div className="notes-list-v3" style={{maxHeight:'320px',overflowY:'auto',marginBottom:'15px',background:'#f8fafc',borderRadius:'8px',padding:'10px'}}>
                      {dossier?.notes_array?.length > 0 ? (
                        [...(dossier.notes_array)].reverse().map((note, i) => {
                          const prioriteStyle = note.priorite === 'haute_importance'
                            ? {background:'#fef2f2',border:'1px solid #fecaca',borderLeft:'4px solid #ef4444'}
                            : note.priorite === 'importante'
                            ? {background:'#fffbeb',border:'1px solid #fde68a',borderLeft:'4px solid #f59e0b'}
                            : {background:'white',border:'1px solid #e2e8f0'};
                          const prioriteBadge = note.priorite === 'haute_importance'
                            ? <span style={{fontSize:'10px',padding:'2px 6px',borderRadius:'8px',background:'#fef2f2',color:'#dc2626',fontWeight:'700',border:'1px solid #fecaca'}}>🔥 URGENTE</span>
                            : note.priorite === 'importante'
                            ? <span style={{fontSize:'10px',padding:'2px 6px',borderRadius:'8px',background:'#fffbeb',color:'#d97706',fontWeight:'700',border:'1px solid #fde68a'}}>⭐ IMPORTANTE</span>
                            : <span style={{fontSize:'10px',padding:'2px 6px',borderRadius:'8px',background:'#f0fdf4',color:'#16a34a',fontWeight:'600',border:'1px solid #bbf7d0'}}>✓ Normale</span>;
                          const statutBadge = note.statut === 'resolue'
                            ? <span style={{fontSize:'10px',padding:'2px 6px',borderRadius:'8px',background:'#dcfce7',color:'#166534',fontWeight:'600'}}>Résolue</span>
                            : note.statut === 'en_cours'
                            ? <span style={{fontSize:'10px',padding:'2px 6px',borderRadius:'8px',background:'#dbeafe',color:'#1d4ed8',fontWeight:'600'}}>En cours</span>
                            : <span style={{fontSize:'10px',padding:'2px 6px',borderRadius:'8px',background:'#f1f5f9',color:'#64748b',fontWeight:'600'}}>Ouverte</span>;
                          return (
                            <div key={note._id || i} style={{padding:'12px',marginBottom:'8px',borderRadius:'8px',...prioriteStyle,fontSize:'0.88rem'}}>
                              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'6px',flexWrap:'wrap',gap:'4px'}}>
                                <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
                                  <strong style={{color:'#059669'}}>{note.author || 'Inconnu'}</strong>
                                  <small style={{color:'#94a3b8'}}>{formatDate(note.date)}</small>
                                  <div style={{display:'flex',gap:'4px',flexWrap:'wrap'}}>{prioriteBadge}{statutBadge}</div>
                                </div>
                                {hasPermission('dossiers', 'delete') && (
                                  <button onClick={() => handleDeleteDossierNote(note._id)} style={{background:'none',border:'none',color:'#ef4444',cursor:'pointer',padding:'4px',opacity:0.7}} title="Supprimer">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" /></svg>
                                  </button>
                                )}
                              </div>
                              <p style={{margin:'0 0 6px 0',color:'#475569',lineHeight:'1.4'}}>{note.text}</p>
                              {note.reponses?.length > 0 && (
                                <button onClick={() => setExpandedNote(expandedNote === `d-${note._id}` ? null : `d-${note._id}`)} style={{fontSize:'11px',color:'#059669',background:'none',border:'1px solid #a7f3d0',borderRadius:'6px',padding:'2px 8px',cursor:'pointer',marginBottom:'6px'}}>
                                  {note.reponses.length} réponse{note.reponses.length > 1 ? 's' : ''} {expandedNote === `d-${note._id}` ? '▲' : '▼'}
                                </button>
                              )}
                              {expandedNote === `d-${note._id}` && (
                                <div style={{marginLeft:'12px',borderLeft:'2px solid #a7f3d0',paddingLeft:'10px',marginBottom:'8px'}}>
                                  {note.reponses.map((r,ri) => (
                                    <div key={ri} style={{marginBottom:'8px',fontSize:'0.82rem'}}>
                                      <strong style={{color:'#059669'}}>{r.author || 'Admin'}</strong> <small style={{color:'#94a3b8'}}>{formatDate(r.date)}</small>
                                      <p style={{margin:'2px 0',color:'#334155'}}>{r.text}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p style={{textAlign:'center',color:'#94a3b8',padding:'20px'}}>Aucune note pour ce dossier.</p>
                      )}
                    </div>
                    {hasPermission('dossiers', 'update') && (
                      <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                        <textarea
                          className="premium-input"
                          placeholder="Ajouter une note dossier..."
                          value={newDossierNote}
                          onChange={(e) => setNewDossierNote(e.target.value)}
                          rows={2}
                          style={{resize:'vertical',minHeight:'60px'}}
                        />
                        <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
                          <select value={newNotePriorite} onChange={(e) => setNewNotePriorite(e.target.value)} style={{padding:'6px 10px',borderRadius:'8px',border:'1px solid #e2e8f0',fontSize:'12px',fontWeight:'600',cursor:'pointer',background:'white'}}>
                            <option value="normale">✓ Normale</option>
                            <option value="importante">⭐ Importante</option>
                            <option value="haute_importance">🔥 Urgente</option>
                          </select>
                          <select value={newNoteAffecteA} onChange={(e) => setNewNoteAffecteA(e.target.value)} style={{padding:'6px 10px',borderRadius:'8px',border:'1px solid #e2e8f0',fontSize:'12px',fontWeight:'600',cursor:'pointer',background:'white'}}>
                            <option value="">👤 Affecter à...</option>
                            {allAdminsModerateurs.map(u => (
                              <option key={u._id} value={u._id}>{u.prenom} {u.nom} ({u.typeUser})</option>
                            ))}
                          </select>
                          <button className="btn-save-inline" onClick={handleAddDossierNote} style={{padding:'8px 16px',display:'flex',alignItems:'center',gap:'6px',fontSize:'13px',background:'#059669',width:'auto',height:'auto',flexShrink:0}}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            Ajouter
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* HISTORIQUE SYSTEME (AUDIT LOGS) - RÉSUMÉ */}
                <div className="m-v3-panel" style={{marginTop: '20px'}}>
                  <h3 style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                      HISTORIQUE SYSTÈME (RÉCENT)
                    </div>
                    {auditLogs.length > 0 && (
                      <button 
                        onClick={() => setActiveTab('historique')}
                        className="btn-modern-secondary"
                        style={{fontSize: '0.8rem', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '6px'}}
                      >
                        <Eye size={14} /> Voir tout l'historique
                      </button>
                    )}
                  </h3>
                  {auditLogsLoading ? (
                    <div style={{textAlign: 'center', padding: '20px', color: '#64748b'}}>Chargement de l'historique...</div>
                  ) : auditLogs.length > 0 ? (
                    <div style={{overflowX: 'auto'}}>
                      <table className="logs-table" style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem'}}>
                        <thead>
                          <tr style={{background: '#f1f5f9', borderBottom: '2px solid #cbd5e1'}}>
                            <th style={{padding: '10px', textAlign: 'left', color: '#475569'}}>Date / Heure</th>
                            <th style={{padding: '10px', textAlign: 'left', color: '#475569'}}>Action</th>
                            <th style={{padding: '10px', textAlign: 'left', color: '#475569'}}>Utilisateur</th>
                            <th style={{padding: '10px', textAlign: 'left', color: '#475569'}}>Commentaire</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditLogs.slice(0, 3).map((log, index) => {
                            const cfg = getActionConfig(log.action);
                            const logKey = log._id || `${log.action || 'audit'}-${log.timestamp || index}`;
                            const auditRole = formatAuditRole(log.actor);
                            return (
                              <tr key={logKey} style={{borderBottom: '1px solid #e2e8f0', background: 'white'}}>
                                <td style={{padding: '10px', color: '#64748b', whiteSpace: 'nowrap'}}>{formatDateTime(log.timestamp)}</td>
                                <td style={{padding: '10px'}}>
                                  <span style={{background: cfg.bg, color: cfg.color, padding: '4px 8px', borderRadius: '12px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                                    {cfg.icon} {cfg.label}
                                  </span>
                                </td>
                                <td style={{padding: '10px'}}>
                                  <div>{formatAuditActorName(log.actor)}</div>
                                  <span style={{fontSize: '0.75rem', padding: '2px 6px', borderRadius: '10px', background: ROLE_COLORS[auditRole] || '#94a3b8', color: 'white'}}>{auditRole}</span>
                                </td>
                                <td style={{padding: '10px', color: '#475569'}}>{log.comment || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p style={{textAlign: 'center', color: '#94a3b8', padding: '20px'}}>Aucune activité système enregistrée pour ce dossier.</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
            
            {/* ONGLET PRODUITS */}
            {(activeTab === 'produit' || activeTab === 'produits') && (
              <div className="produits-section">
                <div className="produits-section-header">
                  <h3>Produits installés</h3>
                  <div style={{display: 'flex', gap: '10px'}}>
                    {selectedProduitsIds.length > 0 && hasPermission('dossiers', 'update') && (
                      <button
                        className="btn-transfert"
                        onClick={() => setShowTransfertModal(true)}
                        style={{
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          padding: '10px 20px',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer'
                        }}
                      >
                        🔄 Transférer ({selectedProduitsIds.length})
                      </button>
                    )}
                    {hasPermission('documents', 'create') && (
                      <button
                        className="btn-bl-toggle"
                        onClick={() => setShowBLForm(true)}
                        disabled={getProduitsLivrables().length === 0}
                        title={getProduitsLivrables().length > 0 ? 'Generer un Bon de Livraison' : 'Aucun produit disponible pour livraison'}
                        style={{
                          backgroundColor: getProduitsLivrables().length > 0 ? '#1e3a5f' : '#94a3b8',
                          color: 'white',
                          padding: '10px 20px',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: getProduitsLivrables().length > 0 ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontWeight: '500',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <FileText size={16} /> Generer Bon de Livraison
                        {selectedProduitsIds.length > 0 && (
                          <span style={{
                            background: '#ef4444',
                            color: 'white',
                            borderRadius: '50%',
                            padding: '2px 6px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            display: 'inline-flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minWidth: '18px',
                            height: '18px'
                          }}>
                            {selectedProduitsIds.length}
                          </span>
                        )}
                      </button>
                    )}
                    {hasPermission('dossiers', 'update') && !dossier.isCanceled && (
                      <button
                        className="btn-add"
                        onClick={() => {
                          setProduitFormData({
                            produitId: '',
                            paletteId: '',
                            quantite: 1,
                            prixUnitaire: 0,
                            TVA: 19,
                            tauxGain: 0,
                            prixTotal: 0,
                            prixProduit: 0,
                            dateInstallation: '',
                            garantieDateDebut: '',
                            garantieDateFin: '',
                            garantieDureeAnnees: 5,
                            numeroSerie: '',
                            numerosSerie: [],
                            statut: 'commande',
                            specifications: '',
                            notes: ''
                          });
                          setPalettes([]);
                          setNumerosSerieDisponibles([]);
                          setNumerosSerieSelectionnes([]);
                          setShowInlineAddForm(true);
                          setSelectedCategory(null);
                          setSelectedCatalogProduct(null);
                          fetchAllProduits();
                        }}
                      >
                        + Ajouter un Produit
                      </button>
                    )}
                  </div>
                </div>

                <div className="produits-overview">
                  <div className="produits-overview-card">
                    <span>Affectés au client</span>
                    <strong>{produits.length}</strong>
                  </div>
                  <div className="produits-overview-card ready">
                    <span>Prêts pour livraison</span>
                    <strong>{getProduitsLivrables().length}</strong>
                  </div>
                  <div className="produits-overview-card pending">
                    <span>À commander</span>
                    <strong>{produits.length - getProduitsLivrables().length}</strong>
                  </div>
                </div>

                {produits.length > 0 && (
                  <div className="installation-split-panel">
                    <div className="installation-split-column installed">
                      <h4><CheckCircle size={16} /> Produits Installés ({getProduitsInstalles().length})</h4>
                      <div className="installation-split-list">
                        {getProduitsInstalles().length > 0 ? getProduitsInstalles().map(produit => (
                          <span key={`installed-${produit._id}`}>{getProduitName(produit)} <strong>({produit.quantite})</strong></span>
                        )) : <em>Aucun produit installÃ©</em>}
                      </div>
                    </div>
                    <div className="installation-split-column pending">
                      <h4><Package size={16} /> Produits non Installés ({getProduitsNonInstalles().length})</h4>
                      <div className="installation-split-list">
                        {getProduitsNonInstalles().length > 0 ? getProduitsNonInstalles().map(produit => (
                          <span key={`not-installed-${produit._id}`}>{getProduitName(produit)} <strong>({produit.quantite})</strong></span>
                        )) : <em>Tous les produits sont Installés</em>}
                      </div>
                    </div>
                  </div>
                )}

                {!showInlineAddForm ? (
                  <>
                    <div className="produits-dashboard-layout">
                      <div className="produits-dashboard-main">
                    {showBLForm && (
                      <div className="bl-generation-card" style={{
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        border: '1px solid #cbd5e1',
                        borderRadius: '12px',
                        padding: '24px',
                        marginBottom: '24px',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: '2px solid #e2e8f0',
                          paddingBottom: '12px',
                          marginBottom: '20px'
                        }}>
                          <h4 style={{
                            color: '#1e3a5f',
                            margin: 0,
                            fontSize: '1.2rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span>📦</span> Préparation du Bon de Livraison
                          </h4>
                          <button 
                            type="button" 
                            onClick={() => setShowBLForm(false)}
                            style={{
                              background: 'none',
                              border: 'none',
                              fontSize: '1.2rem',
                              cursor: 'pointer',
                              color: '#64748b'
                            }}
                          >
                            ✕
                          </button>
                        </div>

                        <form onSubmit={handleGenererBL}>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                            gap: '20px',
                            marginBottom: '20px'
                          }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label style={{
                                color: '#475569',
                                fontWeight: '600',
                                fontSize: '0.85rem'
                              }}>
                                Date du BL
                              </label>
                              <input
                                type="date"
                                value={dateBL}
                                onChange={(e) => setDateBL(e.target.value)}
                                style={{
                                  padding: '10px 14px',
                                  borderRadius: '6px',
                                  border: '1px solid #cbd5e1',
                                  fontSize: '0.95rem',
                                  outline: 'none',
                                  transition: 'border-color 0.2s',
                                  background: '#ffffff'
                                }}
                                required
                              />
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label style={{
                                color: '#475569',
                                fontWeight: '600',
                                fontSize: '0.85rem'
                              }}>
                                Vehicule
                              </label>
                              <select
                                value={vehiculeBLId}
                                onChange={(e) => setVehiculeBLId(e.target.value)}
                                style={{
                                  padding: '10px 14px',
                                  borderRadius: '6px',
                                  border: '1px solid #cbd5e1',
                                  fontSize: '0.95rem',
                                  outline: 'none',
                                  transition: 'border-color 0.2s',
                                  background: '#ffffff'
                                }}
                                required
                                disabled={loadingOptionsBL}
                              >
                                <option value="">Selectionner un vehicule</option>
                                {vehiculesBL.map(vehicule => (
                                  <option key={vehicule._id} value={vehicule._id}>
                                    {vehicule.matricule} - {vehicule.marque || vehicule.type || 'Vehicule'}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label style={{
                                color: '#475569',
                                fontWeight: '600',
                                fontSize: '0.85rem'
                              }}>
                                Chauffeur
                              </label>
                              <select
                                value={chauffeurBLKey}
                                onChange={(e) => setChauffeurBLKey(e.target.value)}
                                style={{
                                  padding: '10px 14px',
                                  borderRadius: '6px',
                                  border: '1px solid #cbd5e1',
                                  fontSize: '0.95rem',
                                  outline: 'none',
                                  transition: 'border-color 0.2s',
                                  background: '#ffffff'
                                }}
                                required
                                disabled={loadingOptionsBL}
                              >
                                <option value="">Selectionner un chauffeur</option>
                                {getEquipeChauffeurOptions().map(option => (
                                  <option key={option.key} value={option.key}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div style={{
                            background: '#f1f5f9',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '20px'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '12px'
                            }}>
                              <span style={{
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                color: '#334155'
                              }}>
                                Produits du dossier à livrer ({selectedProduitsIds.length} sélectionné{selectedProduitsIds.length > 1 ? 's' : ''})
                              </span>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  type="button"
                                  onClick={() => setSelectedProduitsIds(getProduitsLivrables().map(p => p._id))}
                                  style={{
                                    background: 'white',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    fontSize: '0.75rem',
                                    cursor: 'pointer',
                                    color: '#475569',
                                    fontWeight: '500'
                                  }}
                                >
                                  Tout cocher
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setSelectedProduitsIds([])}
                                  style={{
                                    background: 'white',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    fontSize: '0.75rem',
                                    cursor: 'pointer',
                                    color: '#475569',
                                    fontWeight: '500'
                                  }}
                                >
                                  Tout décocher
                                </button>
                              </div>
                            </div>

                            {produits.length > 0 ? (
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                                gap: '10px',
                                maxHeight: '180px',
                                overflowY: 'auto',
                                padding: '4px'
                              }}>
                                {produits.map(p => {
                                  const isChecked = selectedProduitsIds.includes(p._id);
                                  const livrable = isProduitLivrable(p);
                                  return (
                                    <label
                                      key={p._id}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        background: !livrable ? '#fff7ed' : (isChecked ? '#e0f2fe' : 'white'),
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid',
                                        borderColor: !livrable ? '#fed7aa' : (isChecked ? '#bae6fd' : '#e2e8f0'),
                                        cursor: livrable ? 'pointer' : 'not-allowed',
                                        opacity: livrable ? 1 : 0.72,
                                        fontSize: '0.85rem',
                                        color: '#1e293b',
                                        transition: 'all 0.15s ease'
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleToggleSelectProduit(p._id)}
                                        style={{ cursor: 'pointer' }}
                                      />
                                      <span style={{
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontWeight: isChecked ? '600' : 'normal'
                                      }}>
                                        {p.produitId?.nomProduit || 'Produit'}
                                      </span>
                                      <span style={{
                                        color: livrable ? '#64748b' : '#b45309',
                                        fontSize: '0.8rem',
                                        fontWeight: '500'
                                      }}>
                                        {livrable ? `x${p.quantite}` : 'à commander'}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.85rem', padding: '10px 0' }}>
                                Aucun produit à afficher
                              </div>
                            )}
                          </div>

                          <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px'
                          }}>
                            <button
                              type="button"
                              onClick={() => {
                                setShowBLForm(false);
                                setSelectedProduitsIds([]);
                              }}
                              style={{
                                background: '#f1f5f9',
                                border: '1px solid #cbd5e1',
                                color: '#475569',
                                padding: '10px 20px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500'
                              }}
                              disabled={isGeneratingBL}
                            >
                              Annuler
                            </button>
                            <button
                              type="submit"
                              style={{
                                background: '#10b981',
                                border: 'none',
                                color: 'white',
                                padding: '10px 24px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.2)'
                              }}
                              disabled={isGeneratingBL || selectedProduitsIds.length === 0 || getSelectedProduitsNonLivrables().length > 0 || dossier.isCanceled}
                            >
                              {isGeneratingBL ? 'Génération...' : '📦 Générer le Bon de Livraison'}
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    {produits.length > 0 ? (
                      <div className="produits-table-shell">
                        <div className="produits-table-toolbar">
                          <label className="produits-search">
                            <Search size={16} />
                            <input
                              type="text"
                              value={installedProductSearch}
                              onChange={(e) => setInstalledProductSearch(e.target.value)}
                              placeholder="Rechercher un produit installe..."
                            />
                          </label>
                          <span>
                            {(() => {
                              const total = getInstalledProducts().length;
                              const start = total === 0 ? 0 : ((installedProductPage - 1) * PRODUCTS_PAGE_SIZE) + 1;
                              const end = Math.min(installedProductPage * PRODUCTS_PAGE_SIZE, total);
                              return `Affichage de ${start} a ${end} sur ${total} produits`;
                            })()}
                          </span>
                        </div>
                        <div className="produits-table-head">
                          <span></span>
                          <span>Produit</span>
                          <span>Quantite</span>
                          <span>Disponibilite</span>
                          <span>Statut</span>
                          <span>Actions</span>
                        </div>
                        <div className="produits-grid-modern">
                          {(() => {
                            const filteredProducts = getInstalledProducts();
                            const pageCount = Math.max(1, Math.ceil(filteredProducts.length / PRODUCTS_PAGE_SIZE));
                            const currentPage = Math.min(installedProductPage, pageCount);
                            const paginatedProducts = filteredProducts.slice((currentPage - 1) * PRODUCTS_PAGE_SIZE, currentPage * PRODUCTS_PAGE_SIZE);

                            if (paginatedProducts.length === 0) {
                              return (
                                <div className="products-empty-filter">
                                  Aucun produit ne correspond a cette recherche.
                                </div>
                              );
                            }

                            return paginatedProducts.map((produit) => {
                          const livrable = isProduitLivrable(produit);
                          return (
                          <div key={produit._id} className={`produit-card-modern ${!livrable ? 'not-deliverable' : ''}`}>
                            <div className="produit-card-selection">
                              <input
                                type="checkbox"
                                checked={selectedProduitsIds.includes(produit._id)}
                                onChange={() => handleToggleSelectProduit(produit._id)}
                                title={livrable ? 'Sélectionner' : 'Sélection possible, BL indisponible'}
                              />
                            </div>
                            
                            <div className="produit-card-body">
                              <div className="produit-card-visual">
                                {produit.produitId?.imageProduit ? (
                                  <img 
                                    src={`${BASE_URL}${produit.produitId.imageProduit}`} 
                                    alt={produit.produitId.nomProduit} 
                                    className="produit-img-thumb"
                                  />
                                ) : (
                                  <div className="produit-icon-placeholder">
                                    <Package size={32} />
                                  </div>
                                )}
                              </div>
                              
                              <div className="produit-card-info">
                                <div className="produit-card-header">
                                  <div className="produit-main-info">
                                    <h4>{getProduitName(produit)}</h4>
                                    <span className="produit-marque">{produit.produitId?.marque?.name || 'Sastra Energy'}</span>
                                    {getProduitReference(produit) && (
                                      <span className="produit-reference">Ref: {getProduitReference(produit)}</span>
                                    )}
                                  </div>
                                  <span
                                    className="statut-badge-modern"
                                    style={{
                                      backgroundColor: getStatutColor(produit.statut) + '20',
                                      color: getStatutColor(produit.statut),
                                      borderColor: getStatutColor(produit.statut) + '40'
                                    }}
                                  >
                                    {produit.statut}
                                  </span>
                                </div>

                                <span className={`delivery-state-badge ${livrable ? 'ready' : 'pending'}`}>
                                  {livrable ? 'Livrable' : 'À commander'}
                                </span>

                                <div className="produit-card-details">
                                  {produit.produitId?.modeGestionStock === 'lineaire' && produit.decoupeLineaire ? (
                                    <>
                                      <div className="detail-row">
                                        <Hash size={14} />
                                        <span>Découpe: <strong>{produit.decoupeLineaire.nombrePieces} pcs × {produit.decoupeLineaire.longueurPieceMetre} m</strong></span>
                                      </div>
                                      <div className="detail-row">
                                        <Package size={14} />
                                        <span>Total: <strong>{produit.decoupeLineaire.metres} m</strong></span>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="detail-row">
                                      <Hash size={14} />
                                      <span>Quantité: <strong>{produit.quantite}</strong></span>
                                    </div>
                                  )}
                                  
                                  {(produit.numeroSerie || (produit.numerosSerie && produit.numerosSerie.length > 0)) && (
                                    <div className="detail-row">
                                      <Package size={14} />
                                      <span className="serial-numbers">
                                        {produit.numeroSerie ? produit.numeroSerie : `${produit.numerosSerie.length} numéros de série`}
                                      </span>
                                    </div>
                                  )}

                                  {produit.dateInstallation && (
                                    <div className="detail-row">
                                      <Calendar size={14} />
                                      <span>Installé le: <strong>{formatDate(produit.dateInstallation)}</strong></span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="produit-qty-cell">
                              <strong>{produit.quantite}</strong>
                              <small>{produit?.uniteMesure || 'unite(s)'}</small>
                            </div>

                            <div className="produit-availability-cell">
                              <span className={`delivery-state-badge ${livrable ? 'ready' : 'pending'}`}>
                                {livrable ? 'Disponible' : 'Non disponible'}
                              </span>
                            </div>

                            <div className="produit-status-cell">
                              <span
                                className="statut-badge-modern"
                                style={{
                                  backgroundColor: getStatutColor(produit.statut) + '20',
                                  color: getStatutColor(produit.statut),
                                  borderColor: getStatutColor(produit.statut) + '40'
                                }}
                              >
                                {produit.statut}
                              </span>
                            </div>

                            <div className="produit-card-actions">
                              <button 
                                className="btn-card-action view" 
                                onClick={() => {
                                  setSelectedProduit(produit);
                                  setShowDetailProduitModal(true);
                                }}
                                title="Détails"
                              >
                                <Eye size={16} />
                              </button>
                              
                              {hasPermission('dossiers', 'update') && (
                                <button 
                                  className="btn-card-action edit" 
                                  onClick={() => handleEditProduit(produit)}
                                  title="Modifier"
                                >
                                  <Edit size={16} />
                                </button>
                              )}

                              {hasPermission('dossiers', 'delete') && (
                                <button 
                                  className="btn-card-action delete" 
                                  onClick={() => handleDeleteProduit(produit._id)}
                                  title="Supprimer"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </div>
                          );
                            });
                          })()}
                        </div>
                        {(() => {
                          const total = getInstalledProducts().length;
                          const pageCount = Math.max(1, Math.ceil(total / PRODUCTS_PAGE_SIZE));
                          if (pageCount <= 1) return null;
                          return (
                            <div className="products-pagination">
                              <button type="button" onClick={() => setInstalledProductPage(page => Math.max(1, page - 1))} disabled={installedProductPage <= 1}>
                                Precedent
                              </button>
                              {Array.from({ length: pageCount }, (_, index) => (
                                <button
                                  key={index + 1}
                                  type="button"
                                  className={installedProductPage === index + 1 ? 'active' : ''}
                                  onClick={() => setInstalledProductPage(index + 1)}
                                >
                                  {index + 1}
                                </button>
                              ))}
                              <button type="button" onClick={() => setInstalledProductPage(page => Math.min(pageCount, page + 1))} disabled={installedProductPage >= pageCount}>
                                Suivant
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="no-data-modern">
                        <Package size={48} />
                        <p>Aucun produit installé pour ce dossier</p>
                        {hasPermission('dossiers', 'update') && (
                          <button
                            className="btn-add-mini"
                            onClick={() => {
                              setProduitFormData({
                                produitId: '',
                                paletteId: '',
                                quantite: 1,
                                prixUnitaire: 0,
                                TVA: 19,
                                tauxGain: 0,
                                prixTotal: 0,
                                prixProduit: 0,
                                dateInstallation: '',
                                garantieDateDebut: '',
                                garantieDateFin: '',
                                garantieDureeAnnees: 5,
                                numeroSerie: '',
                                numerosSerie: [],
                                statut: 'commande',
                                specifications: '',
                                notes: ''
                              });
                              setPalettes([]);
                              setNumerosSerieDisponibles([]);
                              setNumerosSerieSelectionnes([]);
                              setShowInlineAddForm(true);
                              setSelectedCategory(null);
                              setSelectedCatalogProduct(null);
                              fetchAllProduits();
                            }}
                          >
                            Ajouter le premier produit
                          </button>
                        )}
                      </div>
                    )}
                      </div>

                      <aside className="produits-summary-panel">
                        {(() => {
                          const livrables = getProduitsLivrables();
                          const nonLivrables = getProduitsNonLivrables();
                          const total = produits.length;
                          const readyPct = total > 0 ? Math.round((livrables.length / total) * 100) : 0;

                          return (
                            <>
                              <h4>Resume des produits</h4>
                              <div className="summary-donut" style={{ '--ready': readyPct }}>
                                <div>
                                  <strong>{total}</strong>
                                  <span>Total</span>
                                </div>
                              </div>

                              <div className="summary-legend">
                                <div>
                                  <span className="legend-dot ready"></span>
                                  <span>Disponibles</span>
                                  <strong>{livrables.length} ({total ? Math.round((livrables.length / total) * 100) : 0}%)</strong>
                                </div>
                                <div>
                                  <span className="legend-dot pending"></span>
                                  <span>Non disponibles</span>
                                  <strong>{nonLivrables.length} ({total ? Math.round((nonLivrables.length / total) * 100) : 0}%)</strong>
                                </div>
                              </div>

                              {nonLivrables.length > 0 && (
                                <div className="summary-warning">
                                  <AlertTriangle size={16} />
                                  <div>
                                    <strong>Attention</strong>
                                    <p>Certains produits ne sont pas disponibles en stock. Le bon de livraison peut etre genere uniquement avec les produits selectionnes disponibles.</p>
                                  </div>
                                </div>
                              )}

                              <button
                                type="button"
                                className="summary-bl-button"
                                disabled={produits.length === 0}
                                onClick={() => setShowBLForm(true)}
                              >
                                <FileText size={16} /> Generer Bon de Livraison
                              </button>
                              <p className="summary-bl-help">Selectionnez un ou plusieurs produits disponibles dans le formulaire.</p>
                            </>
                          );
                        })()}
                      </aside>
                    </div>

                    <div className="availability-detail-panel">
                      <div className="availability-detail-header">
                        <h4>Detail de la disponibilite</h4>
                        <div className="availability-tools">
                          <label className="availability-search">
                            <Search size={16} />
                            <input
                              type="text"
                              value={availabilitySearch}
                              onChange={(e) => setAvailabilitySearch(e.target.value)}
                              placeholder="Rechercher un produit..."
                            />
                          </label>
                          <select
                            value={availabilityFilter}
                            onChange={(e) => setAvailabilityFilter(e.target.value)}
                          >
                            <option value="all">Tous les statuts</option>
                            <option value="available">Disponibles</option>
                            <option value="unavailable">Non disponibles</option>
                          </select>
                        </div>
                      </div>

                      {(() => {
                        const filtered = getAvailabilityProducts();
                        const pageCount = Math.max(1, Math.ceil(filtered.length / PRODUCTS_PAGE_SIZE));
                        const currentPage = Math.min(availabilityPage, pageCount);
                        const paginated = filtered.slice((currentPage - 1) * PRODUCTS_PAGE_SIZE, currentPage * PRODUCTS_PAGE_SIZE);
                        const disponibles = paginated.filter(isProduitLivrable);
                        const nonDisponibles = paginated.filter(produit => !isProduitLivrable(produit));
                        const totalDisponibles = filtered.filter(isProduitLivrable).length;
                        const totalNonDisponibles = filtered.length - totalDisponibles;

                        return (
                          <>
                            <div className="availability-group available">
                              <h5><CheckCircle size={15} /> Disponibles en stock ({totalDisponibles})</h5>
                              <div className="availability-list-grid">
                                {disponibles.length > 0 ? disponibles.map((produit) => {
                                  const disponibilite = getProduitDisponibiliteInfo(produit);
                                  return (
                                    <div className="availability-item" key={`available-${produit._id}`}>
                                      <div className="availability-item-main">
                                        <span className="availability-item-title">{getProduitName(produit)} ({getProduitQuantityText(produit)})</span>
                                        <small>{disponibilite.detail}</small>
                                      </div>
                                      <strong>{disponibilite.label}</strong>
                                    </div>
                                  );
                                }) : <p>Aucun produit disponible dans ce filtre.</p>}
                              </div>
                            </div>

                            <div className="availability-group unavailable">
                              <h5><XCircle size={15} /> Non disponibles ({totalNonDisponibles})</h5>
                              <div className="availability-list-grid">
                                {nonDisponibles.length > 0 ? nonDisponibles.map((produit) => {
                                  const disponibilite = getProduitDisponibiliteInfo(produit);
                                  return (
                                    <div className="availability-item" key={`unavailable-${produit._id}`}>
                                      <div className="availability-item-main">
                                        <span className="availability-item-title">{getProduitName(produit)} ({getProduitQuantityText(produit)})</span>
                                        <small>{disponibilite.detail}</small>
                                      </div>
                                      <strong>{disponibilite.label}</strong>
                                    </div>
                                  );
                                }) : <p>Aucun produit non disponible dans ce filtre.</p>}
                              </div>
                            </div>

                            {pageCount > 1 && (
                              <div className="products-pagination availability-pagination">
                                <span>
                                  Page {currentPage} sur {pageCount} - {filtered.length} produits
                                </span>
                                <button type="button" onClick={() => setAvailabilityPage(page => Math.max(1, page - 1))} disabled={availabilityPage <= 1}>
                                  Precedent
                                </button>
                                {Array.from({ length: pageCount }, (_, index) => (
                                  <button
                                    key={index + 1}
                                    type="button"
                                    className={availabilityPage === index + 1 ? 'active' : ''}
                                    onClick={() => setAvailabilityPage(index + 1)}
                                  >
                                    {index + 1}
                                  </button>
                                ))}
                                <button type="button" onClick={() => setAvailabilityPage(page => Math.min(pageCount, page + 1))} disabled={availabilityPage >= pageCount}>
                                  Suivant
                                </button>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </>
                ) : (
                  <div className="inline-assignment-panel">
                    <div className="inline-panel-header">
                      <h4>📦 Affectation de Produit au Client</h4>
                      <button 
                        type="button" 
                        className="btn-back-to-products" 
                        onClick={() => {
                          setShowInlineAddForm(false);
                          setSelectedCategory(null);
                          setSelectedCatalogProduct(null);
                        }}
                      >
                        ✕ Retour aux produits installés
                      </button>
                    </div>

                    {/* Commutateur de mode d'affectation : Produit Unique vs Pack */}
                    <div className="assignment-mode-selector" style={{
                      display: 'flex',
                      gap: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      padding: '8px',
                      borderRadius: '10px',
                      marginBottom: '20px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <button
                        type="button"
                        className={`mode-btn ${assignmentMode === 'single' ? 'active' : ''}`}
                        onClick={() => setAssignmentMode('single')}
                        style={{
                          flex: 1,
                          padding: '12px 16px',
                          borderRadius: '8px',
                          background: assignmentMode === 'single' ? 'linear-gradient(135deg, #1e3a5f 0%, #112237 100%)' : 'transparent',
                          color: assignmentMode === 'single' ? '#ffd700' : '#94a3b8',
                          border: assignmentMode === 'single' ? '1px solid #ffd700' : 'none',
                          fontWeight: '700',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        📦 Affecter un Produit
                      </button>
                      <button
                        type="button"
                        className={`mode-btn ${assignmentMode === 'pack' ? 'active' : ''}`}
                        onClick={() => {
                          setAssignmentMode('pack');
                          fetchPacks();
                        }}
                        style={{
                          flex: 1,
                          padding: '12px 16px',
                          borderRadius: '8px',
                          background: assignmentMode === 'pack' ? 'linear-gradient(135deg, #1e3a5f 0%, #112237 100%)' : 'transparent',
                          color: assignmentMode === 'pack' ? '#ffd700' : '#94a3b8',
                          border: assignmentMode === 'pack' ? '1px solid #ffd700' : 'none',
                          fontWeight: '700',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        🎁 Affecter un Pack de Produits
                      </button>
                    </div>

                    {assignmentMode === 'pack' ? (
                      <div className="pack-assignment-container" style={{ animation: 'fadeIn 0.4s ease' }}>
                        
                        {/* Bannière Pack appliqué sur le dossier */}
                        {dossier?.packApplique && (
                          <div style={{
                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            borderRadius: '12px',
                            padding: '16px 20px',
                            marginBottom: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '12px'
                          }}>
                            <div>
                              <div style={{ color: '#10b981', fontWeight: '700', fontSize: '0.9rem', marginBottom: '4px' }}>
                                ✅ Pack actuellement appliqué au dossier
                              </div>
                              <div style={{ color: '#ffffff', fontSize: '1.1rem', fontWeight: '700' }}>
                                {dossier.packApplique.nom} <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 'normal' }}>({dossier.packApplique.puissance || 'N/A'})</span>
                              </div>
                              <div style={{ fontSize: '0.85rem', color: '#cbd5e1', marginTop: '2px' }}>
                                Prix de vente : <strong style={{ color: '#ffd700' }}>{Number(dossier.packApplique.prixFinal || 0).toFixed(3)} TND</strong> | Ref: {dossier.packApplique.reference || 'N/A'}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                              <button
                                type="button"
                                onClick={handleUnassignPack}
                                disabled={isApplyingPack}
                                style={{
                                  background: 'rgba(239, 68, 68, 0.15)',
                                  color: '#fca5a5',
                                  border: '1px solid rgba(239, 68, 68, 0.3)',
                                  padding: '8px 16px',
                                  borderRadius: '8px',
                                  cursor: isApplyingPack ? 'not-allowed' : 'pointer',
                                  fontWeight: '600',
                                  fontSize: '0.85rem'
                                }}
                              >
                                🚫 Désaffecter le Pack
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="assignment-step">
                          <h5 className="step-title">
                            <span className="step-number">1</span>
                            Sélectionner un Pack de Produits {dossier?.packApplique ? '(Pour remplacer le pack actuel)' : ''}
                          </h5>
                          {packs.length === 0 ? (
                            <p style={{ color: '#94a3b8', padding: '10px 0' }}>Aucun pack actif trouvé dans le système.</p>
                          ) : (
                            <div className="packs-selection-grid" style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                              gap: '20px',
                              marginBottom: '24px'
                            }}>
                              {packs.map(p => {
                                const isSelected = selectedPackId === p._id;
                                const isCurrentlyApplied = dossier?.packApplique?.packId === p._id;
                                const finalPNum = Number(p.prixFinal);
                                const calcPNum = Number(p.prixCalcule);
                                const packSellingPriceVal = (!isNaN(finalPNum) && finalPNum > 0)
                                  ? finalPNum
                                  : ((!isNaN(calcPNum) && calcPNum > 0) ? calcPNum : null);
                                const packSellingPriceStr = packSellingPriceVal !== null
                                  ? `${packSellingPriceVal.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT`
                                  : 'Prix non défini';
                                const powerStr = p.puissance ? (String(p.puissance).toLowerCase().includes('kwc') ? p.puissance : `${p.puissance} kWc`) : 'Puissance N/A';

                                return (
                                  <div
                                    key={p._id}
                                    className={`pack-selector-card ${isSelected ? 'active' : ''}`}
                                    onClick={() => {
                                      setSelectedPackId(p._id);
                                      handleCheckPackStock(p._id);
                                    }}
                                    style={{
                                      background: isSelected ? 'rgba(30, 58, 95, 0.4)' : (isCurrentlyApplied ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255, 255, 255, 0.03)'),
                                      border: isSelected ? '2px solid #ffd700' : (isCurrentlyApplied ? '2px solid #10b981' : '1px solid rgba(255, 255, 255, 0.1)'),
                                      borderRadius: '12px',
                                      padding: '20px',
                                      cursor: 'pointer',
                                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'space-between',
                                      boxShadow: isSelected ? '0 10px 25px -5px rgba(255, 215, 0, 0.15)' : 'none'
                                    }}
                                  >
                                    <div>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                        <span className="badge-power" style={{
                                          background: 'linear-gradient(135deg, #1e3a5f 0%, #112237 100%)',
                                          color: '#ffd700',
                                          fontSize: '0.75rem',
                                          fontWeight: '700',
                                          padding: '4px 10px',
                                          borderRadius: '6px',
                                          border: '1px solid rgba(255, 215, 0, 0.3)'
                                        }}>{powerStr}</span>
                                        {isCurrentlyApplied && (
                                          <span style={{ color: '#10b981', fontSize: '0.75rem', fontWeight: '700', background: 'rgba(16, 185, 129, 0.15)', padding: '2px 8px', borderRadius: '4px' }}>
                                            Appliqué
                                          </span>
                                        )}
                                        {isSelected && !isCurrentlyApplied && (
                                          <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', fontWeight: '700' }}>
                                            <Check size={14} /> Sélectionné
                                          </span>
                                        )}
                                      </div>
                                      <h6 style={{ fontSize: '1.05rem', color: '#ffffff', fontWeight: '700', margin: '0 0 8px 0' }}>{p.nom} — {powerStr} — {packSellingPriceStr}</h6>
                                      {p.description && <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: '0 0 16px 0', lineBreak: 'anywhere' }}>{p.description}</p>}
                                    </div>
                                    <div style={{ borderTop: '1px dashed rgba(255, 255, 255, 0.1)', paddingTop: '14px', marginTop: '12px' }}>
                                      <span style={{ fontSize: '0.8rem', color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
                                        Composition : <strong>{p.produits?.length || 0}</strong> article(s)
                                      </span>
                                      <span style={{ fontSize: '0.95rem', color: '#ffffff', fontWeight: '600' }}>
                                        Prix de vente : <strong style={{ color: '#ffd700' }}>{packSellingPriceStr}</strong>
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {selectedPackId && (
                          <div className="pack-details-panel assignment-step" style={{
                            background: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            borderRadius: '12px',
                            padding: '24px',
                            marginTop: '24px',
                            animation: 'fadeIn 0.3s ease'
                          }}>
                            <h5 className="step-title" style={{ marginBottom: '16px' }}>
                              <span className="step-number">2</span>
                              Aperçu des Stocks <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '400', marginLeft: '8px' }}>(Informatif — n'empêche pas l'affectation)</span>
                            </h5>

                            {isCheckingPackStock ? (
                              <div style={{ color: '#94a3b8', fontSize: '0.9rem', padding: '20px 0', textAlign: 'center' }}>
                                🔄 Analyse de la disponibilité des stocks en cours...
                              </div>
                            ) : (
                              <>
                                <div className="pack-products-table-wrapper" style={{ overflowX: 'auto', marginBottom: '24px' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', color: '#f8fafc' }}>
                                    <thead>
                                      <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.1)', textAlign: 'left', color: '#94a3b8' }}>
                                        <th style={{ padding: '12px 8px' }}>Produit</th>
                                        <th style={{ padding: '12px 8px', textAlign: 'center' }}>Qté Recommandée</th>
                                        <th style={{ padding: '12px 8px', textAlign: 'center' }}>Stock Disponible</th>
                                        <th style={{ padding: '12px 8px', textAlign: 'center' }}>Info Stock <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>(indicatif)</span></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {packStockWarnings.map(detail => {
                                        const isOutOfStock = detail.enRupture || detail.enAlerte;
                                        return (
                                          <tr key={detail.produitId} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                            <td style={{ padding: '12px 8px', fontWeight: '600' }}>{detail.nomProduit}</td>
                                            <td style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 'bold' }}>{detail.quantiteRecommandee}</td>
                                            <td style={{ padding: '12px 8px', textAlign: 'center', color: isOutOfStock ? '#ef4444' : '#10b981', fontWeight: 'bold' }}>
                                              {detail.quantiteDisponible}
                                            </td>
                                            <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                              {detail.enAlerte ? (
                                                <span style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold', border: '1px solid rgba(239, 68, 68, 0.3)' }}>Rupture</span>
                                              ) : detail.enRupture ? (
                                                <span style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold', border: '1px solid rgba(245, 158, 11, 0.3)' }}>Insuffisant</span>
                                              ) : (
                                                <span style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold', border: '1px solid rgba(16, 185, 129, 0.3)' }}>Disponible</span>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>

                                <div style={{ marginBottom: '16px', padding: '10px 14px', background: 'rgba(255, 215, 0, 0.05)', border: '1px solid rgba(255, 215, 0, 0.15)', borderRadius: '8px', fontSize: '0.82rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  ℹ️ <span>Les niveaux de stock sont affichés à titre indicatif. L'affectation peut être réalisée même en cas de rupture ou stock insuffisant.</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedPackId('');
                                      setPackStockWarnings([]);
                                    }}
                                    style={{
                                      background: 'rgba(255, 255, 255, 0.05)',
                                      color: '#94a3b8',
                                      border: '1px solid rgba(255, 255, 255, 0.1)',
                                      padding: '10px 20px',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      fontWeight: '600',
                                      transition: 'all 0.2s ease'
                                    }}
                                  >
                                    Annuler la sélection
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleApplyPack}
                                    disabled={isApplyingPack}
                                    style={{
                                      background: 'linear-gradient(135deg, #1e3a5f 0%, #112237 100%)',
                                      color: '#ffd700',
                                      border: '1px solid #ffd700',
                                      padding: '10px 24px',
                                      borderRadius: '8px',
                                      cursor: isApplyingPack ? 'not-allowed' : 'pointer',
                                      fontWeight: '700',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      boxShadow: '0 4px 15px rgba(255, 215, 0, 0.1)'
                                    }}
                                  >
                                    {isApplyingPack ? 'Affectation du pack...' : '🚀 Confirmer l\'Affectation du Pack'}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {/* Étape 1 : Choisir une Catégorie */}
                        <div className="assignment-step">
                          <h5 className="step-title">
                            <span className="step-number">1</span>
                            Choisir une Catégorie
                          </h5>
                          {allProduitsLoading ? (
                            <div className="catalog-loading">Chargement des produits du catalogue...</div>
                          ) : (
                            <div className="category-card-grid">
                              {(() => {
                                const categoriesMap = {};
                                allProduits.forEach(p => {
                                  const catId = p.categorie?._id || 'autres';
                                  const catLabel = p.categorie?.label || 'Autres';
                                  if (!categoriesMap[catId]) {
                                    categoriesMap[catId] = { _id: catId, label: catLabel, produits: [] };
                                  }
                                  categoriesMap[catId].produits.push(p);
                                });
                                const categoriesList = Object.values(categoriesMap);

                                return categoriesList.map(cat => {
                                  const isSelected = selectedCategory === cat._id;
                                  return (
                                    <div 
                                      key={cat._id} 
                                      className={`category-card ${isSelected ? 'active' : ''}`}
                                      onClick={() => {
                                        setSelectedCategory(cat._id);
                                        setSelectedCatalogProduct(null);
                                      }}
                                    >
                                      <div className="category-card-icon">📁</div>
                                      <div className="category-card-info">
                                        <span className="category-card-label">{cat.label}</span>
                                        <span className="category-card-count">{cat.produits.length} produit(s)</span>
                                      </div>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          )}
                        </div>

                        {/* Étape 2 : Sélectionner le Produit */}
                        {selectedCategory && (
                          <div className="assignment-step" style={{ marginTop: '24px' }}>
                            <h5 className="step-title">
                              <span className="step-number">2</span>
                              Sélectionner le Produit
                            </h5>
                            <div className="catalog-products-list">
                              {(() => {
                                const filteredCatalog = allProduits.filter(p => {
                                  const catId = p.categorie?._id || 'autres';
                                  return catId === selectedCategory;
                                });

                                if (filteredCatalog.length === 0) {
                                  return <p style={{ color: '#94a3b8' }}>Aucun produit dans cette catégorie.</p>;
                                }

                                return filteredCatalog.map(p => {
                                  const isSelected = selectedCatalogProduct?._id === p._id;
                                  const stockDisponible = getProduitStockDisponible(p);
                                  const isOutOfStock = stockDisponible <= 0;
                                  return (
                                    <div 
                                      key={p._id} 
                                      className={`catalog-product-selector-card ${isSelected ? 'active' : ''} ${isOutOfStock ? 'is-out-of-stock' : ''}`}
                                      onClick={() => {
                                        setSelectedCatalogProduct(p);
                                        setProduitFormData(prev => ({
                                          ...prev,
                                          produitId: p._id,
                                          prixProduit: p.prix || 0,
                                          prixUnitaire: p.prix || 0,
                                          TVA: p.TVA || 19,
                                          tauxGain: p.tauxGain || 0,
                                          paletteId: '',
                                          numeroSerie: '',
                                          quantite: 1,
                                          statut: 'commande',
                                          notes: '',
                                          specifications: p.description || ''
                                        }));
                                        setPalettes([]);
                                        setNumerosSerieDisponibles([]);
                                        setNumerosSerieSelectionnes([]);
                                        fetchPalettesForProduit(p._id);
                                      }}
                                    >
                                      <div className="catalog-product-img-wrapper">
                                        {p.imageProduit ? (
                                          <img src={`${BASE_URL}${p.imageProduit}`} alt={p.nomProduit} className="catalog-product-img" />
                                        ) : (
                                          <div className="catalog-product-icon-placeholder"><Package size={20} /></div>
                                        )}
                                      </div>
                                      <div className="catalog-product-main">
                                        <span className="catalog-product-name">{p.nomProduit}</span>
                                        <span className="catalog-product-ref">Réf: {p.reference || 'N/A'}</span>
                                        <span className="catalog-product-brand">{p.marque?.name || 'Sastra Energy'}</span>
                                        <span className={`catalog-stock-pill ${isOutOfStock ? 'empty' : 'available'}`}>
                                          Stock: {stockDisponible}
                                        </span>
                                      </div>
                                      <div className="catalog-product-price">
                                        <strong>{(p.prix || 0).toFixed(3)} TND</strong>
                                      </div>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Étape 3 : Configurer l'Affectation */}
                        {selectedCatalogProduct && (
                          <div className="assignment-step" style={{ marginTop: '24px' }}>
                            <h5 className="step-title">
                              <span className="step-number">3</span>
                              Configurer l'Affectation
                            </h5>
                            <form onSubmit={handleAddProduit} className="inline-assignment-form">
                              <div className="product-assignment-details-grid">
                                
                                {/* Product Info Card (Auto-filled) */}
                                <div className="selected-product-info-card">
                                  <div className="sip-header">
                                    <h6>Détails du Produit</h6>
                                    <span className="sip-brand">{selectedCatalogProduct.marque?.name || 'Sastra Energy'}</span>
                                  </div>
                                  <div className="sip-body">
                                    <div className="sip-info-row">
                                      <span>Nom:</span>
                                      <strong>{selectedCatalogProduct.nomProduit}</strong>
                                    </div>
                                    <div className="sip-info-row">
                                      <span>Référence:</span>
                                      <strong>{selectedCatalogProduct.reference || '—'}</strong>
                                    </div>
                                    <div className="sip-info-row">
                                      <span>Stock catalogue:</span>
                                      <strong>{getProduitStockDisponible(selectedCatalogProduct)}</strong>
                                    </div>
                                    <div className="sip-info-row">
                                      <span>Prix Unitaire HT:</span>
                                      <strong>{(selectedCatalogProduct.prix || 0).toFixed(3)} TND</strong>
                                    </div>
                                    <div className="sip-info-row">
                                      <span>TVA:</span>
                                      <strong>{produitFormData.TVA} %</strong>
                                    </div>
                                    <div className="sip-info-row">
                                      <span>Taux Gain:</span>
                                      <strong>{produitFormData.tauxGain} %</strong>
                                    </div>
                                    <div className="sip-info-row highlight-total">
                                      <span>Prix Total TTC (Calculé):</span>
                                      <strong>{parseFloat(produitFormData.prixTotal || 0).toFixed(3)} TND</strong>
                                    </div>
                                  </div>
                                </div>

                                {/* Inputs form */}
                                <div className="assignment-form-fields">
                                  
                                  <div className="form-fields-grid">
                                    <div className="form-field-group">
                                      <label>Palette (Optionnel)</label>
                                      <select
                                        value={produitFormData.paletteId}
                                        onChange={(e) => {
                                          const pId = e.target.value;
                                          setProduitFormData(prev => ({...prev, paletteId: pId}));
                                          fetchNumerosSerieFromPalette(pId, produitFormData.quantite);
                                        }}
                                      >
                                        <option value="">Sélectionner une palette</option>
                                        {palettes.map(palette => {
                                          const nbDisponibles = palette.numerosSerie?.filter(ns => ns.statut === 'disponible').length || 0;
                                          return (
                                            <option key={palette._id} value={palette._id}>
                                              {palette.numeroPalette} ({nbDisponibles} dispo)
                                            </option>
                                          );
                                        })}
                                      </select>
                                    </div>

                                    <div className="form-field-group">
                                      <label>Quantité *</label>
                                      <input
                                        type="number"
                                        min="1"
                                        value={produitFormData.quantite}
                                        onChange={(e) => {
                                          const newQte = parseInt(e.target.value) || 1;
                                          setProduitFormData(prev => ({...prev, quantite: newQte}));
                                          if (produitFormData.paletteId) {
                                            fetchNumerosSerieFromPalette(produitFormData.paletteId, newQte);
                                          }
                                        }}
                                        required
                                      />
                                      {!produitFormData.paletteId && selectedCatalogProduct && Number(produitFormData.quantite) > getProduitStockDisponible(selectedCatalogProduct) && (
                                        <small className="stock-warning-note">
                                          Stock catalogue visible: {getProduitStockDisponible(selectedCatalogProduct)}. Le serveur vérifiera aussi les lignes de stock disponibles.
                                        </small>
                                      )}
                                    </div>

                                    <div className="form-field-group ">
                                      <label>Numéro de Série</label>
                                      <input
                                        type="text"
                                        value={produitFormData.numeroSerie}
                                        onChange={(e) => setProduitFormData(prev => ({...prev, numeroSerie: e.target.value}))}
                                        placeholder={produitFormData.paletteId ? "Généré depuis la palette ci-dessous" : "Saisir le(s) numéro(s) de série manuellement"}
                                        disabled={!!produitFormData.paletteId}
                                      />
                                      {numerosSerieDisponibles.length > 0 && (
                                        <small style={{color: '#64748b', marginTop: '4px', display: 'block'}}>
                                          {numerosSerieDisponibles.length} numéro(s) de série disponible(s) dans cette palette
                                        </small>
                                      )}
                                    </div>

                                    {numerosSerieDisponibles.length > 0 && (
                                      <div className="form-field-group ">
                                        <label style={{color: '#1e3a5f', fontWeight: '600'}}>
                                          Numéros de Série Disponibles
                                          <span className="serial-selection-count">
                                            {numerosSerieSelectionnes.length} / {produitFormData.quantite || 1} sélectionné(s)
                                          </span>
                                        </label>
                                        <div className="inline-numeros-serie-list">
                                          {numerosSerieDisponibles.map((serie, index) => {
                                            const isSelected = numerosSerieSelectionnes.includes(serie.numero);
                                            const maxSelection = Math.max(1, parseInt(produitFormData.quantite, 10) || 1);
                                            const selectionLimitReached = !isSelected && numerosSerieSelectionnes.length >= maxSelection;

                                            return (
                                            <label key={serie._id || serie.numero} className={`inline-numero-serie-item ${isSelected ? 'selected' : ''} ${selectionLimitReached ? 'disabled' : ''}`}>
                                              <span className="numero-index">{index + 1}.</span>
                                              <input
                                                type="checkbox"
                                                checked={isSelected}
                                                disabled={selectionLimitReached}
                                                onChange={() => handleToggleNumeroSerieSelection(serie.numero)}
                                              />
                                              <span className="numero-serie-value">{serie.numero}</span>
                                            </label>
                                          );
                                          })}
                                        </div>
                                      </div>
                                    )}

                                    <div className="form-field-group">
                                      <label>Statut de l'installation</label>
                                      <select
                                        value={produitFormData.statut}
                                        onChange={(e) => setProduitFormData(prev => ({...prev, statut: e.target.value}))}
                                      >
                                        <option value="commande">Commandé</option>
                                        <option value="en_stock">En stock</option>
                                        <option value="installe">Installé</option>
                                        <option value="maintenance">En maintenance</option>
                                      </select>
                                    </div>

                                    <div className="form-field-group">
                                      <label>Date d'installation</label>
                                      <input
                                        type="date"
                                        value={produitFormData.dateInstallation}
                                        onChange={(e) => setProduitFormData(prev => ({...prev, dateInstallation: e.target.value}))}
                                      />
                                    </div>

                                    <div className="form-field-group ">
                                      <label>Notes / Spécifications</label>
                                      <textarea
                                        rows="2"
                                        value={produitFormData.notes}
                                        onChange={(e) => setProduitFormData(prev => ({...prev, notes: e.target.value}))}
                                        placeholder="Notes ou détails supplémentaires sur l'affectation..."
                                      />
                                    </div>

                                  </div>

                                  <div className="assignment-form-actions">
                                    <button
                                      type="button"
                                      className="btn-assignment-reset"
                                      onClick={() => {
                                        setSelectedCatalogProduct(null);
                                        setPalettes([]);
                                        setNumerosSerieDisponibles([]);
                                        setNumerosSerieSelectionnes([]);
                                      }}
                                    >
                                      <RotateCcw size={16} />
                                      <span>Réinitialiser</span>
                                    </button>
                                    <button type="submit" className="btn-assignment-submit">
                                      <PackageCheck size={17} />
                                      <span>Confirmer l'Affectation au Client</span>
                                    </button>
                                  </div>

                                </div>

                              </div>
                            </form>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ONGLET FINANCE */}
            {activeTab === 'finance' && (
              <div className="finance-section">
                <div className="finance-workflow-shell">
                  <GestionFinanciere clientId={clientId} dossierId={dossierId} />
                </div>
              </div>
            )}

            {/* ONGLET SCÉNARIOS PROSOL ELEC */}
            {activeTab === 'scenarios' && (
              <div className="scenarios-section">
                <ScenariosProsolElec
                  clientId={clientId}
                  dossierId={dossierId}
                  dossier={dossier}
                  onScenarioApplied={() => {
                    fetchClientAndDossier();
                    setMessage('Scénario appliqué avec succès ! Le dossier a été mis à jour.');
                  }}
                />
              </div>
            )}

            {/* ONGLET HISTORIQUE PAIEMENT */}
            {activeTab === 'historique-paiement' && (
              <div className="historique-paiement-section">
                <div className="finance-credit-shell" style={{marginBottom: '20px'}}>
                  <div className="echeancier-section-header">
                    <h3>Programmation des échéances</h3>
                    {hasPermission('dossiers', 'update') && (
                      <button className="btn-add" onClick={() => {
                        // Dossier 100% STEG : interdire la programmation d'échéances
                        const is100Steg = dossier.typePaiement === 'credit' &&
                          Number(dossier.montantSteg) > 0 &&
                          Math.abs(Number(dossier.montantSteg) - Number(dossier.montantTotalPaiement)) <= 1;
                        if (is100Steg) {
                          Swal.fire({
                            html: `
                              <div style="padding: 10px 5px; text-align: left; font-family: inherit;">
                                <div style="display: flex; align-items: center; gap: 14px; margin-bottom: 20px;">
                                  <div style="width: 52px; height: 52px; border-radius: 14px; background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: #ffffff; display: flex; align-items: center; justify-content: center; font-size: 1.75rem; boxShadow: 0 8px 20px rgba(245, 158, 11, 0.35); border: 2px solid #fef3c7; flex-shrink: 0;">
                                    ⚡
                                  </div>
                                  <div>
                                    <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                                      <h3 style="color: #0f172a; font-size: 1.25rem; font-weight: 800; margin: 0; letter-spacing: -0.01em;">
                                        Paiement 100% STEG
                                      </h3>
                                      <span style="background: #fef3c7; color: #b45309; border: 1px solid #fde047; font-weight: 700; font-size: 0.74rem; padding: 3px 10px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.04em;">
                                        Financement Intégral STEG
                                      </span>
                                    </div>
                                    <p style="color: #475569; font-size: 0.88rem; margin: 3px 0 0 0; font-weight: 500;">
                                      Ce dossier est financé entièrement par le crédit STEG.
                                    </p>
                                  </div>
                                </div>

                                <div style="background: #fffdf5; border: 1.5px solid #f59e0b; border-radius: 14px; padding: 18px 20px; color: #0f172a; margin-bottom: 20px; box-shadow: 0 4px 16px rgba(245, 158, 11, 0.12);">
                                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; border-bottom: 1px solid #fde68a; padding-bottom: 14px;">
                                    <div>
                                      <span style="font-size: 0.76rem; color: #64748b; font-weight: 600; display: block; margin-bottom: 2px;">Montant total dossier :</span>
                                      <strong style="font-size: 1.05rem; color: #0f172a; font-weight: 700;">${formatCurrency(montantTotalPaiement)}</strong>
                                    </div>
                                    <div>
                                      <span style="font-size: 0.76rem; color: #b45309; font-weight: 600; display: block; margin-bottom: 2px;">Prise en charge STEG (100%) :</span>
                                      <strong style="font-size: 1.05rem; color: #d97706; font-weight: 700;">${formatCurrency(dossier?.montantSteg || montantTotalPaiement)}</strong>
                                    </div>
                                  </div>

                                  <div style="display: flex; align-items: flex-start; gap: 10px; background: #fffbe7; border-left: 3.5px solid #f59e0b; border-radius: 6px; padding: 10px 12px; border: 1px solid #fde047;">
                                    <span style="font-size: 1.1rem; line-height: 1;">💡</span>
                                    <p style="margin: 0; font-size: 0.86rem; color: #78350f; line-height: 1.45; font-weight: 500;">
                                      <strong style="color: #92400e;">Il n'y a pas d'échéancier client à programmer.</strong><br/>
                                      L'intégralité du montant est couverte par la convention STEG. Utilisez le bouton <strong style="color: #b45309;">⚡ Payer STEG</strong> dans la section <em>Montant STEG (crédit)</em> pour valider le règlement.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            `,
                            showConfirmButton: true,
                            confirmButtonText: '⚡ Aller au bloc STEG',
                            confirmButtonColor: '#d97706',
                            showCancelButton: true,
                            cancelButtonText: 'Fermer',
                            cancelButtonColor: '#64748b',
                            customClass: {
                              popup: 'swal2-modal-premium-card'
                            }
                          }).then((result) => {
                            if (result.isConfirmed) {
                              const stegSection = document.getElementById('section-montant-steg') || document.querySelector('.steg-payment-card');
                              if (stegSection) {
                                stegSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }
                          });
                          return;
                        }
                        handleOpenConfigEcheancierModal();
                      }}>
                        {hasEcheancier ? '↻ Reprogrammer les échéances' : '⚙️ Programmer les échéances'}
                      </button>
                    )}
                  </div>

                  {/* Formulaire Intégré "Programmer / Reprogrammer les Échéances" (Design Modulaire à 6 Sections) */}
                  {showConfigEcheancierModal && (() => {
                    if (isPaiementEcheanceMaintenanceActive) {
                      return (
                        <div className="inline-financial-config-card" style={{
                          background: 'linear-gradient(135deg, #fffdf5 0%, #ffffff 100%)',
                          border: '2px solid #f59e0b',
                          borderRadius: '16px',
                          padding: '32px 28px',
                          margin: '16px 0 28px 0',
                          boxShadow: '0 12px 28px -4px rgba(245, 158, 11, 0.15)',
                          textAlign: 'center'
                        }}>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: '#fef3c7',
                            color: '#b45309',
                            border: '1px solid #fde047',
                            padding: '5px 14px',
                            borderRadius: '20px',
                            fontSize: '0.82rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            marginBottom: '16px'
                          }}>
                            <Wrench size={14} />
                            <span>Maintenance en cours</span>
                          </div>

                          <div style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justify: 'center',
                            margin: '0 auto 16px auto',
                            boxShadow: '0 8px 20px rgba(245, 158, 11, 0.3)'
                          }}>
                            <Wrench size={32} />
                          </div>

                          <h3 style={{ margin: '0 0 10px 0', color: '#0f172a', fontSize: '1.35rem', fontWeight: 800 }}>
                            Fonctionnalité temporairement indisponible
                          </h3>

                          <p style={{ margin: '0 auto 24px auto', color: '#475569', fontSize: '0.94rem', lineHeight: '1.6', maxWidth: '580px' }}>
                            Cette fonctionnalité est actuellement en cours de maintenance et de vérification par l’équipe IT afin d’améliorer la fiabilité des calculs financiers. Elle sera de nouveau disponible prochainement.
                          </p>

                          <button
                            type="button"
                            onClick={() => setShowConfigEcheancierModal(false)}
                            style={{
                              padding: '10px 24px',
                              borderRadius: '8px',
                              border: '1px solid #cbd5e1',
                              background: '#ffffff',
                              color: '#475569',
                              fontWeight: 700,
                              fontSize: '0.9rem',
                              cursor: 'pointer'
                            }}
                          >
                            Fermer
                          </button>
                        </div>
                      );
                    }

                    const autofinancement = dossier?.autofinancement || {};
                    const existingList = Array.isArray(autofinancement?.echeancier) ? autofinancement.echeancier : [];
                    const stegNonPayeDeduire = ((dossier?.typePaiement === 'mixte' || Number(dossier?.montantSteg || 0) > 0) && !dossier?.stegPaye) ? Number(dossier.montantSteg || 0) : 0;
                    const totalDossier = Number(dossier?.montantTotalPaiement || 0);

                    const activeAcompteSum = dossier?.autofinancement?.historiquePaiements
                      ? dossier.autofinancement.historiquePaiements
                          .filter(p => p && p.categorie === 'comptant' && p.statut === 'paye' && p.sansImpactSolde !== true)
                          .reduce((acc, p) => acc + Number(p.montant || 0), 0)
                      : 0;

                    let montantLibereEcheances = 0;
                    let uncancelledActiveSum = 0;
                    let totalPaidPreserved = 0;

                    existingList.forEach((e, idx) => {
                      if (!e || e.statut === 'annule') return;
                      const totalAmt = Number(e.montant || 0);
                      const paidAmt = Number(e.montantPaye || 0);
                      const isPaid = e.statut === 'paye';
                      const isSelected = selectedEcheancesToCancel.includes(idx);

                      if (isPaid) {
                        totalPaidPreserved += totalAmt || paidAmt;
                      } else if (isSelected && reprogrammMode !== 'keep') {
                        if (e.statut === 'partiellement_paye' && paidAmt > 0) {
                          totalPaidPreserved += paidAmt;
                          montantLibereEcheances += Math.max(0, totalAmt - paidAmt);
                        } else {
                          montantLibereEcheances += totalAmt;
                        }
                      } else {
                        uncancelledActiveSum += totalAmt;
                        if (paidAmt > 0) totalPaidPreserved += paidAmt;
                      }
                    });

                    const montantLibereAcompte = annulerAcompteOption ? activeAcompteSum : 0;
                    const montantLiberableTotal = Number((montantLibereEcheances + montantLibereAcompte).toFixed(2));
                    const totalPayeDossierReel = activeAcompteSum + totalPaidPreserved;
                    const totalRestantReel = Math.max(0, Number((totalDossier - stegNonPayeDeduire - totalPayeDossierReel).toFixed(2)));

                    const soldeNonAlloueActuel = Math.max(0, Number((totalRestantReel - uncancelledActiveSum).toFixed(2)));
                    const capaciteMax = Number((soldeNonAlloueActuel + montantLibereEcheances + montantLibereAcompte).toFixed(2));
                    const currentInputVal = Number(echeancierFormData.montantRestant || echeancierFormData.montantTotal || 0);
                    const soldeApresOperation = Math.max(0, Number((capaciteMax - currentInputVal).toFixed(2)));
                    const isOverflow = currentInputVal - capaciteMax > 0.009;

                    const isReprogrammer = echeancierFormData.isReprogrammer;

                    return (
                      <div className="inline-financial-config-card" style={{
                        background: '#ffffff',
                        border: '1.5px solid #cbd5e1',
                        borderRadius: '16px',
                        padding: '24px 28px',
                        margin: '20px 0 28px 0',
                        boxShadow: '0 12px 36px -6px rgba(15, 23, 42, 0.1)',
                        width: '100%',
                        boxSizing: 'border-box',
                        transition: 'all 0.3s ease'
                      }}>
                        {/* En-tête MODERNE du Formulaire */}
                        <div style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          paddingBottom: '16px', marginBottom: '22px', borderBottom: '1.5px solid #f1f5f9',
                          flexWrap: 'wrap', gap: '12px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div style={{
                              width: '46px', height: '46px', borderRadius: '12px',
                              background: isReprogrammer
                                ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                                : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                              color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              boxShadow: isReprogrammer ? '0 6px 16px rgba(37, 99, 235, 0.25)' : '0 6px 16px rgba(5, 150, 105, 0.25)'
                            }}>
                              {isReprogrammer ? <RotateCcw size={24} /> : <Sliders size={24} />}
                            </div>
                            <div>
                              <h4 style={{ margin: 0, color: '#0f172a', fontSize: '1.25rem', fontWeight: 800, letterSpacing: '-0.01em' }}>
                                {isReprogrammer ? 'Reprogrammation de l\'Échéancier' : 'Configuration de la Logique Financière'}
                              </h4>
                              <p style={{ margin: '3px 0 0 0', color: '#64748b', fontSize: '0.86rem', fontWeight: 500 }}>
                                {isReprogrammer
                                  ? 'Ajuster les échéances non payées et recalculer les mensualités'
                                  : 'Définir la structure de paiement initiale (Comptant, STEG ou Échéancier)'}
                              </p>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => setShowConfigEcheancierModal(false)}
                            style={{
                              background: '#ffffff', border: '1.5px solid #cbd5e1', borderRadius: '50%',
                              width: '36px', height: '36px', cursor: 'pointer', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', color: '#64748b',
                              transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
                            }}
                            title="Fermer"
                          >
                            <XCircle size={20} />
                          </button>
                        </div>

                        <form onSubmit={handleConfigurerEcheancier} className="dd-credit-config-form">

                          {/* ── SECTION 1 : SITUATION FINANCIÈRE ── */}
                          <div style={{ marginBottom: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                              <DollarSign size={18} color="#0284c7" />
                              <span style={{ fontSize: '0.88rem', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                1. Situation Financière du Dossier
                              </span>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px' }}>
                              <div style={{ background: '#f8fafc', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0' }}>
                                <span style={{ color: '#64748b', display: 'block', fontSize: '0.76rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '4px' }}>Total dossier</span>
                                <strong style={{ color: '#0f172a', fontSize: '1.05rem', fontWeight: '800', whiteSpace: 'nowrap' }}>{formatCurrency(syntheseFinanciere.montantTotalDossier)}</strong>
                              </div>
                              <div style={{ background: '#f0fdf4', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #bbf7d0' }}>
                                <span style={{ color: '#166534', display: 'block', fontSize: '0.76rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '4px' }}>Total payé</span>
                                <strong style={{ color: '#15803d', fontSize: '1.05rem', fontWeight: '800', whiteSpace: 'nowrap' }}>{formatCurrency(syntheseFinanciere.totalDejaPaye)}</strong>
                              </div>
                              <div style={{ background: '#f8fafc', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0' }}>
                                <span style={{ color: '#475569', display: 'block', fontSize: '0.76rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '4px' }}>Total restant</span>
                                <strong style={{ color: '#334155', fontSize: '1.05rem', fontWeight: '800', whiteSpace: 'nowrap' }}>{formatCurrency(syntheseFinanciere.resteAPayerReel)}</strong>
                              </div>
                              <div style={{ background: '#fffbe7', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #fde047' }}>
                                <span style={{ color: '#b45309', display: 'block', fontSize: '0.76rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '4px' }}>Total configuré</span>
                                <strong style={{ color: '#d97706', fontSize: '1.05rem', fontWeight: '800', whiteSpace: 'nowrap' }}>{formatCurrency(syntheseFinanciere.totalConfigure)}</strong>
                              </div>
                              <div style={{ background: '#f0f9ff', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #bae6fd' }}>
                                <span style={{ color: '#0369a1', display: 'block', fontSize: '0.76rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '4px' }}>Restant à configurer</span>
                                <strong style={{ color: '#0284c7', fontSize: '1.08rem', fontWeight: '800', whiteSpace: 'nowrap' }}>{formatCurrency(syntheseFinanciere.totalRestantAConfigurer)}</strong>
                              </div>
                              <div style={{ background: isOverflow ? '#fef2f2' : '#ecfdf5', padding: '12px 14px', borderRadius: '10px', border: isOverflow ? '1.5px solid #fca5a5' : '1.5px solid #6ee7b7' }}>
                                <span style={{ color: isOverflow ? '#991b1b' : '#047857', display: 'block', fontSize: '0.76rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '4px' }}>Capacité max programmable</span>
                                <strong style={{ color: isOverflow ? '#dc2626' : '#059669', fontSize: '1.08rem', fontWeight: '800', whiteSpace: 'nowrap' }}>{formatCurrency(capaciteMax)}</strong>
                              </div>
                            </div>
                          </div>

                          {isReprogrammer ? (
                            /* ── Mode Reprogrammation ── */
                            <>
                              {/* ── SECTION 2 : MODE DE REPROGRAMMATION ── */}
                              <div style={{ marginBottom: '22px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                  <Sliders size={18} color="#2563eb" />
                                  <span style={{ fontSize: '0.88rem', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    2. Mode de Reprogrammation
                                  </span>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '12px' }}>
                                  <div
                                    onClick={() => {
                                      setReprogrammMode('keep');
                                      setSelectedEcheancesToCancel([]);
                                    }}
                                    style={{
                                      border: reprogrammMode === 'keep' ? '2.5px solid #059669' : '1.5px solid #cbd5e1',
                                      background: reprogrammMode === 'keep' ? '#ecfdf5' : '#ffffff',
                                      borderRadius: '12px',
                                      padding: '14px 16px',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      boxShadow: reprogrammMode === 'keep' ? '0 4px 12px rgba(5, 150, 105, 0.15)' : 'none'
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Lock size={18} color={reprogrammMode === 'keep' ? '#047857' : '#64748b'} />
                                        <strong style={{ fontSize: '0.92rem', color: reprogrammMode === 'keep' ? '#047857' : '#1e293b' }}>
                                          Conserver existant
                                        </strong>
                                      </div>
                                      {reprogrammMode === 'keep' && <CheckCircle size={18} color="#059669" />}
                                    </div>
                                    <p style={{ margin: 0, fontSize: '0.82rem', color: '#475569', lineHeight: '1.4' }}>
                                      Conserver toutes les échéances actuelles et ajouter les nouvelles.
                                    </p>
                                  </div>

                                  <div
                                    onClick={() => {
                                      setReprogrammMode('all');
                                      const allCancellableIndices = existingList
                                        .map((e, idx) => (e && e.statut !== 'paye' && e.statut !== 'annule' ? idx : null))
                                        .filter((idx) => idx !== null);
                                      setSelectedEcheancesToCancel(allCancellableIndices);
                                    }}
                                    style={{
                                      border: reprogrammMode === 'all' ? '2.5px solid #2563eb' : '1.5px solid #cbd5e1',
                                      background: reprogrammMode === 'all' ? '#eff6ff' : '#ffffff',
                                      borderRadius: '12px',
                                      padding: '14px 16px',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      boxShadow: reprogrammMode === 'all' ? '0 4px 12px rgba(37, 99, 235, 0.15)' : 'none'
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <RotateCcw size={18} color={reprogrammMode === 'all' ? '#1d4ed8' : '#64748b'} />
                                        <strong style={{ fontSize: '0.92rem', color: reprogrammMode === 'all' ? '#1d4ed8' : '#1e293b' }}>
                                          Remplacer tout
                                        </strong>
                                      </div>
                                      {reprogrammMode === 'all' && <CheckCircle size={18} color="#2563eb" />}
                                    </div>
                                    <p style={{ margin: 0, fontSize: '0.82rem', color: '#475569', lineHeight: '1.4' }}>
                                      Remplacer toutes les échéances non payées par le nouveau plan.
                                    </p>
                                  </div>

                                  <div
                                    onClick={() => setReprogrammMode('custom')}
                                    style={{
                                      border: reprogrammMode === 'custom' ? '2.5px solid #7c3aed' : '1.5px solid #cbd5e1',
                                      background: reprogrammMode === 'custom' ? '#f5f3ff' : '#ffffff',
                                      borderRadius: '12px',
                                      padding: '14px 16px',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      boxShadow: reprogrammMode === 'custom' ? '0 4px 12px rgba(124, 58, 237, 0.15)' : 'none'
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Sliders size={18} color={reprogrammMode === 'custom' ? '#6d28d9' : '#64748b'} />
                                        <strong style={{ fontSize: '0.92rem', color: reprogrammMode === 'custom' ? '#6d28d9' : '#1e293b' }}>
                                          Personnalisée
                                        </strong>
                                      </div>
                                      {reprogrammMode === 'custom' && <CheckCircle size={18} color="#7c3aed" />}
                                    </div>
                                    <p style={{ margin: 0, fontSize: '0.82rem', color: '#475569', lineHeight: '1.4' }}>
                                      Sélectionner manuellement par case à cocher ci-dessous.
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* ── SECTION 3 : ÉCHÉANCES EXISTANTES ── */}
                              {existingList.length > 0 && (
                                <div style={{ marginBottom: '22px', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '18px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <Calendar size={18} color="#334155" />
                                      <span style={{ fontSize: '0.88rem', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        3. Échéances Existantes ({existingList.length})
                                      </span>
                                    </div>
                                    <span style={{ fontSize: '0.8rem', color: '#475569', fontWeight: 600, background: '#ffffff', padding: '3px 10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                                      {reprogrammMode === 'all' ? 'Toutes les échéances non payées sélectionnées' : 'Cocher les échéances à remplacer'}
                                    </span>
                                  </div>

                                  <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1.5px solid #cbd5e1', borderRadius: '10px', background: '#ffffff', marginBottom: '14px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
                                      <thead>
                                        <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left', color: '#334155' }}>
                                          <th style={{ padding: '10px 14px', width: '50px', textAlign: 'center' }}>Rempl.</th>
                                          <th style={{ padding: '10px 14px' }}>Date</th>
                                          <th style={{ padding: '10px 14px', textAlign: 'right' }}>Montant</th>
                                          <th style={{ padding: '10px 14px', textAlign: 'right' }}>Payé</th>
                                          <th style={{ padding: '10px 14px', textAlign: 'right' }}>Restant</th>
                                          <th style={{ padding: '10px 14px', textAlign: 'center' }}>Statut</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {existingList.map((ech, idx) => {
                                          if (!ech || ech.statut === 'annule') return null;
                                          const isPaid = ech.statut === 'paye';
                                          const isChecked = selectedEcheancesToCancel.includes(idx);
                                          const totalAmt = Number(ech.montant || 0);
                                          const paidAmt = Number(ech.montantPaye || 0);
                                          const remainingAmt = Math.max(0, totalAmt - paidAmt);

                                          const getStatusBadge = (s) => {
                                            switch (s) {
                                              case 'paye': return <span style={{ background: '#dcfce7', color: '#15803d', padding: '4px 10px', borderRadius: '20px', fontWeight: '700', fontSize: '0.76rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Lock size={12} /> Payé</span>;
                                              case 'partiellement_paye': return <span style={{ background: '#fef3c7', color: '#b45309', padding: '4px 10px', borderRadius: '20px', fontWeight: '700', fontSize: '0.76rem' }}>Partiel</span>;
                                              case 'retard': return <span style={{ background: '#fee2e2', color: '#b91c1c', padding: '4px 10px', borderRadius: '20px', fontWeight: '700', fontSize: '0.76rem' }}>En retard</span>;
                                              default: return <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '4px 10px', borderRadius: '20px', fontWeight: '700', fontSize: '0.76rem' }}>En attente</span>;
                                            }
                                          };

                                          return (
                                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: isPaid ? '#f8fafc' : (isChecked ? '#f0fdf4' : '#ffffff') }}>
                                              <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                                {isPaid ? (
                                                  <Lock size={16} color="#94a3b8" title="Échéance payée non annulable" />
                                                ) : (
                                                  <input
                                                    type="checkbox"
                                                    disabled={reprogrammMode === 'all'}
                                                    checked={isChecked}
                                                    onChange={(e) => {
                                                      if (e.target.checked) {
                                                        setSelectedEcheancesToCancel([...selectedEcheancesToCancel, idx]);
                                                      } else {
                                                        setSelectedEcheancesToCancel(selectedEcheancesToCancel.filter(i => i !== idx));
                                                      }
                                                    }}
                                                    style={{ cursor: reprogrammMode === 'all' ? 'not-allowed' : 'pointer', accentColor: '#2563eb', width: '18px', height: '18px' }}
                                                  />
                                                )}
                                              </td>
                                              <td style={{ padding: '10px 14px', fontWeight: '600', color: '#1e293b' }}>
                                                {ech.dateEcheance ? new Date(ech.dateEcheance).toLocaleDateString('fr-FR') : `Mois ${ech.mois}/${ech.annee}`}
                                              </td>
                                              <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: '700', color: '#0f172a' }}>
                                                {formatCurrency(totalAmt)}
                                              </td>
                                              <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>
                                                {formatCurrency(paidAmt)}
                                              </td>
                                              <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: '700', color: remainingAmt > 0 ? '#0284c7' : '#16a34a' }}>
                                                {formatCurrency(remainingAmt)}
                                              </td>
                                              <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                                {getStatusBadge(ech.statut)}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>

                                  {/* Banner synthétique Montant Libéré */}
                                  <div style={{
                                    background: '#fffbe7', border: '1.5px solid #fde047', borderRadius: '10px',
                                    padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <RotateCcw size={18} color="#b45309" />
                                      <span style={{ fontSize: '0.86rem', color: '#92400e', fontWeight: '700' }}>
                                        Montant total libéré par la sélection :
                                      </span>
                                    </div>
                                    <strong style={{ fontSize: '1.05rem', color: '#b45309', fontWeight: 800 }}>
                                      +{formatCurrency(montantLiberableTotal)}
                                    </strong>
                                  </div>

                                  {/* Option Annulation Acompte */}
                                  {Array.isArray(dossier?.autofinancement?.historiquePaiements) &&
                                    dossier.autofinancement.historiquePaiements.some(p => p && p.categorie === 'comptant' && p.statut === 'paye' && p.sansImpactSolde !== true) && (
                                      <div style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe', padding: '12px 16px', borderRadius: '10px', marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <input
                                          type="checkbox"
                                          id="annulerAcompteCheckbox"
                                          checked={annulerAcompteOption}
                                          onChange={(e) => setAnnulerAcompteOption(e.target.checked)}
                                          style={{ width: '18px', height: '18px', accentColor: '#2563eb', cursor: 'pointer', flexShrink: 0 }}
                                        />
                                        <label htmlFor="annulerAcompteCheckbox" style={{ fontSize: '0.85rem', color: '#1e40af', fontWeight: '600', cursor: 'pointer', margin: 0, lineHeight: '1.4' }}>
                                          Annuler l'acompte actif client ({formatCurrency(
                                            dossier.autofinancement.historiquePaiements
                                              .filter(p => p && p.categorie === 'comptant' && p.statut === 'paye' && p.sansImpactSolde !== true)
                                              .reduce((acc, p) => acc + Number(p.montant || 0), 0)
                                          )}) pour libérer ce montant dans la reprogrammation.
                                        </label>
                                      </div>
                                  )}
                                </div>
                              )}

                              {/* ── SECTION 4 : NOUVEL ÉCHÉANCIER ── */}
                              <div style={{ marginBottom: '22px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                                  <PlusCircle size={18} color="#059669" />
                                  <span style={{ fontSize: '0.88rem', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    4. Nouvel Échéancier
                                  </span>
                                </div>

                                <div className="dd-credit-config-row" style={{ marginBottom: '16px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap', gap: '8px' }}>
                                    <label style={{ fontSize: '0.86rem', color: '#1e293b', fontWeight: '700', margin: 0 }}>
                                      Montant à reprogrammer (TND) *
                                    </label>
                                    <button
                                      type="button"
                                      onClick={() => setEcheancierFormData({
                                        ...echeancierFormData,
                                        montantRestant: String(capaciteMax),
                                        montantTotal: String(capaciteMax)
                                      })}
                                      style={{ background: '#ecfdf5', color: '#047857', border: '1.5px solid #a7f3d0', borderRadius: '8px', padding: '5px 12px', fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer' }}
                                    >
                                      Appliquer le disponible max ({formatCurrency(capaciteMax)})
                                    </button>
                                  </div>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    step="any"
                                    min="0"
                                    required
                                    value={echeancierFormData.montantRestant}
                                    onChange={(e) => setEcheancierFormData({ ...echeancierFormData, montantRestant: normalizeDecimalInput(e.target.value) })}
                                    style={{ fontSize: '1.1rem', fontWeight: '700', color: '#0f172a', background: '#ffffff', border: '1.5px solid #94a3b8', borderRadius: '10px', padding: '10px 14px', width: '100%', boxSizing: 'border-box', height: '46px' }}
                                  />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '16px' }}>
                                  <div className="dd-credit-config-row">
                                    <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#1e293b' }}>Nombre de Mois *</label>
                                    <input
                                      type="number" min="1" max="240" required
                                      value={echeancierFormData.nombreMois}
                                      onChange={(e) => {
                                        const newMois = e.target.value;
                                        const newCustom = generateAutoEcheancesList(
                                          newMois,
                                          echeancierFormData.dateDebutCredit,
                                          echeancierFormData.frequenceMois,
                                          Number(echeancierFormData.montantRestant) || 0
                                        );
                                        setEcheancierFormData({ ...echeancierFormData, nombreMois: newMois, echeancesCustom: newCustom });
                                      }}
                                      style={{ height: '42px', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', fontWeight: 600 }}
                                    />
                                  </div>
                                  <div className="dd-credit-config-row">
                                    <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#1e293b' }}>Fréquence *</label>
                                    <select
                                      value={echeancierFormData.frequenceMois || '1'}
                                      onChange={(e) => {
                                        const newFreq = e.target.value;
                                        const newCustom = generateAutoEcheancesList(
                                          echeancierFormData.nombreMois,
                                          echeancierFormData.dateDebutCredit,
                                          newFreq,
                                          Number(echeancierFormData.montantRestant) || 0,
                                          echeancierFormData.echeancesCustom
                                        );
                                        setEcheancierFormData({ ...echeancierFormData, frequenceMois: newFreq, echeancesCustom: newCustom });
                                      }}
                                      style={{ width: '100%', height: '42px', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', fontWeight: 600, background: '#ffffff' }}
                                    >
                                      <option value="1">Chaque mois (1 mois)</option>
                                      <option value="2">Tous les 2 mois</option>
                                      <option value="3">Tous les 3 mois</option>
                                      <option value="6">Tous les 6 mois</option>
                                      <option value="custom">Personnalisée</option>
                                    </select>
                                  </div>
                                  <div className="dd-credit-config-row">
                                    <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#1e293b' }}>Date de première échéance *</label>
                                    <input
                                      type="date" required
                                      value={echeancierFormData.dateDebutCredit}
                                      onChange={(e) => {
                                        const newDate = e.target.value;
                                        const newCustom = generateAutoEcheancesList(
                                          echeancierFormData.nombreMois,
                                          newDate,
                                          echeancierFormData.frequenceMois,
                                          Number(echeancierFormData.montantRestant) || 0,
                                          echeancierFormData.echeancesCustom
                                        );
                                        setEcheancierFormData({ ...echeancierFormData, dateDebutCredit: newDate, echeancesCustom: newCustom });
                                      }}
                                      style={{ height: '42px', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', fontWeight: 600 }}
                                    />
                                  </div>
                                </div>

                                {/* Aperçu interactif des Échéances Générées */}
                                {(() => {
                                  const activeList = (
                                    Array.isArray(echeancierFormData.echeancesCustom) &&
                                    echeancierFormData.echeancesCustom.length === Number(echeancierFormData.nombreMois)
                                  )
                                    ? echeancierFormData.echeancesCustom
                                    : generateAutoEcheancesList(
                                        echeancierFormData.nombreMois,
                                        echeancierFormData.dateDebutCredit,
                                        echeancierFormData.frequenceMois,
                                        Number(echeancierFormData.montantRestant) || 0,
                                        echeancierFormData.echeancesCustom
                                      );

                                  return (
                                    <div style={{ marginTop: '16px', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1.5px solid #cbd5e1' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                          <h5 style={{ margin: 0, color: '#1e293b', fontSize: '0.92rem', fontWeight: 700 }}>
                                            📋 Aperçu des échéances AVANT validation ({activeList.length} échéance{activeList.length > 1 ? 's' : ''})
                                          </h5>
                                          <span style={{ color: '#0284c7', fontSize: '0.78rem', fontWeight: 700, background: '#e0f2fe', padding: '4px 10px', borderRadius: '20px', border: '1px solid #bae6fd' }}>
                                            Dates & Modes de paiement modifiables
                                          </span>
                                        </div>
                                        <small style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 500 }}>
                                          Vous pouvez modifier individuellement la date et le mode de paiement de chaque échéance ci-dessous :
                                        </small>
                                      </div>
                                      <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1.5px solid #cbd5e1', borderRadius: '10px', background: '#ffffff' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem', tableLayout: 'auto' }}>
                                          <thead>
                                            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1', color: '#475569', textAlign: 'left' }}>
                                              <th style={{ padding: '10px 14px', width: '90px', whiteSpace: 'nowrap' }}>Échéance</th>
                                              <th style={{ padding: '10px 14px', minWidth: '150px' }}>Date d'échéance</th>
                                              <th style={{ padding: '10px 14px', minWidth: '170px' }}>Mode de paiement</th>
                                              <th style={{ padding: '10px 14px', textAlign: 'right', minWidth: '120px' }}>Montant</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {activeList.map((ech, idx) => (
                                              <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '10px 14px', fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>
                                                  N° {idx + 1}
                                                </td>
                                                <td style={{ padding: '8px 14px' }}>
                                                  <input
                                                    type="date"
                                                    required
                                                    value={ech.dateEcheance || ''}
                                                    onChange={(e) => {
                                                      const updated = [...activeList];
                                                      updated[idx] = { ...updated[idx], dateEcheance: e.target.value };
                                                      setEcheancierFormData({ ...echeancierFormData, echeancesCustom: updated });
                                                    }}
                                                    style={{
                                                      padding: '6px 10px',
                                                      borderRadius: '6px',
                                                      border: '1.5px solid #cbd5e1',
                                                      fontSize: '0.88rem',
                                                      fontWeight: 600,
                                                      color: '#0f172a',
                                                      width: '100%',
                                                      minWidth: '140px',
                                                      maxWidth: '180px',
                                                      boxSizing: 'border-box',
                                                      height: '38px',
                                                      background: '#ffffff'
                                                    }}
                                                  />
                                                </td>
                                                <td style={{ padding: '8px 14px' }}>
                                                  <select
                                                    value={ech.modePaiement || 'especes'}
                                                    onChange={(e) => {
                                                      const updated = [...activeList];
                                                      updated[idx] = { ...updated[idx], modePaiement: e.target.value };
                                                      setEcheancierFormData({ ...echeancierFormData, echeancesCustom: updated });
                                                    }}
                                                    style={{
                                                      padding: '6px 10px',
                                                      borderRadius: '6px',
                                                      border: '1.5px solid #cbd5e1',
                                                      fontSize: '0.88rem',
                                                      fontWeight: 600,
                                                      color: '#0f172a',
                                                      width: '100%',
                                                      minWidth: '150px',
                                                      maxWidth: '200px',
                                                      boxSizing: 'border-box',
                                                      height: '38px',
                                                      background: '#ffffff'
                                                    }}
                                                  >
                                                    {PAYMENT_MODES.map(mode => (
                                                      <option key={mode.value} value={mode.value}>{mode.label}</option>
                                                    ))}
                                                  </select>
                                                </td>
                                                <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 800, color: '#0284c7', whiteSpace: 'nowrap' }}>
                                                  {formatCurrency(ech.montant)}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                      {Number(echeancierFormData.nombreMois) > 0 && (
                                        <small style={{ display: 'block', marginTop: '10px', color: '#0369a1', fontStyle: 'italic', fontWeight: '700', fontSize: '0.84rem' }}>
                                          Mensualité estimée : {formatCurrency(Number(echeancierFormData.montantRestant) / Number(echeancierFormData.nombreMois))}
                                        </small>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </>
                          ) : (
                            /* ── Mode Première Programmation ── */
                            <>
                              {/* ── SECTION 2 & 4 COMPACTE POUR PREMIÈRE PROGRAMMATION ── */}
                              <div style={{ marginBottom: '22px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                                  <Sliders size={18} color="#059669" />
                                  <span style={{ fontSize: '0.88rem', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    2. Configuration Financière Initiale
                                  </span>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '16px' }}>
                                  <div className="dd-credit-config-row">
                                    <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#1e293b' }}>Montant Total Projet (TND) *</label>
                                    <input
                                      type="text" inputMode="decimal" step="any" min="0" required
                                      value={echeancierFormData.montantTotalPaiement}
                                      onChange={(e) => {
                                        const newTotal = normalizeDecimalInput(e.target.value);
                                        const newRep = calculerRepartitionPaiement({
                                          montantTotalPaiement: newTotal,
                                          typePaiement: echeancierFormData.typePaiement,
                                          montantSteg: echeancierFormData.montantSteg,
                                          montantComptant: echeancierFormData.montantComptant,
                                          nombreMoisCredit: echeancierFormData.nombreMois
                                        });
                                        const newCustom = generateAutoEcheancesList(
                                          echeancierFormData.nombreMois,
                                          echeancierFormData.dateDebutCredit,
                                          echeancierFormData.frequenceMois,
                                          newRep.montantCredit,
                                          echeancierFormData.echeancesCustom
                                        );
                                        setEcheancierFormData({ ...echeancierFormData, montantTotalPaiement: newTotal, echeancesCustom: newCustom });
                                      }}
                                      style={{ height: '42px', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', fontWeight: 600, color: '#0f172a', background: '#ffffff' }}
                                    />
                                  </div>

                                  <div className="dd-credit-config-row">
                                    <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#1e293b' }}>Type de Paiement *</label>
                                    <select
                                      value={echeancierFormData.typePaiement || 'comptant'}
                                      onChange={(e) => {
                                        const newType = e.target.value;
                                        const totalP = Number(echeancierFormData.montantTotalPaiement) || 0;
                                        let newSteg = echeancierFormData.montantSteg;
                                        if (newType === 'comptant') {
                                          newSteg = '0';
                                        } else if (newType === 'credit') {
                                          newSteg = String(Math.min(totalP, 10000));
                                        } else if (newType === 'mixte' && (!newSteg || Number(newSteg) === 0)) {
                                          newSteg = String(Math.min(totalP, 10000));
                                        }
                                        const newRep = calculerRepartitionPaiement({
                                          montantTotalPaiement: echeancierFormData.montantTotalPaiement,
                                          typePaiement: newType,
                                          montantSteg: newSteg,
                                          montantComptant: echeancierFormData.montantComptant,
                                          nombreMoisCredit: echeancierFormData.nombreMois
                                        });
                                        const newCustom = generateAutoEcheancesList(
                                          echeancierFormData.nombreMois,
                                          echeancierFormData.dateDebutCredit,
                                          echeancierFormData.frequenceMois,
                                          newRep.montantCredit,
                                          echeancierFormData.echeancesCustom
                                        );
                                        setEcheancierFormData({
                                          ...echeancierFormData,
                                          typePaiement: newType,
                                          montantSteg: newSteg,
                                          echeancesCustom: newCustom
                                        });
                                      }}
                                      required
                                      style={{ width: '100%', height: '42px', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', fontWeight: 600, background: '#ffffff', color: '#0f172a' }}
                                    >
                                      <option value="comptant">Paiement 100% Comptant</option>
                                      <option value="credit">Crédit STEG (100% STEG - Max 10 000 TND)</option>
                                      <option value="mixte">Paiement Mixte (STEG + Autofinancement / Échéancier)</option>
                                    </select>
                                  </div>

                                  {(echeancierFormData.typePaiement === 'mixte' || echeancierFormData.typePaiement === 'credit') && (
                                    <div className="dd-credit-config-row">
                                      <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#1e293b' }}>Montant Crédit STEG (TND) *</label>
                                      <select
                                        value={echeancierFormData.montantSteg !== undefined && echeancierFormData.montantSteg !== null ? echeancierFormData.montantSteg : ''}
                                        onChange={(e) => {
                                          const newSteg = e.target.value;
                                          const newRep = calculerRepartitionPaiement({
                                            montantTotalPaiement: echeancierFormData.montantTotalPaiement,
                                            typePaiement: echeancierFormData.typePaiement,
                                            montantSteg: newSteg,
                                            montantComptant: echeancierFormData.montantComptant,
                                            nombreMoisCredit: echeancierFormData.nombreMois
                                          });
                                          const newCustom = generateAutoEcheancesList(
                                            echeancierFormData.nombreMois,
                                            echeancierFormData.dateDebutCredit,
                                            echeancierFormData.frequenceMois,
                                            newRep.montantCredit,
                                            echeancierFormData.echeancesCustom
                                          );
                                          setEcheancierFormData({ ...echeancierFormData, montantSteg: newSteg, echeancesCustom: newCustom });
                                        }}
                                        style={{ width: '100%', height: '42px', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', fontWeight: 600, background: '#ffffff', color: '#0f172a' }}
                                      >
                                        <option value="0">0 DT (Pas de STEG)</option>
                                        {getStegOptionsList(echeancierFormData.montantTotalPaiement, echeancierFormData.montantSteg).map(opt => (
                                          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                            {opt.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                </div>

                                {(() => {
                                  const rep = calculerRepartitionPaiement({
                                    montantTotalPaiement: echeancierFormData.montantTotalPaiement,
                                    typePaiement: echeancierFormData.typePaiement,
                                    montantSteg: echeancierFormData.montantSteg,
                                    montantComptant: echeancierFormData.montantComptant,
                                    nombreMoisCredit: echeancierFormData.nombreMois
                                  });
                                  return (
                                    <>
                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '16px' }}>
                                        <div className="dd-credit-config-row">
                                          <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#1e293b' }}>Part STEG (Calculée)</label>
                                          <input type="number" step="any" value={rep.montantSteg} readOnly style={{backgroundColor: '#f8fafc', fontWeight: '700', color: '#d97706', height: '42px', borderRadius: '8px', border: '1.5px solid #cbd5e1'}} />
                                        </div>
                                        <div className="dd-credit-config-row">
                                          <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#1e293b' }}>Part Autofinancement</label>
                                          <input type="number" step="any" value={rep.montantAutofinancement} readOnly style={{backgroundColor: '#f8fafc', fontWeight: '700', color: '#2563eb', height: '42px', borderRadius: '8px', border: '1.5px solid #cbd5e1'}} />
                                        </div>
                                        <div className="dd-credit-config-row">
                                          <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#1e293b' }}>{rep.typePaiement === 'mixte' ? 'Acompte Client (Hors STEG)' : 'Acompte Client (TND)'}</label>
                                          <input
                                            type="text" inputMode="decimal" step="any" min="0"
                                            value={echeancierFormData.montantComptant}
                                            onChange={(e) => setEcheancierFormData({...echeancierFormData, montantComptant: normalizeDecimalInput(e.target.value)})}
                                            placeholder="0.00"
                                            style={{ height: '42px', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', fontWeight: 600 }}
                                          />
                                        </div>
                                      </div>

                                      <div style={{background: '#f0f9ff', padding: '18px', borderRadius: '12px', border: '1.5px solid #bae6fd', marginBottom: '16px'}}>
                                        <h4 style={{margin: '0 0 14px 0', color: '#1e3a5f', fontSize: '0.96rem', fontWeight: 800}}>Configuration du Crédit / Échéancier</h4>
                                        <div style={{display: 'flex', gap: '20px', marginBottom: '16px', padding: '12px 16px', background: 'white', borderRadius: '10px', border: '1.5px dashed #3b82f6', flexWrap: 'wrap'}}>
                                          <div style={{flex: 1, minWidth: '140px'}}>
                                            <span style={{fontSize: '0.78rem', color: '#64748b', display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em'}}>Part Comptant Finale</span>
                                            <strong style={{fontSize: '1.1rem', color: '#0f172a', fontWeight: 800}}>{formatCurrency(rep.montantComptant)}</strong>
                                          </div>
                                          <div style={{flex: 1, minWidth: '140px'}}>
                                            <span style={{fontSize: '0.78rem', color: '#64748b', display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em'}}>Part Crédit Finale</span>
                                            <strong style={{fontSize: '1.1rem', color: '#2563eb', fontWeight: 800}}>{formatCurrency(rep.montantCredit)}</strong>
                                          </div>
                                        </div>

                                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px'}}>
                                          <div>
                                            <label style={{fontSize: '0.85rem', fontWeight: '700', color: '#1e293b', display: 'block', marginBottom: '4px'}}>Nombre de Mois *</label>
                                            <input
                                              type="number" min="1" required
                                              value={echeancierFormData.nombreMois}
                                              onChange={(e) => {
                                                const newMois = e.target.value;
                                                const newCustom = generateAutoEcheancesList(
                                                  newMois,
                                                  echeancierFormData.dateDebutCredit,
                                                  echeancierFormData.frequenceMois,
                                                  rep.montantCredit,
                                                  echeancierFormData.echeancesCustom
                                                );
                                                setEcheancierFormData({ ...echeancierFormData, nombreMois: newMois, echeancesCustom: newCustom });
                                              }}
                                              style={{ height: '42px', width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', fontWeight: 600 }}
                                            />
                                          </div>
                                          <div>
                                            <label style={{fontSize: '0.85rem', fontWeight: '700', color: '#1e293b', display: 'block', marginBottom: '4px'}}>Fréquence *</label>
                                            <select
                                              value={echeancierFormData.frequenceMois || '1'}
                                              onChange={(e) => {
                                                const newFreq = e.target.value;
                                                const newCustom = generateAutoEcheancesList(
                                                  echeancierFormData.nombreMois,
                                                  echeancierFormData.dateDebutCredit,
                                                  newFreq,
                                                  rep.montantCredit,
                                                  echeancierFormData.echeancesCustom
                                                );
                                                setEcheancierFormData({ ...echeancierFormData, frequenceMois: newFreq, echeancesCustom: newCustom });
                                              }}
                                              style={{ width: '100%', height: '42px', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', fontWeight: 600, background: '#ffffff' }}
                                            >
                                              <option value="1">Chaque mois (1 mois)</option>
                                              <option value="2">Tous les 2 mois</option>
                                              <option value="3">Tous les 3 mois</option>
                                              <option value="6">Tous les 6 mois</option>
                                              <option value="custom">Personnalisée</option>
                                            </select>
                                          </div>
                                          <div>
                                            <label style={{fontSize: '0.85rem', fontWeight: '700', color: '#1e293b', display: 'block', marginBottom: '4px'}}>Date démarrage *</label>
                                            <input
                                              type="date" required
                                              value={echeancierFormData.dateDebutCredit}
                                              onChange={(e) => {
                                                const newDate = e.target.value;
                                                const newCustom = generateAutoEcheancesList(
                                                  echeancierFormData.nombreMois,
                                                  newDate,
                                                  echeancierFormData.frequenceMois,
                                                  rep.montantCredit,
                                                  echeancierFormData.echeancesCustom
                                                );
                                                setEcheancierFormData({ ...echeancierFormData, dateDebutCredit: newDate, echeancesCustom: newCustom });
                                              }}
                                              style={{ height: '42px', width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', fontWeight: 600 }}
                                            />
                                          </div>
                                        </div>

                                        {/* Aperçu interactif AVANT validation */}
                                        {rep.montantCredit > 0 && (() => {
                                          const activeList = (
                                            Array.isArray(echeancierFormData.echeancesCustom) &&
                                            echeancierFormData.echeancesCustom.length === Number(echeancierFormData.nombreMois)
                                          )
                                            ? echeancierFormData.echeancesCustom
                                            : generateAutoEcheancesList(
                                                echeancierFormData.nombreMois,
                                                echeancierFormData.dateDebutCredit,
                                                echeancierFormData.frequenceMois,
                                                rep.montantCredit,
                                                echeancierFormData.echeancesCustom
                                              );

                                          return (
                                            <div style={{ marginTop: '16px', background: '#ffffff', padding: '14px', borderRadius: '10px', border: '1.5px solid #bae6fd' }}>
                                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                                                <h5 style={{ margin: 0, color: '#1e3a5f', fontSize: '0.88rem', fontWeight: 700 }}>
                                                  📋 Aperçu des Échéances AVANT validation ({activeList.length} échéance{activeList.length > 1 ? 's' : ''})
                                                </h5>
                                                <small style={{ color: '#0284c7', fontSize: '0.78rem', fontWeight: 700, background: '#e0f2fe', padding: '3px 10px', borderRadius: '20px', border: '1px solid #bae6fd' }}>
                                                  Vous pouvez modifier la date et le mode de paiement de chaque échéance
                                                </small>
                                              </div>
                                              <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1.5px solid #cbd5e1', borderRadius: '8px', background: '#ffffff' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
                                                  <thead>
                                                    <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1', color: '#475569', textAlign: 'left' }}>
                                                      <th style={{ padding: '8px 12px', width: '90px', whiteSpace: 'nowrap' }}>Échéance</th>
                                                      <th style={{ padding: '8px 12px', minWidth: '150px' }}>Date d'échéance</th>
                                                      <th style={{ padding: '8px 12px', minWidth: '170px' }}>Mode de paiement</th>
                                                      <th style={{ padding: '8px 12px', textAlign: 'right', minWidth: '120px' }}>Montant</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {activeList.map((ech, idx) => (
                                                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td style={{ padding: '8px 12px', fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>
                                                          N° {idx + 1}
                                                        </td>
                                                        <td style={{ padding: '6px 12px' }}>
                                                          <input
                                                            type="date"
                                                            required
                                                            value={ech.dateEcheance || ''}
                                                            onChange={(e) => {
                                                              const updated = [...activeList];
                                                              updated[idx] = { ...updated[idx], dateEcheance: e.target.value };
                                                              setEcheancierFormData({ ...echeancierFormData, echeancesCustom: updated });
                                                            }}
                                                            style={{
                                                              padding: '5px 10px',
                                                              borderRadius: '6px',
                                                              border: '1.5px solid #cbd5e1',
                                                              fontSize: '0.86rem',
                                                              fontWeight: 600,
                                                              color: '#0f172a',
                                                              width: '100%',
                                                              minWidth: '140px',
                                                              maxWidth: '180px',
                                                              boxSizing: 'border-box',
                                                              height: '36px',
                                                              background: '#ffffff'
                                                            }}
                                                          />
                                                        </td>
                                                        <td style={{ padding: '6px 12px' }}>
                                                          <select
                                                            value={ech.modePaiement || 'especes'}
                                                            onChange={(e) => {
                                                              const updated = [...activeList];
                                                              updated[idx] = { ...updated[idx], modePaiement: e.target.value };
                                                              setEcheancierFormData({ ...echeancierFormData, echeancesCustom: updated });
                                                            }}
                                                            style={{
                                                              padding: '5px 10px',
                                                              borderRadius: '6px',
                                                              border: '1.5px solid #cbd5e1',
                                                              fontSize: '0.86rem',
                                                              fontWeight: 600,
                                                              color: '#0f172a',
                                                              width: '100%',
                                                              minWidth: '150px',
                                                              maxWidth: '190px',
                                                              boxSizing: 'border-box',
                                                              height: '36px',
                                                              background: '#ffffff'
                                                            }}
                                                          >
                                                            {PAYMENT_MODES.map(mode => (
                                                              <option key={mode.value} value={mode.value}>{mode.label}</option>
                                                            ))}
                                                          </select>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, color: '#0284c7', whiteSpace: 'nowrap' }}>
                                                          {formatCurrency(ech.montant)}
                                                        </td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </div>
                                            </div>
                                          );
                                        })()}

                                        {rep.montantCredit > 0 && (
                                          <small style={{display: 'block', marginTop: '10px', color: '#0369a1', fontStyle: 'italic', fontWeight: '700', fontSize: '0.84rem'}}>
                                            Mensualité estimée : {formatCurrency(rep.montantCredit / (Number(echeancierFormData.nombreMois) || 1))}
                                          </small>
                                        )}
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            </>
                          )}

                          {/* ── SECTION 5 : SYNTHÈSE DYNAMIQUE AVANT VALIDATION ── */}
                          <div style={{
                            background: isOverflow ? '#fef2f2' : '#f8fafc',
                            border: isOverflow ? '2px solid #fca5a5' : '1.5px solid #cbd5e1',
                            borderRadius: '14px', padding: '18px 20px', marginBottom: '22px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                              <CheckCircle size={18} color={isOverflow ? '#dc2626' : '#059669'} />
                              <span style={{ fontSize: '0.88rem', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                5. Synthèse Dynamique Avant Validation
                              </span>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '14px' }}>
                              <div style={{ background: '#ffffff', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0' }}>
                                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.02em', display: 'block' }}>Restant à configurer</span>
                                <strong style={{ fontSize: '1.02rem', color: '#0284c7', fontWeight: '800', display: 'block', marginTop: '3px' }}>{formatCurrency(syntheseFinanciere.totalRestantAConfigurer)}</strong>
                              </div>
                              <div style={{ background: '#ffffff', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0' }}>
                                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.02em', display: 'block' }}>+ Montant libéré</span>
                                <strong style={{ fontSize: '1.02rem', color: '#b45309', fontWeight: '800', display: 'block', marginTop: '3px' }}>+{formatCurrency(montantLiberableTotal)}</strong>
                              </div>
                              <div style={{ background: '#ffffff', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0' }}>
                                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.02em', display: 'block' }}>= Capacité disponible</span>
                                <strong style={{ fontSize: '1.02rem', color: '#059669', fontWeight: '800', display: 'block', marginTop: '3px' }}>{formatCurrency(capaciteMax)}</strong>
                              </div>
                              <div style={{ background: '#ffffff', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0' }}>
                                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.02em', display: 'block' }}>Montant programmé</span>
                                <strong style={{ fontSize: '1.02rem', color: '#2563eb', fontWeight: '800', display: 'block', marginTop: '3px' }}>{formatCurrency(currentInputVal)}</strong>
                              </div>
                              <div style={{ background: isOverflow ? '#fee2e2' : '#ecfdf5', padding: '10px 14px', borderRadius: '10px', border: isOverflow ? '1.5px solid #fca5a5' : '1.5px solid #a7f3d0' }}>
                                <span style={{ fontSize: '0.75rem', color: isOverflow ? '#991b1b' : '#047857', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.02em', display: 'block' }}>Solde après opération</span>
                                <strong style={{ fontSize: '1.08rem', color: isOverflow ? '#dc2626' : '#047857', fontWeight: '800', display: 'block', marginTop: '3px' }}>{formatCurrency(soldeApresOperation)}</strong>
                              </div>
                            </div>

                            {isOverflow && (
                              <div style={{ background: '#fee2e2', borderLeft: '4px solid #dc2626', padding: '12px 16px', borderRadius: '8px', color: '#991b1b', fontSize: '0.86rem', fontWeight: '700', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <AlertCircle size={18} color="#dc2626" />
                                Le montant programmé ({formatCurrency(currentInputVal)}) dépasse la capacité maximale disponible ({formatCurrency(capaciteMax)}).
                              </div>
                            )}

                            {/* Raison de l'opération (Affichée uniquement en mode reprogrammation, optionnelle) */}
                            {isReprogrammer && (
                              <div style={{ marginTop: '14px' }}>
                                <label style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.86rem', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                  <span>Raison de la reprogrammation / modification</span>
                                  <span style={{ color: '#64748b', fontWeight: '500', fontSize: '0.8rem' }}>(optionnelle)</span>
                                </label>
                                <input
                                  type="text"
                                  placeholder="Saisissez le motif de la modification (ex: Accord client, restructuration d'échéances)..."
                                  value={echeancierFormData.raisonReprogrammation || ''}
                                  onChange={(e) => setEcheancierFormData({ ...echeancierFormData, raisonReprogrammation: e.target.value })}
                                  style={{ width: '100%', height: '42px', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', fontWeight: 600, boxSizing: 'border-box' }}
                                />
                              </div>
                            )}
                          </div>

                          {/* ── SECTION 6 : ACTIONS EN BAS ── */}
                          <div className="dd-credit-config-actions" style={{
                            display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '14px',
                            marginTop: '22px', paddingTop: '18px', borderTop: '1.5px solid #f1f5f9'
                          }}>
                            <button
                              type="button"
                              className="dd-credit-config-cancel"
                              onClick={() => setShowConfigEcheancierModal(false)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                padding: '11px 22px', borderRadius: '10px', background: '#f1f5f9',
                                color: '#475569', border: '1.5px solid #cbd5e1', fontWeight: 700,
                                fontSize: '0.92rem', cursor: 'pointer'
                              }}
                            >
                              <XCircle size={18} />
                              <span>Annuler</span>
                            </button>

                            <button
                              type="submit"
                              className="dd-credit-config-save"
                              disabled={isOverflow}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                padding: '11px 26px', borderRadius: '10px',
                                background: isOverflow ? '#cbd5e1' : (isReprogrammer ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : 'linear-gradient(135deg, #059669 0%, #047857 100%)'),
                                color: '#ffffff', border: 'none', fontWeight: 700,
                                fontSize: '0.94rem', cursor: isOverflow ? 'not-allowed' : 'pointer',
                                boxShadow: isOverflow ? 'none' : (isReprogrammer ? '0 4px 14px rgba(37, 99, 235, 0.35)' : '0 4px 14px rgba(5, 150, 105, 0.35)')
                              }}
                            >
                              {isReprogrammer ? <RotateCcw size={20} /> : <CheckCircle size={20} />}
                              <span>{isReprogrammer ? 'Confirmer la reprogrammation' : 'Programmer les échéances'}</span>
                            </button>
                          </div>
                        </form>
                      </div>
                    );
                  })()}

                  {dossier.typePaiement === 'comptant' && (
                    <div className="finance-credit-note">
                      Ce dossier est en paiement comptant. La programmation d’un échéancier permet d’échelonner le solde restant en mensualités.
                    </div>
                  )}

                  {/* MONITORING FINANCIER V3 (IMAGE INSPIRED) */}
                  <div className="monitoring-v3-container">
                 

                    <div className="monitoring-v3-top-grid">
                      <div className="m-v3-card">
                        <span className="label">Aperçu du dossier</span>
                        <strong className="value">{formatCurrency(montantTotalPaiement)}</strong>
                        <div className="icon-badge">📅</div>
                      </div>
                      <div id="section-montant-steg" className="m-v3-card steg-payment-card">
                        {/* ── En-tête : montant + badge statut ── */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                          <span className="label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
                            Montant STEG (crédit)
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <strong className="value">{formatCurrency(dossier.montantSteg)}</strong>
                            <span className={`status-pill ${isStegPayeEffectif ? 'paye' : (Number(dossier.montantSteg) === 0 && dossier.typePaiement === 'comptant' ? 'comptant' : 'attente')}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              {isStegPayeEffectif ? <><Check size={12} strokeWidth={3} /> PAYÉ</> : (Number(dossier.montantSteg) === 0 && dossier.typePaiement === 'comptant' ? 'COMPTANT' : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 22h14"></path><path d="M5 2h14"></path><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"></path><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"></path></svg> EN ATTENTE</>)}
                            </span>
                            {isStegPayeEffectif && dossier.datePaiementSteg && (
                              <span className="sub-label" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                le {formatDate(dossier.datePaiementSteg)}
                              </span>
                            )}
                            {!isStegPayeEffectif && hasPermission('dossiers', 'update') && !(Number(dossier.montantSteg) === 0 && dossier.typePaiement === 'comptant') && (
                              <button
                                className="btn-action-mini"
                                onClick={() => setShowStegPaymentModal(prev => !prev)}
                                title={showStegPaymentModal ? 'Annuler' : 'Saisir le paiement STEG'}
                                style={{ background: showStegPaymentModal ? '#fee2e2' : undefined, color: showStegPaymentModal ? '#dc2626' : undefined }}
                              >
                                {showStegPaymentModal
                                  ? <><span style={{ fontSize: '1rem' }}>✕</span> Annuler</>
                                  : <><Check size={14} style={{ marginRight: '4px' }} />Payer STEG</>
                                }
                              </button>
                            )}
                            {isStegPayeEffectif && stegPaiementActif && hasPermission('dossiers', 'update') && (
                              <button
                                className="btn-action-mini"
                                onClick={() => handleAnnulerPaiement(stegPaiementActif)}
                                title="Annuler le paiement STEG avec traçabilité"
                                style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #ffedd5' }}
                              >
                                 Annuler STEG
                              </button>
                            )}
                          </div>
                        </div>

                        {/* ── Workflow Payer STEG (Formulaire ou Confirmation) ── */}
                        {!isStegPayeEffectif && showStegPaymentModal && (
                          stegPaymentStep === 'form' ? (
                            <form
                              onSubmit={handleProceedToStegConfirmation}
                              style={{
                                marginTop: '16px',
                                padding: '16px',
                                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                                border: '1.5px solid #bae6fd',
                                borderRadius: '10px',
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '12px',
                                alignItems: 'end'
                              }}
                            >
                              {/* Titre */}
                              <div style={{ gridColumn: '1 / -1', marginBottom: '4px' }}>
                                <p style={{ margin: 0, fontWeight: 700, color: '#1e3a5f', fontSize: '0.92rem' }}>
                                  ⚡ Saisie du paiement STEG — {formatCurrency(dossier.montantSteg)}
                                </p>
                                <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#64748b' }}>
                                  Remplissez les informations ci-dessous. Une étape de confirmation vous sera proposée avant enregistrement.
                                </p>
                              </div>

                              {/* Date du paiement */}
                              <div>
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e3a5f', marginBottom: '4px' }}>
                                  📅 Date du paiement <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <input
                                  type="date"
                                  required
                                  value={stegPaymentForm.datePaiement}
                                  max={new Date().toISOString().split('T')[0]}
                                  onChange={(e) => setStegPaymentForm({ ...stegPaymentForm, datePaiement: e.target.value })}
                                  style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1.5px solid #93c5fd', fontSize: '0.9rem', boxSizing: 'border-box', background: '#fff' }}
                                />
                                <small style={{ color: '#64748b', fontSize: '0.74rem' }}>Date réelle du règlement</small>
                              </div>

                              {/* Mode de paiement */}
                              <div>
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e3a5f', marginBottom: '4px' }}>
                                  💳 Mode de paiement
                                </label>
                                <select
                                  value={stegPaymentForm.modePaiement}
                                  onChange={(e) => setStegPaymentForm({ ...stegPaymentForm, modePaiement: e.target.value })}
                                  style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1.5px solid #93c5fd', fontSize: '0.9rem', boxSizing: 'border-box', background: '#fff' }}
                                >
                                  {PAYMENT_MODES.map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                  ))}
                                </select>
                              </div>

                              {/* Référence */}
                              <div>
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e3a5f', marginBottom: '4px' }}>
                                  🔖 Référence / N° virement
                                </label>
                                <input
                                  type="text"
                                  placeholder={`Règlement STEG - ${dossier?.referenceSteg || ''}`}
                                  value={stegPaymentForm.reference}
                                  onChange={(e) => setStegPaymentForm({ ...stegPaymentForm, reference: e.target.value })}
                                  style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1.5px solid #93c5fd', fontSize: '0.9rem', boxSizing: 'border-box', background: '#fff' }}
                                />
                              </div>

                              {/* Notes */}
                              <div>
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e3a5f', marginBottom: '4px' }}>
                                  📝 Notes
                                </label>
                                <input
                                  type="text"
                                  placeholder="Observations..."
                                  value={stegPaymentForm.notes}
                                  onChange={(e) => setStegPaymentForm({ ...stegPaymentForm, notes: e.target.value })}
                                  style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1.5px solid #93c5fd', fontSize: '0.9rem', boxSizing: 'border-box', background: '#fff' }}
                                />
                              </div>

                              {/* Récap + boutons */}
                              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', borderTop: '1px solid #bae6fd', paddingTop: '12px', marginTop: '4px' }}>
                                <div style={{ fontSize: '0.82rem', color: '#0c4a6e' }}>
                                  <strong>Récap :</strong>&nbsp;
                                  {formatCurrency(dossier.montantSteg)} &nbsp;·&nbsp;
                                  {stegPaymentForm.datePaiement
                                    ? new Date(stegPaymentForm.datePaiement).toLocaleDateString('fr-FR')
                                    : <span style={{ color: '#dc2626' }}>date manquante</span>
                                  }
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button
                                    type="button"
                                    onClick={() => setShowStegPaymentModal(false)}
                                    disabled={stegPaymentSubmitting}
                                    style={{ padding: '8px 18px', borderRadius: '7px', border: '1.5px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', color: '#475569' }}
                                  >
                                    Annuler
                                  </button>
                                  <button
                                    type="submit"
                                    disabled={!stegPaymentForm.datePaiement}
                                    style={{
                                      padding: '8px 20px', borderRadius: '7px', border: 'none',
                                      background: !stegPaymentForm.datePaiement
                                        ? '#94a3b8'
                                        : 'linear-gradient(135deg, #1e3a5f, #2d5a8e)',
                                      color: '#fff',
                                      cursor: !stegPaymentForm.datePaiement ? 'not-allowed' : 'pointer',
                                      fontWeight: 700, fontSize: '0.88rem',
                                      display: 'flex', alignItems: 'center', gap: '6px'
                                    }}
                                  >
                                    Continuer <ArrowRight size={16} />
                                  </button>
                                </div>
                              </div>
                            </form>
                          ) : (
                            /* ── CARD VÉRIFICATION & CONFIRMATION (Étape 2) ── */
                            <div
                              style={{
                                marginTop: '16px',
                                padding: '20px',
                                background: '#ffffff',
                                border: '1.5px solid #93c5fd',
                                borderRadius: '12px',
                                boxShadow: '0 4px 14px rgba(30, 58, 95, 0.08)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px'
                              }}
                            >
                              {/* En-tête */}
                              <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                <div style={{ background: '#eff6ff', color: '#1e3a5f', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <ShieldCheck size={22} />
                                </div>
                                <div>
                                  <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e3a5f' }}>
                                    Vérification du paiement STEG
                                  </h4>
                                  <p style={{ margin: '3px 0 0', fontSize: '0.81rem', color: '#64748b' }}>
                                    Veuillez vérifier les informations avant l'enregistrement définitif du paiement.
                                  </p>
                                </div>
                              </div>

                              {/* Montant mis en évidence avec badge */}
                              <div
                                style={{
                                  background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                                  border: '1px solid #bae6fd',
                                  borderRadius: '10px',
                                  padding: '14px 18px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  flexWrap: 'wrap',
                                  gap: '10px'
                                }}
                              >
                                <div>
                                  <span style={{ display: 'block', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, color: '#0369a1', marginBottom: '2px' }}>
                                    Montant à enregistrer
                                  </span>
                                  <strong style={{ fontSize: '1.45rem', fontWeight: 800, color: '#1e3a5f' }}>
                                    {formatCurrency(dossier.montantSteg)}
                                  </strong>
                                </div>
                                <span
                                  style={{
                                    background: '#1e3a5f',
                                    color: '#ffffff',
                                    fontSize: '0.76rem',
                                    fontWeight: 700,
                                    padding: '5px 12px',
                                    borderRadius: '20px',
                                    letterSpacing: '0.03em'
                                  }}
                                >
                                  Paiement STEG
                                </span>
                              </div>

                              {/* Grid d'informations */}
                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                  gap: '12px',
                                  background: '#f8fafc',
                                  padding: '14px',
                                  borderRadius: '8px',
                                  border: '1px solid #f1f5f9'
                                }}
                              >
                                <div>
                                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Client</span>
                                  <strong style={{ fontSize: '0.88rem', color: '#1e293b' }}>
                                    {client?.prenom || ''} {client?.nom || ''}
                                  </strong>
                                </div>

                                <div>
                                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Référence STEG</span>
                                  <strong style={{ fontSize: '0.88rem', color: '#1e293b' }}>
                                    {dossier?.referenceSteg || 'N/A'}
                                  </strong>
                                </div>

                                <div>
                                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Date du paiement</span>
                                  <strong style={{ fontSize: '0.88rem', color: '#1e293b' }}>
                                    {stegPaymentForm.datePaiement
                                      ? new Date(stegPaymentForm.datePaiement).toLocaleDateString('fr-FR')
                                      : 'N/A'
                                    }
                                  </strong>
                                </div>

                                <div>
                                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Mode de paiement</span>
                                  <strong style={{ fontSize: '0.88rem', color: '#1e293b' }}>
                                    {getPaymentModeLabel(stegPaymentForm.modePaiement)}
                                  </strong>
                                </div>

                                {stegPaymentForm.reference && (
                                  <div style={{ gridColumn: '1 / -1' }}>
                                    <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Référence du paiement</span>
                                    <span style={{ fontSize: '0.86rem', color: '#334155', fontWeight: 600 }}>
                                      {stegPaymentForm.reference}
                                    </span>
                                  </div>
                                )}

                                {stegPaymentForm.notes && (
                                  <div style={{ gridColumn: '1 / -1' }}>
                                    <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Notes / Observations</span>
                                    <span style={{ fontSize: '0.84rem', color: '#475569', fontStyle: 'italic' }}>
                                      {stegPaymentForm.notes}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Avertissement informatif */}
                              <div style={{ fontSize: '0.8rem', color: '#475569', background: '#f1f5f9', padding: '10px 12px', borderRadius: '6px', borderLeft: '3px solid #3b82f6', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Info size={16} style={{ color: '#2563eb', flexShrink: 0 }} />
                                <span>Après confirmation, ce paiement sera enregistré dans l'historique financier du dossier.</span>
                              </div>

                              {/* Boutons d'action Modifier vs Confirmer */}
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '8px' }}>
                                <button
                                  type="button"
                                  onClick={() => setStegPaymentStep('form')}
                                  disabled={stegPaymentSubmitting}
                                  style={{
                                    padding: '9px 18px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #cbd5e1',
                                    background: '#ffffff',
                                    color: '#334155',
                                    fontWeight: 600,
                                    fontSize: '0.88rem',
                                    cursor: stegPaymentSubmitting ? 'not-allowed' : 'pointer',
                                    opacity: stegPaymentSubmitting ? 0.6 : 1
                                  }}
                                >
                                  Modifier
                                </button>

                                <button
                                  type="button"
                                  onClick={handleConfirmPayerSteg}
                                  disabled={stegPaymentSubmitting}
                                  style={{
                                    padding: '9px 22px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: stegPaymentSubmitting ? '#94a3b8' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    color: '#ffffff',
                                    fontWeight: 700,
                                    fontSize: '0.88rem',
                                    cursor: stegPaymentSubmitting ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    boxShadow: stegPaymentSubmitting ? 'none' : '0 2px 8px rgba(16, 185, 129, 0.25)'
                                  }}
                                >
                                  {stegPaymentSubmitting ? (
                                    '⏳ Enregistrement...'
                                  ) : (
                                    <><Check size={16} /> Confirmer le paiement</>
                                  )}
                                </button>
                              </div>
                            </div>
                          )
                        )}
                      </div>

                      <div className="m-v3-card">
                        <span className="label">Montant total crédit (client)</span>
                        <strong className="value">{formatCurrency(montantTotalCredit)}</strong>
                      </div>
                      <div className="m-v3-card">
                        <span className="label">Mensualité</span>
                        <strong className="value">{formatCurrency(montantMensuelCredit)}</strong>
                      </div>
                      <div className="m-v3-card" style={{ borderLeft: '3.5px solid #2563eb' }}>
                        <span className="label">Montant total configuré</span>
                        <strong className="value" style={{ color: '#2563eb' }}>{formatCurrency(syntheseFinanciere.totalConfigure)}</strong>
                        <div className="icon-badge">📋</div>
                      </div>
                      <div className="m-v3-card" style={{ borderLeft: '3.5px solid #059669' }}>
                        <span className="label">Montant restant à configurer</span>
                        <strong className="value" style={{ color: '#059669' }}>{formatCurrency(syntheseFinanciere.totalRestantAConfigurer)}</strong>
                        <div className="icon-badge">🔓</div>
                      </div>
                    </div>

                    <div className="monitoring-v3-main-grid">
                      {/* Section Paiements */}
                      <div className="m-v3-panel">
                        <h3>Paiements</h3>
                        <div className="m-v3-payments-content">
                          <div className="chart-box">
                            <div className="donut-wrapper">
                              <svg width="120" height="120" viewBox="0 0 120 120">
                                <circle cx="60" cy="60" r="50" fill="transparent" stroke="#f1f5f9" strokeWidth="12" />
                                <circle 
                                  cx="60" 
                                  cy="60" 
                                  r="50" 
                                  fill="transparent" 
                                  stroke="#10b981" 
                                  strokeWidth="12" 
                                  strokeDasharray={`${((montantTotalPaiement - montantTotalRestant) / (montantTotalPaiement || 1)) * 314.15} 314.15`}
                                  strokeLinecap="round"
                                  transform="rotate(-90 60 60)"
                                />
                              </svg>
                              <div className="donut-text">{montantTotalPaiement > 0 ? Math.round(((montantTotalPaiement - montantTotalRestant) / montantTotalPaiement) * 100) : 0}%</div>
                            </div>
                            <div className="chart-legend">
                              {formatCurrency(montantTotalPaiement - montantTotalRestant)}<br/>payés
                            </div>
                          </div>

                          <div className="m-v3-list">
                            <div className="m-v3-list-item">
                              <div className="item-label">
                                <span className="dot green"></span> Payé (crédit)
                              </div>
                              <span className="item-value">{formatCurrency(montantPayeCredit)}</span>
                            </div>
                            <div className="m-v3-list-item">
                              <div className="item-label">
                                <span className="dot green"></span> Payé (acompte)
                              </div>
                              <span className="item-value">{formatCurrency(montantComptantPaye)}</span>
                            </div>
                            {dossier.stegPaye && (
                              <div className="m-v3-list-item">
                                <div className="item-label">
                                  <span className="dot green"></span> Payé (STEG)
                                </div>
                                <span className="item-value">{formatCurrency(montantSteg)}</span>
                              </div>
                            )}
                            
                            <div className="m-v3-total-row">
                              <span className="total-label">Total payé</span>
                              <span className="total-value green">{formatCurrency(montantTotalPaiement - montantTotalRestant)}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Section Restants */}
                      <div className="m-v3-panel">
                        <h3>Restants à payer</h3>
                        <div className="m-v3-list">
                          <div className="m-v3-list-item">
                            <div className="item-label">
                              <span className="dot red-outline"></span> Restant (crédit)
                            </div>
                            <span className="item-value">{formatCurrency(montantRestantCredit)}</span>
                          </div>
                          <div className="m-v3-list-item">
                            <div className="item-label">
                              <span className="dot red-outline"></span> Restant (acompte)
                            </div>
                            <span className="item-value">{formatCurrency(montantComptantRestant)}</span>
                          </div>

                          <div className="m-v3-highlight-box">
                            <span className="box-label">Total restant</span>
                            <span className="box-value">{formatCurrency(montantTotalRestant)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="m-v3-footer">
                      <h3>Avancement global</h3>
                      <div className="m-v3-progress-container">
                        <div className="m-v3-progress-bar-bg">
                          <div 
                            className="m-v3-progress-bar-fill" 
                            style={{width: `${montantTotalPaiement > 0 ? Math.round(((montantTotalPaiement - montantTotalRestant) / montantTotalPaiement) * 100) : 0}%`}}
                          ></div>
                        </div>
                        <div className="m-v3-progress-info">
                          <span className="progress-text">
                            {formatCurrency(montantTotalPaiement - montantTotalRestant)} payés sur {formatCurrency(montantTotalPaiement)}
                          </span>
                          <span className="progress-percent">{montantTotalPaiement > 0 ? Math.round(((montantTotalPaiement - montantTotalRestant) / montantTotalPaiement) * 100) : 0}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>


                  {/* Alerte Échéances en Retard si existantes */}
                  {(() => {
                    const retardsSummary = dossierAutofinancement?.retardsSummary || { nombre: 0, montantTotal: 0, plusAncienneDate: null };
                    if (!retardsSummary || retardsSummary.nombre <= 0) return null;
                    return (
                      <div style={{
                        background: 'linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)',
                        border: '1.5px solid #fca5a5',
                        borderRadius: '10px',
                        padding: '16px 20px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.08)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                          <div style={{
                            background: '#ef4444',
                            color: 'white',
                            width: '42px',
                            height: '42px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold'
                          }}>
                            <AlertTriangle size={22} />
                          </div>
                          <div>
                            <h4 style={{ margin: 0, color: '#991b1b', fontSize: '1rem', fontWeight: '700' }}>
                              ⚠️ {retardsSummary.nombre} échéance{retardsSummary.nombre > 1 ? 's' : ''} en retard
                            </h4>
                            <p style={{ margin: '4px 0 0 0', color: '#b91c1c', fontSize: '0.88rem' }}>
                              Montant total en retard : <strong>{formatCurrency(retardsSummary.montantTotal)}</strong>
                              {retardsSummary.plusAncienneDate && (
                                <span> &nbsp;·&nbsp; Plus ancienne : <strong>{formatDate(retardsSummary.plusAncienneDate)}</strong></span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {hasEcheancier ? (
                    <div className="table-container finance-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Mois</th>
                            <th>Date Échéance</th>
                            <th>Mode de Paiement</th>
                            <th>Montant</th>
                            <th>Payé</th>
                            <th>Restant</th>
                            <th>Statut</th>
                            <th>Date Paiement</th>
                            <th>Validé par</th>
                            <th>Notes</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Ligne spéciale pour le paiement initial / comptant (Acompte) */}
                          {montantComptantInitial > 0 && (
                            <tr className={montantComptantRestant > 0 ? "row-highlight-pending" : ""}>
                              <td style={{fontWeight: '600'}}>INITIAL</td>
                              <td style={{fontStyle: 'italic'}}>Acompte / Comptant</td>
                              <td>{getPaymentModeLabel(dossierAutofinancement.modePaiementComptant || 'especes')}</td>
                              <td style={{fontWeight: '600'}}>{formatCurrency(montantComptantInitial)}</td>
                              <td>{formatCurrency(montantComptantPaye)}</td>
                              <td>{formatCurrency(montantComptantRestant)}</td>
                              <td>
                                <span className={`dd-echeance-status ${montantComptantRestant <= 0 ? 'dd-status-paye' : 'dd-status-attente'}`}>
                                  {montantComptantRestant <= 0 ? 'Payé' : 'À payer'}
                                </span>
                              </td>
                              <td>{montantComptantRestant <= 0 ? (dossierAutofinancement.datePaiementComptant ? formatDate(dossierAutofinancement.datePaiementComptant) : 'Validé') : '-'}</td>
                              <td>-</td>
                              <td>-</td>
                              <td>
                                {hasPermission('dossiers', 'update') && (
                                  <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
                                    {montantComptantRestant > 0 && (
                                      <button
                                        className="btn-view"
                                        onClick={handlePayerComptant}
                                        style={{fontSize: '0.85rem', padding: '6px 12px'}}
                                      >
                                        ✓ Payer acompte
                                      </button>
                                    )}
                                    {acomptePaiementActif ? (
                                      <button
                                        className="btn-delete"
                                        onClick={() => handleAnnulerPaiement(acomptePaiementActif)}
                                        style={{fontSize: '0.85rem', padding: '6px 12px'}}
                                        title="Annuler le paiement d'acompte effectué"
                                      >
                                         Annuler
                                      </button>
                                    ) : (
                                      <button
                                        className="btn-delete"
                                        onClick={handleAnnulerAcompteUnpaid}
                                        style={{fontSize: '0.85rem', padding: '6px 12px'}}
                                        title="Annuler l'acompte initial"
                                      >
                                         Annuler
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}

                          {echeancierRows.map((echeance, index) => {
                            const montantTotalEch = Number(echeance.montant) || 0;
                            let montantPayeEch = Number(echeance.montantPaye) || 0;
                            if (echeance.statut === 'paye' && montantPayeEch === 0 && montantTotalEch > 0) {
                              montantPayeEch = montantTotalEch;
                            }
                            const montantRestantEch = echeance.montantRestant !== undefined && echeance.montantRestant !== null
                              ? Number(echeance.montantRestant)
                              : Math.max(0, montantTotalEch - montantPayeEch);

                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const dateEchObj = echeance.dateEcheance ? new Date(echeance.dateEcheance) : null;
                            if (dateEchObj && !Number.isNaN(dateEchObj.getTime())) {
                              dateEchObj.setHours(0, 0, 0, 0);
                            }
                            const isEcheanceOverdue = Boolean(dateEchObj && dateEchObj < today && montantRestantEch > 0);

                            return (
                              <tr key={index}>
                                <td>{`${echeance.mois}/${echeance.annee}`}</td>
                                <td>{formatDate(echeance.dateEcheance)}</td>
                                <td style={{fontWeight: '600'}}>{formatCurrency(montantTotalEch)}</td>
                                <td style={{color: '#10b981', fontWeight: '600'}}>{formatCurrency(montantPayeEch)}</td>
                                <td style={{color: montantRestantEch > 0 ? '#ef4444' : '#64748b', fontWeight: '600'}}>{formatCurrency(montantRestantEch)}</td>
                                <td>
                                  <div style={{display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center'}}>
                                    {echeance.statut === 'paye' || montantRestantEch <= 0 ? (
                                      <span className="dd-echeance-status dd-status-paye">Payé</span>
                                    ) : echeance.statut === 'annule' ? (
                                      <span className="dd-echeance-status dd-status-attente">Annulé</span>
                                    ) : (
                                      <>
                                        {(echeance.statut === 'partiellement_paye' || montantPayeEch > 0) && (
                                          <span className="dd-echeance-status" style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #ffedd5' }}>
                                            Partiellement payé
                                          </span>
                                        )}
                                        {(echeance.statut === 'retard' || isEcheanceOverdue) && (
                                          <span className="dd-echeance-status dd-status-retard">
                                            En retard
                                          </span>
                                        )}
                                        {echeance.statut !== 'partiellement_paye' && montantPayeEch <= 0 && !isEcheanceOverdue && echeance.statut !== 'retard' && (
                                          <span className="dd-echeance-status dd-status-attente">
                                            En attente
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </td>
                                <td>{echeance.datePaiement ? formatDate(echeance.datePaiement) : '-'}</td>
                                <td>{formatUserDisplay(echeance.validePar)}</td>
                                <td>{Array.isArray(echeance.notes) ? echeance.notes.join(', ') : (echeance.notes || '-')}</td>
                                <td>
                                  {hasPermission('dossiers', 'update') && (
                                    <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
                                      {(echeance.statut !== 'paye' && montantRestantEch > 0) && (
                                        <button
                                          className="btn-view"
                                          onClick={() => handlePayerEcheance(index)}
                                          style={{fontSize: '0.85rem', padding: '6px 12px'}}
                                        >
                                          ✓ Payer échéance
                                        </button>
                                      )}
                                      <button
                                        className="btn-edit"
                                        onClick={() => handleOpenEditEcheanceModal(index)}
                                        style={{fontSize: '0.85rem', padding: '6px 12px'}}
                                      >
                                        Modifier
                                      </button>
                                      {echeance.statut !== 'annule' && (
                                        <button
                                          className="btn-delete"
                                          onClick={() => handleAnnulerEcheance(index)}
                                          style={{fontSize: '0.85rem', padding: '6px 12px'}}
                                          title={echeance.statut === 'paye' ? "Une échéance payée doit être annulée depuis l'historique" : "Annuler cette échéance"}
                                        >
                                           Annuler
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="no-data">Aucun échéancier programmé. Utilisez le bouton "Programmer les échéances" pour créer le plan.</p>
                  )}

                {/* FORMULAIRE INTÉGRÉ EN LIGNE : AJOUTER / MODIFIER UN PAIEMENT */}
                {showInlinePaymentForm && (() => {
                  if (isPaiementEcheanceMaintenanceActive) {
                    return (
                      <div
                        ref={paymentFormRef}
                        className="dd-payment-inline-card"
                        style={{
                          margin: '20px 0 24px 0',
                          padding: '32px 28px',
                          background: 'linear-gradient(135deg, #fffdf5 0%, #ffffff 100%)',
                          border: '2px solid #f59e0b',
                          borderRadius: '16px',
                          boxShadow: '0 12px 28px -4px rgba(245, 158, 11, 0.15)',
                          textAlign: 'center'
                        }}
                      >
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: '#fef3c7',
                          color: '#b45309',
                          border: '1px solid #fde047',
                          padding: '5px 14px',
                          borderRadius: '20px',
                          fontSize: '0.82rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          marginBottom: '16px'
                        }}>
                          <Wrench size={14} />
                          <span>Maintenance en cours</span>
                        </div>

                        <div style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '16px',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justify: 'center',
                          margin: '0 auto 16px auto',
                          boxShadow: '0 8px 20px rgba(245, 158, 11, 0.3)'
                        }}>
                          <Wrench size={32} />
                        </div>

                        <h3 style={{ margin: '0 0 10px 0', color: '#0f172a', fontSize: '1.35rem', fontWeight: 800 }}>
                          Fonctionnalité temporairement indisponible
                        </h3>

                        <p style={{ margin: '0 auto 24px auto', color: '#475569', fontSize: '0.94rem', lineHeight: '1.6', maxWidth: '580px' }}>
                          Cette fonctionnalité est actuellement en cours de maintenance et de vérification par l’équipe IT afin d’améliorer la fiabilité des calculs financiers. Elle sera de nouveau disponible prochainement.
                        </p>

                        <button
                          type="button"
                          onClick={handleClosePaymentForm}
                          style={{
                            padding: '10px 24px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            background: '#ffffff',
                            color: '#475569',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            cursor: 'pointer'
                          }}
                        >
                          Fermer
                        </button>
                      </div>
                    );
                  }

                  const syntheseFinanciere = computeSyntheseFinanciere(dossier);
                  const totalDossierAmtCard = syntheseFinanciere.montantTotalDossier;
                  const totalDejaPayeCard = syntheseFinanciere.totalDejaPaye;
                  const resteAPayerReelCard = syntheseFinanciere.resteAPayerReel;
                  const totalEcheancesConfiguredCard = syntheseFinanciere.totalConfigure;
                  const montantLibreDirectCard = syntheseFinanciere.montantLibre;

                  let oldPaymentAmount = 0;
                  if (editingPaiementId && Array.isArray(dossier?.paiements)) {
                    const pEdit = dossier.paiements.find(p => p._id === editingPaiementId);
                    if (pEdit) oldPaymentAmount = Math.max(0, Number(pEdit.montant) || 0);
                  }

                  const maxAutoriseCalculated = editingPaiementId
                    ? Math.max(0, montantLibreDirectCard + oldPaymentAmount)
                    : (echeanceAPayerIndex !== null
                      ? (dossier?.autofinancement?.echeancier?.[echeanceAPayerIndex]
                        ? Math.max(0, Number(dossier.autofinancement.echeancier[echeanceAPayerIndex].montantRestant ?? dossier.autofinancement.echeancier[echeanceAPayerIndex].montant) || 0)
                        : 0)
                      : (paiementFormData.categorie === 'comptant'
                        ? Math.min(montantComptantRestant, montantLibreDirectCard)
                        : (paiementFormData.categorie === 'credit'
                          ? Math.min(montantRestantCredit, montantLibreDirectCard)
                          : montantLibreDirectCard)));

                  const editingPaiementObj = editingPaiementId && Array.isArray(dossier?.paiements)
                    ? dossier.paiements.find(p => p._id === editingPaiementId)
                    : null;
                  const editingPaiementOrigineEcheance = !!(editingPaiementObj && (editingPaiementObj.sansImpactSolde || editingPaiementObj.origineEcheance));

                  const is100StegCard = dossier?.typePaiement === 'credit' &&
                    Number(dossier?.montantSteg) > 0 &&
                    (
                      Math.abs(Number(dossier?.montantSteg) - Number(dossier?.montantTotalPaiement)) <= 1 ||
                      Number(dossier?.montantSteg) >= Number(dossier?.montantTotalPaiement)
                    );

                  if (is100StegCard && !editingPaiementId) {
                    return (
                      <div
                        ref={paymentFormRef}
                        style={{
                          margin: '20px 0 24px 0',
                          padding: '26px 30px',
                          background: 'linear-gradient(135deg, #ffffff 0%, #fffdf5 100%)',
                          border: '2px solid #f59e0b',
                          borderRadius: '16px',
                          boxShadow: '0 12px 28px -4px rgba(245, 158, 11, 0.2), 0 4px 12px rgba(15, 23, 42, 0.05)',
                          color: '#0f172a',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {/* Décoration d'arrière-plan avec cercle lumineux */}
                        <div style={{
                          position: 'absolute', top: '-40px', right: '-40px', width: '180px', height: '180px',
                          background: 'radial-gradient(circle, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0) 70%)',
                          pointerEvents: 'none'
                        }} />

                        {/* En-tête de la carte */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '14px', marginBottom: '20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div style={{
                              width: '50px', height: '50px', borderRadius: '12px',
                              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                              color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '1.6rem', boxShadow: '0 8px 20px rgba(245, 158, 11, 0.35)', border: '2px solid #fef3c7'
                            }}>
                              ⚡
                            </div>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.3rem', fontWeight: 800, letterSpacing: '-0.01em' }}>
                                  Paiement 100% STEG
                                </h3>
                                <span style={{
                                  background: '#fef3c7', color: '#b45309', border: '1px solid #fde047',
                                  fontSize: '0.78rem', fontWeight: 700, padding: '3px 12px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.04em'
                                }}>
                                  Financement Intégral STEG
                                </span>
                              </div>
                              <p style={{ margin: '4px 0 0 0', color: '#475569', fontSize: '0.92rem', fontWeight: 500 }}>
                                Ce dossier est financé entièrement par le crédit STEG.
                              </p>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={handleClosePaymentForm}
                            style={{
                              background: '#ffffff', border: '1px solid #cbd5e1',
                              color: '#475569', padding: '6px 14px', borderRadius: '8px', fontSize: '0.84rem', cursor: 'pointer', fontWeight: 600,
                              transition: 'all 0.2s'
                            }}
                          >
                            Fermer ✕
                          </button>
                        </div>

                        {/* Synthèse financière STEG en carte claire */}
                        <div style={{
                          background: '#ffffff',
                          border: '1.5px solid #fde68a',
                          borderRadius: '12px',
                          padding: '18px 22px',
                          marginBottom: '20px',
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                          gap: '16px',
                          boxShadow: '0 2px 8px rgba(245, 158, 11, 0.08)'
                        }}>
                          <div>
                            <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Montant total dossier</span>
                            <strong style={{ fontSize: '1.1rem', color: '#0f172a', fontWeight: 700 }}>{formatCurrency(totalDossierAmtCard)}</strong>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.78rem', color: '#b45309', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Prise en charge STEG (100%)</span>
                            <strong style={{ fontSize: '1.1rem', color: '#d97706', fontWeight: 700 }}>{formatCurrency(dossier?.montantSteg || totalDossierAmtCard)}</strong>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Statut de prise en charge</span>
                            <span style={{
                              display: 'inline-block',
                              background: dossier?.stegPaye ? '#dcfce7' : '#fef3c7',
                              color: dossier?.stegPaye ? '#15803d' : '#b45309',
                              border: `1px solid ${dossier?.stegPaye ? '#86efac' : '#fde047'}`,
                              padding: '2px 10px', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700
                            }}>
                              {dossier?.stegPaye ? '✓ STEG Validé & Réglé' : '⏳ STEG En Attente'}
                            </span>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Reste client direct</span>
                            <strong style={{ fontSize: '1.1rem', color: '#2563eb', fontWeight: 700 }}>0,000 DT</strong>
                          </div>
                        </div>

                        {/* Note explicative claire */}
                        <div style={{
                          background: '#fffbe7',
                          borderLeft: '4px solid #f59e0b',
                          border: '1px solid #fde047',
                          borderRadius: '8px',
                          padding: '14px 18px',
                          marginBottom: '20px',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px'
                        }}>
                          <Info size={20} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                          <div style={{ fontSize: '0.88rem', color: '#78350f', lineHeight: '1.5' }}>
                            <strong style={{ color: '#92400e' }}>Information Importante :</strong> Les paiements directs manuels sont désactivés pour ce dossier car la totalité du montant est couverte par la convention STEG.
                            Pour enregistrer ou consulter le déblocage du règlement STEG, veuillez utiliser le module <strong style={{ color: '#b45309' }}>⚡ Payer STEG</strong> disponible dans le bloc financier STEG ci-dessus.
                          </div>
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                          <button
                            type="button"
                            onClick={() => {
                              handleClosePaymentForm();
                              const stegSection = document.getElementById('section-montant-steg') || document.querySelector('.steg-payment-card');
                              if (stegSection) {
                                stegSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }}
                            style={{
                              padding: '10px 22px', borderRadius: '8px', border: 'none',
                              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                              color: '#ffffff', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                              boxShadow: '0 4px 14px rgba(245, 158, 11, 0.4)', display: 'flex', alignItems: 'center', gap: '8px'
                            }}
                          >
                            <span>⚡ Aller au bloc STEG</span>
                          </button>
                        </div>
                      </div>
                    );
                  }


                  const isFullyAllocatedOrPaidCard = !is100StegCard &&
                    (syntheseFinanciere.montantLibre <= 0.009 && syntheseFinanciere.totalRestantAConfigurer <= 0.009);

                  if (isFullyAllocatedOrPaidCard && !editingPaiementId) {
                    const isDossierSoldeTotalementPaye = syntheseFinanciere.resteAPayerReel <= 0.009;
                    return (
                      <div
                        ref={paymentFormRef}
                        style={{
                          margin: '20px 0 24px 0',
                          padding: '24px 28px',
                          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                          border: `2px solid ${isDossierSoldeTotalementPaye ? '#10b981' : '#0284c7'}`,
                          borderRadius: '16px',
                          boxShadow: '0 8px 24px -4px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(2, 132, 199, 0.06)',
                          color: '#0f172a',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {/* En-tête */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '14px', marginBottom: '20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div style={{
                              width: '48px', height: '48px', borderRadius: '12px',
                              background: isDossierSoldeTotalementPaye
                                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                : 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                              color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '1.5rem', boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)', border: '2px solid #ffffff'
                            }}>
                              {isDossierSoldeTotalementPaye ? '🎉' : '🛡️'}
                            </div>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.25rem', fontWeight: 800, letterSpacing: '-0.01em' }}>
                                  {isDossierSoldeTotalementPaye ? 'Dossier Totalement Payé (100%)' : 'Formulaire Ajouter un Paiement Désactivé'}
                                </h3>
                                <span style={{
                                  background: isDossierSoldeTotalementPaye ? '#dcfce7' : '#e0f2fe',
                                  color: isDossierSoldeTotalementPaye ? '#15803d' : '#0369a1',
                                  border: `1px solid ${isDossierSoldeTotalementPaye ? '#86efac' : '#7dd3fc'}`,
                                  fontSize: '0.78rem', fontWeight: 700, padding: '3px 12px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.04em'
                                }}>
                                  {isDossierSoldeTotalementPaye ? 'Réglé à 100%' : '0 DT Disponible'}
                                </span>
                              </div>
                              <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.9rem', fontWeight: 500 }}>
                                {isDossierSoldeTotalementPaye
                                  ? 'L\'intégralité du montant de ce dossier a été réglée.'
                                  : 'Tous les montants restants sont déjà planifiés dans les échéances du dossier.'}
                              </p>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={handleClosePaymentForm}
                            style={{
                              background: '#ffffff', border: '1px solid #cbd5e1',
                              color: '#475569', padding: '6px 14px', borderRadius: '8px', fontSize: '0.84rem', cursor: 'pointer', fontWeight: 600,
                              transition: 'all 0.2s'
                            }}
                          >
                            Fermer ✕
                          </button>
                        </div>

                        {/* Synthèse financière professionnelle (5 blocs) */}
                        <div style={{
                          background: '#ffffff',
                          border: '1.5px solid #e2e8f0',
                          borderRadius: '12px',
                          padding: '18px 20px',
                          marginBottom: '20px',
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                          gap: '16px',
                          boxShadow: '0 2px 6px rgba(15, 23, 42, 0.03)'
                        }}>
                          <div>
                            <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Montant total dossier</span>
                            <strong style={{ fontSize: '1.05rem', color: '#0f172a', fontWeight: 700 }}>{formatCurrency(syntheseFinanciere.montantTotalDossier)}</strong>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.78rem', color: '#166534', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Total payé</span>
                            <strong style={{ fontSize: '1.05rem', color: '#15803d', fontWeight: 700 }}>{formatCurrency(syntheseFinanciere.totalDejaPaye)}</strong>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.78rem', color: '#b45309', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Montant total configuré</span>
                            <strong style={{ fontSize: '1.05rem', color: '#d97706', fontWeight: 700 }}>{formatCurrency(syntheseFinanciere.totalConfigure)}</strong>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Montant restant à configurer</span>
                            <strong style={{ fontSize: '1.05rem', color: syntheseFinanciere.totalRestantAConfigurer > 0 ? '#0284c7' : '#15803d', fontWeight: 800 }}>{formatCurrency(syntheseFinanciere.totalRestantAConfigurer || 0)}</strong>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Montant libre pour nouveau paiement</span>
                            <strong style={{ fontSize: '1.05rem', color: syntheseFinanciere.montantLibre > 0 ? '#0284c7' : '#15803d', fontWeight: 800 }}>{formatCurrency(syntheseFinanciere.montantLibre || 0)}</strong>
                          </div>
                        </div>

                        {/* Note d'information avec l'exact message requis */}
                        <div style={{
                          background: '#f0f9ff',
                          borderLeft: '4px solid #0284c7',
                          border: '1px solid #bae6fd',
                          borderRadius: '10px',
                          padding: '16px 20px',
                          marginBottom: '20px',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '14px'
                        }}>
                          <Info size={22} color="#0284c7" style={{ flexShrink: 0, marginTop: '2px' }} />
                          <div style={{ fontSize: '0.92rem', color: '#0369a1', lineHeight: '1.5', fontWeight: 600 }}>
                            La totalité du montant restant est déjà payée ou configurée dans les échéances. Aucun nouveau paiement ne peut être ajouté actuellement.
                          </div>
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                          <button
                            type="button"
                            onClick={() => {
                              handleClosePaymentForm();
                              const echSection = document.querySelector('.compact-schedule-table') || document.querySelector('.autofinancement-card');
                              if (echSection) {
                                echSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }}
                            style={{
                              padding: '10px 22px', borderRadius: '8px', border: 'none',
                              background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                              color: '#ffffff', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                              boxShadow: '0 4px 14px rgba(2, 132, 199, 0.3)', display: 'flex', alignItems: 'center', gap: '8px'
                            }}
                          >
                            <span>📅 Consulter l'Échéancier</span>
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      ref={paymentFormRef}
                      className={`dd-payment-inline-card ${editingPaiementId ? 'is-editing-mode' : ''}`}
                      style={{
                        margin: '20px 0 24px 0',
                        padding: '20px 24px',
                        background: editingPaiementId
                          ? 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
                          : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        border: editingPaiementId ? '2px solid #0284c7' : '1.5px solid #cbd5e1',
                        borderRadius: '14px',
                        boxShadow: editingPaiementId ? '0 8px 24px -4px rgba(2, 132, 199, 0.18)' : '0 4px 12px rgba(15, 23, 42, 0.04)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {/* En-tête du Formulaire Intégré */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '42px', height: '42px', borderRadius: '10px',
                            background: editingPaiementId ? '#0284c7' : (echeanceAPayerIndex !== null ? '#7c3aed' : '#10b981'),
                            color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '1.25rem', boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                          }}>
                            {editingPaiementId ? '✏️' : (echeanceAPayerIndex !== null ? '📅' : '💰')}
                          </div>
                          <div>
                            <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.15rem', fontWeight: 800 }}>
                              {editingPaiementId
                                ? 'Modifier le Paiement'
                                : (echeanceAPayerIndex !== null ? 'Payer l\'Échéance' : 'Saisir un Nouveau Paiement')}
                            </h3>
                            <p style={{ margin: '2px 0 0 0', color: '#64748b', fontSize: '0.83rem' }}>
                              {editingPaiementId
                                ? 'Mettez à jour les informations du règlement sélectionné'
                                : (echeanceAPayerIndex !== null ? 'Enregistrement du règlement pour l\'échéance sélectionnée' : 'Saisissez les détails financiers pour ajouter un versement')}
                            </p>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {editingPaiementId && (
                            <span style={{
                              background: '#0284c7', color: '#ffffff', fontSize: '0.78rem',
                              fontWeight: 700, padding: '4px 12px', borderRadius: '20px',
                              textTransform: 'uppercase', letterSpacing: '0.05em', boxShadow: '0 2px 6px rgba(2,132,199,0.3)'
                            }}>
                              Mode Modification
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={handleClosePaymentForm}
                            style={{
                              background: '#ffffff', border: '1px solid #cbd5e1', color: '#64748b',
                              padding: '5px 12px', borderRadius: '6px', fontSize: '0.82rem', cursor: 'pointer', fontWeight: 600
                            }}
                          >
                            Fermer ✕
                          </button>
                        </div>
                      </div>

                      {/* Carte des 6 indicateurs financiers */}
                      <div style={{
                        margin: '0 0 18px 0',
                        padding: '14px 18px',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '10px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                        gap: '12px'
                      }}>
                        <div>
                          <span style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 600, display: 'block' }}>Montant total dossier</span>
                          <strong style={{ fontSize: '0.95rem', color: '#1e293b', fontWeight: 700 }}>{formatCurrency(syntheseFinanciere.montantTotalDossier)}</strong>
                        </div>
                        <div>
                          <span style={{ fontSize: '0.74rem', color: '#059669', fontWeight: 600, display: 'block' }}>Total déjà payé</span>
                          <strong style={{ fontSize: '0.95rem', color: '#059669', fontWeight: 700 }}>{formatCurrency(syntheseFinanciere.totalDejaPaye)}</strong>
                        </div>
                        <div>
                          <span style={{ fontSize: '0.74rem', color: '#d97706', fontWeight: 600, display: 'block' }}>Montant total configuré</span>
                          <strong style={{ fontSize: '0.95rem', color: '#d97706', fontWeight: 700 }}>{formatCurrency(syntheseFinanciere.totalConfigure)}</strong>
                        </div>
                        <div>
                          <span style={{ fontSize: '0.74rem', color: '#2563eb', fontWeight: 600, display: 'block' }}>Montant restant à configurer</span>
                          <strong style={{ fontSize: '0.95rem', color: '#2563eb', fontWeight: 700 }}>{formatCurrency(syntheseFinanciere.totalRestantAConfigurer)}</strong>
                        </div>
                        <div>
                          <span style={{ fontSize: '0.74rem', color: '#dc2626', fontWeight: 600, display: 'block' }}>Reste à payer réel</span>
                          <strong style={{ fontSize: '0.95rem', color: '#dc2626', fontWeight: 700 }}>{formatCurrency(syntheseFinanciere.resteAPayerReel)}</strong>
                        </div>
                        <div style={{ background: '#ecfdf5', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #a7f3d0' }}>
                          <span style={{ fontSize: '0.74rem', color: '#047857', fontWeight: 700, display: 'block' }}>Montant libre pour nouveau paiement</span>
                          <strong style={{ fontSize: '1.02rem', color: '#047857', fontWeight: 800 }}>{formatCurrency(syntheseFinanciere.montantLibre)}</strong>
                        </div>
                      </div>

                      {editingPaiementOrigineEcheance && (
                        <div style={{
                          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px',
                          padding: '10px 14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px'
                        }}>
                          <Lock size={18} color="#2563eb" />
                          <span style={{ fontSize: '0.84rem', color: '#1e40af', fontWeight: 600 }}>
                            Paiement lié à une échéance : Le montant et la catégorie sont verrouillés pour préserver la synchronisation.
                          </span>
                        </div>
                      )}

                      {/* Formulaire de Saisie */}
                      <form onSubmit={editingPaiementId ? handleUpdatePaiement : handleAddPaiement} className="dd-payment-entry-form">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '14px', marginBottom: '16px' }}>
                          {/* Date */}
                          <div>
                            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', marginBottom: '4px' }}>
                              <Calendar size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Date du paiement *
                            </label>
                            <input
                              type="date"
                              value={paiementFormData.date}
                              onChange={(e) => setPaiementFormData({ ...paiementFormData, date: e.target.value })}
                              required
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                            />
                          </div>

                          {/* Montant */}
                          <div>
                            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', marginBottom: '4px' }}>
                              <DollarSign size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Montant (TND) *
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <input
                                type="text"
                                inputMode="decimal"
                                step="any"
                                min="0"
                                max={maxAutoriseCalculated > 0 ? maxAutoriseCalculated : undefined}
                                value={paiementFormData.montant}
                                onChange={(e) => setPaiementFormData({ ...paiementFormData, montant: normalizeDecimalInput(e.target.value) })}
                                disabled={editingPaiementOrigineEcheance}
                                placeholder="0.000"
                                required
                                style={{ width: '100%', padding: '8px 12px', borderRadius: '8px 0 0 8px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                              />
                              <span style={{ padding: '8px 12px', background: '#e2e8f0', border: '1px solid #cbd5e1', borderLeft: 'none', borderRadius: '0 8px 8px 0', fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>TND</span>
                            </div>
                            <span style={{ fontSize: '0.74rem', color: '#64748b', marginTop: '3px', display: 'block' }}>
                              Max autorisé: <strong>{formatCurrency(maxAutoriseCalculated)}</strong>
                            </span>
                          </div>

                          {/* Catégorie */}
                          <div>
                            {echeanceAPayerIndex === null ? (
                              <>
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', marginBottom: '4px' }}>
                                  <Tag size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Catégorie de paiement *
                                </label>
                                <select
                                  value={paiementFormData.categorie}
                                  onChange={(e) => setPaiementFormData({ ...paiementFormData, categorie: e.target.value })}
                                  disabled={editingPaiementOrigineEcheance}
                                  required
                                  style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                                >
                                  {montantComptantRestant > 0 && <option value="comptant">Partie comptant </option>}
                                  {montantRestantCredit > 0 && <option value="credit">Partie crédit</option>}
                                  {montantComptantRestant <= 0 && montantRestantCredit <= 0 && <option value="credit">Partie crédit</option>}
                                </select>
                              </>
                            ) : (
                              <div>
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', marginBottom: '4px' }}>
                                  <Tag size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Catégorie
                                </label>
                                <input type="text" value="Règlement Échéance" readOnly disabled style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f1f5f9', fontSize: '0.9rem' }} />
                              </div>
                            )}
                          </div>

                          {/* Méthode */}
                          <div>
                            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', marginBottom: '4px' }}>
                              <CreditCard size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Méthode de paiement *
                            </label>
                            <select
                              value={paiementFormData.methode}
                              onChange={(e) => setPaiementFormData({ ...paiementFormData, methode: e.target.value })}
                              required
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                            >
                              {PAYMENT_MODES.map(m => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                              ))}
                            </select>
                          </div>

                          {/* Statut du paiement */}
                          <div>
                            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', marginBottom: '4px' }}>
                              <ShieldCheck size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Statut du paiement *
                            </label>
                            <select
                              value={paiementFormData.statut || 'paye'}
                              onChange={(e) => setPaiementFormData({ ...paiementFormData, statut: e.target.value })}
                              required
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                            >
                              <option value="paye">Payé (Encaissé)</option>
                              <option value="en_attente">En attente</option>
                              <option value="retard">En retard</option>
                              <option value="annule">Annulé</option>
                            </select>
                          </div>

                          {/* Référence */}
                          <div>
                            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', marginBottom: '4px' }}>
                              <FileText size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Référence / N° Chèque
                            </label>
                            <input
                              type="text"
                              value={paiementFormData.reference}
                              onChange={(e) => setPaiementFormData({ ...paiementFormData, reference: e.target.value })}
                              placeholder="Ex: CHQ-987654"
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                            />
                          </div>

                          {/* Notes */}
                          <div>
                            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', marginBottom: '4px' }}>
                              <Info size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Notes / Remarques
                            </label>
                            <input
                              type="text"
                              value={paiementFormData.notes}
                              onChange={(e) => setPaiementFormData({ ...paiementFormData, notes: e.target.value })}
                              placeholder="Remarques éventuelles..."
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                            />
                          </div>
                        </div>

                        {/* Actions Formulaire */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                          <button
                            type="button"
                            onClick={handleClosePaymentForm}
                            style={{
                              padding: '9px 18px', borderRadius: '8px', border: '1px solid #cbd5e1',
                              background: '#ffffff', color: '#475569', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer'
                            }}
                          >
                            Annuler {editingPaiementId ? 'la modification' : ''}
                          </button>
                          <button
                            type="submit"
                            disabled={paiementSubmitting}
                            style={{
                              padding: '9px 22px', borderRadius: '8px', border: 'none',
                              background: editingPaiementId
                                ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)'
                                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: '#ffffff', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer',
                              boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                            }}
                          >
                            {paiementSubmitting
                              ? 'Enregistrement...'
                              : (editingPaiementId
                                ? '✓ Enregistrer les modifications'
                                : (echeanceAPayerIndex !== null ? '✓ Valider le règlement' : '✓ Enregistrer le paiement'))}
                          </button>
                        </div>
                      </form>
                    </div>
                  );
                })()}

                <div className="historique-section-header extensions-header">
                  <h3>Historique des Paiements</h3>
                  {hasPermission('dossiers', 'update') && (
                    <button
                      className="btn-add"
                      onClick={() => {
                        if (showInlinePaymentForm && !editingPaiementId) {
                          handleClosePaymentForm();
                        } else {
                          handleResetPaiementForm();
                          setShowInlinePaymentForm(true);
                          setTimeout(() => {
                            paymentFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }, 50);
                        }
                      }}
                    >
                      {showInlinePaymentForm && !editingPaiementId ? '✕ Masquer' : '+ Nouveau paiement'}
                    </button>
                  )}
                </div>

                {paiementsActifs.length > 0 ? (
                  <div className="table-container">
                    <table className="data-table compact-payments-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Montant</th>
                          <th>Catégorie</th>
                          <th>Méthode</th>
                          <th>Référence</th>
                          <th>Statut</th>
                          <th>Notes</th>
                          <th>Ajouté par</th>
                          <th>Validé par</th>
                          <th>Modifié par</th>
                          <th>Modifié le</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paiementsActifs.map((paiement, index) => (
                          <tr key={index}>
                            <td>{formatDate(paiement.datePaiement)}</td>
                            <td>{formatCurrency(paiement.montant)}</td>
                            <td>{paiement.categorie || getCategoriePaiementParDefaut(dossier)}</td>
                            <td>{getPaymentModeLabel(paiement.modePaiement)}</td>
                            <td>{paiement.reference || 'N/A'}</td>
                            <td>
                              <span className={`dd-paiement-status ${paiement.statut === 'paye' ? 'dd-status-paye' :
                                paiement.statut === 'retard' ? 'dd-status-retard' :
                                  paiement.statut === 'annule' ? 'dd-status-annule' :
                                    paiement.statut === 'supprime' ? 'dd-status-supprime' :
                                    'dd-status-attente'
                                }`}>
                                {paiement.statut === 'paye' ? 'Payé' :
                                  paiement.statut === 'en_attente' ? 'En attente' :
                                    paiement.statut === 'annule' ? 'Annulé' :
                                      paiement.statut === 'supprime' ? 'Supprimé' :
                                        paiement.statut}
                              </span>
                            </td>
                            <td>{paiement.notes || '-'}</td>
                            <td>{formatUserDisplay(paiement.ajoutePar)}</td>
                            <td>{formatUserDisplay(paiement.validePar)}</td>
                            <td>{formatUserDisplay(paiement.modifiePar)}</td>
                            <td>{formatDateTime(paiement.dateModification)}</td>
                            <td>
                              {((hasPermission('dossiers', 'update') && paiement.statut !== 'supprime') ||
                                (hasPermission('dossiers', 'delete') && paiement.statut !== 'supprime')) && (
                                <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
                                  {hasPermission('dossiers', 'update') && paiement.statut !== 'supprime' && (
                                    <button
                                      className="btn-edit"
                                      onClick={() => handleOpenEditPaiementModal(paiement)}
                                      style={{fontSize: '0.85rem', padding: '6px 12px'}}
                                    >
                                      Modifier
                                    </button>
                                  )}
                                  {hasPermission('dossiers', 'update') && paiement.statut !== 'supprime' && paiement.statut !== 'annule' && (
                                    <button
                                      className="btn-edit"
                                      onClick={() => handleAnnulerPaiement(paiement)}
                                      style={{fontSize: '0.85rem', padding: '6px 12px', background: '#fff7ed', color: '#c2410c', border: '1px solid #ffedd5'}}
                                      title="Annuler ce paiement avec traçabilité"
                                    >
                                       Annuler
                                    </button>
                                  )}
                                  {hasPermission('dossiers', 'delete') && paiement.statut !== 'supprime' && (
                                    <button
                                      className="btn-delete"
                                      onClick={() => handleOpenDeletePaiementModal(paiement)}
                                      style={{fontSize: '0.85rem', padding: '6px 12px'}}
                                    >
                                      Supprimer
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-data">Aucun paiement actif enregistré pour ce dossier</p>
                )}

                <div className="historique-section-header" style={{marginTop: '20px'}}>
                  <h3>Historique des Paiements Supprimés</h3>
                </div>

                {paiementsSupprimes.length > 0 ? (
                  <div className="table-container">
                    <table className="data-table compact-payments-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Montant</th>
                          <th>Catégorie</th>
                          <th>Méthode</th>
                          <th>Référence</th>
                          <th>Statut</th>
                          <th>Notes</th>
                          <th>Ajouté par</th>
                          <th>Supprimé par</th>
                          <th>Supprimé le</th>
                          <th>Modifié par</th>
                          <th>Modifié le</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paiementsSupprimes.map((paiement, index) => (
                          <tr key={index}>
                            <td>{formatDate(paiement.datePaiement)}</td>
                            <td>{formatCurrency(paiement.montant)}</td>
                            <td>{paiement.categorie || getCategoriePaiementParDefaut(dossier)}</td>
                            <td>{getPaymentModeLabel(paiement.modePaiement)}</td>
                            <td>{paiement.reference || 'N/A'}</td>
                            <td>
                              <span className="dd-paiement-status dd-status-supprime">Supprimé</span>
                            </td>
                            <td>{paiement.notes || '-'}</td>
                            <td>{formatUserDisplay(paiement.ajoutePar)}</td>
                            <td>{formatUserDisplay(paiement.supprimePar)}</td>
                            <td>{formatDateTime(paiement.dateSuppression)}</td>
                            <td>{formatUserDisplay(paiement.modifiePar)}</td>
                            <td>{formatDateTime(paiement.dateModification)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-data">Aucun paiement supprimé</p>
                )}
              </div>
            )}

            {/* ONGLET EXTENSIONS */}
            {activeTab === 'extensions' && (
              <div className="extensions-section">
                <div className="historique-section-header">
                  <h3>Extensions de Dossier</h3>
                  {dossier.etatDossier === 'Réceptionné' && hasPermission('dossiers', 'update') && (
                    <button className="btn-add" onClick={() => setShowAddExtensionModal(true)}>
                      + Nouvelle Extension
                    </button>
                  )}
                </div>

                {dossier.etatDossier !== 'Réceptionné' && (
                  <div className="info-box-modern" style={{background: '#fff7ed', color: '#9a3412', border: '1px solid #ffedd5', padding: '15px', borderRadius: '8px', marginBottom: '20px'}}>
                    <p>💡 Une extension peut être ajoutée uniquement si le dossier est <strong>Réceptionné</strong>.</p>
                  </div>
                )}

                <div className="table-container extensions-table-card">
                  <table className="data-table extensions-table">
                    <thead>
                      <tr>
                        <th>Date Création</th>
                        <th>Panneaux</th>
                        <th>Puissance (+kWc)</th>
                        <th>Coût (TND)</th>
                        <th>Statut</th>
                        <th>Audit</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dossier.extensions && dossier.extensions.filter(ext => ext.statut !== 'supprimee').length > 0 ? (
                        dossier.extensions.filter(ext => ext.statut !== 'supprimee').map((ext, idx) => (
                          <tr key={idx}>
                            <td>{formatDate(ext.dateCreation)}</td>
                            <td>{ext.nbrPanneaux}</td>
                            <td style={{fontWeight: '700', color: '#2563eb'}}>+{ext.puissanceAjoutee} kWc</td>
                            <td style={{fontWeight: '700'}}>{formatCurrency(ext.coutExtension)}</td>
                            <td>
                              <span className={`dd-echeance-status ${
                                ext.statut === 'validee' ? 'dd-status-paye' : 
                                ext.statut === 'rejetee' ? 'dd-status-retard' : 
                                'dd-status-attente'
                              }`}>
                                {ext.statut === 'en_attente' ? 'En attente' : ext.statut === 'validee' ? 'Validée' : 'Rejetée'}
                              </span>
                            </td>
                            <td style={{fontSize: '0.85rem'}}>
                                <div>Créé par: {formatUserDisplay(ext.ajoutePar)}</div>
                                {ext.validePar && <div>Validé par: {formatUserDisplay(ext.validePar)} le {formatDate(ext.dateValidation)}</div>}
                            </td>
                            <td>
                              <div className="extensions-actions">
                                {ext.statut === 'en_attente' && hasPermission('dossiers', 'update') && (
                                  <>
                                    <button className="btn-view" onClick={() => handleValidateExtension(ext._id, 'validee')}>Valider</button>
                                    <button className="btn-delete" onClick={() => handleValidateExtension(ext._id, 'rejetee')}>Rejeter</button>
                                  </>
                                )}
                                {hasPermission('dossiers', 'delete') && (
                                  <button className="btn-delete" style={{background: '#f1f5f9', color: '#ef4444'}} onClick={() => handleValidateExtension(ext._id, 'supprimee')}>
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan="7" className="no-data">Aucune extension enregistrée</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {dossier.historiqueEtat && dossier.historiqueEtat.filter(h => h.action.includes('Extension')).length > 0 && (
                   <div className="audit-section extensions-audit-section">
                     <h4>Audit des extensions</h4>
                     <div className="audit-timeline">
                        {dossier.historiqueEtat.filter(h => h.action.includes('Extension')).map((h, i) => (
                           <div key={i} className="audit-item">
                              <div className="audit-date">{formatDateTime(h.date)} - {formatUserDisplay(h.utilisateur)}</div>
                              <div className="audit-action">{h.action}</div>
                              <div className="audit-details">{h.details}</div>
                           </div>
                        ))}
                     </div>
                   </div>
                )}
              </div>
            )}

            {/* ONGLET HISTORIQUE */}
            {activeTab === 'historique' && (
              <div className="historique-paiement-section">
                <div className="historique-section-header">
                  <h3>Historique des Échéances (Validation/Modification)</h3>
                </div>

                {hasEcheancier ? (
                  <div className="table-container finance-table-container" style={{marginBottom: '20px'}}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Mois</th>
                          <th>Date échéance</th>
                          <th>Montant</th>
                          <th>Statut</th>
                          <th>Validé par</th>
                          <th>Date validation</th>
                          <th>Modifié par</th>
                          <th>Modifié le</th>
                        </tr>
                      </thead>
                      <tbody>
                        {echeancierRows.map((echeance, index) => {
                          const statutEcheance = getEcheanceStatut(echeance);
                          const classeStatutEcheance = `dd-histo-echeance-status-badge ${statutEcheance === 'paye'
                            ? 'dd-histo-echeance-status-paid'
                            : statutEcheance === 'retard'
                              ? 'dd-histo-echeance-status-late'
                              : 'dd-histo-echeance-status-pending'
                            }`;
                          const libelleStatutEcheance = statutEcheance === 'paye' ? 'Payé' : statutEcheance === 'retard' ? 'En retard' : 'En attente';
                          return (
                            <tr key={index}>
                              <td>{`${echeance.mois}/${echeance.annee}`}</td>
                              <td>{formatDate(echeance.dateEcheance)}</td>
                              <td>{formatCurrency(echeance.montant)}</td>
                              <td>
                                <span className={classeStatutEcheance}>{libelleStatutEcheance}</span>
                              </td>
                              <td>{formatUserDisplay(echeance.validePar)}</td>
                              <td>{formatDateTime(echeance.dateValidation || echeance.datePaiement)}</td>
                              <td>{formatUserDisplay(echeance.modifiePar)}</td>
                              <td>{formatDateTime(echeance.dateModification)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-data">Aucun échéancier disponible</p>
                )}

                <div className="historique-section-header">
                  <h3>Historique des Paiements (Validation/Modification)</h3>
                </div>

                {paiementsActifs.length > 0 ? (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Montant</th>
                          <th>Catégorie</th>
                          <th>Statut</th>
                          <th>Ajouté par</th>
                          <th>Validé par</th>
                          <th>Supprimé par</th>
                          <th>Date suppression</th>
                          <th>Date validation</th>
                          <th>Modifié par</th>
                          <th>Modifié le</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paiementsActifs.map((paiement, index) => {
                          const categoriePaiement = paiement.categorie || getCategoriePaiementParDefaut(dossier);
                          const classeCategoriePaiement = `dd-histo-paiement-category-badge ${categoriePaiement === 'comptant'
                            ? 'dd-histo-paiement-category-cash'
                            : categoriePaiement === 'steg'
                              ? 'dd-histo-paiement-category-steg'
                              : 'dd-histo-paiement-category-credit'}`;
                          const libelleCategoriePaiement = categoriePaiement === 'comptant'
                            ? 'Comptant'
                            : categoriePaiement === 'steg'
                              ? 'STEG'
                              : 'Crédit';

                          const statutPaiement = paiement.statut === 'retard'
                            ? 'retard'
                            : paiement.statut === 'annule'
                              ? 'annule'
                              : paiement.statut === 'supprime'
                                ? 'supprime'
                              : paiement.statut === 'paye'
                                ? 'paye'
                                : 'en_attente';

                          const classeStatutPaiement = `dd-histo-paiement-status-badge ${statutPaiement === 'paye'
                            ? 'dd-histo-paiement-status-paid'
                            : statutPaiement === 'retard'
                              ? 'dd-histo-paiement-status-late'
                              : statutPaiement === 'annule'
                                ? 'dd-histo-paiement-status-cancelled'
                                : statutPaiement === 'supprime'
                                  ? 'dd-histo-paiement-status-deleted'
                                : 'dd-histo-paiement-status-pending'
                            }`;

                          const libelleStatutPaiement = statutPaiement === 'paye'
                            ? 'Payé'
                            : statutPaiement === 'retard'
                              ? 'En retard'
                              : statutPaiement === 'annule'
                                ? 'Annulé'
                                : statutPaiement === 'supprime'
                                  ? 'Supprimé'
                                : 'En attente';

                          return (
                            <tr key={index}>
                              <td>{formatDate(paiement.datePaiement)}</td>
                              <td>{formatCurrency(paiement.montant)}</td>
                              <td>
                                <span className={classeCategoriePaiement}>{libelleCategoriePaiement}</span>
                              </td>
                              <td>
                                <span className={classeStatutPaiement}>{libelleStatutPaiement}</span>
                              </td>
                              <td>{formatUserDisplay(paiement.ajoutePar)}</td>
                              <td>{formatUserDisplay(paiement.validePar)}</td>
                              <td>{formatUserDisplay(paiement.supprimePar)}</td>
                              <td>{formatDateTime(paiement.dateSuppression)}</td>
                              <td>{formatDateTime(paiement.dateValidation)}</td>
                              <td>{formatUserDisplay(paiement.modifiePar)}</td>
                              <td>{formatDateTime(paiement.dateModification)}</td>
                              <td>
                                {hasPermission('dossiers', 'update') && paiement.statut !== 'supprime' && paiement.statut !== 'annule' && (
                                  <button
                                    className="btn-edit"
                                    onClick={() => handleAnnulerPaiement(paiement)}
                                    style={{fontSize: '0.85rem', padding: '6px 12px', background: '#fff7ed', color: '#c2410c', border: '1px solid #ffedd5'}}
                                    title="Annuler ce paiement avec traçabilité"
                                  >
                                     Annuler
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-data">Aucun paiement actif enregistré pour ce dossier</p>
                )}

                <div className="historique-section-header" style={{marginTop: '20px'}}>
                  <h3>Historique des Paiements Supprimés</h3>
                </div>

                {paiementsSupprimes.length > 0 ? (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Montant</th>
                          <th>Catégorie</th>
                          <th>Statut</th>
                          <th>Ajouté par</th>
                          <th>Supprimé par</th>
                          <th>Date suppression</th>
                          <th>Modifié par</th>
                          <th>Modifié le</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paiementsSupprimes.map((paiement, index) => {
                          const categoriePaiement = paiement.categorie || getCategoriePaiementParDefaut(dossier);
                          const classeCategoriePaiement = `dd-histo-paiement-category-badge ${categoriePaiement === 'comptant'
                            ? 'dd-histo-paiement-category-cash'
                            : categoriePaiement === 'steg'
                              ? 'dd-histo-paiement-category-steg'
                              : 'dd-histo-paiement-category-credit'}`;
                          const libelleCategoriePaiement = categoriePaiement === 'comptant'
                            ? 'Comptant'
                            : categoriePaiement === 'steg'
                              ? 'STEG'
                              : 'Crédit';

                          return (
                            <tr key={index}>
                              <td>{formatDate(paiement.datePaiement)}</td>
                              <td>{formatCurrency(paiement.montant)}</td>
                              <td>
                                <span className={classeCategoriePaiement}>{libelleCategoriePaiement}</span>
                              </td>
                              <td>
                                <span className="dd-histo-paiement-status-badge dd-histo-paiement-status-deleted">Supprimé</span>
                              </td>
                              <td>{formatUserDisplay(paiement.ajoutePar)}</td>
                              <td>{formatUserDisplay(paiement.supprimePar)}</td>
                              <td>{formatDateTime(paiement.dateSuppression)}</td>
                              <td>{formatUserDisplay(paiement.modifiePar)}</td>
                              <td>{formatDateTime(paiement.dateModification)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-data">Aucun paiement supprimé</p>
                )}

                {/* FULL HISTORIQUE SYSTEME (AUDIT LOGS) MOVED TO HISTORIQUE TAB */}
                <div className="m-v3-panel" style={{marginTop: '40px', background: 'transparent', boxShadow: 'none'}}>
                  <h3 style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px'}}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                    HISTORIQUE SYSTÈME (AUDIT LOGS) COMPLET
                  </h3>
                  {auditLogsLoading ? (
                    <div style={{textAlign: 'center', padding: '20px', color: '#64748b'}}>Chargement de l'historique complet...</div>
                  ) : auditLogs.length > 0 ? (
                    <div style={{overflowX: 'auto', background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
                      <table className="logs-table" style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem'}}>
                        <thead>
                          <tr style={{background: '#f1f5f9', borderBottom: '2px solid #cbd5e1'}}>
                            <th style={{padding: '12px', textAlign: 'left', color: '#475569'}}>Date / Heure</th>
                            <th style={{padding: '12px', textAlign: 'left', color: '#475569'}}>Action</th>
                            <th style={{padding: '12px', textAlign: 'left', color: '#475569'}}>Utilisateur</th>
                            <th style={{padding: '12px', textAlign: 'left', color: '#475569'}}>Commentaire</th>
                            <th style={{padding: '12px', textAlign: 'center'}}>Détails</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditLogs.map((log, index) => {
                            const cfg = getActionConfig(log.action);
                            const logKey = log._id || `${log.action || 'audit'}-${log.timestamp || index}`;
                            const isExpanded = expandedLog === logKey;
                            const auditRole = formatAuditRole(log.actor);
                            const hasDetails = log.changes?.length > 0 || log.deletedSnapshot;
                            return (
                              <React.Fragment key={logKey}>
                                <tr style={{borderBottom: '1px solid #e2e8f0', background: isExpanded ? '#f8fafc' : 'white'}}>
                                  <td style={{padding: '12px', color: '#64748b', whiteSpace: 'nowrap'}}>{formatDateTime(log.timestamp)}</td>
                                  <td style={{padding: '12px'}}>
                                    <span style={{background: cfg.bg, color: cfg.color, padding: '4px 8px', borderRadius: '12px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                                      {cfg.icon} {cfg.label}
                                    </span>
                                  </td>
                                  <td style={{padding: '12px'}}>
                                    <div>{formatAuditActorName(log.actor)}</div>
                                    <span style={{fontSize: '0.75rem', padding: '2px 6px', borderRadius: '10px', background: ROLE_COLORS[auditRole] || '#94a3b8', color: 'white'}}>{auditRole}</span>
                                  </td>
                                  <td style={{padding: '12px', color: '#475569'}}>{log.comment || '—'}</td>
                                  <td style={{padding: '12px', textAlign: 'center'}}>
                                    {hasDetails && (
                                      <button onClick={() => setExpandedLog(isExpanded ? null : logKey)} style={{background: 'none', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '0.75rem', color: '#475569', display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                                        {isExpanded ? '▲' : '▼'} {log.changes?.length > 0 ? `${log.changes.length} champs` : 'Snapshot'}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                                {isExpanded && hasDetails && (
                                  <tr style={{background: '#f8fafc', borderBottom: '2px solid #e2e8f0'}}>
                                    <td colSpan="5" style={{padding: '20px', paddingLeft: '40px', borderLeft: '4px solid #3b82f6'}}>
                                      {log.changes?.length > 0 && (
                                        <table style={{width: '100%', background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8rem', marginBottom: log.deletedSnapshot ? '15px' : '0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'}}>
                                          <thead>
                                            <tr style={{background: '#f1f5f9'}}>
                                              <th style={{padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', width: '30%'}}>Champ modifié</th>
                                              <th style={{padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', width: '35%'}}>Ancienne valeur</th>
                                              <th style={{padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', width: '35%'}}>Nouvelle valeur</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {log.changes.map((c, i) => (
                                              <tr key={i}>
                                                <td style={{padding: '10px', borderBottom: '1px solid #f1f5f9', fontWeight: '600', color: '#334155'}}><code>{c.field}</code></td>
                                                <td style={{padding: '10px', borderBottom: '1px solid #f1f5f9', color: '#ef4444', wordBreak: 'break-word', background: '#fef2f2', borderRight: '1px solid #fee2e2'}}>{formatAuditValue(c.oldValue)}</td>
                                                <td style={{padding: '10px', borderBottom: '1px solid #f1f5f9', color: '#10b981', wordBreak: 'break-word', background: '#ecfdf5'}}>{formatAuditValue(c.newValue)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                      {log.deletedSnapshot && (
                                        <div style={{background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px'}}>
                                          <h4 style={{fontSize: '0.85rem', color: '#475569', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px'}}>
                                            <Trash2 size={14} color="#ef4444" />
                                            Données supprimées (Snapshot)
                                          </h4>
                                          <pre style={{background: '#1e293b', color: '#e2e8f0', padding: '12px', borderRadius: '6px', overflowX: 'auto', fontSize: '0.8rem', margin: 0}}>
                                            {JSON.stringify(log.deletedSnapshot, null, 2)}
                                          </pre>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{background: 'white', padding: '30px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
                      <p style={{color: '#64748b', fontSize: '0.95rem'}}>Aucune activité système enregistrée pour ce dossier.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ONGLET ARCHIVE */}
            {activeTab === 'archive' && (
              <div className="archive-section">
                <div className="archive-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <div>
                    <h3 style={{ margin: 0, color: '#1e3a5f', fontSize: '1.25rem', fontWeight: 800 }}>📦 Archives du Dossier</h3>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.9rem' }}>Fichiers archivés conservés sans duplication d'espace sur la VM.</p>
                  </div>
                  {hasPermission('dossiers', 'update') && !showAddArchiveModal && (
                    <button
                      className="btn-add"
                      onClick={() => {
                        setEditingArchive(null);
                        setArchiveFile(null);
                        setArchiveFormData({ type: 'document', category: 'Autre', description: '' });
                        setDocTargetIsArchived(true);
                        setShowAddArchiveModal(true);
                      }}
                      style={{ background: '#475569', color: 'white', border: 'none', padding: '10px 18px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                    >
                      + Archiver un fichier direct
                    </button>
                  )}
                </div>

                {/* Formulaire Inline d'Ajout / Modification aux Archives */}
                {showAddArchiveModal && docTargetIsArchived && (
                  <div className="inline-add-document-form" style={{ backgroundColor: '#f8fafc', padding: '24px', borderRadius: '12px', marginBottom: '24px', border: '1.5px solid #cbd5e1', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#1e3a8a', fontSize: '1.1rem', fontWeight: '700' }}>
                      📦 {editingArchive ? 'Modifier l\'Archive' : 'Déposer directement dans les Archives'}
                    </h4>
                    <form onSubmit={handleAddArchive} style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                      <div className="form-row-modern">
                        <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>{editingArchive ? 'Remplacer le fichier (optionnel)' : 'Fichier *'}</label>
                        <input
                          type="file"
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                          onChange={(e) => setArchiveFile(e.target.files[0])}
                          required={!editingArchive}
                          style={{ padding: '10px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '8px', width: '100%' }}
                        />
                      </div>
                      <div className="form-row-modern" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>Type *</label>
                          <select
                            value={archiveFormData.type}
                            onChange={(e) => setArchiveFormData({...archiveFormData, type: e.target.value})}
                            style={{ padding: '10px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '8px', width: '100%' }}
                          >
                            <option value="image">Image</option>
                            <option value="pdf">PDF</option>
                            <option value="document">Document</option>
                            <option value="contrat">Contrat</option>
                            <option value="facture">Facture</option>
                            <option value="autre">Autre</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>Catégorie</label>
                          <select
                            value={archiveFormData.category}
                            onChange={(e) => setArchiveFormData({...archiveFormData, category: e.target.value})}
                            style={{ padding: '10px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '8px', width: '100%' }}
                          >
                            <option value="Contrat">Contrat</option>
                            <option value="CIN">CIN</option>
                            <option value="STEG">STEG</option>
                            <option value="Facture">Facture</option>
                            <option value="Devis">Devis</option>
                            <option value="Photo">Photo</option>
                            <option value="Autre">Autre</option>
                          </select>
                        </div>
                      </div>
                      <div className="form-row-modern">
                        <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>Description</label>
                        <textarea
                          value={archiveFormData.description}
                          onChange={(e) => setArchiveFormData({...archiveFormData, description: e.target.value})}
                          rows="2"
                          placeholder="Description de l'archive..."
                          style={{ padding: '10px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '8px', width: '100%' }}
                        />
                      </div>

                      {isUploadingDoc && (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px', fontWeight: 600, color: '#1e3a5f' }}>
                            <span>Upload et optimisation sur la VM...</span>
                            <span>{uploadProgress}%</span>
                          </div>
                          <div style={{ width: '100%', height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${uploadProgress}%`, height: '100%', background: '#3b82f6', transition: 'width 0.2s' }}></div>
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
                        <button type="button" className="btn-cancel-inline-main" onClick={() => { setShowAddArchiveModal(false); setEditingArchive(null); setArchiveFile(null); }}>
                          Annuler
                        </button>
                        <button type="submit" className="btn-save-inline-main" disabled={isUploadingDoc}>
                          {isUploadingDoc ? 'Transfert...' : (editingArchive ? 'Enregistrer' : 'Archiver')}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Liste des Archives (`isArchived === true`) */}
                {(() => {
                  const archivedDocs = (dossier.archives || []).filter(a => a.isArchived === true);
                  if (archivedDocs.length === 0) {
                    return (
                      <div className="no-data" style={{ padding: '40px', background: '#f8fafc', borderRadius: '12px', textAlign: 'center', border: '1px dashed #cbd5e1' }}>
                        <p style={{ fontSize: '1.1rem', fontWeight: 700, color: '#64748b' }}>📦 Aucune archive pour le moment</p>
                        <p style={{ fontSize: '0.88rem', color: '#94a3b8', marginTop: '6px' }}>
                          Vous pouvez archiver un document depuis l'onglet <strong>Documents</strong> via l'action <strong>Archiver</strong>. Le fichier ne sera pas dupliqué sur le disque.
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="archives-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                      {archivedDocs.map((doc, index) => {
                        const isImage = (doc.type === 'image' || /\.(png|jpe?g|webp|gif)$/i.test(doc.url || ''));
                        const fileSizeFormatted = formatFileSizeFormatted(doc.compressedSize || doc.originalSize || 0);

                        return (
                          <div key={doc._id || index} className="archive-card" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: isImage ? '#eff6ff' : '#f1f5f9', color: isImage ? '#2563eb' : '#475569', display: 'flex', alignItems: 'center', justify: 'center', fontWeight: 800, fontSize: '1.2rem' }}>
                                  {isImage ? '🖼️' : '📄'}
                                </div>
                                <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700 }}>
                                  {doc.category || doc.type || 'Archive'}
                                </span>
                              </div>
                              <h4 style={{ margin: '0 0 4px', fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', wordBreak: 'break-word' }}>
                                {doc.nom || doc.originalName || `Archive ${index + 1}`}
                              </h4>
                              <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: '#64748b' }}>
                                {doc.description || 'Fichier archivé'}
                              </p>
                              <div style={{ display: 'flex', gap: '8px', fontSize: '0.78rem', color: '#94a3b8', flexWrap: 'wrap' }}>
                                <span>📅 {formatDate(doc.dateAjout)}</span>
                                <span>💾 {fileSizeFormatted}</span>
                              </div>
                            </div>

                            <div style={{ display: 'flex', gap: '8px', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                              <button
                                type="button"
                                className="btn-view"
                                onClick={() => setPreviewDocModal(doc)}
                                style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#1e3a5f', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
                              >
                                👁️ Voir
                              </button>
                              {hasPermission('dossiers', 'update') && (
                                <button
                                  type="button"
                                  onClick={() => handleToggleArchiveStatus(doc._id)}
                                  style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
                                  title="Restaurer ce document dans les Documents actifs"
                                >
                                  ↩️ Restaurer
                                </button>
                              )}
                              {hasPermission('dossiers', 'delete') && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteArchive(doc._id, doc.nom)}
                                  style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
                                  title="Supprimer définitivement du disque VM"
                                >
                                  🗑️
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ONGLET DOCUMENTS */}
            {activeTab === 'documents' && (
              <div className="documents-section">
                <div className="documents-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <h3 style={{ margin: 0, color: '#1e3a5f', fontSize: '1.25rem', fontWeight: 800 }}>📂 Documents du Dossier</h3>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.9rem' }}>Gestion du stockage optimisé (Sharp WebP/JPEG, compression sans perte, validation des Magic Bytes).</p>
                  </div>
                  {hasPermission('dossiers', 'update') && !showAddArchiveModal && (
                    <button
                      className="documents-add-btn"
                      onClick={() => {
                        setEditingArchive(null);
                        setArchiveFile(null);
                        setArchiveFormData({ type: 'document', category: 'Autre', description: '' });
                        setDocTargetIsArchived(false);
                        setShowAddArchiveModal(true);
                      }}
                      style={{ background: '#1e3a5f', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(30, 58, 95, 0.2)' }}
                    >
                      + Ajouter un document / image
                    </button>
                  )}
                </div>

                {/* Zone d'Upload avec Drag & Drop & Progression */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDraggingFile(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsDraggingFile(false); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDraggingFile(false);
                    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                      setArchiveFile(e.dataTransfer.files[0]);
                      setEditingArchive(null);
                      setDocTargetIsArchived(false);
                      setShowAddArchiveModal(true);
                    }
                  }}
                  style={{
                    border: isDraggingFile ? '2px dashed #2563eb' : '2px dashed #cbd5e1',
                    background: isDraggingFile ? '#eff6ff' : '#f8fafc',
                    borderRadius: '12px',
                    padding: '24px',
                    textAlign: 'center',
                    marginBottom: '24px',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    if (!showAddArchiveModal) {
                      setEditingArchive(null);
                      setArchiveFile(null);
                      setDocTargetIsArchived(false);
                      setShowAddArchiveModal(true);
                    }
                  }}
                >
                  <div style={{ fontSize: '2rem', marginBottom: '8px' }}>☁️</div>
                  <h4 style={{ margin: '0 0 4px', color: '#1e293b', fontSize: '1rem', fontWeight: 700 }}>
                    {isDraggingFile ? 'Déposez votre fichier ici pour l\'ajouter !' : 'Glissez vos fichiers ici ou cliquez pour choisir'}
                  </h4>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem' }}>
                    Formats acceptés : PDF, JPG, PNG, WEBP — Compression automatique Sharp au-delà de 1 Mo.
                  </p>
                </div>

                {/* Formulaire Inline d'Ajout / Modification aux Documents */}
                {showAddArchiveModal && !docTargetIsArchived && (
                  <div className="inline-add-document-form" style={{ backgroundColor: '#ffffff', padding: '24px', borderRadius: '12px', marginBottom: '24px', border: '1.5px solid #3b82f6', boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.1)' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#1e3a8a', fontSize: '1.1rem', fontWeight: '700' }}>
                      📄 {editingArchive ? 'Modifier le Document' : 'Ajouter un nouveau Document (Stockage Optimisé)'}
                    </h4>
                    <form onSubmit={handleAddArchive} style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                      <div className="form-row-modern">
                        <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>
                          {editingArchive ? 'Remplacer le fichier (optionnel)' : 'Fichier à ajouter *'}
                        </label>
                        <input
                          type="file"
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                          onChange={(e) => setArchiveFile(e.target.files[0])}
                          required={!editingArchive}
                          style={{ padding: '10px', background: '#f8fafc', border: '1.5px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }}
                        />
                        {archiveFile && (
                          <div style={{ marginTop: '6px', fontSize: '0.82rem', color: '#059669', fontWeight: 600 }}>
                            ✓ {archiveFile.name} (Taille brute: {formatFileSizeFormatted(archiveFile.size)})
                          </div>
                        )}
                      </div>

                      <div className="form-row-modern" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>Type *</label>
                          <select
                            value={archiveFormData.type}
                            onChange={(e) => setArchiveFormData({...archiveFormData, type: e.target.value})}
                            style={{ padding: '10px', background: '#fff', border: '1.5px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }}
                          >
                            <option value="image">Image</option>
                            <option value="pdf">PDF</option>
                            <option value="document">Document</option>
                            <option value="contrat">Contrat</option>
                            <option value="facture">Facture</option>
                            <option value="autre">Autre</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>Catégorie Métier</label>
                          <select
                            value={archiveFormData.category}
                            onChange={(e) => setArchiveFormData({...archiveFormData, category: e.target.value})}
                            style={{ padding: '10px', background: '#fff', border: '1.5px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }}
                          >
                            <option value="Contrat">Contrat</option>
                            <option value="CIN">CIN</option>
                            <option value="STEG">STEG</option>
                            <option value="Facture">Facture</option>
                            <option value="Devis">Devis</option>
                            <option value="Photo">Photo</option>
                            <option value="Autre">Autre</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-row-modern">
                        <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>Description / Notes</label>
                        <textarea
                          value={archiveFormData.description}
                          onChange={(e) => setArchiveFormData({...archiveFormData, description: e.target.value})}
                          rows="2"
                          placeholder="Exemple: Facture STEG signée du client..."
                          style={{ padding: '10px', background: '#fff', border: '1.5px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }}
                        />
                      </div>

                      {isUploadingDoc && (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px', fontWeight: 600, color: '#1e3a5f' }}>
                            <span>Transfert et optimisation du document...</span>
                            <span>{uploadProgress}%</span>
                          </div>
                          <div style={{ width: '100%', height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${uploadProgress}%`, height: '100%', background: '#2563eb', transition: 'width 0.2s' }}></div>
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
                        <button type="button" className="btn-cancel-inline-main" onClick={() => { setShowAddArchiveModal(false); setEditingArchive(null); setArchiveFile(null); }}>
                          Annuler
                        </button>
                        <button type="submit" className="btn-save-inline-main" disabled={isUploadingDoc} style={{ background: '#2563eb', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', fontWeight: 700, cursor: 'pointer' }}>
                          {isUploadingDoc ? 'Traitement...' : (editingArchive ? 'Enregistrer' : 'Téléverser')}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Filtres et Recherche de Documents */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                    {['TOUS', 'Contrat', 'CIN', 'STEG', 'Facture', 'Devis', 'Photo', 'Autre'].map(cat => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setDocFilterCategory(cat)}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '20px',
                          border: docFilterCategory === cat ? '1px solid #1e3a5f' : '1px solid #cbd5e1',
                          background: docFilterCategory === cat ? '#1e3a5f' : '#ffffff',
                          color: docFilterCategory === cat ? '#ffffff' : '#475569',
                          fontWeight: docFilterCategory === cat ? '700' : '500',
                          fontSize: '0.82rem',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>

                  <input
                    type="text"
                    placeholder="🔍 Rechercher un document..."
                    value={docSearchTerm}
                    onChange={(e) => setDocSearchTerm(e.target.value)}
                    style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', minWidth: '220px' }}
                  />
                </div>

                {/* Résumé Synthétique */}
                <div className="documents-summary-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                  <div className="document-summary-card" style={{ background: '#ffffff', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>Nº BL Officiel</span>
                    <strong style={{ display: 'block', fontSize: '1rem', color: '#1e293b', marginTop: '2px' }}>{dossier.bl || 'N/A'}</strong>
                  </div>
                  <div className="document-summary-card" style={{ background: '#ffffff', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>Nº Facture Officielle</span>
                    <strong style={{ display: 'block', fontSize: '1rem', color: '#1e293b', marginTop: '2px' }}>{dossier.facture || 'N/A'}</strong>
                  </div>
                  <div className="document-summary-card" style={{ background: '#ffffff', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>Date Facturation</span>
                    <strong style={{ display: 'block', fontSize: '1rem', color: '#1e293b', marginTop: '2px' }}>{formatDate(dossier.dateFacture) || 'N/A'}</strong>
                  </div>
                </div>

                {/* Grille des Documents (`!isArchived`) + Documents Financiers */}
                {(() => {
                  const activeUserDocs = (dossier.archives || []).filter(a => a.isArchived !== true);
                  const allDocs = [...activeUserDocs];

                  documentsFinanciers.forEach(df => {
                    allDocs.push({
                      _id: df._id,
                      nom: `${df.type.toUpperCase()} - ${df.numero}`,
                      originalName: `${df.type.toUpperCase()}_${df.numero}.pdf`,
                      description: `Généré automatiquement le ${formatDate(df.dateCreation)}`,
                      type: 'pdf',
                      category: df.type === 'bon_livraison' ? 'BL' : 'Facture',
                      isFinancier: true,
                      dateAjout: df.dateCreation
                    });
                  });

                  // Filtrer par recherche et catégorie
                  const filteredDocs = allDocs.filter(doc => {
                    const matchesCategory = docFilterCategory === 'TOUS' || (doc.category || doc.type || 'Autre').toLowerCase() === docFilterCategory.toLowerCase();
                    const searchTermLower = docSearchTerm.toLowerCase();
                    const matchesSearch = !docSearchTerm || (doc.nom || '').toLowerCase().includes(searchTermLower) || (doc.originalName || '').toLowerCase().includes(searchTermLower) || (doc.description || '').toLowerCase().includes(searchTermLower);
                    return matchesCategory && matchesSearch;
                  });

                  if (filteredDocs.length === 0) {
                    return (
                      <div className="documents-empty-state" style={{ padding: '40px', background: '#ffffff', borderRadius: '12px', textAlign: 'center', border: '1px dashed #cbd5e1' }}>
                        <FileText size={44} color="#94a3b8" />
                        <h4 style={{ margin: '12px 0 4px', color: '#334155' }}>Aucun document ne correspond</h4>
                        <p style={{ margin: 0, color: '#64748b', fontSize: '0.88rem' }}>Déposez un nouveau fichier ou modifiez les filtres de recherche.</p>
                      </div>
                    );
                  }

                  return (
                    <div className="documents-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                      {filteredDocs.map((doc, index) => {
                        const isImage = !doc.isFinancier && (doc.type === 'image' || /\.(png|jpe?g|webp|gif)$/i.test(doc.url || ''));
                        const docIcon = doc.type === 'pdf' ? 'PDF' : doc.type === 'contrat' ? 'CTR' : doc.type === 'facture' ? 'FAC' : isImage ? 'IMG' : 'DOC';
                        const fileSizeFormatted = formatFileSizeFormatted(doc.compressedSize || doc.originalSize || 0);

                        return (
                          <div key={doc._id || index} className="document-card-modern" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: isImage ? '#eff6ff' : (doc.isFinancier ? '#ecfdf5' : '#f1f5f9'), color: isImage ? '#2563eb' : (doc.isFinancier ? '#059669' : '#475569'), display: 'flex', alignItems: 'center', justify: 'center', fontWeight: 800, fontSize: '0.9rem' }}>
                                  {docIcon}
                                </div>
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                  {doc.optimized && (
                                    <span style={{ background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700 }} title={`Économie de stockage : ${formatFileSizeFormatted(doc.compressionSavedBytes || 0)}`}>
                                      ⚡ Optimisé
                                    </span>
                                  )}
                                  <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700 }}>
                                    {doc.category || doc.type || 'Document'}
                                  </span>
                                </div>
                              </div>

                              <h4 style={{ margin: '0 0 4px', fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', wordBreak: 'break-word' }}>
                                {doc.nom || doc.originalName || `Document ${index + 1}`}
                              </h4>
                              <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: '#64748b' }}>
                                {doc.description || 'Aucune description'}
                              </p>
                              
                              <div style={{ display: 'flex', gap: '10px', fontSize: '0.78rem', color: '#94a3b8', flexWrap: 'wrap' }}>
                                <span>📅 {formatDate(doc.dateAjout)}</span>
                                {!doc.isFinancier && <span>💾 {fileSizeFormatted}</span>}
                              </div>
                            </div>

                            <div style={{ display: 'flex', gap: '6px', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                              <button
                                type="button"
                                className="doc-action view"
                                onClick={() => {
                                  if (doc.isFinancier) {
                                    window.open(`${API_URL}/documents-financiers/${doc._id}/pdf?token=${getToken()}`, '_blank');
                                  } else {
                                    setPreviewDocModal(doc);
                                  }
                                }}
                                style={{ flex: 1, padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#1e3a5f', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justify: 'center', gap: '4px' }}
                              >
                                👁️ Voir
                              </button>

                              {!doc.isFinancier && hasPermission('dossiers', 'update') && (
                                <button
                                  type="button"
                                  onClick={() => handleToggleArchiveStatus(doc._id)}
                                  style={{ flex: 1, padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justify: 'center', gap: '4px' }}
                                  title="Déplacer vers les Archives (sans dupliquer le fichier)"
                                >
                                  📦 Archiver
                                </button>
                              )}

                              {!doc.isFinancier && hasPermission('dossiers', 'delete') && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteArchive(doc._id, doc.nom)}
                                  style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
                                  title="Supprimer définitivement du disque VM"
                                >
                                  🗑️
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Modal de Prévisualisation de Document / Image */}
            {previewDocModal && (
              <div className="modal-overlay-small" onClick={() => setPreviewDocModal(null)} style={{ background: 'rgba(15, 23, 42, 0.75)', zIndex: 9999 }}>
                <div className="modal-content-small" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%', borderRadius: '16px', padding: '20px', background: '#ffffff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>{previewDocModal.nom || previewDocModal.originalName}</h3>
                      <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                        Catégorie : {previewDocModal.category || previewDocModal.type || 'Document'} | Taille : {formatFileSizeFormatted(previewDocModal.compressedSize || previewDocModal.originalSize)}
                      </p>
                    </div>
                    <button type="button" onClick={() => setPreviewDocModal(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', fontWeight: 700 }}>✕</button>
                  </div>

                  <div style={{ textAlign: 'center', maxHeight: '550px', overflowY: 'auto', background: '#f8fafc', borderRadius: '8px', padding: '12px' }}>
                    {(previewDocModal.type === 'image' || /\.(png|jpe?g|webp|gif)$/i.test(previewDocModal.url || '')) ? (
                      <img src={`${BASE_URL}${previewDocModal.url}`} alt={previewDocModal.nom} style={{ maxWidth: '100%', maxHeight: '500px', borderRadius: '8px', objectFit: 'contain' }} />
                    ) : (previewDocModal.type === 'pdf' || /\.pdf$/i.test(previewDocModal.url || '')) ? (
                      <iframe src={`${BASE_URL}${previewDocModal.url}`} title={previewDocModal.nom} style={{ width: '100%', height: '480px', border: 'none', borderRadius: '8px' }} />
                    ) : (
                      <div style={{ padding: '40px' }}>
                        <p style={{ fontSize: '1rem', color: '#475569' }}>Prévisualisation non disponible pour ce type de fichier.</p>
                        <a href={`${BASE_URL}${previewDocModal.url}`} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: '12px', padding: '10px 20px', background: '#2563eb', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: 700 }}>
                          📥 Télécharger / Ouvrir le fichier
                        </a>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
                    <a href={`${BASE_URL}${previewDocModal.url}`} target="_blank" rel="noreferrer" style={{ padding: '8px 16px', background: '#1e3a5f', color: 'white', borderRadius: '8px', textDecoration: 'none', fontSize: '0.88rem', fontWeight: 600 }}>
                      ↗️ Ouvrir dans un nouvel onglet
                    </a>
                    <button type="button" onClick={() => setPreviewDocModal(null)} style={{ padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer' }}>
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ONGLET RÉCLAMATIONS DOSSIER */}
            {activeTab === 'reclamations' && (
              <div className="reclamations-dossier-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <h3 style={{ margin: 0, color: '#1e3a5f', fontSize: '1.2rem', fontWeight: 800 }}>⚡ Réclamations Liées à ce Dossier</h3>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.88rem' }}>Suivi des réclamations concernant spécifiquement le dossier {dossier.referenceSteg || 'Pompage'}.</p>
                  </div>
                  {hasPermission('reclamations', 'create') && (
                    <button
                      type="button"
                      onClick={() => setShowAddReclamationModal(true)}
                      style={{ background: '#1e3a5f', color: '#ffffff', border: 'none', padding: '10px 18px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    >
                      + Créer une réclamation pour ce dossier
                    </button>
                  )}
                </div>

                {dossierReclamations.length === 0 ? (
                  <div style={{ padding: '40px', background: '#ffffff', borderRadius: '12px', textAlign: 'center', border: '1px dashed #cbd5e1' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📂</div>
                    <h4 style={{ margin: 0, color: '#334155' }}>Aucune réclamation liée à ce dossier</h4>
                    <p style={{ margin: '6px 0 16px', color: '#64748b', fontSize: '0.88rem' }}>Toutes les réclamations attribuées à ce dossier s'afficheront ici.</p>
                    {hasPermission('reclamations', 'create') && (
                      <button
                        type="button"
                        onClick={() => setShowAddReclamationModal(true)}
                        style={{ background: '#1e3a5f', color: '#ffffff', border: 'none', padding: '10px 18px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                      >
                        + Ajouter une réclamation
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                    {dossierReclamations.map((rec) => (
                      <div key={rec._id} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontWeight: 800, color: '#2563eb', fontSize: '0.85rem' }}>{rec.reference || 'REC-2026'}</span>
                            <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700, background: rec.statut === 'resolved' ? '#dcfce7' : '#eff6ff', color: rec.statut === 'resolved' ? '#166534' : '#1d4ed8' }}>
                              {rec.statut?.toUpperCase()}
                            </span>
                          </div>
                          <h4 style={{ margin: '0 0 6px', fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' }}>{rec.sujet}</h4>
                          <p style={{ margin: '0 0 10px', fontSize: '0.82rem', color: '#64748b', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {rec.message}
                          </p>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                          <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>📅 {new Date(rec.createdAt).toLocaleDateString('fr-FR')}</span>
                          <button
                            type="button"
                            onClick={() => setSelectedReclamationDetail(rec)}
                            style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#1e3a5f', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}
                          >
                            👁️ Détails
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* MODAL CRÉATION RÉCLAMATION DOSSIER (PRÉREMPLI CLIENT + DOSSIER) */}
        {showAddReclamationModal && (
          <ReclamationFormModal
            clientId={client._id}
            dossierId={dossier._id}
            onClose={() => setShowAddReclamationModal(false)}
            onSuccess={() => fetchDossierReclamations()}
          />
        )}

        {/* MODAL DÉTAILS RÉCLAMATION */}
        {selectedReclamationDetail && (
          <ReclamationDetailModal
            reclamation={selectedReclamationDetail}
            onClose={() => setSelectedReclamationDetail(null)}
            onRefresh={() => fetchDossierReclamations()}
            onNavigate={onNavigate}
            hasPermission={hasPermission}
          />
        )}

        {/* MODALS */}

        {/* Modal Modifier Produit */}
        {showEditProduitModal && (
          <div className="modal-overlay-small" onClick={() => setShowEditProduitModal(false)}>
            <div className="modal-content-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-small">
                <h3>Modifier le Produit</h3>
                <button type="button" className="dd-modal-close-btn" onClick={() => setShowEditProduitModal(false)}>×</button>
              </div>
              <form onSubmit={handleUpdateProduit} className="form-modal">
                <div className="form-row">
                  <label>Référence / Numéro</label>
                  <input
                    type="text"
                    value={produitFormData.referenceNumero}
                    readOnly
                    style={{backgroundColor: '#f3f4f6'}}
                  />
                </div>

                <div className="form-row">
                  <label>Quantité</label>
                  <input
                    type="number"
                    min="1"
                    value={produitFormData.quantite}
                    readOnly
                    style={{backgroundColor: '#f3f4f6'}}
                  />
                  <small style={{color: '#6b7280'}}>La quantité ne peut pas être modifiée après ajout pour préserver le stock. Supprimez et rajoutez le produit si besoin.</small>
                </div>

                <div className="form-row ">
                  <label>Palette (Optionnel)</label>
                  <select
                    value={produitFormData.paletteId || ''}
                    onChange={(e) => {
                      const pId = e.target.value;
                      setProduitFormData(prev => ({...prev, paletteId: pId, numeroSerie: ''}));
                      setNumerosSerieSelectionnes([]);
                      fetchNumerosSerieFromPalette(pId, produitFormData.quantite);
                    }}
                  >
                    <option value="">Sans palette</option>
                    {palettes.map(palette => {
                      const selectedNumbers = new Set(numerosSerieSelectionnes);
                      const nbDisponibles = palette.numerosSerie?.filter(ns => ns.statut === 'disponible' || selectedNumbers.has(ns.numero)).length || 0;
                      return (
                        <option key={palette._id} value={palette._id}>
                          {palette.numeroPalette} ({nbDisponibles} dispo)
                        </option>
                      );
                    })}
                    {produitFormData.paletteId && !palettes.some(palette => String(palette._id) === String(produitFormData.paletteId)) && (
                      <option value={produitFormData.paletteId}>{produitFormData.paletteId}</option>
                    )}
                  </select>
                </div>

                <div className="form-row" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                  <div>
                    <label>Prix Unitaire (TND)</label>
                    <input
                      type="number"
                      step="any"
                      value={produitFormData.prixUnitaire}
                      onChange={(e) => setProduitFormData({...produitFormData, prixUnitaire: e.target.value})}
                    />
                  </div>
                  <div>
                    <label>TVA (%)</label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={produitFormData.TVA}
                      onChange={(e) => setProduitFormData({...produitFormData, TVA: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-row" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                  <div>
                    <label>Taux Gain (%)</label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={produitFormData.tauxGain}
                      onChange={(e) => setProduitFormData({...produitFormData, tauxGain: e.target.value})}
                    />
                  </div>
                  <div>
                    <label style={{color: '#1e3a5f', fontWeight: 'bold'}}>Prix Total (Auto)</label>
                    <input
                      type="number"
                      value={produitFormData.prixTotal}
                      readOnly
                      style={{backgroundColor: '#f8fafc', fontWeight: 'bold', border: '1.5px solid #2d6a9f'}}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <label>Statut</label>
                  <select
                    value={produitFormData.statut}
                    onChange={(e) => setProduitFormData({...produitFormData, statut: e.target.value})}
                  >
                    <option value="commande">Commandé</option>
                    <option value="en_stock">En stock</option>
                    <option value="installe">Installé</option>
                    <option value="maintenance">En maintenance</option>
                  </select>
                </div>

                <div className="form-row">
                  <label>Date d'installation</label>
                  <input
                    type="date"
                    value={produitFormData.dateInstallation}
                    onChange={(e) => setProduitFormData({...produitFormData, dateInstallation: e.target.value})}
                  />
                </div>

                <div className="form-row">
                  <label>Numéro de Série (Optionnel)</label>
                  <input
                    type="text"
                    value={produitFormData.numeroSerie}
                    onChange={(e) => setProduitFormData({...produitFormData, numeroSerie: e.target.value})}
                    placeholder={produitFormData.paletteId ? "GÃ©nÃ©rÃ© depuis la palette ci-dessous" : "Saisir manuellement"}
                    disabled={!!produitFormData.paletteId}
                  />
                </div>

                {numerosSerieDisponibles.length > 0 && (
                  <div className="form-row ">
                    <label>
                      Né de SÃ©rie Disponibles
                      <span className="serial-selection-count">
                        {numerosSerieSelectionnes.length} / {produitFormData.quantite || 1} sÃ©lectionnÃ©(s)
                      </span>
                    </label>
                    <div className="inline-numeros-serie-list">
                      {numerosSerieDisponibles.map((serie, index) => {
                        const isSelected = numerosSerieSelectionnes.includes(serie.numero);
                        const maxSelection = Math.max(1, parseInt(produitFormData.quantite, 10) || 1);
                        const selectionLimitReached = !isSelected && numerosSerieSelectionnes.length >= maxSelection;

                        return (
                          <label key={serie._id || serie.numero} className={`inline-numero-serie-item ${isSelected ? 'selected' : ''} ${selectionLimitReached ? 'disabled' : ''}`}>
                            <span className="numero-index">{index + 1}.</span>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={selectionLimitReached}
                              onChange={() => handleToggleNumeroSerieSelection(serie.numero)}
                            />
                            <span className="numero-serie-value">{serie.numero}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <label>Notes / SpÃ©cifications</label>
                  <textarea
                    rows="2"
                    value={produitFormData.notes}
                    onChange={(e) => setProduitFormData({...produitFormData, notes: e.target.value})}
                    placeholder="Notes ou dÃ©tails supplÃ©mentaires sur l'affectation..."
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowEditProduitModal(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="btn-save">Enregistrer</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Détails Produit */}
        {showDetailProduitModal && selectedProduit && (
          <div className="modal-overlay-small" onClick={() => setShowDetailProduitModal(false)}>
            <div className="modal-content-small modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-small">
                <h3>Détails du Produit</h3>
                <button type="button" className="dd-modal-close-btn" onClick={() => setShowDetailProduitModal(false)}>×</button>
              </div>
              <div className="detail-produit-content">
                <div className="detail-section">
                  <h4>Informations générales</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <strong>Nom:</strong>
                      <span>{selectedProduit.produitId?.nomProduit}</span>
                    </div>
                    <div className="info-item">
                      <strong>Référence:</strong>
                      <span>{selectedProduit.produitId?.reference}</span>
                    </div>
                    <div className="info-item">
                      <strong>Quantité:</strong>
                      <span>{selectedProduit.quantite}</span>
                    </div>
                    <div className="info-item">
                      <strong>Prix unitaire:</strong>
                      <span>{formatCurrency(selectedProduit.prixUnitaire)}</span>
                    </div>
                    {getProduitPaletteNumber(selectedProduit) && (
                      <div className="info-item">
                        <strong>Num palette:</strong>
                        <span>{getProduitPaletteNumber(selectedProduit)}</span>
                      </div>
                    )}
                    {getProduitNumerosSerie(selectedProduit).length > 0 && (
                      <div className="info-item">
                        <strong>Numéro(s) de série:</strong>
                        <span>{getProduitNumerosSerie(selectedProduit).join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Détails du Dossier</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <strong>Référence STEG:</strong>
                      <span>{dossier.referenceSteg}</span>
                    </div>
                    <div className="info-item">
                      <strong>État Dossier:</strong>
                      <span>{dossier.etatDossier}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}



        {/* Modal Confirmation Annulation Acompte (DetailDossier) */}
        {showAcompteCancelModal && (
          <div className="modal-overlay-small" style={{ zIndex: 1100 }} onClick={() => setShowAcompteCancelModal(false)}>
            <div className="modal-content-small" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header-small" style={{ background: '#fef2f2', borderBottom: '1px solid #fee2e2' }}>
                <h3 style={{ color: '#991b1b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AlertCircle size={20} color="#dc2626" />
                  Confirmation d'annulation de l'acompte
                </h3>
                <button type="button" className="dd-modal-close-btn" onClick={() => setShowAcompteCancelModal(false)}>&times;</button>
              </div>
              <div style={{ padding: '20px' }}>
                <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#9b2c2c' }}>
                  <p style={{ margin: 0, fontWeight: 'bold' }}>Un acompte actif existe sur ce dossier :</p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>
                    Montant : <strong>{acompteToCancel ? formatCurrency(acompteToCancel.montant) : ''}</strong>
                  </p>
                </div>

                <p style={{ fontSize: '14px', color: '#4a5568', marginBottom: '12px' }}>
                  Pour programmer ou reprogrammer cet échéancier, vous devez obligatoirement confirmer l'annulation de cet acompte et indiquer une raison traçable.
                </p>

                <div className="dd-credit-config-row">
                  <label style={{ fontWeight: 600, fontSize: '13px' }}>
                    Raison de l'annulation <span style={{ color: '#e53e3e' }}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Reprogrammation globale de l'échéancier..."
                    value={raisonAnnulationAcompte}
                    onChange={(e) => setRaisonAnnulationAcompte(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e0', marginTop: '4px' }}
                  />
                </div>

                <div className="dd-credit-config-actions" style={{ marginTop: '20px' }}>
                  <button type="button" className="dd-credit-config-cancel" onClick={() => setShowAcompteCancelModal(false)}>
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="dd-credit-config-save"
                    style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                    onClick={(e) => handleConfigurerEcheancier(e, true)}
                    disabled={!raisonAnnulationAcompte.trim()}
                  >
                    Confirmer et annuler l'acompte
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}



        {/* Modal Modifier Échéance Modernisé */}
        {showEditEcheanceModal && (() => {
          if (isPaiementEcheanceMaintenanceActive) {
            return (
              <div className="modal-overlay-small" onClick={() => {setShowEditEcheanceModal(false); setEditingEcheanceIndex(null);}}>
                <div
                  className="modal-content-small"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    maxWidth: '600px',
                    width: '95%',
                    borderRadius: '16px',
                    padding: '32px 28px',
                    background: 'linear-gradient(135deg, #fffdf5 0%, #ffffff 100%)',
                    border: '2px solid #f59e0b',
                    boxShadow: '0 20px 40px -10px rgba(15, 23, 42, 0.25)',
                    textAlign: 'center'
                  }}
                >
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: '#fef3c7',
                    color: '#b45309',
                    border: '1px solid #fde047',
                    padding: '5px 14px',
                    borderRadius: '20px',
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    marginBottom: '16px'
                  }}>
                    <Wrench size={14} />
                    <span>Maintenance en cours</span>
                  </div>

                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justify: 'center',
                    margin: '0 auto 16px auto',
                    boxShadow: '0 8px 20px rgba(245, 158, 11, 0.3)'
                  }}>
                    <Wrench size={32} />
                  </div>

                  <h3 style={{ margin: '0 0 10px 0', color: '#0f172a', fontSize: '1.35rem', fontWeight: 800 }}>
                    Fonctionnalité temporairement indisponible
                  </h3>

                  <p style={{ margin: '0 auto 24px auto', color: '#475569', fontSize: '0.94rem', lineHeight: '1.6' }}>
                    Cette fonctionnalité est actuellement en cours de maintenance et de vérification par l’équipe IT afin d’améliorer la fiabilité des calculs financiers. Elle sera de nouveau disponible prochainement.
                  </p>

                  <button
                    type="button"
                    onClick={() => {
                      setShowEditEcheanceModal(false);
                      setEditingEcheanceIndex(null);
                    }}
                    style={{
                      padding: '10px 24px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      background: '#ffffff',
                      color: '#475569',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            );
          }

          const syntheseFinanciere = computeSyntheseFinanciere(dossier);
          const targetEcheance = editingEcheanceIndex !== null && Array.isArray(dossier?.autofinancement?.echeancier)
            ? dossier.autofinancement.echeancier[editingEcheanceIndex]
            : null;

          const oldMontant = Math.max(0, Number(targetEcheance?.montant) || 0);
          const oldMontantPaye = Math.max(0, Number(targetEcheance?.montantPaye) || 0);
          const oldRemaining = Math.max(0, oldMontant - oldMontantPaye);
          const maxCapaciteForThisEcheance = Math.max(0, oldRemaining + syntheseFinanciere.totalRestantAConfigurer);

          const inputMontantNum = Math.max(0, Number(echeanceEditFormData.montant) || 0);
          const delta = inputMontantNum - oldRemaining;
          const isOverflow = delta > (syntheseFinanciere.totalRestantAConfigurer + 0.009);
          const soldeApresOperation = Math.max(0, syntheseFinanciere.totalRestantAConfigurer - delta);

          return (
            <div className="modal-overlay-small" onClick={() => {setShowEditEcheanceModal(false); setEditingEcheanceIndex(null);}}>
              <div
                className="modal-content-small"
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxWidth: '720px',
                  width: '95%',
                  borderRadius: '16px',
                  padding: '24px',
                  background: '#ffffff',
                  boxShadow: '0 20px 40px -10px rgba(15, 23, 42, 0.25)',
                  border: '1px solid #e2e8f0'
                }}
              >
                {/* En-tête */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1px solid #f1f5f9', paddingBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '42px', height: '42px', borderRadius: '10px',
                      background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                      color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1.25rem', boxShadow: '0 4px 10px rgba(2, 132, 199, 0.3)'
                    }}>
                      <Sliders size={20} />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.2rem', fontWeight: 800 }}>
                        Modifier l'échéance {editingEcheanceIndex !== null ? `#${editingEcheanceIndex + 1}` : ''}
                      </h3>
                      <p style={{ margin: '2px 0 0 0', color: '#64748b', fontSize: '0.84rem' }}>
                        Ajustement du montant, de la date d'échéance et du statut
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="dd-modal-close-btn"
                    onClick={() => {setShowEditEcheanceModal(false); setEditingEcheanceIndex(null);}}
                    style={{ fontSize: '1.5rem', cursor: 'pointer', background: 'none', border: 'none', color: '#64748b' }}
                  >
                    ×
                  </button>
                </div>

                {editingEcheancePayee && (
                  <div style={{
                    background: '#fffbe7', border: '1px solid #fde047', borderLeft: '4px solid #f59e0b',
                    borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px'
                  }}>
                    <Lock size={20} color="#d97706" />
                    <span style={{ fontSize: '0.86rem', color: '#78350f', fontWeight: 600 }}>
                      Échéance totalement payée : La date, le montant et le statut sont verrouillés pour protéger l'historique financier.
                    </span>
                  </div>
                )}

                {/* Section 1 : Situation financière (6 KPI Cards) */}
                <div style={{
                  margin: '0 0 18px 0',
                  padding: '14px 16px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                  gap: '10px'
                }}>
                  <div>
                    <span style={{ fontSize: '0.73rem', color: '#64748b', fontWeight: 600, display: 'block' }}>Montant total dossier</span>
                    <strong style={{ fontSize: '0.92rem', color: '#1e293b', fontWeight: 700 }}>{formatCurrency(syntheseFinanciere.montantTotalDossier)}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.73rem', color: '#059669', fontWeight: 600, display: 'block' }}>Total déjà payé</span>
                    <strong style={{ fontSize: '0.92rem', color: '#059669', fontWeight: 700 }}>{formatCurrency(syntheseFinanciere.totalDejaPaye)}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.73rem', color: '#d97706', fontWeight: 600, display: 'block' }}>Total configuré</span>
                    <strong style={{ fontSize: '0.92rem', color: '#d97706', fontWeight: 700 }}>{formatCurrency(syntheseFinanciere.totalConfigure)}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.73rem', color: '#2563eb', fontWeight: 600, display: 'block' }}>Restant à configurer</span>
                    <strong style={{ fontSize: '0.92rem', color: '#2563eb', fontWeight: 700 }}>{formatCurrency(syntheseFinanciere.totalRestantAConfigurer)}</strong>
                  </div>
                  <div style={{ background: '#e0f2fe', padding: '6px 10px', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                    <span style={{ fontSize: '0.73rem', color: '#0369a1', fontWeight: 700, display: 'block' }}>Capacité max échéance</span>
                    <strong style={{ fontSize: '0.96rem', color: '#0284c7', fontWeight: 800 }}>{formatCurrency(maxCapaciteForThisEcheance)}</strong>
                  </div>
                  <div style={{ background: isOverflow ? '#fef2f2' : '#ecfdf5', padding: '6px 10px', borderRadius: '8px', border: `1px solid ${isOverflow ? '#fca5a5' : '#a7f3d0'}` }}>
                    <span style={{ fontSize: '0.73rem', color: isOverflow ? '#b91c1c' : '#047857', fontWeight: 700, display: 'block' }}>Solde après opération</span>
                    <strong style={{ fontSize: '0.96rem', color: isOverflow ? '#dc2626' : '#047857', fontWeight: 800 }}>
                      {isOverflow ? 'Dépassement !' : formatCurrency(soldeApresOperation)}
                    </strong>
                  </div>
                </div>

                {/* Banner avertissement si partiellement_paye */}
                {oldMontantPaye > 0 && (
                  <div style={{
                    background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px',
                    padding: '10px 14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px'
                  }}>
                    <Info size={18} color="#0284c7" />
                    <span style={{ fontSize: '0.84rem', color: '#0369a1', fontWeight: 600 }}>
                      Échéance partiellement payée : {formatCurrency(oldMontantPaye)} ont déjà été versés. Le nouveau montant ne peut pas être inférieur à {formatCurrency(oldMontantPaye)}.
                    </span>
                  </div>
                )}

                {/* Banner Avertissement Dépassement */}
                {isOverflow && (
                  <div style={{
                    background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px',
                    padding: '10px 14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px'
                  }}>
                    <AlertCircle size={18} color="#dc2626" />
                    <span style={{ fontSize: '0.84rem', color: '#991b1b', fontWeight: 700 }}>
                      Attention : Le montant saisi ({formatCurrency(inputMontantNum)}) dépasse la capacité maximale disponible ({formatCurrency(maxCapaciteForThisEcheance)}).
                    </span>
                  </div>
                )}

                {/* Formulaire de saisie */}
                <form onSubmit={handleUpdateEcheance} className="form-modal">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '16px' }}>
                    {/* Date échéance */}
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', marginBottom: '4px' }}>
                        <Calendar size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Date d'échéance *
                      </label>
                      <input
                        type="date"
                        value={echeanceEditFormData.dateEcheance}
                        onChange={(e) => {
                          const newDateStr = e.target.value;
                          let currentStatut = echeanceEditFormData.statut;
                          if (newDateStr) {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const selectedDate = new Date(newDateStr);
                            if (!Number.isNaN(selectedDate.getTime())) {
                              selectedDate.setHours(0, 0, 0, 0);
                              if (selectedDate >= today && currentStatut === 'retard') {
                                currentStatut = 'en_attente';
                              }
                            }
                          }
                          setEcheanceEditFormData({
                            ...echeanceEditFormData,
                            dateEcheance: newDateStr,
                            statut: currentStatut
                          });
                        }}
                        disabled={editingEcheancePayee}
                        required
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                      />
                    </div>

                    {/* Montant */}
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', marginBottom: '4px' }}>
                        <DollarSign size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Montant (TND) *
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          step="any"
                          min={oldMontantPaye > 0 ? oldMontantPaye : 0}
                          max={maxCapaciteForThisEcheance}
                          value={echeanceEditFormData.montant}
                          onChange={(e) => setEcheanceEditFormData({...echeanceEditFormData, montant: normalizeDecimalInput(e.target.value)})}
                          disabled={editingEcheancePayee}
                          required
                          style={{
                            width: '100%', padding: '8px 12px', borderRadius: '8px 0 0 8px',
                            border: `1px solid ${isOverflow ? '#ef4444' : '#cbd5e1'}`, fontSize: '0.9rem'
                          }}
                        />
                        <span style={{ padding: '8px 12px', background: '#e2e8f0', border: '1px solid #cbd5e1', borderLeft: 'none', borderRadius: '0 8px 8px 0', fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>TND</span>
                      </div>
                      <span style={{ fontSize: '0.74rem', color: '#64748b', marginTop: '3px', display: 'block' }}>
                        Max disponible: <strong>{formatCurrency(maxCapaciteForThisEcheance)}</strong>
                      </span>
                    </div>

                    {/* Statut */}
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', marginBottom: '4px' }}>
                        <ShieldCheck size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Statut *
                      </label>
                      <select
                        value={echeanceEditFormData.statut}
                        onChange={(e) => setEcheanceEditFormData({...echeanceEditFormData, statut: e.target.value})}
                        disabled={editingEcheancePayee}
                        required
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                      >
                        <option value="en_attente">En attente</option>
                        <option value="retard">En retard</option>
                        <option value="paye">Payé</option>
                      </select>
                    </div>

                    {/* Date paiement si statut paye */}
                    {echeanceEditFormData.statut === 'paye' && (
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', marginBottom: '4px' }}>
                          <Clock size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Date paiement
                        </label>
                        <input
                          type="date"
                          value={echeanceEditFormData.datePaiement}
                          onChange={(e) => setEcheanceEditFormData({...echeanceEditFormData, datePaiement: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', marginBottom: '4px' }}>
                      <FileText size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Remarques / Notes
                    </label>
                    <textarea
                      value={echeanceEditFormData.notes}
                      onChange={(e) => setEcheanceEditFormData({...echeanceEditFormData, notes: e.target.value})}
                      rows="2"
                      placeholder="Informations supplémentaires ou remarques..."
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                    />
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '14px', borderTop: '1px solid #e2e8f0' }}>
                    <button
                      type="button"
                      onClick={() => {setShowEditEcheanceModal(false); setEditingEcheanceIndex(null);}}
                      style={{
                        padding: '9px 18px', borderRadius: '8px', border: '1px solid #cbd5e1',
                        background: '#ffffff', color: '#475569', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer'
                      }}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={isOverflow || editingEcheancePayee}
                      style={{
                        padding: '9px 22px', borderRadius: '8px', border: 'none',
                        background: (isOverflow || editingEcheancePayee)
                          ? '#cbd5e1'
                          : 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                        color: '#ffffff', fontWeight: 700, fontSize: '0.88rem', cursor: (isOverflow || editingEcheancePayee) ? 'not-allowed' : 'pointer',
                        boxShadow: (isOverflow || editingEcheancePayee) ? 'none' : '0 4px 10px rgba(2, 132, 199, 0.3)'
                      }}
                    >
                      ✓ Enregistrer les modifications
                    </button>
                  </div>
                </form>
              </div>
            </div>
          );
        })()}



        {showDeletePaiementModal && paiementToDelete && (
          <div className="modal-overlay-small" onClick={handleCloseDeletePaiementModal}>
            <div className="modal-content-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-small">
                <h3>Confirmer la suppression du paiement</h3>
                <button type="button" className="dd-modal-close-btn" onClick={handleCloseDeletePaiementModal}>×</button>
              </div>

              <div className="dd-delete-alert-box">
                <div className="dd-delete-alert-title">Attention - Action sensible</div>
                <p className="dd-delete-alert-text">
                  Vous êtes sur le point de supprimer ce paiement. Cette action est journalisée et auditable.
                </p>
                <ul className="dd-delete-alert-list">
                  <li>Le paiement restera visible dans l'historique avec le statut Supprimé.</li>
                  <li>Le montant sera réintégré automatiquement au restant à payer.</li>
                  <li>L'utilisateur et la date de suppression seront enregistrés.</li>
                </ul>
                <div className="dd-delete-alert-meta">
                  <div><strong>Montant:</strong> {formatCurrency(paiementToDelete.montant)}</div>
                  <div><strong>Date paiement:</strong> {formatDate(paiementToDelete.datePaiement)}</div>
                  <div><strong>Réf dossier:</strong> {dossier?.referenceSteg || '-'}</div>
                </div>
              </div>

              <div className="form-row" style={{marginTop: '14px'}}>
                <label>Saisissez <strong>SUPPRIMER</strong> pour confirmer</label>
                <input
                  type="text"
                  value={deletePaiementConfirmText}
                  onChange={(e) => setDeletePaiementConfirmText(e.target.value)}
                  placeholder="SUPPRIMER"
                />
              </div>

              <div className="modal-actions" style={{marginTop: 0}}>
                <button type="button" className="btn-cancel" onClick={handleCloseDeletePaiementModal}>
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn-delete"
                  onClick={handleConfirmDeletePaiement}
                  disabled={deletePaiementConfirmText.trim().toUpperCase() !== 'SUPPRIMER'}
                >
                  Confirmer la suppression
                </button>
              </div>
            </div>
          </div>
        )}



        {/* Modal Transfert de Produits */}
        {showTransfertModal && (
          <div className="modal-overlay-small" onClick={() => setShowTransfertModal(false)}>
            <div className="modal-content-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-small">
                <h3>Transférer des produits</h3>
                <button type="button" className="dd-modal-close-btn" onClick={() => setShowTransfertModal(false)}>&times;</button>
              </div>
              <div style={{padding: '20px'}}>
                <p style={{marginBottom: '15px', color: '#6b7280'}}>
                  {selectedProduitsIds.length} produit(s) sélectionné(s)
                </p>

                <div style={{marginBottom: '20px'}}>
                  <label style={{display: 'block', marginBottom: '8px', fontWeight: '500'}}>
                    Référence STEG du dossier de destination:
                  </label>
                  <div style={{display: 'flex', gap: '10px'}}>
                    <input
                      type="text"
                      value={refStegRecherche}
                      onChange={(e) => setRefStegRecherche(e.target.value)}
                      placeholder="Entrez la référence STEG"
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '5px'
                      }}
                    />
                    <button
                      onClick={handleRechercherClientParRefSTEG}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer'
                      }}
                    >
                      🔍 Rechercher
                    </button>
                  </div>
                </div>

                {clientDestination && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #86efac',
                    borderRadius: '5px',
                    marginBottom: '20px'
                  }}>
                    <p style={{margin: 0, fontWeight: '500', color: '#15803d'}}>
                      ✓ Client trouvé
                    </p>
                    <p style={{margin: '8px 0 0 0', color: '#166534'}}>
                      <strong>Nom:</strong> {clientDestination.clientNom} {clientDestination.clientPrenom || ''}
                    </p>
                    <p style={{margin: '4px 0 0 0', color: '#166534'}}>
                      <strong>Dossier:</strong> {clientDestination.referenceSteg}
                    </p>
                  </div>
                )}

                <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                  <button
                    onClick={() => {
                      setShowTransfertModal(false);
                      setRefStegRecherche('');
                      setClientDestination(null);
                    }}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleTransfererProduits}
                    disabled={!clientDestination}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: clientDestination ? '#10b981' : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: clientDestination ? 'pointer' : 'not-allowed'
                    }}
                  >
                    ✓ Valider le transfert
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== MODALE NOTIFICATION STEG ===== */}
        {showStegModal && dossier && (
          <div
            style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)'}}
            onClick={() => !stegSending && setShowStegModal(false)}
          >
            <div
              style={{background: '#fff', borderRadius: '16px', width: '640px', maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 70px rgba(0,0,0,0.35)'}}
              onClick={e => e.stopPropagation()}
            >
              {/* ─ En-tête ─────────────────────────────────────────────────── */}
              <div style={{background: 'linear-gradient(135deg,#1e3a5f,#2d6a9f)', padding: '20px 24px', borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <div>
                  <h2 style={{color: '#fff', margin: 0, fontSize: '17px', fontWeight: 700}}>📧 Notification bureau STEG</h2>
                  <p style={{color: '#a8d4f5', margin: '4px 0 0', fontSize: '13px'}}>
                    {dossier.district?.region || "—"} · Réf. {dossier.referenceSteg} · {dossier.district?.adresseMail || "email non configuré"}
                  </p>
                </div>
                <button onClick={() => setShowStegModal(false)} disabled={stegSending}
                  style={{background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16}}>✕</button>
              </div>

              {/* ─ Onglets ──────────────────────────────────────────────────── */}
              <div style={{display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', padding: '0 24px'}}>
                {[
                  {key: 'compose', label: "✎ Composer"},
                  {key: 'documents', label: `📎 Documents${selectedArchiveIds.length > 0 ? ' (' + selectedArchiveIds.length + ')' : ''}`},
                ].map(tab => (
                  <button key={tab.key} onClick={() => setStegModalTab(tab.key)}
                    style={{
                      padding: '12px 18px', border: 'none', background: 'none', cursor: 'pointer',
                      fontSize: '13px', fontWeight: stegModalTab === tab.key ? 700 : 500,
                      color: stegModalTab === tab.key ? '#2d6a9f' : '#64748b',
                      borderBottom: stegModalTab === tab.key ? '2.5px solid #2d6a9f' : '2.5px solid transparent',
                      transition: 'all 0.15s'
                    }}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* ─ Corps scrollable ─────────────────────────────────────────── */}
              <div style={{padding: '20px 24px', overflowY: 'auto', flex: 1}}>
                
                {stegLocalError && (
                  <div style={{
                    marginBottom: '16px', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fee2e2', 
                    borderRadius: '8px', color: '#991b1b', fontSize: '13px', fontWeight: 500, display: 'flex', gap: '10px', alignItems: 'center'
                  }}>
                    <span style={{fontSize: '18px'}}>⚠️</span>
                    {stegLocalError}
                  </div>
                )}

                {/* ─── Onglet COMPOSER ─── */}
                {stegModalTab === 'compose' && (
                  <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>

                    {/* Sélecteur de template */}
                    <div>
                      <label style={{display: 'block', fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                        Modèle d'email
                      </label>
                      <select
                        value={emailTemplate}
                        onChange={e => handleTemplateChange(e.target.value)}
                        style={{width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '14px', color: '#1e3a5f', background: '#fff', cursor: 'pointer', outline: 'none'}}
                      >
                        <option value="depot">📄 Notification de Dépôt Dossier Photovoltaïque</option>
                        <option value="rappel">🔔 Rappel — Dossier en attente de traitement</option>
                      </select>
                      <p style={{margin: '5px 0 0', fontSize: '11px', color: '#94a3b8'}}>
                        Les variables <code style={{background: '#f1f5f9', padding: '1px 5px', borderRadius: 3}}>{'{{referenceSteg}}'}</code>,{' '}
                        <code style={{background: '#f1f5f9', padding: '1px 5px', borderRadius: 3}}>{'{{nomComplet}}'}</code>,{' '}
                        <code style={{background: '#f1f5f9', padding: '1px 5px', borderRadius: 3}}>{'{{dateDepotSteg}}'}</code>{' '}
                        sont remplacées automatiquement.
                      </p>
                    </div>

                    {/* Objet */}
                    <div>
                      <label style={{display: 'block', fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                        Objet de l'email
                      </label>
                      <input
                        type="text"
                        value={emailSubject}
                        onChange={e => setEmailSubject(e.target.value)}
                        placeholder="Objet du message..."
                        style={{width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '14px', color: '#1e293b', outline: 'none', boxSizing: 'border-box'}}
                      />
                    </div>

                    {/* Corps du message */}
                    <div>
                      <label style={{display: 'block', fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                        Corps du message
                      </label>
                      <textarea
                        value={emailBody}
                        onChange={e => setEmailBody(e.target.value)}
                        rows={8}
                        placeholder="Saisissez le corps du message..."
                        style={{width: '100%', padding: '10px 12px', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '14px', color: '#1e293b', resize: 'vertical', outline: 'none', fontFamily: 'inherit', lineHeight: 1.6, boxSizing: 'border-box'}}
                      />
                      <p style={{margin: '4px 0 0', fontSize: '11px', color: '#94a3b8'}}>
                        Les informations structurées du client et du dossier seront automatiquement ajoutées sous ce message.
                      </p>
                    </div>

                    {/* Aperçu destinataire */}
                    <div style={{background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#065f46', display: 'flex', gap: '10px', alignItems: 'center'}}>
                      <span style={{fontSize: '18px'}}>📧</span>
                      <div>
                        <strong>Destinataire :</strong> {dossier.district?.adresseMail || <span style={{color: '#dc2626'}}>✕ Email non configuré pour ce bureau STEG</span>}
                        {selectedArchiveIds.length > 0 && <span style={{marginLeft: '12px', background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600}}>
                          + {selectedArchiveIds.length} PJ
                        </span>}
                      </div>
                    </div>
                  </div>
                )}

                {/* ─ Onglet DOCUMENTS ─ */}
                {stegModalTab === 'documents' && (
                  <div>
                    <p style={{margin: '0 0 14px', fontSize: '13px', color: '#64748b', lineHeight: 1.6}}>
                      Sélectionnez les documents de l'archive client à joindre à l'email.
                    </p>

                    {client?.archives?.length > 0 ? (
                      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        {/* Tout sélectionner */}
                        <label style={{display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '6px', background: '#f1f5f9', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#334155'}}>
                          <input type="checkbox"
                            style={{width: 16, height: 16, cursor: 'pointer', accentColor: '#2d6a9f'}}
                            checked={selectedArchiveIds.length === client.archives.length && client.archives.length > 0}
                            onChange={() => {
                              if (selectedArchiveIds.length === client.archives.length) setSelectedArchiveIds([]);
                              else setSelectedArchiveIds(client.archives.map(a => a._id));
                            }}
                          />
                          Tout sélectionner ({client.archives.length} document{client.archives.length > 1 ? 's' : ''})
                        </label>

                        {client.archives.map(archive => {
                          const isChecked = selectedArchiveIds.includes(archive._id);
                          const typeIcon = {pdf: "📄", image: "🖼️", document: "📝", contrat: "📋", facture: "🧾", autre: "📁"}[archive.type] || "📁";
                          return (
                            <label key={archive._id} style={{display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', borderRadius: '8px', cursor: 'pointer', border: isChecked ? '1.5px solid #2d6a9f' : '1.5px solid #e2e8f0', background: isChecked ? '#eff6ff' : '#fff', transition: 'all 0.15s'}}>
                              <input type="checkbox" checked={isChecked} onChange={() => toggleArchiveSelection(archive._id)}
                                style={{width: 16, height: 16, cursor: 'pointer', accentColor: '#2d6a9f'}} />
                              <span style={{fontSize: '20px'}}>{typeIcon}</span>
                              <div style={{flex: 1, minWidth: 0}}>
                                <div style={{fontSize: '13px', fontWeight: 600, color: '#1e3a5f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{archive.nom}</div>
                                <div style={{fontSize: '11px', color: '#94a3b8', marginTop: '2px'}}>
                                  {archive.type}{archive.description ? ' · ' + archive.description : ''} · {new Date(archive.dateAjout).toLocaleDateString('fr-FR')}
                                </div>
                              </div>
                              {isChecked && <span style={{color: '#2d6a9f', fontSize: '16px'}}>✓</span>}
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{textAlign: 'center', padding: '32px', color: '#94a3b8', background: '#f8fafc', borderRadius: '8px'}}>
                        <div style={{fontSize: '40px', marginBottom: '10px'}}>📁</div>
                        <p style={{margin: 0, fontWeight: 600}}>Aucun document dans les archives</p>
                        <p style={{margin: '6px 0 0', fontSize: '12px'}}>L'email sera envoyé sans pièce jointe.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ─ Pied de la modale ────────────────────────────────────────── */}
              <div style={{padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '10px', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderRadius: '0 0 16px 16px'}}>
                <div style={{fontSize: '12px', color: '#94a3b8'}}>
                  {selectedArchiveIds.length > 0 &&
                    <span style={{color: '#2d6a9f', fontWeight: 600}}>📎 {selectedArchiveIds.length} PJ sélectionnée{selectedArchiveIds.length > 1 ? 's' : ''}</span>
                  }
                </div>
                <div style={{display: 'flex', gap: '10px'}}>
                  <button onClick={() => setShowStegModal(false)} disabled={stegSending}
                    style={{padding: '9px 20px', background: '#fff', border: '1.5px solid #d1d5db', color: '#374151', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '14px'}}>
                    Annuler
                  </button>
                  <button onClick={handleNotifierSteg} disabled={stegSending || !dossier.district?.adresseMail}
                    style={{
                      padding: '9px 22px',
                      background: (stegSending || !dossier.district?.adresseMail) ? '#9ca3af' : 'linear-gradient(135deg,#f59e0b,#d97706)',
                      color: '#fff', border: 'none', borderRadius: '8px',
                      cursor: (stegSending || !dossier.district?.adresseMail) ? 'not-allowed' : 'pointer',
                      fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px',
                      boxShadow: stegSending ? 'none' : '0 4px 12px rgba(245,158,11,0.4)'
                    }}>
                    {stegSending ? "⏳ Envoi..." : `📧 Envoyer${selectedArchiveIds.length > 0 ? ' + ' + selectedArchiveIds.length + ' PJ' : ''}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* MODALE DE RÉAFFECTATION DE DOSSIER */}
      {showReassignModal && (
        <div className="modal-overlay">
          <div className="modal-content reassign-modal">
            <div className="modal-header">
              <div className="modal-title-with-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 3h5v5M4 20L20.2 3.8M21 16v5h-5M15 15l5.1 5.1M4 4l5 5"/>
                </svg>
                <h3>R\u00E9affecter le Dossier</h3>
              </div>
              <button className="close-button" onClick={() => setShowReassignModal(false)}>\u00D7</button>
            </div>
            
            <div className="modal-body">
              <p className="modal-description">
                Transf\u00E9rez ce dossier vers un autre client existant dans la base.
              </p>

              <div className="reassign-search-box">
                <label>Saisir le CIN du client destinataire :</label>
                <div className="search-input-group">
                  <input
                    type="text"
                    value={targetCIN}
                    onChange={(e) => setTargetCIN(e.target.value)}
                    placeholder="CIN (8 chiffres)"
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchTargetClient()}
                  />
                  <button 
                    onClick={handleSearchTargetClient} 
                    disabled={searchLoading}
                    className="btn-search-client"
                  >
                    {searchLoading ? 'Recherche...' : 'V\u00E9rifier'}
                  </button>
                </div>
              </div>

              {reassignClientTarget && (
                <div className="target-client-info-card animate-fade-in">
                  <h4>Client destinataire identifi\u00E9 :</h4>
                  <div className="info-grid-small">
                    <div className="info-row-small">
                      <span className="label-small">Nom Complet:</span>
                      <span className="value-small">{reassignClientTarget.prenom} {reassignClientTarget.nom}</span>
                    </div>
                    <div className="info-row-small">
                      <span className="label-small">CIN:</span>
                      <span className="value-small">{reassignClientTarget.cin}</span>
                    </div>
                    <div className="info-row-small">
                      <span className="label-small">T\u00E9l\u00E9phone:</span>
                      <span className="value-small">{reassignClientTarget.telephone || '\u2014'}</span>
                    </div>
                  </div>
                  
                  <div className="reassign-confirmation-alert">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span>Confirmez-vous le transfert du dossier <strong>{dossier.referenceSteg}</strong> vers ce client ?</span>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowReassignModal(false)}>Annuler</button>
              <button 
                className="btn-reassign-confirm" 
                onClick={handleConfirmReassign} 
                disabled={!reassignClientTarget || isReassigning}
              >
                {isReassigning ? 'Transfert en cours...' : 'Confirmer le Changement'}
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Modal Ajouter Extension */}
        {showAddExtensionModal && (
          <div className="modal-overlay-small" onClick={() => setShowAddExtensionModal(false)}>
            <div className="modal-content-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-small">
                <h3>Ajouter une Extension de Dossier</h3>
                <button type="button" className="dd-modal-close-btn" onClick={() => setShowAddExtensionModal(false)}>&times;</button>
              </div>
              <form onSubmit={handleAddExtension} className="form-modal">
                <div className="info-box-modern" style={{background: '#f0f9ff', color: '#0369a1', border: '1px solid #e0f2fe', padding: '12px', borderRadius: '8px', marginBottom: '15px', fontSize: '0.9rem'}}>
                  <p style={{margin: 0}}>💰 <strong>Règle de paiement :</strong> 100% comptant uniquement. Le crédit et le mode mixte sont interdits pour les extensions.</p>
                </div>
                
                <div className="form-row">
                  <label>Nombre de panneaux à ajouter *</label>
                  <input
                    type="number"
                    min="1"
                    value={extensionFormData.nbrPanneaux}
                    onChange={(e) => setExtensionFormData({...extensionFormData, nbrPanneaux: parseInt(e.target.value) || 0})}
                    required
                  />
                </div>
                <div className="form-row">
                  <label>Puissance ajoutée (kWc) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.1"
                    value={extensionFormData.puissanceAjoutee}
                    onChange={(e) => setExtensionFormData({...extensionFormData, puissanceAjoutee: parseFloat(e.target.value) || 0})}
                    required
                  />
                </div>
                <div className="form-row">
                  <label>Coût de l'extension (TND) *</label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={extensionFormData.coutExtension}
                    onChange={(e) => setExtensionFormData({...extensionFormData, coutExtension: parseFloat(e.target.value) || 0})}
                    required
                  />
                </div>

                <div className="modal-actions" style={{marginTop: '20px'}}>
                  <button type="button" className="btn-cancel" onClick={() => setShowAddExtensionModal(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="btn-save">Confirmer la demande</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de suppression sensible avec contrôle global de sécurité */}
        <SensitiveDeleteModal
          isOpen={showSensitiveDeleteModal}
          onClose={() => setShowSensitiveDeleteModal(false)}
          onConfirm={handleConfirmSensitiveDelete}
          itemType={deleteTarget?.itemType || 'dossier'}
          itemName={deleteTarget?.name || ''}
          deletionConfig={deletionConfig[deleteTarget?.itemType] || { enabled: true, mode: 'SOFT_DELETE' }}
          loading={deleteLoading}
        />

      </div>
    </div>
  );
};

export default DetailDossier;


